import e$3 from "react-dom";
import * as React from "react";
import React__default, { createContext, useState, useContext, useRef, useCallback, useEffect, useMemo, version, useLayoutEffect as useLayoutEffect$1 } from "react";
import Reconciler from "react-reconciler";
const supportedEventAttributes = [
  "onMouseDown",
  "onMouseEnter",
  "onMouseLeave",
  "onMouseMove",
  "onMouseOut",
  "onMouseOver",
  "onMouseUp",
  "onTouchCancel",
  "onTouchEnd",
  "onTouchMove",
  "onTouchStart",
  "onPointerDown",
  "onPointerMove",
  "onPointerUp",
  "onPointerCancel",
  "onPointerEnter",
  "onPointerLeave",
  "onPointerOver",
  "onPointerOut",
  "onScroll",
  "onWheel",
  "onClick",
  "onDragStart",
  "onDragEnd",
  "onDragEnter",
  "onDragExit",
  "onDrag",
  "onDragOver"
];
function createDefaultEventMap() {
  return supportedEventAttributes.reduce((acc, next) => {
    acc[next] = [];
    return acc;
  }, {});
}
const supportedEventMap = supportedEventAttributes.reduce((acc, ev) => {
  acc[ev.slice(2).toLowerCase()] = ev;
  acc[ev] = ev;
  return acc;
}, {});
const hidePointsOutsideRegion = (points, target, buffer) => {
  const len = points.length;
  const ret = buffer ? buffer : dna(len);
  for (let index = 0; index < len; index++) {
    ret[index] = index % 5 === 0 ? points[index + 1] < target[3] && points[index + 3] > target[1] && points[index + 2] < target[4] && points[index + 4] > target[2] ? 1 : 0 : points[index];
  }
  return ret;
};
function dna(buffer, byteOffset, length) {
  return new Float32Array(buffer, byteOffset, length);
}
const getIntersection = (boxA, boxB, buffer) => {
  const intersects = boxA[1] <= boxB[3] && boxA[3] >= boxB[1] && boxA[2] <= boxB[4] && boxA[4] >= boxB[2];
  const mem = buffer ? buffer : dna(5);
  if (!intersects) {
    mem[0] = 0;
    mem[1] = 0;
    mem[2] = 0;
    mem[3] = 0;
    mem[4] = 0;
    return mem;
  }
  mem[0] = 1;
  mem[1] = Math.max(boxA[1], boxB[1]);
  mem[2] = Math.max(boxA[2], boxB[2]);
  mem[3] = Math.min(boxA[3], boxB[3]);
  mem[4] = Math.min(boxA[4], boxB[4]);
  return mem;
};
const dnaLength = (points) => points.length / 5;
const compose = (transformA, transformB, buffer) => {
  const mem = buffer ? buffer : dna(9);
  mem[0] = transformA[0] * transformB[0] + transformA[1] * transformB[3] + transformA[2] * transformB[6];
  mem[1] = transformA[0] * transformB[1] + transformA[1] * transformB[4] + transformA[2] * transformB[7];
  mem[2] = transformA[0] * transformB[2] + transformA[1] * transformB[5] + transformA[2] * transformB[8];
  mem[3] = transformA[3] * transformB[0] + transformA[4] * transformB[3] + transformA[5] * transformB[6];
  mem[4] = transformA[3] * transformB[1] + transformA[4] * transformB[4] + transformA[5] * transformB[7];
  mem[5] = transformA[3] * transformB[2] + transformA[4] * transformB[5] + transformA[5] * transformB[8];
  mem[6] = transformA[6] * transformB[0] + transformA[7] * transformB[3] + transformA[8] * transformB[6];
  mem[7] = transformA[6] * transformB[1] + transformA[7] * transformB[4] + transformA[8] * transformB[7];
  mem[8] = transformA[6] * transformB[2] + transformA[7] * transformB[5] + transformA[8] * transformB[8];
  return mem;
};
class DnaFactory {
  constructor(length) {
    this.index = 0;
    this.length = length;
    this.points = dna(this.length);
  }
  static grid(columns = 1, rows = 1) {
    return new DnaFactory(5 * columns * rows);
  }
  static point(x2, y2) {
    const point = dna(5);
    point[0] = 1;
    point[1] = x2;
    point[2] = y2;
    point[3] = x2;
    point[4] = y2;
    return point;
  }
  static positionPair(positionPair) {
    const pair = dna(5);
    pair[0] = 1;
    pair[1] = positionPair.x1;
    pair[2] = positionPair.y1;
    pair[3] = positionPair.x2;
    pair[4] = positionPair.y2;
    return pair;
  }
  static projection(projection) {
    return DnaFactory.singleBox(projection.width, projection.height, projection.x, projection.y);
  }
  static singleBox(width, height, x2 = 0, y2 = 0) {
    const box = dna(5);
    box[0] = 1;
    box[1] = x2;
    box[2] = y2;
    box[3] = width + x2;
    box[4] = height + y2;
    return box;
  }
  row(func) {
    return func(this);
  }
  addPoints(x1, y1, x2, y2) {
    this.points[this.index] = 1;
    this.points[1 + this.index] = x1;
    this.points[2 + this.index] = y1;
    this.points[3 + this.index] = x2;
    this.points[4 + this.index] = y2;
    this.index += 5;
    return this;
  }
  addBox(x2, y2, width, height) {
    this.addPoints(x2, y2, x2 + width, y2 + height);
    return this;
  }
  build() {
    return this.points;
  }
}
const mutate = (points, transformation) => {
  const len = points.length;
  for (let index = 0; index < len; index++) {
    points[index] = index % 5 === 0 ? transformation[6] * points[index + 1] + transformation[7] * points[index + 2] + transformation[8] * points[index] : index % 5 % 2 === 1 ? transformation[0] * points[index] + transformation[1] * points[index + 1] + transformation[2] : transformation[3] * points[index - 1] + transformation[4] * points[index] + transformation[5];
  }
  return points;
};
const scale = (factor) => {
  const scaleTransform = dna(9);
  scaleTransform[0] = factor;
  scaleTransform[4] = factor;
  scaleTransform[8] = 1;
  return scaleTransform;
};
const translate = (x2, y2) => {
  const translation = dna(9);
  translation[0] = 1;
  translation[2] = x2;
  translation[4] = 1;
  translation[5] = y2;
  translation[8] = 1;
  return translation;
};
const scaleAtOrigin = (factor, x2, y2) => compose(translate((1 - factor) * x2, (1 - factor) * y2), scale(factor));
const transform = (points, transformation, buffer) => {
  const len = points.length;
  const ret = buffer && buffer.length >= len ? buffer.slice(0, len) : dna(len);
  for (let index = 0; index < len; index++) {
    ret[index] = index % 5 === 0 ? transformation[6] * points[index + 1] + transformation[7] * points[index + 2] + transformation[8] * points[index] : index % 5 % 2 === 1 ? transformation[0] * points[index] + transformation[1] * points[index + 1] + transformation[2] : transformation[3] * points[index - 1] + transformation[4] * points[index] + transformation[5];
  }
  return ret;
};
let nanoid = (size = 21) => crypto.getRandomValues(new Uint8Array(size)).reduce((id, byte) => {
  byte &= 63;
  if (byte < 36) {
    id += byte.toString(36);
  } else if (byte < 62) {
    id += (byte - 26).toString(36).toUpperCase();
  } else if (byte > 62) {
    id += "-";
  } else {
    id += "_";
  }
  return id;
}, "");
class BaseObject {
  constructor() {
    this.__revision = 0;
    this.__owner = { value: void 0 };
    this.__state = {};
    this.scale = 1;
    this.layers = [];
    this.time = [];
    this.addEventListener = (name, cb, options) => {
      const event = supportedEventMap[name];
      if (!this.eventHandlers[event]) {
        throw new Error(`Unknown event ${event}`);
      }
      if (this.eventHandlers[event].indexOf(cb) === -1) {
        this.eventHandlers[event].push(cb);
      }
    };
    this.removeEventListener = (name, cb) => {
      const event = supportedEventMap[name];
      if (!this.eventHandlers[event]) {
        console.warn(`Unknown event ${event}`);
        return;
      }
      if (this.eventHandlers[event].indexOf(cb) !== -1) {
        this.eventHandlers[event] = this.eventHandlers[event].filter((e2) => e2 !== cb);
      }
    };
    this.id = this.__id = nanoid();
    this.eventHandlers = createDefaultEventMap();
  }
  get crop() {
    return this._crop;
  }
  set crop(crop) {
    this._crop = crop;
  }
  getObjectsAt(target) {
    return [];
  }
  getAllPointsAt(target, aggregate, scale2) {
    return [];
  }
  getScheduledUpdates(target, scaleFactor) {
    return [];
  }
  dispatchEvent(name, e2) {
    const listeners = this.eventHandlers[name];
    const len = listeners ? listeners.length : 0;
    let didFire = false;
    if (len) {
      for (let x2 = 0; x2 < len; x2++) {
        try {
          listeners[x2](e2);
          didFire = true;
        } catch (e22) {
          console.error(name, e22);
        }
      }
    }
    return didFire;
  }
  get x() {
    return this.points[1];
  }
  get y() {
    return this.points[2];
  }
  get width() {
    return this.points[3] - this.points[1];
  }
  get height() {
    return this.points[4] - this.points[2];
  }
  translate(x2, y2) {
    mutate(this.points, translate(x2, y2));
  }
  atScale(factor) {
    mutate(this.points, scaleAtOrigin(factor, this.x, this.y));
    this.scale *= factor;
  }
  transform(op) {
    mutate(this.points, op);
  }
  applyProps(props2) {
    this.__revision++;
  }
  appendChild(item) {
  }
  removeChild(item) {
  }
  insertBefore(item, before) {
  }
  hideInstance() {
  }
}
class SingleImage extends BaseObject {
  constructor(data) {
    super();
    this.type = "spacial-content";
    this.style = { opacity: 1 };
    if (!data) {
      this.id = "";
      this.uri = "";
      this.display = { x: 0, y: 0, scale: 1, width: 0, height: 0, points: dna(5) };
      this.points = dna(5);
    } else {
      const scale2 = data.scale || 1;
      this.id = data.id || data.uri;
      this.uri = data.uri;
      this.points = DnaFactory.singleBox(data.width, data.height, data.x, data.y);
      this.display = {
        x: 0,
        y: 0,
        scale: scale2,
        width: data.width / scale2,
        height: data.height / scale2,
        points: DnaFactory.singleBox(data.width / scale2, data.height / scale2),
        rotation: data == null ? void 0 : data.rotation
      };
    }
  }
  applyProps(props2) {
    const width = props2.display ? props2.display.width : props2.target.width;
    const scale2 = props2.target.width / width;
    this.id = props2.id || props2.uri;
    this.uri = props2.uri;
    this.points.set(DnaFactory.singleBox(props2.target.width, props2.target.height, props2.target.x, props2.target.y));
    if (props2.style && typeof props2.style.opacity !== "undefined") {
      this.style.opacity = props2.style.opacity;
    }
    if (props2.crop) {
      this.cropData = props2.crop;
      const crop = DnaFactory.singleBox(props2.crop.width, props2.crop.height, props2.crop.x, props2.crop.y);
      mutate(crop, translate(-props2.crop.x, -props2.crop.y));
      if (!this.crop) {
        this.crop = dna(crop);
      } else {
        this.crop.set(crop);
      }
    }
    if (props2.display) {
      this.display.scale = scale2;
      this.display.width = props2.display.width;
      this.display.height = props2.display.height;
      this.display.rotation = props2.display.rotation;
      this.display.points = DnaFactory.singleBox(props2.display.width, props2.display.height);
    } else {
      this.display.scale = scale2;
      this.display.width = props2.target.width / scale2;
      this.display.height = props2.target.height / scale2;
      this.display.points = DnaFactory.singleBox(props2.target.width / scale2, props2.target.height / scale2);
    }
  }
  getAllPointsAt(target, aggregate, scale2) {
    return [[this, this.crop || this.points, aggregate]];
  }
  static fromSvg(svg, target, display, id) {
    return SingleImage.fromImage("data:image/svg+xml;base64," + btoa(svg), target, display, id);
  }
  static fromImage(uri, target, display, id) {
    const instance = new SingleImage();
    instance.applyProps({
      uri,
      id,
      display,
      target
    });
    return instance;
  }
  getImageUrl() {
    return this.uri;
  }
}
class ImageTexture extends BaseObject {
  constructor(data) {
    super();
    this.type = "spacial-content";
    this.getTexture = () => {
      return { source: void 0, hash: -1 };
    };
    if (!data) {
      this.id = "";
      this.uri = "";
      this.display = { x: 0, y: 0, scale: 1, width: 0, height: 0, points: dna(5) };
      this.points = dna(5);
    } else {
      const scale2 = data.scale || 1;
      this.id = data.id || data.uri;
      this.uri = data.uri;
      this.points = DnaFactory.singleBox(data.width, data.height);
      this.display = {
        x: 0,
        y: 0,
        scale: scale2,
        width: data.width / scale2,
        height: data.height / scale2,
        points: scale2 !== 1 ? DnaFactory.singleBox(data.width / scale2, data.height / scale2) : this.points
      };
    }
  }
  applyProps(props2) {
    const width = props2.display ? props2.display.width : props2.target.width;
    const scale2 = props2.target.width / width;
    this.id = props2.id;
    this.points.set(DnaFactory.singleBox(props2.target.width, props2.target.height));
    this.display.scale = scale2;
    this.display.width = props2.target.width / scale2;
    this.display.height = props2.target.height / scale2;
    this.getTexture = props2.getTexture;
    this.display.points = scale2 !== 1 ? DnaFactory.singleBox(props2.target.width / scale2, props2.target.height / scale2) : this.points;
  }
  getAllPointsAt(target, aggregate, scale2) {
    return [[this, this.points, aggregate]];
  }
}
function bestResourceIndexAtRatio(ratio, resources, quality = 1) {
  const len = resources.length;
  if (len === 0) {
    throw new Error("No resources passed in.");
  }
  let best = 0;
  for (let i2 = 0; i2 < len; i2++) {
    if (!resources[i2] || !resources[i2].display) {
      break;
    }
    best = Math.abs(resources[i2].display.scale - ratio) * quality < Math.abs(resources[best].display.scale - ratio) ? i2 : best;
  }
  return best;
}
function distance1D(a2, b2) {
  return Math.abs(a2 - b2);
}
function distance(a2, b2) {
  const xDelta = distance1D(a2.x, b2.x);
  const yDelta = distance1D(a2.y, b2.y);
  return Math.sqrt(Math.pow(xDelta, 2) + Math.pow(yDelta, 2));
}
function stripInfoJson(id) {
  const len = id.length;
  if (id.indexOf("/info.json") === len - 10) {
    return id.slice(0, -10);
  }
  return id;
}
function composeRefs(...o2) {
  return (e2) => o2.forEach((o3) => function(o4, e3) {
    "function" == typeof o4 ? o4(e3) : null != o4 && (o4.current = e3);
  }(o3, e2));
}
function useComposedRefs(...e2) {
  return React.useCallback(composeRefs(...e2), e2);
}
function _extends$1() {
  _extends$1 = Object.assign ? Object.assign.bind() : function(target) {
    for (var i2 = 1; i2 < arguments.length; i2++) {
      var source = arguments[i2];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1.apply(this, arguments);
}
const Slot = /* @__PURE__ */ React.forwardRef((e2, o2) => {
  const { children: a2, ...s2 } = e2;
  return React.Children.toArray(a2).some(l$2) ? /* @__PURE__ */ React.createElement(React.Fragment, null, React.Children.map(a2, (e3) => l$2(e3) ? /* @__PURE__ */ React.createElement(n$2, _extends$1({}, s2, { ref: o2 }), e3.props.children) : e3)) : /* @__PURE__ */ React.createElement(n$2, _extends$1({}, s2, { ref: o2 }), a2);
});
Slot.displayName = "Slot";
const n$2 = /* @__PURE__ */ React.forwardRef((r2, n2) => {
  const { children: l2, ...a2 } = r2;
  return React.isValidElement(l2) ? /* @__PURE__ */ React.cloneElement(l2, { ...o$3(a2, l2.props), ref: composeRefs(n2, l2.ref) }) : React.Children.count(l2) > 1 ? React.Children.only(null) : null;
});
n$2.displayName = "SlotClone";
const Slottable = ({ children: e2 }) => /* @__PURE__ */ React.createElement(React.Fragment, null, e2);
function l$2(e2) {
  return React.isValidElement(e2) && e2.type === Slottable;
}
function o$3(e2, t2) {
  const r2 = { ...t2 };
  for (const n2 in t2) {
    const l2 = e2[n2], o2 = t2[n2];
    /^on[A-Z]/.test(n2) ? r2[n2] = (...e3) => {
      null == o2 || o2(...e3), null == l2 || l2(...e3);
    } : "style" === n2 ? r2[n2] = { ...l2, ...o2 } : "className" === n2 && (r2[n2] = [l2, o2].filter(Boolean).join(" "));
  }
  return { ...e2, ...r2 };
}
const useLayoutEffect = Boolean(null === globalThis || void 0 === globalThis ? void 0 : globalThis.document) ? React.useLayoutEffect : () => {
};
var Rule = function Rule2(name, fn, args, modifiers) {
  this.name = name;
  this.fn = fn;
  this.args = args;
  this.modifiers = modifiers;
};
Rule.prototype._test = function _test(value) {
  var fn = this.fn;
  try {
    testAux(this.modifiers.slice(), fn, this)(value);
  } catch (ex) {
    fn = function() {
      return false;
    };
  }
  try {
    return testAux(this.modifiers.slice(), fn, this)(value);
  } catch (ex$1) {
    return false;
  }
};
Rule.prototype._check = function _check(value) {
  try {
    testAux(this.modifiers.slice(), this.fn, this)(value);
  } catch (ex) {
    if (testAux(this.modifiers.slice(), function(it) {
      return it;
    }, this)(false)) {
      return;
    }
  }
  if (!testAux(this.modifiers.slice(), this.fn, this)(value)) {
    throw null;
  }
};
Rule.prototype._testAsync = function _testAsync(value) {
  var this$1$1 = this;
  return new Promise(function(resolve, reject) {
    testAsyncAux(this$1$1.modifiers.slice(), this$1$1.fn, this$1$1)(value).then(function(valid) {
      if (valid) {
        resolve(value);
      } else {
        reject(null);
      }
    }).catch(function(ex) {
      return reject(ex);
    });
  });
};
function pickFn(fn, variant) {
  if (variant === void 0)
    variant = "simple";
  return typeof fn === "object" ? fn[variant] : fn;
}
function testAux(modifiers, fn, rule) {
  if (modifiers.length) {
    var modifier = modifiers.shift();
    var nextFn = testAux(modifiers, fn, rule);
    return modifier.perform(nextFn, rule);
  } else {
    return pickFn(fn);
  }
}
function testAsyncAux(modifiers, fn, rule) {
  if (modifiers.length) {
    var modifier = modifiers.shift();
    var nextFn = testAsyncAux(modifiers, fn, rule);
    return modifier.performAsync(nextFn, rule);
  } else {
    return function(value) {
      return Promise.resolve(pickFn(fn, "async")(value));
    };
  }
}
var Modifier = function Modifier2(name, perform, performAsync) {
  this.name = name;
  this.perform = perform;
  this.performAsync = performAsync;
};
var ValidationError = /* @__PURE__ */ function(Error2) {
  function ValidationError2(rule, value, cause, target) {
    var remaining = [], len = arguments.length - 4;
    while (len-- > 0)
      remaining[len] = arguments[len + 4];
    Error2.call(this, remaining);
    if (Error2.captureStackTrace) {
      Error2.captureStackTrace(this, ValidationError2);
    }
    this.rule = rule;
    this.value = value;
    this.cause = cause;
    this.target = target;
  }
  if (Error2)
    ValidationError2.__proto__ = Error2;
  ValidationError2.prototype = Object.create(Error2 && Error2.prototype);
  ValidationError2.prototype.constructor = ValidationError2;
  return ValidationError2;
}(Error);
var Context = function Context2(chain2, nextRuleModifiers) {
  if (chain2 === void 0)
    chain2 = [];
  if (nextRuleModifiers === void 0)
    nextRuleModifiers = [];
  this.chain = chain2;
  this.nextRuleModifiers = nextRuleModifiers;
};
Context.prototype._applyRule = function _applyRule(ruleFn, name) {
  var this$1$1 = this;
  return function() {
    var args = [], len = arguments.length;
    while (len--)
      args[len] = arguments[len];
    this$1$1.chain.push(new Rule(name, ruleFn.apply(this$1$1, args), args, this$1$1.nextRuleModifiers));
    this$1$1.nextRuleModifiers = [];
    return this$1$1;
  };
};
Context.prototype._applyModifier = function _applyModifier(modifier, name) {
  this.nextRuleModifiers.push(new Modifier(name, modifier.simple, modifier.async));
  return this;
};
Context.prototype._clone = function _clone() {
  return new Context(this.chain.slice(), this.nextRuleModifiers.slice());
};
Context.prototype.test = function test(value) {
  return this.chain.every(function(rule) {
    return rule._test(value);
  });
};
Context.prototype.testAll = function testAll(value) {
  var err = [];
  this.chain.forEach(function(rule) {
    try {
      rule._check(value);
    } catch (ex) {
      err.push(new ValidationError(rule, value, ex));
    }
  });
  return err;
};
Context.prototype.check = function check(value) {
  this.chain.forEach(function(rule) {
    try {
      rule._check(value);
    } catch (ex) {
      throw new ValidationError(rule, value, ex);
    }
  });
};
Context.prototype.testAsync = function testAsync(value) {
  var this$1$1 = this;
  return new Promise(function(resolve, reject) {
    executeAsyncRules(value, this$1$1.chain.slice(), resolve, reject);
  });
};
function executeAsyncRules(value, rules, resolve, reject) {
  if (rules.length) {
    var rule = rules.shift();
    rule._testAsync(value).then(function() {
      executeAsyncRules(value, rules, resolve, reject);
    }, function(cause) {
      reject(new ValidationError(rule, value, cause));
    });
  } else {
    resolve(value);
  }
}
var consideredEmpty = function(value, considerTrimmedEmptyString) {
  if (considerTrimmedEmptyString && typeof value === "string" && value.trim().length === 0) {
    return true;
  }
  return value === void 0 || value === null;
};
function optional(validation, considerTrimmedEmptyString) {
  if (considerTrimmedEmptyString === void 0)
    considerTrimmedEmptyString = false;
  return {
    simple: function(value) {
      return consideredEmpty(value, considerTrimmedEmptyString) || validation.check(value) === void 0;
    },
    async: function(value) {
      return consideredEmpty(value, considerTrimmedEmptyString) || validation.testAsync(value);
    }
  };
}
function v8n() {
  return typeof Proxy !== "undefined" ? proxyContext(new Context()) : proxylessContext(new Context());
}
var customRules = {};
v8n.extend = function(newRules) {
  Object.assign(customRules, newRules);
};
v8n.clearCustomRules = function() {
  customRules = {};
};
function proxyContext(context) {
  return new Proxy(context, {
    get: function get(obj, prop) {
      if (prop in obj) {
        return obj[prop];
      }
      var newContext = proxyContext(context._clone());
      if (prop in availableModifiers) {
        return newContext._applyModifier(availableModifiers[prop], prop);
      }
      if (prop in customRules) {
        return newContext._applyRule(customRules[prop], prop);
      }
      if (prop in availableRules) {
        return newContext._applyRule(availableRules[prop], prop);
      }
    }
  });
}
function proxylessContext(context) {
  var addRuleSet = function(ruleSet, targetContext) {
    Object.keys(ruleSet).forEach(function(prop) {
      targetContext[prop] = function() {
        var args = [], len = arguments.length;
        while (len--)
          args[len] = arguments[len];
        var newContext = proxylessContext(targetContext._clone());
        var contextWithRuleApplied = newContext._applyRule(ruleSet[prop], prop).apply(void 0, args);
        return contextWithRuleApplied;
      };
    });
    return targetContext;
  };
  var contextWithAvailableRules = addRuleSet(availableRules, context);
  var contextWithAllRules = addRuleSet(customRules, contextWithAvailableRules);
  Object.keys(availableModifiers).forEach(function(prop) {
    Object.defineProperty(contextWithAllRules, prop, {
      get: function() {
        var newContext = proxylessContext(contextWithAllRules._clone());
        return newContext._applyModifier(availableModifiers[prop], prop);
      }
    });
  });
  return contextWithAllRules;
}
var availableModifiers = {
  not: {
    simple: function(fn) {
      return function(value) {
        return !fn(value);
      };
    },
    async: function(fn) {
      return function(value) {
        return Promise.resolve(fn(value)).then(function(result) {
          return !result;
        }).catch(function() {
          return true;
        });
      };
    }
  },
  some: {
    simple: function(fn) {
      return function(value) {
        return split(value).some(function(item) {
          try {
            return fn(item);
          } catch (ex) {
            return false;
          }
        });
      };
    },
    async: function(fn) {
      return function(value) {
        return Promise.all(split(value).map(function(item) {
          try {
            return fn(item).catch(function() {
              return false;
            });
          } catch (ex) {
            return false;
          }
        })).then(function(result) {
          return result.some(Boolean);
        });
      };
    }
  },
  every: {
    simple: function(fn) {
      return function(value) {
        return value !== false && split(value).every(fn);
      };
    },
    async: function(fn) {
      return function(value) {
        return Promise.all(split(value).map(fn)).then(function(result) {
          return result.every(Boolean);
        });
      };
    }
  },
  strict: {
    simple: function(fn, rule) {
      return function(value) {
        if (isSchemaRule(rule) && value && typeof value === "object") {
          return Object.keys(rule.args[0]).length === Object.keys(value).length && fn(value);
        }
        return fn(value);
      };
    },
    async: function(fn, rule) {
      return function(value) {
        return Promise.resolve(fn(value)).then(function(result) {
          if (isSchemaRule(rule) && value && typeof value === "object") {
            return Object.keys(rule.args[0]).length === Object.keys(value).length && result;
          }
          return result;
        }).catch(function() {
          return false;
        });
      };
    }
  }
};
function isSchemaRule(rule) {
  return rule && rule.name === "schema" && rule.args.length > 0 && typeof rule.args[0] === "object";
}
function split(value) {
  if (typeof value === "string") {
    return value.split("");
  }
  return value;
}
var availableRules = {
  equal: function(expected) {
    return function(value) {
      return value == expected;
    };
  },
  exact: function(expected) {
    return function(value) {
      return value === expected;
    };
  },
  number: function(allowInfinite) {
    if (allowInfinite === void 0)
      allowInfinite = true;
    return function(value) {
      return typeof value === "number" && (allowInfinite || isFinite(value));
    };
  },
  integer: function() {
    return function(value) {
      var isInteger = Number.isInteger || isIntegerPolyfill;
      return isInteger(value);
    };
  },
  numeric: function() {
    return function(value) {
      return !isNaN(parseFloat(value)) && isFinite(value);
    };
  },
  string: function() {
    return testType("string");
  },
  boolean: function() {
    return testType("boolean");
  },
  undefined: function() {
    return testType("undefined");
  },
  null: function() {
    return testType("null");
  },
  array: function() {
    return testType("array");
  },
  object: function() {
    return testType("object");
  },
  instanceOf: function(instance) {
    return function(value) {
      return value instanceof instance;
    };
  },
  pattern: function(expected) {
    return function(value) {
      return expected.test(value);
    };
  },
  lowercase: function() {
    return function(value) {
      return typeof value === "boolean" || value === value.toLowerCase() && value.trim() !== "";
    };
  },
  uppercase: function() {
    return function(value) {
      return value === value.toUpperCase() && value.trim() !== "";
    };
  },
  vowel: function() {
    return function(value) {
      return /^[aeiou]+$/i.test(value);
    };
  },
  consonant: function() {
    return function(value) {
      return /^(?=[^aeiou])([a-z]+)$/i.test(value);
    };
  },
  first: function(expected) {
    return function(value) {
      return value[0] == expected;
    };
  },
  last: function(expected) {
    return function(value) {
      return value[value.length - 1] == expected;
    };
  },
  empty: function() {
    return function(value) {
      return value.length === 0;
    };
  },
  length: function(min, max) {
    return function(value) {
      return value.length >= min && value.length <= (max || min);
    };
  },
  minLength: function(min) {
    return function(value) {
      return value.length >= min;
    };
  },
  maxLength: function(max) {
    return function(value) {
      return value.length <= max;
    };
  },
  negative: function() {
    return function(value) {
      return value < 0;
    };
  },
  positive: function() {
    return function(value) {
      return value >= 0;
    };
  },
  between: function(a2, b2) {
    return function(value) {
      return value >= a2 && value <= b2;
    };
  },
  range: function(a2, b2) {
    return function(value) {
      return value >= a2 && value <= b2;
    };
  },
  lessThan: function(n2) {
    return function(value) {
      return value < n2;
    };
  },
  lessThanOrEqual: function(n2) {
    return function(value) {
      return value <= n2;
    };
  },
  greaterThan: function(n2) {
    return function(value) {
      return value > n2;
    };
  },
  greaterThanOrEqual: function(n2) {
    return function(value) {
      return value >= n2;
    };
  },
  even: function() {
    return function(value) {
      return value % 2 === 0;
    };
  },
  odd: function() {
    return function(value) {
      return value % 2 !== 0;
    };
  },
  includes: function(expected) {
    return function(value) {
      return ~value.indexOf(expected);
    };
  },
  schema: function(schema2) {
    return testSchema(schema2);
  },
  passesAnyOf: function() {
    var validations = [], len = arguments.length;
    while (len--)
      validations[len] = arguments[len];
    return function(value) {
      return validations.some(function(validation) {
        return validation.test(value);
      });
    };
  },
  optional
};
function testType(expected) {
  return function(value) {
    return Array.isArray(value) && expected === "array" || value === null && expected === "null" || typeof value === expected;
  };
}
function isIntegerPolyfill(value) {
  return typeof value === "number" && isFinite(value) && Math.floor(value) === value;
}
function testSchema(schema2) {
  return {
    simple: function(value) {
      var causes = [];
      Object.keys(schema2).forEach(function(key) {
        var nestedValidation = schema2[key];
        try {
          nestedValidation.check((value || {})[key]);
        } catch (ex) {
          ex.target = key;
          causes.push(ex);
        }
      });
      if (causes.length > 0) {
        throw causes;
      }
      return true;
    },
    async: function(value) {
      var causes = [];
      var nested = Object.keys(schema2).map(function(key) {
        var nestedValidation = schema2[key];
        return nestedValidation.testAsync((value || {})[key]).catch(function(ex) {
          ex.target = key;
          causes.push(ex);
        });
      });
      return Promise.all(nested).then(function() {
        if (causes.length > 0) {
          throw causes;
        }
        return true;
      });
    }
  };
}
var e$2 = "colors", t$3 = "sizes", r$3 = "space", n$1 = { gap: r$3, gridGap: r$3, columnGap: r$3, gridColumnGap: r$3, rowGap: r$3, gridRowGap: r$3, inset: r$3, insetBlock: r$3, insetBlockEnd: r$3, insetBlockStart: r$3, insetInline: r$3, insetInlineEnd: r$3, insetInlineStart: r$3, margin: r$3, marginTop: r$3, marginRight: r$3, marginBottom: r$3, marginLeft: r$3, marginBlock: r$3, marginBlockEnd: r$3, marginBlockStart: r$3, marginInline: r$3, marginInlineEnd: r$3, marginInlineStart: r$3, padding: r$3, paddingTop: r$3, paddingRight: r$3, paddingBottom: r$3, paddingLeft: r$3, paddingBlock: r$3, paddingBlockEnd: r$3, paddingBlockStart: r$3, paddingInline: r$3, paddingInlineEnd: r$3, paddingInlineStart: r$3, top: r$3, right: r$3, bottom: r$3, left: r$3, scrollMargin: r$3, scrollMarginTop: r$3, scrollMarginRight: r$3, scrollMarginBottom: r$3, scrollMarginLeft: r$3, scrollMarginX: r$3, scrollMarginY: r$3, scrollMarginBlock: r$3, scrollMarginBlockEnd: r$3, scrollMarginBlockStart: r$3, scrollMarginInline: r$3, scrollMarginInlineEnd: r$3, scrollMarginInlineStart: r$3, scrollPadding: r$3, scrollPaddingTop: r$3, scrollPaddingRight: r$3, scrollPaddingBottom: r$3, scrollPaddingLeft: r$3, scrollPaddingX: r$3, scrollPaddingY: r$3, scrollPaddingBlock: r$3, scrollPaddingBlockEnd: r$3, scrollPaddingBlockStart: r$3, scrollPaddingInline: r$3, scrollPaddingInlineEnd: r$3, scrollPaddingInlineStart: r$3, fontSize: "fontSizes", background: e$2, backgroundColor: e$2, backgroundImage: e$2, borderImage: e$2, border: e$2, borderBlock: e$2, borderBlockEnd: e$2, borderBlockStart: e$2, borderBottom: e$2, borderBottomColor: e$2, borderColor: e$2, borderInline: e$2, borderInlineEnd: e$2, borderInlineStart: e$2, borderLeft: e$2, borderLeftColor: e$2, borderRight: e$2, borderRightColor: e$2, borderTop: e$2, borderTopColor: e$2, caretColor: e$2, color: e$2, columnRuleColor: e$2, fill: e$2, outline: e$2, outlineColor: e$2, stroke: e$2, textDecorationColor: e$2, fontFamily: "fonts", fontWeight: "fontWeights", lineHeight: "lineHeights", letterSpacing: "letterSpacings", blockSize: t$3, minBlockSize: t$3, maxBlockSize: t$3, inlineSize: t$3, minInlineSize: t$3, maxInlineSize: t$3, width: t$3, minWidth: t$3, maxWidth: t$3, height: t$3, minHeight: t$3, maxHeight: t$3, flexBasis: t$3, gridTemplateColumns: t$3, gridTemplateRows: t$3, borderWidth: "borderWidths", borderTopWidth: "borderWidths", borderRightWidth: "borderWidths", borderBottomWidth: "borderWidths", borderLeftWidth: "borderWidths", borderStyle: "borderStyles", borderTopStyle: "borderStyles", borderRightStyle: "borderStyles", borderBottomStyle: "borderStyles", borderLeftStyle: "borderStyles", borderRadius: "radii", borderTopLeftRadius: "radii", borderTopRightRadius: "radii", borderBottomRightRadius: "radii", borderBottomLeftRadius: "radii", boxShadow: "shadows", textShadow: "shadows", transition: "transitions", zIndex: "zIndices" }, i$2 = (e2, t2) => "function" == typeof t2 ? { "()": Function.prototype.toString.call(t2) } : t2, o$2 = () => {
  const e2 = /* @__PURE__ */ Object.create(null);
  return (t2, r2, ...n2) => {
    const o2 = ((e3) => JSON.stringify(e3, i$2))(t2);
    return o2 in e2 ? e2[o2] : e2[o2] = r2(t2, ...n2);
  };
}, l$1 = Symbol.for("sxs.internal"), s$1 = (e2, t2) => Object.defineProperties(e2, Object.getOwnPropertyDescriptors(t2)), a$1 = (e2) => {
  for (const t2 in e2)
    return true;
  return false;
}, { hasOwnProperty: c$2 } = Object.prototype, d$1 = (e2) => e2.includes("-") ? e2 : e2.replace(/[A-Z]/g, (e3) => "-" + e3.toLowerCase()), g$2 = /\s+(?![^()]*\))/, p$2 = (e2) => (t2) => e2(..."string" == typeof t2 ? String(t2).split(g$2) : [t2]), u$1 = { appearance: (e2) => ({ WebkitAppearance: e2, appearance: e2 }), backfaceVisibility: (e2) => ({ WebkitBackfaceVisibility: e2, backfaceVisibility: e2 }), backdropFilter: (e2) => ({ WebkitBackdropFilter: e2, backdropFilter: e2 }), backgroundClip: (e2) => ({ WebkitBackgroundClip: e2, backgroundClip: e2 }), boxDecorationBreak: (e2) => ({ WebkitBoxDecorationBreak: e2, boxDecorationBreak: e2 }), clipPath: (e2) => ({ WebkitClipPath: e2, clipPath: e2 }), content: (e2) => ({ content: e2.includes('"') || e2.includes("'") || /^([A-Za-z]+\([^]*|[^]*-quote|inherit|initial|none|normal|revert|unset)$/.test(e2) ? e2 : `"${e2}"` }), hyphens: (e2) => ({ WebkitHyphens: e2, hyphens: e2 }), maskImage: (e2) => ({ WebkitMaskImage: e2, maskImage: e2 }), maskSize: (e2) => ({ WebkitMaskSize: e2, maskSize: e2 }), tabSize: (e2) => ({ MozTabSize: e2, tabSize: e2 }), textSizeAdjust: (e2) => ({ WebkitTextSizeAdjust: e2, textSizeAdjust: e2 }), userSelect: (e2) => ({ WebkitUserSelect: e2, userSelect: e2 }), marginBlock: p$2((e2, t2) => ({ marginBlockStart: e2, marginBlockEnd: t2 || e2 })), marginInline: p$2((e2, t2) => ({ marginInlineStart: e2, marginInlineEnd: t2 || e2 })), maxSize: p$2((e2, t2) => ({ maxBlockSize: e2, maxInlineSize: t2 || e2 })), minSize: p$2((e2, t2) => ({ minBlockSize: e2, minInlineSize: t2 || e2 })), paddingBlock: p$2((e2, t2) => ({ paddingBlockStart: e2, paddingBlockEnd: t2 || e2 })), paddingInline: p$2((e2, t2) => ({ paddingInlineStart: e2, paddingInlineEnd: t2 || e2 })) }, h$2 = /([\d.]+)([^]*)/, f$1 = (e2, t2) => e2.length ? e2.reduce((e3, r2) => (e3.push(...t2.map((e4) => e4.includes("&") ? e4.replace(/&/g, /[ +>|~]/.test(r2) && /&.*&/.test(e4) ? `:is(${r2})` : r2) : r2 + " " + e4)), e3), []) : t2, m$2 = (e2, t2) => e2 in b$3 && "string" == typeof t2 ? t2.replace(/^((?:[^]*[^\w-])?)(fit-content|stretch)((?:[^\w-][^]*)?)$/, (t3, r2, n2, i2) => r2 + ("stretch" === n2 ? `-moz-available${i2};${d$1(e2)}:${r2}-webkit-fill-available` : `-moz-fit-content${i2};${d$1(e2)}:${r2}fit-content`) + i2) : String(t2), b$3 = { blockSize: 1, height: 1, inlineSize: 1, maxBlockSize: 1, maxHeight: 1, maxInlineSize: 1, maxWidth: 1, minBlockSize: 1, minHeight: 1, minInlineSize: 1, minWidth: 1, width: 1 }, S$1 = (e2) => e2 ? e2 + "-" : "", k$2 = (e2, t2, r2) => e2.replace(/([+-])?((?:\d+(?:\.\d*)?|\.\d+)(?:[Ee][+-]?\d+)?)?(\$|--)([$\w-]+)/g, (e3, n2, i2, o2, l2) => "$" == o2 == !!i2 ? e3 : (n2 || "--" == o2 ? "calc(" : "") + "var(--" + ("$" === o2 ? S$1(t2) + (l2.includes("$") ? "" : S$1(r2)) + l2.replace(/\$/g, "-") : l2) + ")" + (n2 || "--" == o2 ? "*" + (n2 || "") + (i2 || "1") + ")" : "")), y$3 = /\s*,\s*(?![^()]*\))/, B$1 = Object.prototype.toString, $$1 = (e2, t2, r2, n2, i2) => {
  let o2, l2, s2;
  const a2 = (e3, t3, r3) => {
    let c6, g2;
    const p2 = (e4) => {
      for (c6 in e4) {
        const R2 = 64 === c6.charCodeAt(0), z2 = R2 && Array.isArray(e4[c6]) ? e4[c6] : [e4[c6]];
        for (g2 of z2) {
          const e5 = /[A-Z]/.test($2 = c6) ? $2 : $2.replace(/-[^]/g, (e6) => e6[1].toUpperCase()), z3 = "object" == typeof g2 && g2 && g2.toString === B$1 && (!n2.utils[e5] || !t3.length);
          if (e5 in n2.utils && !z3) {
            const t4 = n2.utils[e5];
            if (t4 !== l2) {
              l2 = t4, p2(t4(g2)), l2 = null;
              continue;
            }
          } else if (e5 in u$1) {
            const t4 = u$1[e5];
            if (t4 !== s2) {
              s2 = t4, p2(t4(g2)), s2 = null;
              continue;
            }
          }
          if (R2 && (b2 = c6.slice(1) in n2.media ? "@media " + n2.media[c6.slice(1)] : c6, c6 = b2.replace(/\(\s*([\w-]+)\s*(=|<|<=|>|>=)\s*([\w-]+)\s*(?:(<|<=|>|>=)\s*([\w-]+)\s*)?\)/g, (e6, t4, r4, n3, i3, o3) => {
            const l3 = h$2.test(t4), s3 = 0.0625 * (l3 ? -1 : 1), [a3, c7] = l3 ? [n3, t4] : [t4, n3];
            return "(" + ("=" === r4[0] ? "" : ">" === r4[0] === l3 ? "max-" : "min-") + a3 + ":" + ("=" !== r4[0] && 1 === r4.length ? c7.replace(h$2, (e7, t5, n4) => Number(t5) + s3 * (">" === r4 ? 1 : -1) + n4) : c7) + (i3 ? ") and (" + (">" === i3[0] ? "min-" : "max-") + a3 + ":" + (1 === i3.length ? o3.replace(h$2, (e7, t5, r5) => Number(t5) + s3 * (">" === i3 ? -1 : 1) + r5) : o3) : "") + ")";
          })), z3) {
            const e6 = R2 ? r3.concat(c6) : [...r3], n3 = R2 ? [...t3] : f$1(t3, c6.split(y$3));
            void 0 !== o2 && i2(x$2(...o2)), o2 = void 0, a2(g2, n3, e6);
          } else
            void 0 === o2 && (o2 = [[], t3, r3]), c6 = R2 || 36 !== c6.charCodeAt(0) ? c6 : `--${S$1(n2.prefix)}${c6.slice(1).replace(/\$/g, "-")}`, g2 = z3 ? g2 : "number" == typeof g2 ? g2 && e5 in I ? String(g2) + "px" : String(g2) : k$2(m$2(e5, null == g2 ? "" : g2), n2.prefix, n2.themeMap[e5]), o2[0].push(`${R2 ? `${c6} ` : `${d$1(c6)}:`}${g2}`);
        }
      }
      var b2, $2;
    };
    p2(e3), void 0 !== o2 && i2(x$2(...o2)), o2 = void 0;
  };
  a2(e2, t2, r2);
}, x$2 = (e2, t2, r2) => `${r2.map((e3) => `${e3}{`).join("")}${t2.length ? `${t2.join(",")}{` : ""}${e2.join(";")}${t2.length ? "}" : ""}${Array(r2.length ? r2.length + 1 : 0).join("}")}`, I = { animationDelay: 1, animationDuration: 1, backgroundSize: 1, blockSize: 1, border: 1, borderBlock: 1, borderBlockEnd: 1, borderBlockEndWidth: 1, borderBlockStart: 1, borderBlockStartWidth: 1, borderBlockWidth: 1, borderBottom: 1, borderBottomLeftRadius: 1, borderBottomRightRadius: 1, borderBottomWidth: 1, borderEndEndRadius: 1, borderEndStartRadius: 1, borderInlineEnd: 1, borderInlineEndWidth: 1, borderInlineStart: 1, borderInlineStartWidth: 1, borderInlineWidth: 1, borderLeft: 1, borderLeftWidth: 1, borderRadius: 1, borderRight: 1, borderRightWidth: 1, borderSpacing: 1, borderStartEndRadius: 1, borderStartStartRadius: 1, borderTop: 1, borderTopLeftRadius: 1, borderTopRightRadius: 1, borderTopWidth: 1, borderWidth: 1, bottom: 1, columnGap: 1, columnRule: 1, columnRuleWidth: 1, columnWidth: 1, containIntrinsicSize: 1, flexBasis: 1, fontSize: 1, gap: 1, gridAutoColumns: 1, gridAutoRows: 1, gridTemplateColumns: 1, gridTemplateRows: 1, height: 1, inlineSize: 1, inset: 1, insetBlock: 1, insetBlockEnd: 1, insetBlockStart: 1, insetInline: 1, insetInlineEnd: 1, insetInlineStart: 1, left: 1, letterSpacing: 1, margin: 1, marginBlock: 1, marginBlockEnd: 1, marginBlockStart: 1, marginBottom: 1, marginInline: 1, marginInlineEnd: 1, marginInlineStart: 1, marginLeft: 1, marginRight: 1, marginTop: 1, maxBlockSize: 1, maxHeight: 1, maxInlineSize: 1, maxWidth: 1, minBlockSize: 1, minHeight: 1, minInlineSize: 1, minWidth: 1, offsetDistance: 1, offsetRotate: 1, outline: 1, outlineOffset: 1, outlineWidth: 1, overflowClipMargin: 1, padding: 1, paddingBlock: 1, paddingBlockEnd: 1, paddingBlockStart: 1, paddingBottom: 1, paddingInline: 1, paddingInlineEnd: 1, paddingInlineStart: 1, paddingLeft: 1, paddingRight: 1, paddingTop: 1, perspective: 1, right: 1, rowGap: 1, scrollMargin: 1, scrollMarginBlock: 1, scrollMarginBlockEnd: 1, scrollMarginBlockStart: 1, scrollMarginBottom: 1, scrollMarginInline: 1, scrollMarginInlineEnd: 1, scrollMarginInlineStart: 1, scrollMarginLeft: 1, scrollMarginRight: 1, scrollMarginTop: 1, scrollPadding: 1, scrollPaddingBlock: 1, scrollPaddingBlockEnd: 1, scrollPaddingBlockStart: 1, scrollPaddingBottom: 1, scrollPaddingInline: 1, scrollPaddingInlineEnd: 1, scrollPaddingInlineStart: 1, scrollPaddingLeft: 1, scrollPaddingRight: 1, scrollPaddingTop: 1, shapeMargin: 1, textDecoration: 1, textDecorationThickness: 1, textIndent: 1, textUnderlineOffset: 1, top: 1, transitionDelay: 1, transitionDuration: 1, verticalAlign: 1, width: 1, wordSpacing: 1 }, R$2 = (e2) => String.fromCharCode(e2 + (e2 > 25 ? 39 : 97)), z$1 = (e2) => ((e3) => {
  let t2, r2 = "";
  for (t2 = Math.abs(e3); t2 > 52; t2 = t2 / 52 | 0)
    r2 = R$2(t2 % 52) + r2;
  return R$2(t2 % 52) + r2;
})(((e3, t2) => {
  let r2 = t2.length;
  for (; r2; )
    e3 = 33 * e3 ^ t2.charCodeAt(--r2);
  return e3;
})(5381, JSON.stringify(e2)) >>> 0), W$1 = ["themed", "global", "styled", "onevar", "resonevar", "allvar", "inline"], j$1 = (e2) => {
  if (e2.href && !e2.href.startsWith(location.origin))
    return false;
  try {
    return !!e2.cssRules;
  } catch (e3) {
    return false;
  }
}, E$2 = (e2) => {
  let t2;
  const r2 = () => {
    const { cssRules: e3 } = t2.sheet;
    return [].map.call(e3, (r3, n3) => {
      const { cssText: i2 } = r3;
      let o2 = "";
      if (i2.startsWith("--sxs"))
        return "";
      if (e3[n3 - 1] && (o2 = e3[n3 - 1].cssText).startsWith("--sxs")) {
        if (!r3.cssRules.length)
          return "";
        for (const e4 in t2.rules)
          if (t2.rules[e4].group === r3)
            return `--sxs{--sxs:${[...t2.rules[e4].cache].join(" ")}}${i2}`;
        return r3.cssRules.length ? `${o2}${i2}` : "";
      }
      return i2;
    }).join("");
  }, n2 = () => {
    if (t2) {
      const { rules: e3, sheet: r3 } = t2;
      if (!r3.deleteRule) {
        for (; 3 === Object(Object(r3.cssRules)[0]).type; )
          r3.cssRules.splice(0, 1);
        r3.cssRules = [];
      }
      for (const t3 in e3)
        delete e3[t3];
    }
    const i2 = Object(e2).styleSheets || [];
    for (const e3 of i2)
      if (j$1(e3)) {
        for (let i3 = 0, o3 = e3.cssRules; o3[i3]; ++i3) {
          const l3 = Object(o3[i3]);
          if (1 !== l3.type)
            continue;
          const s2 = Object(o3[i3 + 1]);
          if (4 !== s2.type)
            continue;
          ++i3;
          const { cssText: a2 } = l3;
          if (!a2.startsWith("--sxs"))
            continue;
          const c6 = a2.slice(14, -3).trim().split(/\s+/), d2 = W$1[c6[0]];
          d2 && (t2 || (t2 = { sheet: e3, reset: n2, rules: {}, toString: r2 }), t2.rules[d2] = { group: s2, index: i3, cache: new Set(c6) });
        }
        if (t2)
          break;
      }
    if (!t2) {
      const i3 = (e3, t3) => ({ type: t3, cssRules: [], insertRule(e4, t4) {
        this.cssRules.splice(t4, 0, i3(e4, { import: 3, undefined: 1 }[(e4.toLowerCase().match(/^@([a-z]+)/) || [])[1]] || 4));
      }, get cssText() {
        return "@media{}" === e3 ? `@media{${[].map.call(this.cssRules, (e4) => e4.cssText).join("")}}` : e3;
      } });
      t2 = { sheet: e2 ? (e2.head || e2).appendChild(document.createElement("style")).sheet : i3("", "text/css"), rules: {}, reset: n2, toString: r2 };
    }
    const { sheet: o2, rules: l2 } = t2;
    for (let e3 = W$1.length - 1; e3 >= 0; --e3) {
      const t3 = W$1[e3];
      if (!l2[t3]) {
        const r3 = W$1[e3 + 1], n3 = l2[r3] ? l2[r3].index : o2.cssRules.length;
        o2.insertRule("@media{}", n3), o2.insertRule(`--sxs{--sxs:${e3}}`, n3), l2[t3] = { group: o2.cssRules[n3 + 1], index: n3, cache: /* @__PURE__ */ new Set([e3]) };
      }
      v$2(l2[t3]);
    }
  };
  return n2(), t2;
}, v$2 = (e2) => {
  const t2 = e2.group;
  let r2 = t2.cssRules.length;
  e2.apply = (e3) => {
    try {
      t2.insertRule(e3, r2), ++r2;
    } catch (e4) {
    }
  };
}, T$1 = Symbol(), w$2 = o$2(), M$1 = (e2, t2) => w$2(e2, () => (...r2) => {
  let n2 = { type: null, composers: /* @__PURE__ */ new Set() };
  for (const t3 of r2)
    if (null != t3)
      if (t3[l$1]) {
        null == n2.type && (n2.type = t3[l$1].type);
        for (const e3 of t3[l$1].composers)
          n2.composers.add(e3);
      } else
        t3.constructor !== Object || t3.$$typeof ? null == n2.type && (n2.type = t3) : n2.composers.add(C$1(t3, e2));
  return null == n2.type && (n2.type = "span"), n2.composers.size || n2.composers.add(["PJLV", {}, [], [], {}, []]), P$1(e2, n2, t2);
}), C$1 = ({ variants: e2, compoundVariants: t2, defaultVariants: r2, ...n2 }, i2) => {
  const o2 = `${S$1(i2.prefix)}c-${z$1(n2)}`, l2 = [], s2 = [], d2 = /* @__PURE__ */ Object.create(null), g2 = [];
  for (const e3 in r2)
    d2[e3] = String(r2[e3]);
  if ("object" == typeof e2 && e2)
    for (const t3 in e2) {
      p2 = d2, u2 = t3, c$2.call(p2, u2) || (d2[t3] = "undefined");
      const r3 = e2[t3];
      for (const e3 in r3) {
        const n3 = { [t3]: String(e3) };
        "undefined" === String(e3) && g2.push(t3);
        const i3 = r3[e3], o3 = [n3, i3, !a$1(i3)];
        l2.push(o3);
      }
    }
  var p2, u2;
  if ("object" == typeof t2 && t2)
    for (const e3 of t2) {
      let { css: t3, ...r3 } = e3;
      t3 = "object" == typeof t3 && t3 || {};
      for (const e4 in r3)
        r3[e4] = String(r3[e4]);
      const n3 = [r3, t3, !a$1(t3)];
      s2.push(n3);
    }
  return [o2, n2, l2, s2, d2, g2];
}, P$1 = (e2, t2, r2) => {
  const [n2, i2, o2, a2] = L$1(t2.composers), c6 = "function" == typeof t2.type || t2.type.$$typeof ? ((e3) => {
    function t3() {
      for (let r3 = 0; r3 < t3[T$1].length; r3++) {
        const [n3, i3] = t3[T$1][r3];
        e3.rules[n3].apply(i3);
      }
      return t3[T$1] = [], null;
    }
    return t3[T$1] = [], t3.rules = {}, W$1.forEach((e4) => t3.rules[e4] = { apply: (r3) => t3[T$1].push([e4, r3]) }), t3;
  })(r2) : null, d2 = (c6 || r2).rules, g2 = `.${n2}${i2.length > 1 ? `:where(.${i2.slice(1).join(".")})` : ""}`, p2 = (l2) => {
    l2 = "object" == typeof l2 && l2 || A$1;
    const { css: s2, ...p3 } = l2, u2 = {};
    for (const e3 in o2)
      if (delete p3[e3], e3 in l2) {
        let t3 = l2[e3];
        "object" == typeof t3 && t3 ? u2[e3] = { "@initial": o2[e3], ...t3 } : (t3 = String(t3), u2[e3] = "undefined" !== t3 || a2.has(e3) ? t3 : o2[e3]);
      } else
        u2[e3] = o2[e3];
    const h2 = /* @__PURE__ */ new Set([...i2]);
    for (const [n3, i3, o3, l3] of t2.composers) {
      r2.rules.styled.cache.has(n3) || (r2.rules.styled.cache.add(n3), $$1(i3, [`.${n3}`], [], e2, (e3) => {
        d2.styled.apply(e3);
      }));
      const t3 = O$1(o3, u2, e2.media), s3 = O$1(l3, u2, e2.media, true);
      for (const i4 of t3)
        if (void 0 !== i4)
          for (const [t4, o4, l4] of i4) {
            const i5 = `${n3}-${z$1(o4)}-${t4}`;
            h2.add(i5);
            const s4 = (l4 ? r2.rules.resonevar : r2.rules.onevar).cache, a3 = l4 ? d2.resonevar : d2.onevar;
            s4.has(i5) || (s4.add(i5), $$1(o4, [`.${i5}`], [], e2, (e3) => {
              a3.apply(e3);
            }));
          }
      for (const t4 of s3)
        if (void 0 !== t4)
          for (const [i4, o4] of t4) {
            const t5 = `${n3}-${z$1(o4)}-${i4}`;
            h2.add(t5), r2.rules.allvar.cache.has(t5) || (r2.rules.allvar.cache.add(t5), $$1(o4, [`.${t5}`], [], e2, (e3) => {
              d2.allvar.apply(e3);
            }));
          }
    }
    if ("object" == typeof s2 && s2) {
      const t3 = `${n2}-i${z$1(s2)}-css`;
      h2.add(t3), r2.rules.inline.cache.has(t3) || (r2.rules.inline.cache.add(t3), $$1(s2, [`.${t3}`], [], e2, (e3) => {
        d2.inline.apply(e3);
      }));
    }
    for (const e3 of String(l2.className || "").trim().split(/\s+/))
      e3 && h2.add(e3);
    const f2 = p3.className = [...h2].join(" ");
    return { type: t2.type, className: f2, selector: g2, props: p3, toString: () => f2, deferredInjector: c6 };
  };
  return s$1(p2, { className: n2, selector: g2, [l$1]: t2, toString: () => (r2.rules.styled.cache.has(n2) || p2(), n2) });
}, L$1 = (e2) => {
  let t2 = "";
  const r2 = [], n2 = {}, i2 = [];
  for (const [o2, , , , l2, s2] of e2) {
    "" === t2 && (t2 = o2), r2.push(o2), i2.push(...s2);
    for (const e3 in l2) {
      const t3 = l2[e3];
      (void 0 === n2[e3] || "undefined" !== t3 || s2.includes(t3)) && (n2[e3] = t3);
    }
  }
  return [t2, r2, n2, new Set(i2)];
}, O$1 = (e2, t2, r2, n2) => {
  const i2 = [];
  e:
    for (let [o2, l2, s2] of e2) {
      if (s2)
        continue;
      let e3, a2 = 0, c6 = false;
      for (e3 in o2) {
        const n3 = o2[e3];
        let i3 = t2[e3];
        if (i3 !== n3) {
          if ("object" != typeof i3 || !i3)
            continue e;
          {
            let e4, t3, o3 = 0;
            for (const l3 in i3) {
              if (n3 === String(i3[l3])) {
                if ("@initial" !== l3) {
                  const e5 = l3.slice(1);
                  (t3 = t3 || []).push(e5 in r2 ? r2[e5] : l3.replace(/^@media ?/, "")), c6 = true;
                }
                a2 += o3, e4 = true;
              }
              ++o3;
            }
            if (t3 && t3.length && (l2 = { ["@media " + t3.join(", ")]: l2 }), !e4)
              continue e;
          }
        }
      }
      (i2[a2] = i2[a2] || []).push([n2 ? "cv" : `${e3}-${o2[e3]}`, l2, c6]);
    }
  return i2;
}, A$1 = {}, N$1 = o$2(), D = (e2, t2) => N$1(e2, () => (...r2) => {
  const n2 = () => {
    for (let n3 of r2) {
      n3 = "object" == typeof n3 && n3 || {};
      let r3 = z$1(n3);
      if (!t2.rules.global.cache.has(r3)) {
        if (t2.rules.global.cache.add(r3), "@import" in n3) {
          let e3 = [].indexOf.call(t2.sheet.cssRules, t2.rules.themed.group) - 1;
          for (let r4 of [].concat(n3["@import"]))
            r4 = r4.includes('"') || r4.includes("'") ? r4 : `"${r4}"`, t2.sheet.insertRule(`@import ${r4};`, e3++);
          delete n3["@import"];
        }
        $$1(n3, [], [], e2, (e3) => {
          t2.rules.global.apply(e3);
        });
      }
    }
    return "";
  };
  return s$1(n2, { toString: n2 });
}), H$1 = o$2(), V$2 = (e2, t2) => H$1(e2, () => (r2) => {
  const n2 = `${S$1(e2.prefix)}k-${z$1(r2)}`, i2 = () => {
    if (!t2.rules.global.cache.has(n2)) {
      t2.rules.global.cache.add(n2);
      const i3 = [];
      $$1(r2, [], [], e2, (e3) => i3.push(e3));
      const o2 = `@keyframes ${n2}{${i3.join("")}}`;
      t2.rules.global.apply(o2);
    }
    return n2;
  };
  return s$1(i2, { get name() {
    return i2();
  }, toString: i2 });
}), G$1 = class {
  constructor(e2, t2, r2, n2) {
    this.token = null == e2 ? "" : String(e2), this.value = null == t2 ? "" : String(t2), this.scale = null == r2 ? "" : String(r2), this.prefix = null == n2 ? "" : String(n2);
  }
  get computedValue() {
    return "var(" + this.variable + ")";
  }
  get variable() {
    return "--" + S$1(this.prefix) + S$1(this.scale) + this.token;
  }
  toString() {
    return this.computedValue;
  }
}, F$1 = o$2(), J$1 = (e2, t2) => F$1(e2, () => (r2, n2) => {
  n2 = "object" == typeof r2 && r2 || Object(n2);
  const i2 = `.${r2 = (r2 = "string" == typeof r2 ? r2 : "") || `${S$1(e2.prefix)}t-${z$1(n2)}`}`, o2 = {}, l2 = [];
  for (const t3 in n2) {
    o2[t3] = {};
    for (const r3 in n2[t3]) {
      const i3 = `--${S$1(e2.prefix)}${t3}-${r3}`, s3 = k$2(String(n2[t3][r3]), e2.prefix, t3);
      o2[t3][r3] = new G$1(r3, s3, t3, e2.prefix), l2.push(`${i3}:${s3}`);
    }
  }
  const s2 = () => {
    if (l2.length && !t2.rules.themed.cache.has(r2)) {
      t2.rules.themed.cache.add(r2);
      const i3 = `${n2 === e2.theme ? ":root," : ""}.${r2}{${l2.join(";")}}`;
      t2.rules.themed.apply(i3);
    }
    return r2;
  };
  return { ...o2, get className() {
    return s2();
  }, selector: i2, toString: s2 };
}), U$1 = o$2();
var Y$1 = o$2(), q$1 = (e2) => {
  const t2 = ((e3) => {
    let t3 = false;
    const r2 = U$1(e3, (e4) => {
      t3 = true;
      const r3 = "prefix" in (e4 = "object" == typeof e4 && e4 || {}) ? String(e4.prefix) : "", i2 = "object" == typeof e4.media && e4.media || {}, o2 = "object" == typeof e4.root ? e4.root || null : globalThis.document || null, l2 = "object" == typeof e4.theme && e4.theme || {}, s2 = { prefix: r3, media: i2, theme: l2, themeMap: "object" == typeof e4.themeMap && e4.themeMap || { ...n$1 }, utils: "object" == typeof e4.utils && e4.utils || {} }, a2 = E$2(o2), c6 = { css: M$1(s2, a2), globalCss: D(s2, a2), keyframes: V$2(s2, a2), createTheme: J$1(s2, a2), reset() {
        a2.reset(), c6.theme.toString();
      }, theme: {}, sheet: a2, config: s2, prefix: r3, getCssText: a2.toString, toString: a2.toString };
      return String(c6.theme = c6.createTheme(l2)), c6;
    });
    return t3 || r2.reset(), r2;
  })(e2);
  return t2.styled = (({ config: e3, sheet: t3 }) => Y$1(e3, () => {
    const r2 = M$1(e3, t3);
    return (...e4) => {
      const t4 = r2(...e4), n2 = t4[l$1].type, i2 = React__default.forwardRef((e5, r3) => {
        const i3 = e5 && e5.as || n2, { props: o2, deferredInjector: l2 } = t4(e5);
        return delete o2.as, o2.ref = r3, l2 ? React__default.createElement(React__default.Fragment, null, React__default.createElement(i3, o2), React__default.createElement(l2, null)) : React__default.createElement(i3, o2);
      });
      return i2.className = t4.className, i2.displayName = `Styled.${n2.displayName || n2.name || n2}`, i2.selector = t4.selector, i2.toString = () => t4.selector, i2[l$1] = t4[l$1], i2;
    };
  }))(t2), t2;
};
function clamp$1(v2, min, max) {
  return Math.max(min, Math.min(v2, max));
}
const V$1 = {
  toVector(v2, fallback) {
    if (v2 === void 0)
      v2 = fallback;
    return Array.isArray(v2) ? v2 : [v2, v2];
  },
  add(v1, v2) {
    return [v1[0] + v2[0], v1[1] + v2[1]];
  },
  sub(v1, v2) {
    return [v1[0] - v2[0], v1[1] - v2[1]];
  },
  addTo(v1, v2) {
    v1[0] += v2[0];
    v1[1] += v2[1];
  },
  subTo(v1, v2) {
    v1[0] -= v2[0];
    v1[1] -= v2[1];
  }
};
function rubberband(distance2, dimension, constant) {
  if (dimension === 0 || Math.abs(dimension) === Infinity)
    return Math.pow(distance2, constant * 5);
  return distance2 * dimension * constant / (dimension + constant * distance2);
}
function rubberbandIfOutOfBounds(position, min, max, constant = 0.15) {
  if (constant === 0)
    return clamp$1(position, min, max);
  if (position < min)
    return -rubberband(min - position, max - min, constant) + min;
  if (position > max)
    return +rubberband(position - max, max - min, constant) + max;
  return position;
}
function computeRubberband(bounds, [Vx, Vy], [Rx, Ry]) {
  const [[X0, X1], [Y0, Y1]] = bounds;
  return [rubberbandIfOutOfBounds(Vx, X0, X1, Rx), rubberbandIfOutOfBounds(Vy, Y0, Y1, Ry)];
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null)
    return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== void 0) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object")
      return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}
function _defineProperty$1(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function ownKeys$1(object, enumerableOnly) {
  var keys2 = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys2.push.apply(keys2, symbols);
  }
  return keys2;
}
function _objectSpread2$1(target) {
  for (var i2 = 1; i2 < arguments.length; i2++) {
    var source = null != arguments[i2] ? arguments[i2] : {};
    i2 % 2 ? ownKeys$1(Object(source), true).forEach(function(key) {
      _defineProperty$1(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
const EVENT_TYPE_MAP = {
  pointer: {
    start: "down",
    change: "move",
    end: "up"
  },
  mouse: {
    start: "down",
    change: "move",
    end: "up"
  },
  touch: {
    start: "start",
    change: "move",
    end: "end"
  },
  gesture: {
    start: "start",
    change: "change",
    end: "end"
  }
};
function capitalize(string) {
  if (!string)
    return "";
  return string[0].toUpperCase() + string.slice(1);
}
const actionsWithoutCaptureSupported = ["enter", "leave"];
function hasCapture(capture = false, actionKey) {
  return capture && !actionsWithoutCaptureSupported.includes(actionKey);
}
function toHandlerProp(device, action = "", capture = false) {
  const deviceProps = EVENT_TYPE_MAP[device];
  const actionKey = deviceProps ? deviceProps[action] || action : action;
  return "on" + capitalize(device) + capitalize(actionKey) + (hasCapture(capture, actionKey) ? "Capture" : "");
}
const pointerCaptureEvents = ["gotpointercapture", "lostpointercapture"];
function parseProp(prop) {
  let eventKey = prop.substring(2).toLowerCase();
  const passive = !!~eventKey.indexOf("passive");
  if (passive)
    eventKey = eventKey.replace("passive", "");
  const captureKey = pointerCaptureEvents.includes(eventKey) ? "capturecapture" : "capture";
  const capture = !!~eventKey.indexOf(captureKey);
  if (capture)
    eventKey = eventKey.replace("capture", "");
  return {
    device: eventKey,
    capture,
    passive
  };
}
function toDomEventType(device, action = "") {
  const deviceProps = EVENT_TYPE_MAP[device];
  const actionKey = deviceProps ? deviceProps[action] || action : action;
  return device + actionKey;
}
function isTouch(event) {
  return "touches" in event;
}
function getPointerType(event) {
  if (isTouch(event))
    return "touch";
  if ("pointerType" in event)
    return event.pointerType;
  return "mouse";
}
function getCurrentTargetTouchList(event) {
  return Array.from(event.touches).filter((e2) => {
    var _event$currentTarget, _event$currentTarget$;
    return e2.target === event.currentTarget || ((_event$currentTarget = event.currentTarget) === null || _event$currentTarget === void 0 ? void 0 : (_event$currentTarget$ = _event$currentTarget.contains) === null || _event$currentTarget$ === void 0 ? void 0 : _event$currentTarget$.call(_event$currentTarget, e2.target));
  });
}
function getTouchList(event) {
  return event.type === "touchend" || event.type === "touchcancel" ? event.changedTouches : event.targetTouches;
}
function getValueEvent(event) {
  return isTouch(event) ? getTouchList(event)[0] : event;
}
function touchIds(event) {
  return getCurrentTargetTouchList(event).map((touch) => touch.identifier);
}
function pointerId(event) {
  const valueEvent = getValueEvent(event);
  return isTouch(event) ? valueEvent.identifier : valueEvent.pointerId;
}
function pointerValues(event) {
  const valueEvent = getValueEvent(event);
  return [valueEvent.clientX, valueEvent.clientY];
}
function getEventDetails(event) {
  const payload = {};
  if ("buttons" in event)
    payload.buttons = event.buttons;
  if ("shiftKey" in event) {
    const {
      shiftKey,
      altKey,
      metaKey,
      ctrlKey
    } = event;
    Object.assign(payload, {
      shiftKey,
      altKey,
      metaKey,
      ctrlKey
    });
  }
  return payload;
}
function call(v2, ...args) {
  if (typeof v2 === "function") {
    return v2(...args);
  } else {
    return v2;
  }
}
function noop() {
}
function chain(...fns) {
  if (fns.length === 0)
    return noop;
  if (fns.length === 1)
    return fns[0];
  return function() {
    let result;
    for (const fn of fns) {
      result = fn.apply(this, arguments) || result;
    }
    return result;
  };
}
function assignDefault(value, fallback) {
  return Object.assign({}, fallback, value || {});
}
const BEFORE_LAST_KINEMATICS_DELAY = 32;
class Engine {
  constructor(ctrl, args, key) {
    this.ctrl = ctrl;
    this.args = args;
    this.key = key;
    if (!this.state) {
      this.state = {};
      this.computeValues([0, 0]);
      this.computeInitial();
      if (this.init)
        this.init();
      this.reset();
    }
  }
  get state() {
    return this.ctrl.state[this.key];
  }
  set state(state) {
    this.ctrl.state[this.key] = state;
  }
  get shared() {
    return this.ctrl.state.shared;
  }
  get eventStore() {
    return this.ctrl.gestureEventStores[this.key];
  }
  get timeoutStore() {
    return this.ctrl.gestureTimeoutStores[this.key];
  }
  get config() {
    return this.ctrl.config[this.key];
  }
  get sharedConfig() {
    return this.ctrl.config.shared;
  }
  get handler() {
    return this.ctrl.handlers[this.key];
  }
  reset() {
    const {
      state,
      shared,
      ingKey,
      args
    } = this;
    shared[ingKey] = state._active = state.active = state._blocked = state._force = false;
    state._step = [false, false];
    state.intentional = false;
    state._movement = [0, 0];
    state._distance = [0, 0];
    state._direction = [0, 0];
    state._delta = [0, 0];
    state._bounds = [[-Infinity, Infinity], [-Infinity, Infinity]];
    state.args = args;
    state.axis = void 0;
    state.memo = void 0;
    state.elapsedTime = 0;
    state.direction = [0, 0];
    state.distance = [0, 0];
    state.overflow = [0, 0];
    state._movementBound = [false, false];
    state.velocity = [0, 0];
    state.movement = [0, 0];
    state.delta = [0, 0];
    state.timeStamp = 0;
  }
  start(event) {
    const state = this.state;
    const config = this.config;
    if (!state._active) {
      this.reset();
      this.computeInitial();
      state._active = true;
      state.target = event.target;
      state.currentTarget = event.currentTarget;
      state.lastOffset = config.from ? call(config.from, state) : state.offset;
      state.offset = state.lastOffset;
    }
    state.startTime = state.timeStamp = event.timeStamp;
  }
  computeValues(values) {
    const state = this.state;
    state._values = values;
    state.values = this.config.transform(values);
  }
  computeInitial() {
    const state = this.state;
    state._initial = state._values;
    state.initial = state.values;
  }
  compute(event) {
    const {
      state,
      config,
      shared
    } = this;
    state.args = this.args;
    let dt = 0;
    if (event) {
      state.event = event;
      if (config.preventDefault && event.cancelable)
        state.event.preventDefault();
      state.type = event.type;
      shared.touches = this.ctrl.pointerIds.size || this.ctrl.touchIds.size;
      shared.locked = !!document.pointerLockElement;
      Object.assign(shared, getEventDetails(event));
      shared.down = shared.pressed = shared.buttons % 2 === 1 || shared.touches > 0;
      dt = event.timeStamp - state.timeStamp;
      state.timeStamp = event.timeStamp;
      state.elapsedTime = state.timeStamp - state.startTime;
    }
    if (state._active) {
      const _absoluteDelta = state._delta.map(Math.abs);
      V$1.addTo(state._distance, _absoluteDelta);
    }
    if (this.axisIntent)
      this.axisIntent(event);
    const [_m0, _m1] = state._movement;
    const [t0, t1] = config.threshold;
    const {
      _step,
      values
    } = state;
    if (config.hasCustomTransform) {
      if (_step[0] === false)
        _step[0] = Math.abs(_m0) >= t0 && values[0];
      if (_step[1] === false)
        _step[1] = Math.abs(_m1) >= t1 && values[1];
    } else {
      if (_step[0] === false)
        _step[0] = Math.abs(_m0) >= t0 && Math.sign(_m0) * t0;
      if (_step[1] === false)
        _step[1] = Math.abs(_m1) >= t1 && Math.sign(_m1) * t1;
    }
    state.intentional = _step[0] !== false || _step[1] !== false;
    if (!state.intentional)
      return;
    const movement = [0, 0];
    if (config.hasCustomTransform) {
      const [v0, v1] = values;
      movement[0] = _step[0] !== false ? v0 - _step[0] : 0;
      movement[1] = _step[1] !== false ? v1 - _step[1] : 0;
    } else {
      movement[0] = _step[0] !== false ? _m0 - _step[0] : 0;
      movement[1] = _step[1] !== false ? _m1 - _step[1] : 0;
    }
    if (this.restrictToAxis && !state._blocked)
      this.restrictToAxis(movement);
    const previousOffset = state.offset;
    const gestureIsActive = state._active && !state._blocked || state.active;
    if (gestureIsActive) {
      state.first = state._active && !state.active;
      state.last = !state._active && state.active;
      state.active = shared[this.ingKey] = state._active;
      if (event) {
        if (state.first) {
          if ("bounds" in config)
            state._bounds = call(config.bounds, state);
          if (this.setup)
            this.setup();
        }
        state.movement = movement;
        this.computeOffset();
      }
    }
    const [ox, oy] = state.offset;
    const [[x0, x1], [y0, y1]] = state._bounds;
    state.overflow = [ox < x0 ? -1 : ox > x1 ? 1 : 0, oy < y0 ? -1 : oy > y1 ? 1 : 0];
    state._movementBound[0] = state.overflow[0] ? state._movementBound[0] === false ? state._movement[0] : state._movementBound[0] : false;
    state._movementBound[1] = state.overflow[1] ? state._movementBound[1] === false ? state._movement[1] : state._movementBound[1] : false;
    const rubberband2 = state._active ? config.rubberband || [0, 0] : [0, 0];
    state.offset = computeRubberband(state._bounds, state.offset, rubberband2);
    state.delta = V$1.sub(state.offset, previousOffset);
    this.computeMovement();
    if (gestureIsActive && (!state.last || dt > BEFORE_LAST_KINEMATICS_DELAY)) {
      state.delta = V$1.sub(state.offset, previousOffset);
      const absoluteDelta = state.delta.map(Math.abs);
      V$1.addTo(state.distance, absoluteDelta);
      state.direction = state.delta.map(Math.sign);
      state._direction = state._delta.map(Math.sign);
      if (!state.first && dt > 0) {
        state.velocity = [absoluteDelta[0] / dt, absoluteDelta[1] / dt];
      }
    }
  }
  emit() {
    const state = this.state;
    const shared = this.shared;
    const config = this.config;
    if (!state._active)
      this.clean();
    if ((state._blocked || !state.intentional) && !state._force && !config.triggerAllEvents)
      return;
    const memo = this.handler(_objectSpread2$1(_objectSpread2$1(_objectSpread2$1({}, shared), state), {}, {
      [this.aliasKey]: state.values
    }));
    if (memo !== void 0)
      state.memo = memo;
  }
  clean() {
    this.eventStore.clean();
    this.timeoutStore.clean();
  }
}
function selectAxis([dx, dy], threshold) {
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  if (absDx > absDy && absDx > threshold) {
    return "x";
  }
  if (absDy > absDx && absDy > threshold) {
    return "y";
  }
  return void 0;
}
class CoordinatesEngine extends Engine {
  constructor(...args) {
    super(...args);
    _defineProperty$1(this, "aliasKey", "xy");
  }
  reset() {
    super.reset();
    this.state.axis = void 0;
  }
  init() {
    this.state.offset = [0, 0];
    this.state.lastOffset = [0, 0];
  }
  computeOffset() {
    this.state.offset = V$1.add(this.state.lastOffset, this.state.movement);
  }
  computeMovement() {
    this.state.movement = V$1.sub(this.state.offset, this.state.lastOffset);
  }
  axisIntent(event) {
    const state = this.state;
    const config = this.config;
    if (!state.axis && event) {
      const threshold = typeof config.axisThreshold === "object" ? config.axisThreshold[getPointerType(event)] : config.axisThreshold;
      state.axis = selectAxis(state._movement, threshold);
    }
    state._blocked = (config.lockDirection || !!config.axis) && !state.axis || !!config.axis && config.axis !== state.axis;
  }
  restrictToAxis(v2) {
    if (this.config.axis || this.config.lockDirection) {
      switch (this.state.axis) {
        case "x":
          v2[1] = 0;
          break;
        case "y":
          v2[0] = 0;
          break;
      }
    }
  }
}
const identity = (v2) => v2;
const DEFAULT_RUBBERBAND = 0.15;
const commonConfigResolver = {
  enabled(value = true) {
    return value;
  },
  eventOptions(value, _k, config) {
    return _objectSpread2$1(_objectSpread2$1({}, config.shared.eventOptions), value);
  },
  preventDefault(value = false) {
    return value;
  },
  triggerAllEvents(value = false) {
    return value;
  },
  rubberband(value = 0) {
    switch (value) {
      case true:
        return [DEFAULT_RUBBERBAND, DEFAULT_RUBBERBAND];
      case false:
        return [0, 0];
      default:
        return V$1.toVector(value);
    }
  },
  from(value) {
    if (typeof value === "function")
      return value;
    if (value != null)
      return V$1.toVector(value);
  },
  transform(value, _k, config) {
    const transform2 = value || config.shared.transform;
    this.hasCustomTransform = !!transform2;
    return transform2 || identity;
  },
  threshold(value) {
    return V$1.toVector(value, 0);
  }
};
const DEFAULT_AXIS_THRESHOLD = 0;
const coordinatesConfigResolver = _objectSpread2$1(_objectSpread2$1({}, commonConfigResolver), {}, {
  axis(_v, _k, {
    axis
  }) {
    this.lockDirection = axis === "lock";
    if (!this.lockDirection)
      return axis;
  },
  axisThreshold(value = DEFAULT_AXIS_THRESHOLD) {
    return value;
  },
  bounds(value = {}) {
    if (typeof value === "function") {
      return (state) => coordinatesConfigResolver.bounds(value(state));
    }
    if ("current" in value) {
      return () => value.current;
    }
    if (typeof HTMLElement === "function" && value instanceof HTMLElement) {
      return value;
    }
    const {
      left = -Infinity,
      right = Infinity,
      top = -Infinity,
      bottom = Infinity
    } = value;
    return [[left, right], [top, bottom]];
  }
});
const KEYS_DELTA_MAP = {
  ArrowRight: (displacement, factor = 1) => [displacement * factor, 0],
  ArrowLeft: (displacement, factor = 1) => [-1 * displacement * factor, 0],
  ArrowUp: (displacement, factor = 1) => [0, -1 * displacement * factor],
  ArrowDown: (displacement, factor = 1) => [0, displacement * factor]
};
class DragEngine extends CoordinatesEngine {
  constructor(...args) {
    super(...args);
    _defineProperty$1(this, "ingKey", "dragging");
  }
  reset() {
    super.reset();
    const state = this.state;
    state._pointerId = void 0;
    state._pointerActive = false;
    state._keyboardActive = false;
    state._preventScroll = false;
    state._delayed = false;
    state.swipe = [0, 0];
    state.tap = false;
    state.canceled = false;
    state.cancel = this.cancel.bind(this);
  }
  setup() {
    const state = this.state;
    if (state._bounds instanceof HTMLElement) {
      const boundRect = state._bounds.getBoundingClientRect();
      const targetRect = state.currentTarget.getBoundingClientRect();
      const _bounds = {
        left: boundRect.left - targetRect.left + state.offset[0],
        right: boundRect.right - targetRect.right + state.offset[0],
        top: boundRect.top - targetRect.top + state.offset[1],
        bottom: boundRect.bottom - targetRect.bottom + state.offset[1]
      };
      state._bounds = coordinatesConfigResolver.bounds(_bounds);
    }
  }
  cancel() {
    const state = this.state;
    if (state.canceled)
      return;
    state.canceled = true;
    state._active = false;
    setTimeout(() => {
      this.compute();
      this.emit();
    }, 0);
  }
  setActive() {
    this.state._active = this.state._pointerActive || this.state._keyboardActive;
  }
  clean() {
    this.pointerClean();
    this.state._pointerActive = false;
    this.state._keyboardActive = false;
    super.clean();
  }
  pointerDown(event) {
    const config = this.config;
    const state = this.state;
    if (event.buttons != null && (Array.isArray(config.pointerButtons) ? !config.pointerButtons.includes(event.buttons) : config.pointerButtons !== -1 && config.pointerButtons !== event.buttons))
      return;
    const ctrlIds = this.ctrl.setEventIds(event);
    if (config.pointerCapture) {
      event.target.setPointerCapture(event.pointerId);
    }
    if (ctrlIds && ctrlIds.size > 1 && state._pointerActive)
      return;
    this.start(event);
    this.setupPointer(event);
    state._pointerId = pointerId(event);
    state._pointerActive = true;
    this.computeValues(pointerValues(event));
    this.computeInitial();
    if (config.preventScrollAxis && getPointerType(event) !== "mouse") {
      state._active = false;
      this.setupScrollPrevention(event);
    } else if (config.delay > 0) {
      this.setupDelayTrigger(event);
      if (config.triggerAllEvents) {
        this.compute(event);
        this.emit();
      }
    } else {
      this.startPointerDrag(event);
    }
  }
  startPointerDrag(event) {
    const state = this.state;
    state._active = true;
    state._preventScroll = true;
    state._delayed = false;
    this.compute(event);
    this.emit();
  }
  pointerMove(event) {
    const state = this.state;
    const config = this.config;
    if (!state._pointerActive)
      return;
    if (state.type === event.type && event.timeStamp === state.timeStamp)
      return;
    const id = pointerId(event);
    if (state._pointerId !== void 0 && id !== state._pointerId)
      return;
    const _values = pointerValues(event);
    if (document.pointerLockElement === event.target) {
      state._delta = [event.movementX, event.movementY];
    } else {
      state._delta = V$1.sub(_values, state._values);
      this.computeValues(_values);
    }
    V$1.addTo(state._movement, state._delta);
    this.compute(event);
    if (state._delayed && state.intentional) {
      this.timeoutStore.remove("dragDelay");
      state.active = false;
      this.startPointerDrag(event);
      return;
    }
    if (config.preventScrollAxis && !state._preventScroll) {
      if (state.axis) {
        if (state.axis === config.preventScrollAxis || config.preventScrollAxis === "xy") {
          state._active = false;
          this.clean();
          return;
        } else {
          this.timeoutStore.remove("startPointerDrag");
          this.startPointerDrag(event);
          return;
        }
      } else {
        return;
      }
    }
    this.emit();
  }
  pointerUp(event) {
    this.ctrl.setEventIds(event);
    try {
      if (this.config.pointerCapture && event.target.hasPointerCapture(event.pointerId)) {
        ;
        event.target.releasePointerCapture(event.pointerId);
      }
    } catch (_unused) {
    }
    const state = this.state;
    const config = this.config;
    if (!state._active || !state._pointerActive)
      return;
    const id = pointerId(event);
    if (state._pointerId !== void 0 && id !== state._pointerId)
      return;
    this.state._pointerActive = false;
    this.setActive();
    this.compute(event);
    const [dx, dy] = state._distance;
    state.tap = dx <= config.tapsThreshold && dy <= config.tapsThreshold;
    if (state.tap && config.filterTaps) {
      state._force = true;
    } else {
      const [dirx, diry] = state.direction;
      const [vx, vy] = state.velocity;
      const [mx, my] = state.movement;
      const [svx, svy] = config.swipe.velocity;
      const [sx, sy] = config.swipe.distance;
      const sdt = config.swipe.duration;
      if (state.elapsedTime < sdt) {
        if (Math.abs(vx) > svx && Math.abs(mx) > sx)
          state.swipe[0] = dirx;
        if (Math.abs(vy) > svy && Math.abs(my) > sy)
          state.swipe[1] = diry;
      }
    }
    this.emit();
  }
  pointerClick(event) {
    if (!this.state.tap && event.detail > 0) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
  setupPointer(event) {
    const config = this.config;
    const device = config.device;
    if (config.pointerLock) {
      event.currentTarget.requestPointerLock();
    }
    if (!config.pointerCapture) {
      this.eventStore.add(this.sharedConfig.window, device, "change", this.pointerMove.bind(this));
      this.eventStore.add(this.sharedConfig.window, device, "end", this.pointerUp.bind(this));
      this.eventStore.add(this.sharedConfig.window, device, "cancel", this.pointerUp.bind(this));
    }
  }
  pointerClean() {
    if (this.config.pointerLock && document.pointerLockElement === this.state.currentTarget) {
      document.exitPointerLock();
    }
  }
  preventScroll(event) {
    if (this.state._preventScroll && event.cancelable) {
      event.preventDefault();
    }
  }
  setupScrollPrevention(event) {
    this.state._preventScroll = false;
    persistEvent(event);
    const remove = this.eventStore.add(this.sharedConfig.window, "touch", "change", this.preventScroll.bind(this), {
      passive: false
    });
    this.eventStore.add(this.sharedConfig.window, "touch", "end", remove);
    this.eventStore.add(this.sharedConfig.window, "touch", "cancel", remove);
    this.timeoutStore.add("startPointerDrag", this.startPointerDrag.bind(this), this.config.preventScrollDelay, event);
  }
  setupDelayTrigger(event) {
    this.state._delayed = true;
    this.timeoutStore.add("dragDelay", () => {
      this.state._step = [0, 0];
      this.startPointerDrag(event);
    }, this.config.delay);
  }
  keyDown(event) {
    const deltaFn = KEYS_DELTA_MAP[event.key];
    if (deltaFn) {
      const state = this.state;
      const factor = event.shiftKey ? 10 : event.altKey ? 0.1 : 1;
      this.start(event);
      state._delta = deltaFn(this.config.keyboardDisplacement, factor);
      state._keyboardActive = true;
      V$1.addTo(state._movement, state._delta);
      this.compute(event);
      this.emit();
    }
  }
  keyUp(event) {
    if (!(event.key in KEYS_DELTA_MAP))
      return;
    this.state._keyboardActive = false;
    this.setActive();
    this.compute(event);
    this.emit();
  }
  bind(bindFunction) {
    const device = this.config.device;
    bindFunction(device, "start", this.pointerDown.bind(this));
    if (this.config.pointerCapture) {
      bindFunction(device, "change", this.pointerMove.bind(this));
      bindFunction(device, "end", this.pointerUp.bind(this));
      bindFunction(device, "cancel", this.pointerUp.bind(this));
      bindFunction("lostPointerCapture", "", this.pointerUp.bind(this));
    }
    if (this.config.keys) {
      bindFunction("key", "down", this.keyDown.bind(this));
      bindFunction("key", "up", this.keyUp.bind(this));
    }
    if (this.config.filterTaps) {
      bindFunction("click", "", this.pointerClick.bind(this), {
        capture: true,
        passive: false
      });
    }
  }
}
function persistEvent(event) {
  "persist" in event && typeof event.persist === "function" && event.persist();
}
const isBrowser = typeof window !== "undefined" && window.document && window.document.createElement;
function supportsTouchEvents() {
  return isBrowser && "ontouchstart" in window;
}
function isTouchScreen() {
  return supportsTouchEvents() || isBrowser && window.navigator.maxTouchPoints > 1;
}
function supportsPointerEvents() {
  return isBrowser && "onpointerdown" in window;
}
function supportsPointerLock() {
  return isBrowser && "exitPointerLock" in window.document;
}
function supportsGestureEvents() {
  try {
    return "constructor" in GestureEvent;
  } catch (e2) {
    return false;
  }
}
const SUPPORT = {
  isBrowser,
  gesture: supportsGestureEvents(),
  touch: isTouchScreen(),
  touchscreen: isTouchScreen(),
  pointer: supportsPointerEvents(),
  pointerLock: supportsPointerLock()
};
const DEFAULT_PREVENT_SCROLL_DELAY = 250;
const DEFAULT_DRAG_DELAY = 180;
const DEFAULT_SWIPE_VELOCITY = 0.5;
const DEFAULT_SWIPE_DISTANCE = 50;
const DEFAULT_SWIPE_DURATION = 250;
const DEFAULT_KEYBOARD_DISPLACEMENT = 10;
const DEFAULT_DRAG_AXIS_THRESHOLD = {
  mouse: 0,
  touch: 0,
  pen: 8
};
const dragConfigResolver = _objectSpread2$1(_objectSpread2$1({}, coordinatesConfigResolver), {}, {
  device(_v, _k, {
    pointer: {
      touch = false,
      lock = false,
      mouse = false
    } = {}
  }) {
    this.pointerLock = lock && SUPPORT.pointerLock;
    if (SUPPORT.touch && touch)
      return "touch";
    if (this.pointerLock)
      return "mouse";
    if (SUPPORT.pointer && !mouse)
      return "pointer";
    if (SUPPORT.touch)
      return "touch";
    return "mouse";
  },
  preventScrollAxis(value, _k, {
    preventScroll
  }) {
    this.preventScrollDelay = typeof preventScroll === "number" ? preventScroll : preventScroll || preventScroll === void 0 && value ? DEFAULT_PREVENT_SCROLL_DELAY : void 0;
    if (!SUPPORT.touchscreen || preventScroll === false)
      return void 0;
    return value ? value : preventScroll !== void 0 ? "y" : void 0;
  },
  pointerCapture(_v, _k, {
    pointer: {
      capture = true,
      buttons = 1,
      keys: keys2 = true
    } = {}
  }) {
    this.pointerButtons = buttons;
    this.keys = keys2;
    return !this.pointerLock && this.device === "pointer" && capture;
  },
  threshold(value, _k, {
    filterTaps = false,
    tapsThreshold = 3,
    axis = void 0
  }) {
    const threshold = V$1.toVector(value, filterTaps ? tapsThreshold : axis ? 1 : 0);
    this.filterTaps = filterTaps;
    this.tapsThreshold = tapsThreshold;
    return threshold;
  },
  swipe({
    velocity = DEFAULT_SWIPE_VELOCITY,
    distance: distance2 = DEFAULT_SWIPE_DISTANCE,
    duration = DEFAULT_SWIPE_DURATION
  } = {}) {
    return {
      velocity: this.transform(V$1.toVector(velocity)),
      distance: this.transform(V$1.toVector(distance2)),
      duration
    };
  },
  delay(value = 0) {
    switch (value) {
      case true:
        return DEFAULT_DRAG_DELAY;
      case false:
        return 0;
      default:
        return value;
    }
  },
  axisThreshold(value) {
    if (!value)
      return DEFAULT_DRAG_AXIS_THRESHOLD;
    return _objectSpread2$1(_objectSpread2$1({}, DEFAULT_DRAG_AXIS_THRESHOLD), value);
  },
  keyboardDisplacement(value = DEFAULT_KEYBOARD_DISPLACEMENT) {
    return value;
  }
});
_objectSpread2$1(_objectSpread2$1({}, commonConfigResolver), {}, {
  device(_v, _k, {
    shared,
    pointer: {
      touch = false
    } = {}
  }) {
    const sharedConfig = shared;
    if (sharedConfig.target && !SUPPORT.touch && SUPPORT.gesture)
      return "gesture";
    if (SUPPORT.touch && touch)
      return "touch";
    if (SUPPORT.touchscreen) {
      if (SUPPORT.pointer)
        return "pointer";
      if (SUPPORT.touch)
        return "touch";
    }
  },
  bounds(_v, _k, {
    scaleBounds = {},
    angleBounds = {}
  }) {
    const _scaleBounds = (state) => {
      const D2 = assignDefault(call(scaleBounds, state), {
        min: -Infinity,
        max: Infinity
      });
      return [D2.min, D2.max];
    };
    const _angleBounds = (state) => {
      const A2 = assignDefault(call(angleBounds, state), {
        min: -Infinity,
        max: Infinity
      });
      return [A2.min, A2.max];
    };
    if (typeof scaleBounds !== "function" && typeof angleBounds !== "function")
      return [_scaleBounds(), _angleBounds()];
    return (state) => [_scaleBounds(state), _angleBounds(state)];
  },
  threshold(value, _k, config) {
    this.lockDirection = config.axis === "lock";
    const threshold = V$1.toVector(value, this.lockDirection ? [0.1, 3] : 0);
    return threshold;
  },
  modifierKey(value) {
    if (value === void 0)
      return "ctrlKey";
    return value;
  },
  pinchOnWheel(value = true) {
    return value;
  }
});
_objectSpread2$1(_objectSpread2$1({}, coordinatesConfigResolver), {}, {
  mouseOnly: (value = true) => value
});
_objectSpread2$1(_objectSpread2$1({}, coordinatesConfigResolver), {}, {
  mouseOnly: (value = true) => value
});
const EngineMap = /* @__PURE__ */ new Map();
const ConfigResolverMap = /* @__PURE__ */ new Map();
function registerAction(action) {
  EngineMap.set(action.key, action.engine);
  ConfigResolverMap.set(action.key, action.resolver);
}
const dragAction = {
  key: "drag",
  engine: DragEngine,
  resolver: dragConfigResolver
};
function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null)
    return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i2;
  for (i2 = 0; i2 < sourceKeys.length; i2++) {
    key = sourceKeys[i2];
    if (excluded.indexOf(key) >= 0)
      continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties$1(source, excluded) {
  if (source == null)
    return {};
  var target = _objectWithoutPropertiesLoose$1(source, excluded);
  var key, i2;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i2 = 0; i2 < sourceSymbolKeys.length; i2++) {
      key = sourceSymbolKeys[i2];
      if (excluded.indexOf(key) >= 0)
        continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key))
        continue;
      target[key] = source[key];
    }
  }
  return target;
}
const sharedConfigResolver = {
  target(value) {
    if (value) {
      return () => "current" in value ? value.current : value;
    }
    return void 0;
  },
  enabled(value = true) {
    return value;
  },
  window(value = SUPPORT.isBrowser ? window : void 0) {
    return value;
  },
  eventOptions({
    passive = true,
    capture = false
  } = {}) {
    return {
      passive,
      capture
    };
  },
  transform(value) {
    return value;
  }
};
const _excluded = ["target", "eventOptions", "window", "enabled", "transform"];
function resolveWith(config = {}, resolvers) {
  const result = {};
  for (const [key, resolver] of Object.entries(resolvers)) {
    switch (typeof resolver) {
      case "function":
        {
          result[key] = resolver.call(result, config[key], key, config);
        }
        break;
      case "object":
        result[key] = resolveWith(config[key], resolver);
        break;
      case "boolean":
        if (resolver)
          result[key] = config[key];
        break;
    }
  }
  return result;
}
function parse(newConfig, gestureKey, _config = {}) {
  const _ref = newConfig, {
    target,
    eventOptions,
    window: window2,
    enabled,
    transform: transform2
  } = _ref, rest2 = _objectWithoutProperties$1(_ref, _excluded);
  _config.shared = resolveWith({
    target,
    eventOptions,
    window: window2,
    enabled,
    transform: transform2
  }, sharedConfigResolver);
  if (gestureKey) {
    const resolver = ConfigResolverMap.get(gestureKey);
    _config[gestureKey] = resolveWith(_objectSpread2$1({
      shared: _config.shared
    }, rest2), resolver);
  } else {
    for (const key in rest2) {
      const resolver = ConfigResolverMap.get(key);
      if (resolver) {
        _config[key] = resolveWith(_objectSpread2$1({
          shared: _config.shared
        }, rest2[key]), resolver);
      }
    }
  }
  return _config;
}
class EventStore {
  constructor(ctrl, gestureKey) {
    _defineProperty$1(this, "_listeners", /* @__PURE__ */ new Set());
    this._ctrl = ctrl;
    this._gestureKey = gestureKey;
  }
  add(element, device, action, handler, options) {
    const listeners = this._listeners;
    const type = toDomEventType(device, action);
    const _options = this._gestureKey ? this._ctrl.config[this._gestureKey].eventOptions : {};
    const eventOptions = _objectSpread2$1(_objectSpread2$1({}, _options), options);
    element.addEventListener(type, handler, eventOptions);
    const remove = () => {
      element.removeEventListener(type, handler, eventOptions);
      listeners.delete(remove);
    };
    listeners.add(remove);
    return remove;
  }
  clean() {
    this._listeners.forEach((remove) => remove());
    this._listeners.clear();
  }
}
class TimeoutStore {
  constructor() {
    _defineProperty$1(this, "_timeouts", /* @__PURE__ */ new Map());
  }
  add(key, callback, ms = 140, ...args) {
    this.remove(key);
    this._timeouts.set(key, window.setTimeout(callback, ms, ...args));
  }
  remove(key) {
    const timeout = this._timeouts.get(key);
    if (timeout)
      window.clearTimeout(timeout);
  }
  clean() {
    this._timeouts.forEach((timeout) => void window.clearTimeout(timeout));
    this._timeouts.clear();
  }
}
class Controller {
  constructor(handlers) {
    _defineProperty$1(this, "gestures", /* @__PURE__ */ new Set());
    _defineProperty$1(this, "_targetEventStore", new EventStore(this));
    _defineProperty$1(this, "gestureEventStores", {});
    _defineProperty$1(this, "gestureTimeoutStores", {});
    _defineProperty$1(this, "handlers", {});
    _defineProperty$1(this, "config", {});
    _defineProperty$1(this, "pointerIds", /* @__PURE__ */ new Set());
    _defineProperty$1(this, "touchIds", /* @__PURE__ */ new Set());
    _defineProperty$1(this, "state", {
      shared: {
        shiftKey: false,
        metaKey: false,
        ctrlKey: false,
        altKey: false
      }
    });
    resolveGestures(this, handlers);
  }
  setEventIds(event) {
    if (isTouch(event)) {
      this.touchIds = new Set(touchIds(event));
      return this.touchIds;
    } else if ("pointerId" in event) {
      if (event.type === "pointerup" || event.type === "pointercancel")
        this.pointerIds.delete(event.pointerId);
      else if (event.type === "pointerdown")
        this.pointerIds.add(event.pointerId);
      return this.pointerIds;
    }
  }
  applyHandlers(handlers, nativeHandlers) {
    this.handlers = handlers;
    this.nativeHandlers = nativeHandlers;
  }
  applyConfig(config, gestureKey) {
    this.config = parse(config, gestureKey, this.config);
  }
  clean() {
    this._targetEventStore.clean();
    for (const key of this.gestures) {
      this.gestureEventStores[key].clean();
      this.gestureTimeoutStores[key].clean();
    }
  }
  effect() {
    if (this.config.shared.target)
      this.bind();
    return () => this._targetEventStore.clean();
  }
  bind(...args) {
    const sharedConfig = this.config.shared;
    const props2 = {};
    let target;
    if (sharedConfig.target) {
      target = sharedConfig.target();
      if (!target)
        return;
    }
    if (sharedConfig.enabled) {
      for (const gestureKey of this.gestures) {
        const gestureConfig = this.config[gestureKey];
        const bindFunction = bindToProps(props2, gestureConfig.eventOptions, !!target);
        if (gestureConfig.enabled) {
          const Engine2 = EngineMap.get(gestureKey);
          new Engine2(this, args, gestureKey).bind(bindFunction);
        }
      }
      const nativeBindFunction = bindToProps(props2, sharedConfig.eventOptions, !!target);
      for (const eventKey in this.nativeHandlers) {
        nativeBindFunction(eventKey, "", (event) => this.nativeHandlers[eventKey](_objectSpread2$1(_objectSpread2$1({}, this.state.shared), {}, {
          event,
          args
        })), void 0, true);
      }
    }
    for (const handlerProp in props2) {
      props2[handlerProp] = chain(...props2[handlerProp]);
    }
    if (!target)
      return props2;
    for (const handlerProp in props2) {
      const {
        device,
        capture,
        passive
      } = parseProp(handlerProp);
      this._targetEventStore.add(target, device, "", props2[handlerProp], {
        capture,
        passive
      });
    }
  }
}
function setupGesture(ctrl, gestureKey) {
  ctrl.gestures.add(gestureKey);
  ctrl.gestureEventStores[gestureKey] = new EventStore(ctrl, gestureKey);
  ctrl.gestureTimeoutStores[gestureKey] = new TimeoutStore();
}
function resolveGestures(ctrl, internalHandlers) {
  if (internalHandlers.drag)
    setupGesture(ctrl, "drag");
  if (internalHandlers.wheel)
    setupGesture(ctrl, "wheel");
  if (internalHandlers.scroll)
    setupGesture(ctrl, "scroll");
  if (internalHandlers.move)
    setupGesture(ctrl, "move");
  if (internalHandlers.pinch)
    setupGesture(ctrl, "pinch");
  if (internalHandlers.hover)
    setupGesture(ctrl, "hover");
}
const bindToProps = (props2, eventOptions, withPassiveOption) => (device, action, handler, options = {}, isNative = false) => {
  var _options$capture, _options$passive;
  const capture = (_options$capture = options.capture) !== null && _options$capture !== void 0 ? _options$capture : eventOptions.capture;
  const passive = (_options$passive = options.passive) !== null && _options$passive !== void 0 ? _options$passive : eventOptions.passive;
  let handlerProp = isNative ? device : toHandlerProp(device, action, capture);
  if (withPassiveOption && passive)
    handlerProp += "Passive";
  props2[handlerProp] = props2[handlerProp] || [];
  props2[handlerProp].push(handler);
};
function useRecognizers(handlers, config = {}, gestureKey, nativeHandlers) {
  const ctrl = React__default.useMemo(() => new Controller(handlers), []);
  ctrl.applyHandlers(handlers, nativeHandlers);
  ctrl.applyConfig(config, gestureKey);
  React__default.useEffect(ctrl.effect.bind(ctrl));
  React__default.useEffect(() => {
    return ctrl.clean.bind(ctrl);
  }, []);
  if (config.target === void 0) {
    return ctrl.bind.bind(ctrl);
  }
  return void 0;
}
function useDrag$1(handler, config) {
  registerAction(dragAction);
  return useRecognizers({
    drag: handler
  }, config || {}, "drag");
}
const r$2 = React["useId".toString()] || (() => {
});
let i$1 = 0;
function useId(n2) {
  const [o2, s2] = React.useState(r$2());
  return useLayoutEffect(() => {
    n2 || s2((e2) => null != e2 ? e2 : String(i$1++));
  }, [n2]), n2 || (o2 ? `radix-${o2}` : "");
}
const Primitive = ["a", "button", "div", "h2", "h3", "img", "li", "nav", "p", "span", "svg", "ul"].reduce((t2, s2) => ({ ...t2, [s2]: /* @__PURE__ */ React.forwardRef((t3, n2) => {
  const { asChild: a2, ...m2 } = t3, d2 = a2 ? Slot : s2;
  return React.useEffect(() => {
    window[Symbol.for("radix-ui")] = true;
  }, []), t3.as && console.error(o$1), /* @__PURE__ */ React.createElement(d2, _extends$1({}, m2, { ref: n2 }));
}) }), {});
const o$1 = "Warning: The `as` prop has been removed in favour of `asChild`. For details, see https://radix-ui.com/docs/primitives/overview/styling#changing-the-rendered-element";
const VisuallyHidden = /* @__PURE__ */ React.forwardRef((i2, o2) => /* @__PURE__ */ React.createElement(Primitive.span, _extends$1({}, i2, { ref: o2, style: { ...i2.style, position: "absolute", border: 0, width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", wordWrap: "normal" } })));
const Root$3 = VisuallyHidden;
const Portal = /* @__PURE__ */ React.forwardRef((a2, i2) => {
  var n2, d2;
  const { containerRef: s2, style: u2, ...c6 } = a2, m2 = null !== (n2 = null == s2 ? void 0 : s2.current) && void 0 !== n2 ? n2 : null === globalThis || void 0 === globalThis || null === (d2 = globalThis.document) || void 0 === d2 ? void 0 : d2.body, [, f2] = React.useState({});
  return useLayoutEffect(() => {
    f2({});
  }, []), m2 ? /* @__PURE__ */ e$3.createPortal(/* @__PURE__ */ React.createElement(Primitive.div, _extends$1({ "data-radix-portal": "" }, c6, { ref: i2, style: m2 === document.body ? { position: "absolute", top: 0, left: 0, zIndex: 2147483647, ...u2 } : void 0 })), m2) : null;
});
const Arrow$2 = /* @__PURE__ */ React.forwardRef((o2, i2) => {
  const { children: n2, width: s2 = 10, height: m2 = 5, ...p2 } = o2;
  return React.createElement(Primitive.svg, _extends$1({}, p2, { ref: i2, width: s2, height: m2, viewBox: "0 0 30 10", preserveAspectRatio: "none" }), o2.asChild ? n2 : /* @__PURE__ */ React.createElement("polygon", { points: "0,0 30,0 15,10" }));
});
const Root$2 = Arrow$2;
function useSize(r2) {
  const [i2, t2] = React.useState(void 0);
  return React.useEffect(() => {
    if (r2) {
      const e2 = new ResizeObserver((e3) => {
        if (!Array.isArray(e3))
          return;
        if (!e3.length)
          return;
        const i3 = e3[0];
        let o2, n2;
        if ("borderBoxSize" in i3) {
          const e4 = i3.borderBoxSize, r3 = Array.isArray(e4) ? e4[0] : e4;
          o2 = r3.inlineSize, n2 = r3.blockSize;
        } else {
          const e4 = r2.getBoundingClientRect();
          o2 = e4.width, n2 = e4.height;
        }
        t2({ width: o2, height: n2 });
      });
      return e2.observe(r2, { box: "border-box" }), () => {
        t2(void 0), e2.unobserve(r2);
      };
    }
  }, [r2]), i2;
}
function observeElementRect(n2, o2) {
  const i2 = e$1.get(n2);
  return void 0 === i2 ? (e$1.set(n2, { rect: {}, callbacks: [o2] }), 1 === e$1.size && (t$2 = requestAnimationFrame(c$1))) : (i2.callbacks.push(o2), o2(n2.getBoundingClientRect())), () => {
    const c6 = e$1.get(n2);
    if (void 0 === c6)
      return;
    const i3 = c6.callbacks.indexOf(o2);
    i3 > -1 && c6.callbacks.splice(i3, 1), 0 === c6.callbacks.length && (e$1.delete(n2), 0 === e$1.size && cancelAnimationFrame(t$2));
  };
}
let t$2;
const e$1 = /* @__PURE__ */ new Map();
function c$1() {
  const n2 = [];
  e$1.forEach((t2, e2) => {
    const c6 = e2.getBoundingClientRect();
    var o2, i2;
    o2 = t2.rect, i2 = c6, (o2.width !== i2.width || o2.height !== i2.height || o2.top !== i2.top || o2.right !== i2.right || o2.bottom !== i2.bottom || o2.left !== i2.left) && (t2.rect = c6, n2.push(t2));
  }), n2.forEach((t2) => {
    t2.callbacks.forEach((e2) => e2(t2.rect));
  }), t$2 = requestAnimationFrame(c$1);
}
function useRect(e2) {
  const [o2, c6] = React.useState();
  return React.useEffect(() => {
    if (e2) {
      const r2 = observeElementRect(e2, c6);
      return () => {
        c6(void 0), r2();
      };
    }
  }, [e2]), o2;
}
function createContextScope(n2, o2 = []) {
  let r2 = [];
  const c6 = () => {
    const t2 = r2.map((t3) => /* @__PURE__ */ React.createContext(t3));
    return function(o3) {
      const r3 = (null == o3 ? void 0 : o3[n2]) || t2;
      return React.useMemo(() => ({ [`__scope${n2}`]: { ...o3, [n2]: r3 } }), [o3, r3]);
    };
  };
  return c6.scopeName = n2, [function(t2, o3) {
    const c7 = /* @__PURE__ */ React.createContext(o3), u2 = r2.length;
    function s2(t3) {
      const { scope: o4, children: r3, ...s3 } = t3, i2 = (null == o4 ? void 0 : o4[n2][u2]) || c7, a2 = React.useMemo(() => s3, Object.values(s3));
      return React.createElement(i2.Provider, { value: a2 }, r3);
    }
    return r2 = [...r2, o3], s2.displayName = t2 + "Provider", [s2, function(r3, s3) {
      const i2 = (null == s3 ? void 0 : s3[n2][u2]) || c7, a2 = React.useContext(i2);
      if (a2)
        return a2;
      if (void 0 !== o3)
        return o3;
      throw new Error(`\`${r3}\` must be used within \`${t2}\``);
    }];
  }, t$1(c6, ...o2)];
}
function t$1(...t2) {
  const n2 = t2[0];
  if (1 === t2.length)
    return n2;
  const o2 = () => {
    const o3 = t2.map((e2) => ({ useScope: e2(), scopeName: e2.scopeName }));
    return function(t3) {
      const r2 = o3.reduce((e2, { useScope: n3, scopeName: o4 }) => ({ ...e2, ...n3(t3)[`__scope${o4}`] }), {});
      return React.useMemo(() => ({ [`__scope${n2.scopeName}`]: r2 }), [r2]);
    };
  };
  return o2.scopeName = n2.scopeName, o2;
}
function getPlacementData({ anchorRect: p2, popperSize: c6, arrowSize: f2, arrowOffset: l2 = 0, side: d2, sideOffset: h2 = 0, align: x2, alignOffset: g2 = 0, shouldAvoidCollisions: u2 = true, collisionBoundariesRect: w2, collisionTolerance: m2 = 0 }) {
  if (!p2 || !c6 || !w2)
    return { popperStyles: o, arrowStyles: n };
  const y2 = function(e2, r2, o2 = 0, n2 = 0, i2) {
    const p3 = i2 ? i2.height : 0, a2 = t(r2, e2, "x"), s2 = t(r2, e2, "y"), c7 = s2.before - o2 - p3, f3 = s2.after + o2 + p3, l3 = a2.before - o2 - p3, d3 = a2.after + o2 + p3;
    return { top: { start: { x: a2.start + n2, y: c7 }, center: { x: a2.center, y: c7 }, end: { x: a2.end - n2, y: c7 } }, right: { start: { x: d3, y: s2.start + n2 }, center: { x: d3, y: s2.center }, end: { x: d3, y: s2.end - n2 } }, bottom: { start: { x: a2.start + n2, y: f3 }, center: { x: a2.center, y: f3 }, end: { x: a2.end - n2, y: f3 } }, left: { start: { x: l3, y: s2.start + n2 }, center: { x: l3, y: s2.center }, end: { x: l3, y: s2.end - n2 } } };
  }(c6, p2, h2, g2, f2), b2 = y2[d2][x2];
  if (false === u2) {
    const t2 = e(b2);
    let o2 = n;
    f2 && (o2 = i({ popperSize: c6, arrowSize: f2, arrowOffset: l2, side: d2, align: x2 }));
    return { popperStyles: { ...t2, "--radix-popper-transform-origin": r$1(c6, d2, x2, l2, f2) }, arrowStyles: o2, placedSide: d2, placedAlign: x2 };
  }
  const S2 = DOMRect.fromRect({ ...c6, ...b2 }), $2 = (O2 = w2, z2 = m2, DOMRect.fromRect({ width: O2.width - 2 * z2, height: O2.height - 2 * z2, x: O2.left + z2, y: O2.top + z2 }));
  var O2, z2;
  const R2 = s(S2, $2), M2 = y2[a(d2)][x2], D2 = function(t2, e2, r2) {
    const o2 = a(t2);
    return e2[t2] && !r2[o2] ? o2 : t2;
  }(d2, R2, s(DOMRect.fromRect({ ...c6, ...M2 }), $2)), A2 = function(t2, e2, r2, o2, n2) {
    const i2 = "top" === r2 || "bottom" === r2, p3 = i2 ? "left" : "top", a2 = i2 ? "right" : "bottom", s2 = i2 ? "width" : "height", c7 = e2[s2] > t2[s2];
    if (("start" === o2 || "center" === o2) && (n2[p3] && c7 || n2[a2] && !c7))
      return "end";
    if (("end" === o2 || "center" === o2) && (n2[a2] && c7 || n2[p3] && !c7))
      return "start";
    return o2;
  }(c6, p2, d2, x2, R2), I2 = e(y2[D2][A2]);
  let C2 = n;
  f2 && (C2 = i({ popperSize: c6, arrowSize: f2, arrowOffset: l2, side: D2, align: A2 }));
  return { popperStyles: { ...I2, "--radix-popper-transform-origin": r$1(c6, D2, A2, l2, f2) }, arrowStyles: C2, placedSide: D2, placedAlign: A2 };
}
function t(t2, e2, r2) {
  const o2 = t2["x" === r2 ? "left" : "top"], n2 = "x" === r2 ? "width" : "height", i2 = t2[n2], p2 = e2[n2];
  return { before: o2 - p2, start: o2, center: o2 + (i2 - p2) / 2, end: o2 + i2 - p2, after: o2 + i2 };
}
function e(t2) {
  return { position: "absolute", top: 0, left: 0, minWidth: "max-content", willChange: "transform", transform: `translate3d(${Math.round(t2.x + window.scrollX)}px, ${Math.round(t2.y + window.scrollY)}px, 0)` };
}
function r$1(t2, e2, r2, o2, n2) {
  const i2 = "top" === e2 || "bottom" === e2, p2 = n2 ? n2.width : 0, a2 = n2 ? n2.height : 0, s2 = p2 / 2 + o2;
  let c6 = "", f2 = "";
  return i2 ? (c6 = { start: `${s2}px`, center: "center", end: t2.width - s2 + "px" }[r2], f2 = "top" === e2 ? `${t2.height + a2}px` : -a2 + "px") : (c6 = "left" === e2 ? `${t2.width + a2}px` : -a2 + "px", f2 = { start: `${s2}px`, center: "center", end: t2.height - s2 + "px" }[r2]), `${c6} ${f2}`;
}
const o = { position: "fixed", top: 0, left: 0, opacity: 0, transform: "translate3d(0, -200%, 0)" }, n = { position: "absolute", opacity: 0 };
function i({ popperSize: t2, arrowSize: e2, arrowOffset: r2, side: o2, align: n2 }) {
  const i2 = (t2.width - e2.width) / 2, a2 = (t2.height - e2.width) / 2, s2 = { top: 0, right: 90, bottom: 180, left: -90 }[o2], c6 = Math.max(e2.width, e2.height), f2 = { width: `${c6}px`, height: `${c6}px`, transform: `rotate(${s2}deg)`, willChange: "transform", position: "absolute", [o2]: "100%", direction: p$1(o2, n2) };
  return "top" !== o2 && "bottom" !== o2 || ("start" === n2 && (f2.left = `${r2}px`), "center" === n2 && (f2.left = `${i2}px`), "end" === n2 && (f2.right = `${r2}px`)), "left" !== o2 && "right" !== o2 || ("start" === n2 && (f2.top = `${r2}px`), "center" === n2 && (f2.top = `${a2}px`), "end" === n2 && (f2.bottom = `${r2}px`)), f2;
}
function p$1(t2, e2) {
  return ("top" !== t2 && "right" !== t2 || "end" !== e2) && ("bottom" !== t2 && "left" !== t2 || "end" === e2) ? "ltr" : "rtl";
}
function a(t2) {
  return { top: "bottom", right: "left", bottom: "top", left: "right" }[t2];
}
function s(t2, e2) {
  return { top: t2.top < e2.top, right: t2.right > e2.right, bottom: t2.bottom > e2.bottom, left: t2.left < e2.left };
}
const [c, l] = createContextScope("Popper");
const [f, d] = c("Popper");
const Popper = (e2) => {
  const { __scopePopper: o2, children: r2 } = e2, [t2, n2] = React.useState(null);
  return React.createElement(f, { scope: o2, anchor: t2, onAnchorChange: n2 }, r2);
};
const PopperAnchor = /* @__PURE__ */ React.forwardRef((e2, r2) => {
  const { __scopePopper: t2, virtualRef: n2, ...p2 } = e2, c6 = d("PopperAnchor", t2), l2 = React.useRef(null), f2 = useComposedRefs(r2, l2);
  return React.useEffect(() => {
    c6.onAnchorChange((null == n2 ? void 0 : n2.current) || l2.current);
  }), n2 ? null : /* @__PURE__ */ React.createElement(Primitive.div, _extends$1({}, p2, { ref: f2 }));
});
const [u, m$1] = c("PopperContent");
const PopperContent = /* @__PURE__ */ React.forwardRef((e2, n2) => {
  const { __scopePopper: c6, side: l2 = "bottom", sideOffset: f2, align: m2 = "center", alignOffset: w2, collisionTolerance: h2, avoidCollisions: x2 = true, ...v2 } = e2, P2 = d("PopperContent", c6), [A2, g2] = React.useState(), E2 = useRect(P2.anchor), [y2, C2] = React.useState(null), S2 = useSize(y2), [R2, O2] = React.useState(null), _2 = useSize(R2), b2 = useComposedRefs(n2, (e3) => C2(e3)), z2 = function() {
    const [e3, o2] = React.useState(void 0);
    return React.useEffect(() => {
      let e4;
      function r2() {
        o2({ width: window.innerWidth, height: window.innerHeight });
      }
      function t2() {
        window.clearTimeout(e4), e4 = window.setTimeout(r2, 100);
      }
      return r2(), window.addEventListener("resize", t2), () => window.removeEventListener("resize", t2);
    }, []), e3;
  }(), T2 = z2 ? DOMRect.fromRect({ ...z2, x: 0, y: 0 }) : void 0, { popperStyles: k2, arrowStyles: L2, placedSide: B2, placedAlign: D2 } = getPlacementData({ anchorRect: E2, popperSize: S2, arrowSize: _2, arrowOffset: A2, side: l2, sideOffset: f2, align: m2, alignOffset: w2, shouldAvoidCollisions: x2, collisionBoundariesRect: T2, collisionTolerance: h2 }), H2 = void 0 !== B2;
  return React.createElement("div", { style: k2, "data-radix-popper-content-wrapper": "" }, /* @__PURE__ */ React.createElement(u, { scope: c6, arrowStyles: L2, onArrowChange: O2, onArrowOffsetChange: g2 }, /* @__PURE__ */ React.createElement(Primitive.div, _extends$1({ "data-side": B2, "data-align": D2 }, v2, { style: { ...v2.style, animation: H2 ? void 0 : "none" }, ref: b2 }))));
});
const PopperArrow = /* @__PURE__ */ React.forwardRef(function(o2, r2) {
  const { __scopePopper: t2, offset: n2, ...i2 } = o2, p2 = m$1("PopperArrow", t2), { onArrowOffsetChange: c6 } = p2;
  return React.useEffect(() => c6(n2), [c6, n2]), /* @__PURE__ */ React.createElement("span", { style: { ...p2.arrowStyles, pointerEvents: "none" } }, /* @__PURE__ */ React.createElement("span", { ref: p2.onArrowChange, style: { display: "inline-block", verticalAlign: "top", pointerEvents: "auto" } }, /* @__PURE__ */ React.createElement(Root$2, _extends$1({}, i2, { ref: r2, style: { ...i2.style, display: "block" } }))));
});
const Root$1 = Popper;
const Anchor = PopperAnchor;
const Content$1 = PopperContent;
const Arrow$1 = PopperArrow;
const Presence = (u2) => {
  const { present: o2, children: i2 } = u2, s2 = function(n2) {
    const [u3, o3] = React.useState(), i3 = React.useRef({}), s3 = React.useRef(n2), c7 = React.useRef("none"), a3 = n2 ? "mounted" : "unmounted", [d2, m2] = function(e2, n3) {
      return React.useReducer((e3, t2) => {
        const r2 = n3[e3][t2];
        return null != r2 ? r2 : e3;
      }, e2);
    }(a3, { mounted: { UNMOUNT: "unmounted", ANIMATION_OUT: "unmountSuspended" }, unmountSuspended: { MOUNT: "mounted", ANIMATION_END: "unmounted" }, unmounted: { MOUNT: "mounted" } });
    return React.useEffect(() => {
      const e2 = r(i3.current);
      c7.current = "mounted" === d2 ? e2 : "none";
    }, [d2]), useLayoutEffect(() => {
      const e2 = i3.current, t2 = s3.current;
      if (t2 !== n2) {
        const u4 = c7.current, o4 = r(e2);
        if (n2)
          m2("MOUNT");
        else if ("none" === o4 || "none" === (null == e2 ? void 0 : e2.display))
          m2("UNMOUNT");
        else {
          const e3 = u4 !== o4;
          m2(t2 && e3 ? "ANIMATION_OUT" : "UNMOUNT");
        }
        s3.current = n2;
      }
    }, [n2, m2]), useLayoutEffect(() => {
      if (u3) {
        const e2 = (e3) => {
          const n4 = r(i3.current).includes(e3.animationName);
          e3.target === u3 && n4 && m2("ANIMATION_END");
        }, n3 = (e3) => {
          e3.target === u3 && (c7.current = r(i3.current));
        };
        return u3.addEventListener("animationstart", n3), u3.addEventListener("animationcancel", e2), u3.addEventListener("animationend", e2), () => {
          u3.removeEventListener("animationstart", n3), u3.removeEventListener("animationcancel", e2), u3.removeEventListener("animationend", e2);
        };
      }
    }, [u3, m2]), { isPresent: ["mounted", "unmountSuspended"].includes(d2), ref: React.useCallback((e2) => {
      e2 && (i3.current = getComputedStyle(e2)), o3(e2);
    }, []) };
  }(o2), c6 = "function" == typeof i2 ? i2({ present: s2.isPresent }) : React.Children.only(i2), a2 = useComposedRefs(s2.ref, c6.ref);
  return "function" == typeof i2 || s2.isPresent ? /* @__PURE__ */ React.cloneElement(c6, { ref: a2 }) : null;
};
function r(e2) {
  return (null == e2 ? void 0 : e2.animationName) || "none";
}
Presence.displayName = "Presence";
function usePrevious(r2) {
  const t2 = React.useRef(r2);
  return React.useEffect(() => {
    t2.current = r2;
  }, [r2]), t2.current;
}
function useCallbackRef(r2) {
  const t2 = React.useRef(r2);
  return React.useEffect(() => {
    t2.current = r2;
  }), React.useMemo(() => (...e2) => {
    var r3;
    return null === (r3 = t2.current) || void 0 === r3 ? void 0 : r3.call(t2, ...e2);
  }, []);
}
function useEscapeKeydown(n2) {
  const o2 = useCallbackRef(n2);
  React.useEffect(() => {
    const e2 = (e3) => {
      "Escape" === e3.key && o2(e3);
    };
    return document.addEventListener("keydown", e2), () => document.removeEventListener("keydown", e2);
  }, [o2]);
}
function useControllableState({ prop: o2, defaultProp: r2, onChange: n2 = () => {
} }) {
  const [a2, u2] = function({ defaultProp: o3, onChange: r3 }) {
    const n3 = React.useState(o3), [a3] = n3, u3 = React.useRef(a3), c7 = useCallbackRef(r3);
    return React.useEffect(() => {
      u3.current !== a3 && (c7(a3), u3.current = a3);
    }, [a3, u3, c7]), n3;
  }({ defaultProp: r2, onChange: n2 }), c6 = void 0 !== o2, f2 = c6 ? o2 : a2, l2 = useCallbackRef(n2);
  return [f2, React.useCallback((e2) => {
    if (c6) {
      const t2 = e2, r3 = "function" == typeof e2 ? t2(o2) : e2;
      r3 !== o2 && l2(r3);
    } else
      u2(e2);
  }, [c6, o2, u2, l2])];
}
function composeEventHandlers(e2, n2, { checkForDefaultPrevented: t2 = true } = {}) {
  return function(r2) {
    if (null == e2 || e2(r2), false === t2 || !r2.defaultPrevented)
      return null == n2 ? void 0 : n2(r2);
  };
}
const [w$1, x$1] = createContextScope("Tooltip", [l]);
const g$1 = l(), E$1 = 700, [v$1, b$2] = w$1("TooltipProvider", { isOpenDelayed: true, delayDuration: E$1, onOpen: () => {
}, onClose: () => {
} });
const [y$2, _$1] = w$1("Tooltip");
const Tooltip = (o2) => {
  const { __scopeTooltip: t2, children: r2, open: i2, defaultOpen: a2 = false, onOpenChange: l2, delayDuration: c6 } = o2, s2 = b$2("Tooltip", t2), u2 = g$1(t2), [d2, m2] = React.useState(null), f2 = useId(), C2 = React.useRef(0), w2 = null != c6 ? c6 : s2.delayDuration, x2 = React.useRef(false), { onOpen: E2, onClose: v2 } = s2, [_2 = false, h2] = useControllableState({ prop: i2, defaultProp: a2, onChange: (e2) => {
    e2 && (document.dispatchEvent(new CustomEvent("tooltip.open")), E2()), null == l2 || l2(e2);
  } }), k2 = React.useMemo(() => _2 ? x2.current ? "delayed-open" : "instant-open" : "closed", [_2]), D2 = React.useCallback(() => {
    window.clearTimeout(C2.current), x2.current = false, h2(true);
  }, [h2]), O2 = React.useCallback(() => {
    window.clearTimeout(C2.current), C2.current = window.setTimeout(() => {
      x2.current = true, h2(true);
    }, w2);
  }, [w2, h2]);
  return React.useEffect(() => () => window.clearTimeout(C2.current), []), /* @__PURE__ */ React.createElement(Root$1, u2, /* @__PURE__ */ React.createElement(y$2, { scope: t2, contentId: f2, open: _2, stateAttribute: k2, trigger: d2, onTriggerChange: m2, onTriggerEnter: React.useCallback(() => {
    s2.isOpenDelayed ? O2() : D2();
  }, [s2.isOpenDelayed, O2, D2]), onOpen: React.useCallback(D2, [D2]), onClose: React.useCallback(() => {
    window.clearTimeout(C2.current), h2(false), v2();
  }, [h2, v2]) }, r2));
};
const TooltipTrigger = /* @__PURE__ */ React.forwardRef((e2, o2) => {
  const { __scopeTooltip: t2, ...r2 } = e2, i2 = _$1("TooltipTrigger", t2), l2 = g$1(t2), c6 = useComposedRefs(o2, i2.onTriggerChange), s2 = React.useRef(false), u2 = React.useCallback(() => s2.current = false, []);
  return React.useEffect(() => () => document.removeEventListener("mouseup", u2), [u2]), /* @__PURE__ */ React.createElement(Anchor, _extends$1({ asChild: true }, l2), /* @__PURE__ */ React.createElement(Primitive.button, _extends$1({ "aria-describedby": i2.open ? i2.contentId : void 0, "data-state": i2.stateAttribute }, r2, { ref: c6, onMouseEnter: composeEventHandlers(e2.onMouseEnter, i2.onTriggerEnter), onMouseLeave: composeEventHandlers(e2.onMouseLeave, i2.onClose), onMouseDown: composeEventHandlers(e2.onMouseDown, () => {
    i2.onClose(), s2.current = true, document.addEventListener("mouseup", u2, { once: true });
  }), onFocus: composeEventHandlers(e2.onFocus, () => {
    s2.current || i2.onOpen();
  }), onBlur: composeEventHandlers(e2.onBlur, i2.onClose), onClick: composeEventHandlers(e2.onClick, i2.onClose) })));
});
const TooltipContent = /* @__PURE__ */ React.forwardRef((e2, o2) => {
  const { forceMount: t2, ...r2 } = e2, n2 = _$1("TooltipContent", e2.__scopeTooltip);
  return React.createElement(Presence, { present: t2 || n2.open }, /* @__PURE__ */ React.createElement(h$1, _extends$1({ ref: o2 }, r2)));
});
const h$1 = /* @__PURE__ */ React.forwardRef((e2, i2) => {
  const { __scopeTooltip: a2, children: l2, "aria-label": c6, portalled: s2 = true, ...p2 } = e2, d2 = _$1("TooltipContent", a2), m2 = g$1(a2), f2 = s2 ? Portal : React.Fragment, { onClose: w2 } = d2;
  return useEscapeKeydown(() => w2()), React.useEffect(() => (document.addEventListener("tooltip.open", w2), () => document.removeEventListener("tooltip.open", w2)), [w2]), /* @__PURE__ */ React.createElement(f2, null, /* @__PURE__ */ React.createElement(k$1, { __scopeTooltip: a2 }), /* @__PURE__ */ React.createElement(Content$1, _extends$1({ "data-state": d2.stateAttribute }, m2, p2, { ref: i2, style: { ...p2.style, "--radix-tooltip-content-transform-origin": "var(--radix-popper-transform-origin)" } }), /* @__PURE__ */ React.createElement(Slottable, null, l2), /* @__PURE__ */ React.createElement(Root$3, { id: d2.contentId, role: "tooltip" }, c6 || l2)));
});
const TooltipArrow = /* @__PURE__ */ React.forwardRef((e2, o2) => {
  const { __scopeTooltip: t2, ...r2 } = e2, i2 = g$1(t2);
  return React.createElement(Arrow$1, _extends$1({}, i2, r2, { ref: o2 }));
});
function k$1(e2) {
  const { __scopeTooltip: o2 } = e2, t2 = _$1("CheckTriggerMoved", o2), r2 = useRect(t2.trigger), n2 = null == r2 ? void 0 : r2.left, i2 = usePrevious(n2), a2 = null == r2 ? void 0 : r2.top, l2 = usePrevious(a2), u2 = t2.onClose;
  return React.useEffect(() => {
    (void 0 !== i2 && i2 !== n2 || void 0 !== l2 && l2 !== a2) && u2();
  }, [u2, i2, l2, n2, a2]), null;
}
const Root = Tooltip;
const Trigger = TooltipTrigger;
const Content = TooltipContent;
const Arrow = TooltipArrow;
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null)
    return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i2;
  for (i2 = 0; i2 < sourceKeys.length; i2++) {
    key = sourceKeys[i2];
    if (excluded.indexOf(key) >= 0)
      continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null)
    return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i2;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i2 = 0; i2 < sourceSymbolKeys.length; i2++) {
      key = sourceSymbolKeys[i2];
      if (excluded.indexOf(key) >= 0)
        continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key))
        continue;
      target[key] = source[key];
    }
  }
  return target;
}
let LevaErrors;
(function(LevaErrors2) {
  LevaErrors2[LevaErrors2["UNSUPPORTED_INPUT"] = 0] = "UNSUPPORTED_INPUT";
  LevaErrors2[LevaErrors2["NO_COMPONENT_FOR_TYPE"] = 1] = "NO_COMPONENT_FOR_TYPE";
  LevaErrors2[LevaErrors2["UNKNOWN_INPUT"] = 2] = "UNKNOWN_INPUT";
  LevaErrors2[LevaErrors2["DUPLICATE_KEYS"] = 3] = "DUPLICATE_KEYS";
  LevaErrors2[LevaErrors2["ALREADY_REGISTERED_TYPE"] = 4] = "ALREADY_REGISTERED_TYPE";
  LevaErrors2[LevaErrors2["CLIPBOARD_ERROR"] = 5] = "CLIPBOARD_ERROR";
  LevaErrors2[LevaErrors2["THEME_ERROR"] = 6] = "THEME_ERROR";
  LevaErrors2[LevaErrors2["PATH_DOESNT_EXIST"] = 7] = "PATH_DOESNT_EXIST";
  LevaErrors2[LevaErrors2["INPUT_TYPE_OVERRIDE"] = 8] = "INPUT_TYPE_OVERRIDE";
  LevaErrors2[LevaErrors2["EMPTY_KEY"] = 9] = "EMPTY_KEY";
})(LevaErrors || (LevaErrors = {}));
const ErrorList = {
  [LevaErrors.UNSUPPORTED_INPUT]: (type, path) => [`An input with type \`${type}\` input was found at path \`${path}\` but it's not supported yet.`],
  [LevaErrors.NO_COMPONENT_FOR_TYPE]: (type, path) => [`Type \`${type}\` found at path \`${path}\` can't be displayed in panel because no component supports it yet.`],
  [LevaErrors.UNKNOWN_INPUT]: (path, value) => [`input at path \`${path}\` is not recognized.`, value],
  [LevaErrors.DUPLICATE_KEYS]: (key, path, prevPath) => [`Key \`${key}\` of path \`${path}\` already exists at path \`${prevPath}\`. Even nested keys need to be unique. Rename one of the keys.`],
  [LevaErrors.ALREADY_REGISTERED_TYPE]: (type) => [`Type ${type} has already been registered. You can't register a component with the same type.`],
  [LevaErrors.CLIPBOARD_ERROR]: (value) => [`Error copying the value`, value],
  [LevaErrors.THEME_ERROR]: (category, key) => [`Error accessing the theme \`${category}.${key}\` value.`],
  [LevaErrors.PATH_DOESNT_EXIST]: (path) => [`Error getting the value at path \`${path}\`. There is probably an error in your \`render\` function.`],
  [LevaErrors.PATH_DOESNT_EXIST]: (path) => [`Error accessing the value at path \`${path}\``],
  [LevaErrors.INPUT_TYPE_OVERRIDE]: (path, type, wrongType) => [`Input at path \`${path}\` already exists with type: \`${type}\`. Its type cannot be overridden with type \`${wrongType}\`.`],
  [LevaErrors.EMPTY_KEY]: () => ["Keys can not be empty, if you want to hide a label use whitespace."]
};
function _log(fn, errorType, ...args) {
  const [message, ...rest2] = ErrorList[errorType](...args);
  console[fn]("LEVA: " + message, ...rest2);
}
const warn$1 = _log.bind(null, "warn");
_log.bind(null, "log");
function createInternalPlugin(plugin) {
  return plugin;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function ownKeys(object, enumerableOnly) {
  var keys2 = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys2.push.apply(keys2, symbols);
  }
  return keys2;
}
function _objectSpread2(target) {
  for (var i2 = 1; i2 < arguments.length; i2++) {
    var source = null != arguments[i2] ? arguments[i2] : {};
    i2 % 2 ? ownKeys(Object(source), true).forEach(function(key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
const clamp = (x2, min, max) => x2 > max ? max : x2 < min ? min : x2;
const parseNumber = (v2) => {
  if (v2 === "" || typeof v2 === "number")
    return v2;
  try {
    const _v = evaluate(v2);
    if (!isNaN(_v))
      return _v;
  } catch (_unused) {
  }
  return parseFloat(v2);
};
const log10 = Math.log(10);
function getStep(number) {
  let n2 = Math.abs(+String(number).replace(".", ""));
  if (n2 === 0)
    return 0.01;
  while (n2 !== 0 && n2 % 10 === 0)
    n2 /= 10;
  const significantDigits = Math.floor(Math.log(n2) / log10) + 1;
  const numberLog = Math.floor(Math.log10(Math.abs(number)));
  const step = Math.pow(10, numberLog - significantDigits);
  return Math.max(step, 1e-3);
}
const range = (v2, min, max) => {
  if (max === min)
    return 0;
  const _v = clamp(v2, min, max);
  return (_v - min) / (max - min);
};
const invertedRange = (p2, min, max) => p2 * (max - min) + min;
const parens = /\(([0-9+\-*/^ .]+)\)/;
const exp = /(\d+(?:\.\d+)?) ?\^ ?(\d+(?:\.\d+)?)/;
const mul = /(\d+(?:\.\d+)?) ?\* ?(\d+(?:\.\d+)?)/;
const div = /(\d+(?:\.\d+)?) ?\/ ?(\d+(?:\.\d+)?)/;
const add = /(\d+(?:\.\d+)?) ?\+ ?(\d+(?:\.\d+)?)/;
const sub = /(\d+(?:\.\d+)?) ?- ?(\d+(?:\.\d+)?)/;
function evaluate(expr) {
  if (isNaN(Number(expr))) {
    if (parens.test(expr)) {
      const newExpr = expr.replace(parens, (match, subExpr) => String(evaluate(subExpr)));
      return evaluate(newExpr);
    } else if (exp.test(expr)) {
      const newExpr = expr.replace(exp, (match, base, pow2) => String(Math.pow(Number(base), Number(pow2))));
      return evaluate(newExpr);
    } else if (mul.test(expr)) {
      const newExpr = expr.replace(mul, (match, a2, b2) => String(Number(a2) * Number(b2)));
      return evaluate(newExpr);
    } else if (div.test(expr)) {
      const newExpr = expr.replace(div, (match, a2, b2) => {
        if (b2 != 0)
          return String(Number(a2) / Number(b2));
        else
          throw new Error("Division by zero");
      });
      return evaluate(newExpr);
    } else if (add.test(expr)) {
      const newExpr = expr.replace(add, (match, a2, b2) => String(Number(a2) + Number(b2)));
      return evaluate(newExpr);
    } else if (sub.test(expr)) {
      const newExpr = expr.replace(sub, (match, a2, b2) => String(Number(a2) - Number(b2)));
      return evaluate(newExpr);
    } else {
      return Number(expr);
    }
  }
  return Number(expr);
}
let SpecialInputs;
(function(SpecialInputs2) {
  SpecialInputs2["BUTTON"] = "BUTTON";
  SpecialInputs2["BUTTON_GROUP"] = "BUTTON_GROUP";
  SpecialInputs2["MONITOR"] = "MONITOR";
  SpecialInputs2["FOLDER"] = "FOLDER";
})(SpecialInputs || (SpecialInputs = {}));
let LevaInputs;
(function(LevaInputs2) {
  LevaInputs2["SELECT"] = "SELECT";
  LevaInputs2["IMAGE"] = "IMAGE";
  LevaInputs2["NUMBER"] = "NUMBER";
  LevaInputs2["COLOR"] = "COLOR";
  LevaInputs2["STRING"] = "STRING";
  LevaInputs2["BOOLEAN"] = "BOOLEAN";
  LevaInputs2["INTERVAL"] = "INTERVAL";
  LevaInputs2["VECTOR3D"] = "VECTOR3D";
  LevaInputs2["VECTOR2D"] = "VECTOR2D";
})(LevaInputs || (LevaInputs = {}));
const multiplyStep = (event) => event.shiftKey ? 5 : event.altKey ? 1 / 5 : 1;
const _excluded$8 = ["value"], _excluded2$2 = ["min", "max"];
const schema$3 = (v2) => {
  if (typeof v2 === "number")
    return true;
  if (typeof v2 === "string") {
    const _v = parseFloat(v2);
    if (isNaN(_v))
      return false;
    const suffix = v2.substring(("" + _v).length).trim();
    return suffix.length < 4;
  }
  return false;
};
const sanitize$3 = (v2, {
  min: _min = -Infinity,
  max: _max = Infinity,
  suffix
}) => {
  const _v = parseFloat(v2);
  if (v2 === "" || isNaN(_v))
    throw Error("Invalid number");
  const f2 = clamp(_v, _min, _max);
  return suffix ? f2 + suffix : f2;
};
const format$1 = (v2, {
  pad: _pad = 0,
  suffix
}) => {
  const f2 = parseFloat(v2).toFixed(_pad);
  return suffix ? f2 + suffix : f2;
};
const normalize$2 = (_ref) => {
  let {
    value
  } = _ref, settings = _objectWithoutProperties(_ref, _excluded$8);
  const {
    min = -Infinity,
    max = Infinity
  } = settings, _settings = _objectWithoutProperties(settings, _excluded2$2);
  let _value = parseFloat(value);
  const suffix = typeof value === "string" ? value.substring(("" + _value).length) : void 0;
  _value = clamp(_value, min, max);
  let step = settings.step;
  if (!step) {
    if (Number.isFinite(min)) {
      if (Number.isFinite(max))
        step = +(Math.abs(max - min) / 100).toPrecision(1);
      else
        step = +(Math.abs(_value - min) / 100).toPrecision(1);
    } else if (Number.isFinite(max))
      step = +(Math.abs(max - _value) / 100).toPrecision(1);
  }
  const padStep = step ? getStep(step) * 10 : getStep(_value);
  step = step || padStep / 10;
  const pad = Math.round(clamp(Math.log10(1 / padStep), 0, 2));
  return {
    value: suffix ? _value + suffix : _value,
    settings: _objectSpread2({
      initialValue: _value,
      step,
      pad,
      min,
      max,
      suffix
    }, _settings)
  };
};
const sanitizeStep$1 = (v2, {
  step,
  initialValue
}) => {
  const steps = Math.round((v2 - initialValue) / step);
  return initialValue + steps * step;
};
var props$3 = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  schema: schema$3,
  sanitize: sanitize$3,
  format: format$1,
  normalize: normalize$2,
  sanitizeStep: sanitizeStep$1
});
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function(target) {
    for (var i2 = 1; i2 < arguments.length; i2++) {
      var source = arguments[i2];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
const InputContext = createContext({});
function useInputContext() {
  return useContext(InputContext);
}
const ThemeContext = createContext(null);
createContext(null);
const PanelSettingsContext = createContext(null);
function usePanelSettingsContext() {
  return useContext(PanelSettingsContext);
}
const getDefaultTheme = () => ({
  colors: {
    elevation1: "#292d39",
    elevation2: "#181c20",
    elevation3: "#373c4b",
    accent1: "#0066dc",
    accent2: "#007bff",
    accent3: "#3c93ff",
    highlight1: "#535760",
    highlight2: "#8c92a4",
    highlight3: "#fefefe",
    vivid1: "#ffcc00",
    folderWidgetColor: "$highlight2",
    folderTextColor: "$highlight3",
    toolTipBackground: "$highlight3",
    toolTipText: "$elevation2"
  },
  radii: {
    xs: "2px",
    sm: "3px",
    lg: "10px"
  },
  space: {
    xs: "3px",
    sm: "6px",
    md: "10px",
    rowGap: "7px",
    colGap: "7px"
  },
  fonts: {
    mono: `ui-monospace, SFMono-Regular, Menlo, 'Roboto Mono', monospace`,
    sans: `system-ui, sans-serif`
  },
  fontSizes: {
    root: "11px",
    toolTip: "$root"
  },
  sizes: {
    rootWidth: "280px",
    controlWidth: "160px",
    numberInputMinWidth: "38px",
    scrubberWidth: "8px",
    scrubberHeight: "16px",
    rowHeight: "24px",
    folderTitleHeight: "20px",
    checkboxSize: "16px",
    joystickWidth: "100px",
    joystickHeight: "100px",
    colorPickerWidth: "$controlWidth",
    colorPickerHeight: "100px",
    imagePreviewWidth: "$controlWidth",
    imagePreviewHeight: "100px",
    monitorHeight: "60px",
    titleBarHeight: "39px"
  },
  shadows: {
    level1: "0 0 9px 0 #00000088",
    level2: "0 4px 14px #00000033"
  },
  borderWidths: {
    root: "0px",
    input: "1px",
    focus: "1px",
    hover: "1px",
    active: "1px",
    folder: "1px"
  },
  fontWeights: {
    label: "normal",
    folder: "normal",
    button: "normal"
  }
});
function createStateClass(value, options) {
  const [borderColor, bgColor] = value.split(" ");
  const css2 = {};
  if (borderColor !== "none") {
    css2.boxShadow = `${options.inset ? "inset " : ""}0 0 0 $borderWidths${[options.key]} $colors${borderColor !== "default" && borderColor || options.borderColor}`;
  }
  if (bgColor) {
    css2.backgroundColor = bgColor;
  }
  return css2;
}
const utils = {
  $inputStyle: () => (value) => createStateClass(value, {
    key: "$input",
    borderColor: "$highlight1",
    inset: true
  }),
  $focusStyle: () => (value) => createStateClass(value, {
    key: "$focus",
    borderColor: "$accent2"
  }),
  $hoverStyle: () => (value) => createStateClass(value, {
    key: "$hover",
    borderColor: "$accent1",
    inset: true
  }),
  $activeStyle: () => (value) => createStateClass(value, {
    key: "$active",
    borderColor: "$accent1",
    inset: true
  })
};
const {
  styled,
  css,
  createTheme,
  globalCss,
  keyframes
} = q$1({
  prefix: "leva",
  theme: getDefaultTheme(),
  utils: _objectSpread2(_objectSpread2({}, utils), {}, {
    $flex: () => ({
      display: "flex",
      alignItems: "center"
    }),
    $flexCenter: () => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }),
    $reset: () => ({
      outline: "none",
      fontSize: "inherit",
      fontWeight: "inherit",
      color: "inherit",
      fontFamily: "inherit",
      border: "none",
      backgroundColor: "transparent",
      appearance: "none"
    }),
    $draggable: () => ({
      touchAction: "none",
      WebkitUserDrag: "none",
      userSelect: "none"
    }),
    $focus: (value) => ({
      "&:focus": utils.$focusStyle()(value)
    }),
    $focusWithin: (value) => ({
      "&:focus-within": utils.$focusStyle()(value)
    }),
    $hover: (value) => ({
      "&:hover": utils.$hoverStyle()(value)
    }),
    $active: (value) => ({
      "&:active": utils.$activeStyle()(value)
    })
  })
});
globalCss({
  ".leva__panel__dragged": {
    WebkitUserDrag: "none",
    userSelect: "none",
    input: {
      userSelect: "none"
    },
    "*": {
      cursor: "ew-resize !important"
    }
  }
});
function useTh(category, key) {
  const {
    theme
  } = useContext(ThemeContext);
  if (!(category in theme) || !(key in theme[category])) {
    warn$1(LevaErrors.THEME_ERROR, category, key);
    return "";
  }
  let _key = key;
  while (true) {
    let value = theme[category][_key];
    if (typeof value === "string" && value.charAt(0) === "$")
      _key = value.substr(1);
    else
      return value;
  }
}
const StyledInput = styled("input", {
  $reset: "",
  padding: "0 $sm",
  width: 0,
  minWidth: 0,
  flex: 1,
  height: "100%",
  variants: {
    levaType: {
      number: {
        textAlign: "right"
      }
    },
    as: {
      textarea: {
        padding: "$sm"
      }
    }
  }
});
const InnerLabel = styled("div", {
  $draggable: "",
  height: "100%",
  $flexCenter: "",
  position: "relative",
  padding: "0 $xs",
  fontSize: "0.8em",
  opacity: 0.8,
  cursor: "default",
  touchAction: "none",
  [`& + ${StyledInput}`]: {
    paddingLeft: 0
  }
});
const InnerNumberLabel = styled(InnerLabel, {
  cursor: "ew-resize",
  marginRight: "-$xs",
  textTransform: "uppercase",
  opacity: 0.3,
  "&:hover": {
    opacity: 1
  },
  variants: {
    dragging: {
      true: {
        backgroundColor: "$accent2",
        opacity: 1
      }
    }
  }
});
const InputContainer = styled("div", {
  $flex: "",
  position: "relative",
  borderRadius: "$sm",
  overflow: "hidden",
  color: "inherit",
  height: "$rowHeight",
  backgroundColor: "$elevation3",
  $inputStyle: "$elevation1",
  $hover: "",
  $focusWithin: "",
  variants: {
    textArea: {
      true: {
        height: "auto"
      }
    }
  }
});
const _excluded$7 = ["innerLabel", "value", "onUpdate", "onChange", "onKeyDown", "type", "id", "inputType", "rows"], _excluded2$1 = ["onUpdate"];
function ValueInput(_ref) {
  let {
    innerLabel,
    value,
    onUpdate,
    onChange,
    onKeyDown,
    type,
    id,
    inputType = "text",
    rows = 0
  } = _ref, props2 = _objectWithoutProperties(_ref, _excluded$7);
  const {
    id: _id,
    emitOnEditStart,
    emitOnEditEnd,
    disabled
  } = useInputContext();
  const inputId = id || _id;
  const inputRef = useRef(null);
  const isTextArea = rows > 0;
  const asType = isTextArea ? "textarea" : "input";
  const update = useCallback((fn) => (event) => {
    const _value = event.currentTarget.value;
    fn(_value);
  }, []);
  React__default.useEffect(() => {
    const ref = inputRef.current;
    const _onUpdate = update((value2) => {
      onUpdate(value2);
      emitOnEditEnd();
    });
    ref === null || ref === void 0 ? void 0 : ref.addEventListener("blur", _onUpdate);
    return () => ref === null || ref === void 0 ? void 0 : ref.removeEventListener("blur", _onUpdate);
  }, [update, onUpdate, emitOnEditEnd]);
  const onKeyPress = useCallback((event) => {
    if (event.key === "Enter") {
      update(onUpdate)(event);
    }
  }, [update, onUpdate]);
  const inputProps = Object.assign({
    as: asType
  }, isTextArea ? {
    rows
  } : {}, props2);
  return React__default.createElement(InputContainer, {
    textArea: isTextArea
  }, innerLabel && typeof innerLabel === "string" ? React__default.createElement(InnerLabel, null, innerLabel) : innerLabel, React__default.createElement(StyledInput, _extends({
    levaType: type,
    ref: inputRef,
    id: inputId,
    type: inputType,
    autoComplete: "off",
    spellCheck: "false",
    value,
    onChange: update(onChange),
    onFocus: () => emitOnEditStart(),
    onKeyPress,
    onKeyDown,
    disabled
  }, inputProps)));
}
function NumberInput(_ref2) {
  let {
    onUpdate
  } = _ref2, props2 = _objectWithoutProperties(_ref2, _excluded2$1);
  const _onUpdate = useCallback((v2) => onUpdate(parseNumber(v2)), [onUpdate]);
  const onKeyDown = useCallback((event) => {
    const dir = event.key === "ArrowUp" ? 1 : event.key === "ArrowDown" ? -1 : 0;
    if (dir) {
      event.preventDefault();
      const step = event.altKey ? 0.1 : event.shiftKey ? 10 : 1;
      onUpdate((v2) => parseFloat(v2) + dir * step);
    }
  }, [onUpdate]);
  return React__default.createElement(ValueInput, _extends({}, props2, {
    onUpdate: _onUpdate,
    onKeyDown,
    type: "number"
  }));
}
const StyledFolder = styled("div", {});
const StyledWrapper = styled("div", {
  position: "relative",
  background: "$elevation2",
  transition: "height 300ms ease",
  variants: {
    fill: {
      true: {},
      false: {}
    },
    flat: {
      false: {},
      true: {}
    },
    isRoot: {
      true: {},
      false: {
        paddingLeft: "$md",
        "&::after": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          width: "$borderWidths$folder",
          height: "100%",
          backgroundColor: "$folderWidgetColor",
          opacity: 0.4,
          transform: "translateX(-50%)"
        }
      }
    }
  },
  compoundVariants: [{
    isRoot: true,
    fill: false,
    css: {
      overflowY: "auto",
      maxHeight: "calc(100vh - 20px - $$titleBarHeight)"
    }
  }, {
    isRoot: true,
    flat: false,
    css: {
      borderRadius: "$lg"
    }
  }]
});
styled("div", {
  $flex: "",
  color: "$folderTextColor",
  userSelect: "none",
  cursor: "pointer",
  height: "$folderTitleHeight",
  fontWeight: "$folder",
  "> svg": {
    marginLeft: -4,
    marginRight: 4,
    cursor: "pointer",
    fill: "$folderWidgetColor",
    opacity: 0.6
  },
  "&:hover > svg": {
    fill: "$folderWidgetColor"
  },
  [`&:hover + ${StyledWrapper}::after`]: {
    opacity: 0.6
  },
  [`${StyledFolder}:hover > & + ${StyledWrapper}::after`]: {
    opacity: 0.6
  },
  [`${StyledFolder}:hover > & > svg`]: {
    opacity: 1
  }
});
const StyledContent = styled("div", {
  position: "relative",
  display: "grid",
  gridTemplateColumns: "100%",
  rowGap: "$rowGap",
  transition: "opacity 250ms ease",
  variants: {
    toggled: {
      true: {
        opacity: 1,
        transitionDelay: "250ms"
      },
      false: {
        opacity: 0,
        transitionDelay: "0ms",
        pointerEvents: "none"
      }
    },
    isRoot: {
      true: {
        "& > div": {
          paddingLeft: "$md",
          paddingRight: "$md"
        },
        "& > div:first-of-type": {
          paddingTop: "$sm"
        },
        "& > div:last-of-type": {
          paddingBottom: "$sm"
        },
        [`> ${StyledFolder}:not(:first-of-type)`]: {
          paddingTop: "$sm",
          marginTop: "$md",
          borderTop: "$borderWidths$folder solid $colors$elevation1"
        }
      }
    }
  }
});
const StyledRow = styled("div", {
  position: "relative",
  zIndex: 100,
  display: "grid",
  rowGap: "$rowGap",
  gridTemplateRows: "minmax($sizes$rowHeight, max-content)",
  alignItems: "center",
  color: "$highlight2",
  [`${StyledContent} > &`]: {
    "&:first-of-type": {
      marginTop: "$rowGap"
    },
    "&:last-of-type": {
      marginBottom: "$rowGap"
    }
  },
  variants: {
    disabled: {
      true: {
        pointerEvents: "none"
      },
      false: {
        "&:hover,&:focus-within": {
          color: "$highlight3"
        }
      }
    }
  }
});
const StyledInputRow = styled(StyledRow, {
  gridTemplateColumns: "auto $sizes$controlWidth",
  columnGap: "$colGap"
});
const CopyLabelContainer = styled("div", {
  $flex: "",
  height: "100%",
  position: "relative",
  overflow: "hidden",
  "& > div": {
    marginLeft: "$colGap",
    padding: "0 $xs",
    opacity: 0.4
  },
  "& > div:hover": {
    opacity: 0.8
  },
  "& > div > svg": {
    display: "none",
    cursor: "pointer",
    width: 13,
    minWidth: 13,
    height: 13,
    backgroundColor: "$elevation2"
  },
  "&:hover > div > svg": {
    display: "block"
  },
  variants: {
    align: {
      top: {
        height: "100%",
        alignItems: "flex-start",
        paddingTop: "$sm"
      }
    }
  }
});
const StyledOptionalToggle = styled("input", {
  $reset: "",
  height: 0,
  width: 0,
  opacity: 0,
  margin: 0,
  "& + label": {
    position: "relative",
    $flexCenter: "",
    height: "100%",
    userSelect: "none",
    cursor: "pointer",
    paddingLeft: 2,
    paddingRight: "$sm",
    pointerEvents: "auto"
  },
  "& + label:after": {
    content: '""',
    width: 6,
    height: 6,
    backgroundColor: "$elevation3",
    borderRadius: "50%",
    $activeStyle: ""
  },
  "&:focus + label:after": {
    $focusStyle: ""
  },
  "& + label:active:after": {
    backgroundColor: "$accent1",
    $focusStyle: ""
  },
  "&:checked + label:after": {
    backgroundColor: "$accent1"
  }
});
const StyledLabel = styled("label", {
  fontWeight: "$label",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "& > svg": {
    display: "block"
  }
});
styled("div", {
  opacity: 1,
  variants: {
    disabled: {
      true: {
        opacity: 0.6,
        pointerEvents: "none",
        [`& ${StyledLabel}`]: {
          pointerEvents: "auto"
        }
      }
    }
  }
});
styled("div", {
  position: "fixed",
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  zIndex: 1e3,
  userSelect: "none"
});
const StyledToolTipContent = styled("div", {
  background: "$toolTipBackground",
  fontFamily: "$sans",
  fontSize: "$toolTip",
  padding: "$xs $sm",
  color: "$toolTipText",
  borderRadius: "$xs",
  boxShadow: "$level2",
  maxWidth: 260
});
const ToolTipArrow = styled(Arrow, {
  fill: "$toolTipBackground"
});
const _excluded$6 = ["align"];
function OptionalToggle() {
  const {
    id,
    disable,
    disabled
  } = useInputContext();
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(StyledOptionalToggle, {
    id: id + "__disable",
    type: "checkbox",
    checked: !disabled,
    onChange: () => disable(!disabled)
  }), React__default.createElement("label", {
    htmlFor: id + "__disable"
  }));
}
function RawLabel(props2) {
  const {
    id,
    optional: optional2,
    hint
  } = useInputContext();
  const htmlFor = props2.htmlFor || (id ? {
    htmlFor: id
  } : null);
  const title = !hint && typeof props2.children === "string" ? {
    title: props2.children
  } : null;
  return React__default.createElement(React__default.Fragment, null, optional2 && React__default.createElement(OptionalToggle, null), hint !== void 0 ? React__default.createElement(Root, null, React__default.createElement(Trigger, {
    asChild: true
  }, React__default.createElement(StyledLabel, _extends({}, htmlFor, props2))), React__default.createElement(Content, {
    side: "top",
    sideOffset: 2
  }, React__default.createElement(StyledToolTipContent, null, hint, React__default.createElement(ToolTipArrow, null)))) : React__default.createElement(StyledLabel, _extends({}, htmlFor, title, props2)));
}
function Label(_ref) {
  let {
    align
  } = _ref, props2 = _objectWithoutProperties(_ref, _excluded$6);
  const {
    value,
    label,
    key,
    disabled
  } = useInputContext();
  const {
    hideCopyButton
  } = usePanelSettingsContext();
  const copyEnabled = !hideCopyButton && key !== void 0;
  const [copied, setCopied] = useState(false);
  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({
        [key]: value !== null && value !== void 0 ? value : ""
      }));
      setCopied(true);
    } catch (_unused) {
      warn$1(LevaErrors.CLIPBOARD_ERROR, {
        [key]: value
      });
    }
  };
  return React__default.createElement(CopyLabelContainer, {
    align,
    onPointerLeave: () => setCopied(false)
  }, React__default.createElement(RawLabel, props2), copyEnabled && !disabled && React__default.createElement("div", {
    title: `Click to copy ${typeof label === "string" ? label : key} value`
  }, !copied ? React__default.createElement("svg", {
    onClick: handleClick,
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 20 20",
    fill: "currentColor"
  }, React__default.createElement("path", {
    d: "M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
  }), React__default.createElement("path", {
    d: "M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"
  })) : React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 20 20",
    fill: "currentColor"
  }, React__default.createElement("path", {
    d: "M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"
  }), React__default.createElement("path", {
    fillRule: "evenodd",
    d: "M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
    clipRule: "evenodd"
  }))));
}
const _excluded$5 = ["toggled"];
const Svg = styled("svg", {
  fill: "currentColor",
  transition: "transform 350ms ease, fill 250ms ease"
});
function Chevron(_ref) {
  let {
    toggled
  } = _ref, props2 = _objectWithoutProperties(_ref, _excluded$5);
  return React__default.createElement(Svg, _extends({
    width: "9",
    height: "5",
    viewBox: "0 0 9 5",
    xmlns: "http://www.w3.org/2000/svg",
    style: {
      transform: `rotate(${toggled ? 0 : -90}deg)`
    }
  }, props2), React__default.createElement("path", {
    d: "M3.8 4.4c.4.3 1 .3 1.4 0L8 1.7A1 1 0 007.4 0H1.6a1 1 0 00-.7 1.7l3 2.7z"
  }));
}
const _excluded$4 = ["input"];
function Row(_ref) {
  let {
    input
  } = _ref, props2 = _objectWithoutProperties(_ref, _excluded$4);
  if (input)
    return React__default.createElement(StyledInputRow, props2);
  return React__default.createElement(StyledRow, props2);
}
function useDrag(handler, config) {
  const {
    emitOnEditStart,
    emitOnEditEnd
  } = useInputContext();
  return useDrag$1((state) => {
    if (state.first) {
      document.body.classList.add("leva__panel__dragged");
      emitOnEditStart === null || emitOnEditStart === void 0 ? void 0 : emitOnEditStart();
    }
    const result = handler(state);
    if (state.last) {
      document.body.classList.remove("leva__panel__dragged");
      emitOnEditEnd === null || emitOnEditEnd === void 0 ? void 0 : emitOnEditEnd();
    }
    return result;
  }, config);
}
const RangeGrid = styled("div", {
  variants: {
    hasRange: {
      true: {
        position: "relative",
        display: "grid",
        gridTemplateColumns: "auto $sizes$numberInputMinWidth",
        columnGap: "$colGap",
        alignItems: "center"
      }
    }
  }
});
const Range = styled("div", {
  position: "relative",
  width: "100%",
  height: 2,
  borderRadius: "$xs",
  backgroundColor: "$elevation1"
});
const Scrubber = styled("div", {
  position: "absolute",
  width: "$scrubberWidth",
  height: "$scrubberHeight",
  borderRadius: "$xs",
  boxShadow: "0 0 0 2px $colors$elevation2",
  backgroundColor: "$accent2",
  cursor: "pointer",
  $active: "none $accent1",
  $hover: "none $accent3",
  variants: {
    position: {
      left: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        transform: "translateX(calc(-0.5 * ($sizes$scrubberWidth + 4px)))"
      },
      right: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        transform: "translateX(calc(0.5 * ($sizes$scrubberWidth + 4px)))"
      }
    }
  }
});
const RangeWrapper = styled("div", {
  position: "relative",
  $flex: "",
  height: "100%",
  cursor: "pointer",
  touchAction: "none"
});
const Indicator = styled("div", {
  position: "absolute",
  height: "100%",
  backgroundColor: "$accent2"
});
function RangeSlider({
  value,
  min,
  max,
  onDrag,
  step,
  initialValue
}) {
  const ref = useRef(null);
  const scrubberRef = useRef(null);
  const rangeWidth = useRef(0);
  const scrubberWidth = useTh("sizes", "scrubberWidth");
  const bind = useDrag(({
    event,
    first,
    xy: [x2],
    movement: [mx],
    memo
  }) => {
    if (first) {
      const {
        width,
        left
      } = ref.current.getBoundingClientRect();
      rangeWidth.current = width - parseFloat(scrubberWidth);
      const targetIsScrub = (event === null || event === void 0 ? void 0 : event.target) === scrubberRef.current;
      memo = targetIsScrub ? value : invertedRange((x2 - left) / width, min, max);
    }
    const newValue = memo + invertedRange(mx / rangeWidth.current, 0, max - min);
    onDrag(sanitizeStep$1(newValue, {
      step,
      initialValue
    }));
    return memo;
  });
  const pos = range(value, min, max);
  return React__default.createElement(RangeWrapper, _extends({
    ref
  }, bind()), React__default.createElement(Range, null, React__default.createElement(Indicator, {
    style: {
      left: 0,
      right: `${(1 - pos) * 100}%`
    }
  })), React__default.createElement(Scrubber, {
    ref: scrubberRef,
    style: {
      left: `calc(${pos} * (100% - ${scrubberWidth}))`
    }
  }));
}
const DraggableLabel = React__default.memo(({
  label,
  onUpdate,
  step,
  innerLabelTrim
}) => {
  const [dragging, setDragging] = useState(false);
  const bind = useDrag(({
    active,
    delta: [dx],
    event,
    memo: _memo = 0
  }) => {
    setDragging(active);
    _memo += dx / 2;
    if (Math.abs(_memo) >= 1) {
      onUpdate((v2) => parseFloat(v2) + Math.floor(_memo) * step * multiplyStep(event));
      _memo = 0;
    }
    return _memo;
  });
  return React__default.createElement(InnerNumberLabel, _extends({
    dragging,
    title: label.length > 1 ? label : ""
  }, bind()), label.slice(0, innerLabelTrim));
});
function Number$1({
  label,
  id,
  displayValue,
  onUpdate,
  onChange,
  settings,
  innerLabelTrim = 1
}) {
  const InnerLabel2 = innerLabelTrim > 0 && React__default.createElement(DraggableLabel, {
    label,
    step: settings.step,
    onUpdate,
    innerLabelTrim
  });
  return React__default.createElement(NumberInput, {
    id,
    value: String(displayValue),
    onUpdate,
    onChange,
    innerLabel: InnerLabel2
  });
}
function NumberComponent() {
  const props2 = useInputContext();
  const {
    label,
    value,
    onUpdate,
    settings,
    id
  } = props2;
  const {
    min,
    max
  } = settings;
  const hasRange = max !== Infinity && min !== -Infinity;
  return React__default.createElement(Row, {
    input: true
  }, React__default.createElement(Label, null, label), React__default.createElement(RangeGrid, {
    hasRange
  }, hasRange && React__default.createElement(RangeSlider, _extends({
    value: parseFloat(value),
    onDrag: onUpdate
  }, settings)), React__default.createElement(Number$1, _extends({}, props2, {
    id,
    label: "value",
    innerLabelTrim: hasRange ? 0 : 1
  }))));
}
const rest = _objectWithoutProperties(props$3, ["sanitizeStep"]);
createInternalPlugin(_objectSpread2({
  component: NumberComponent
}, rest));
const schema$2 = (_o, s2) => v8n().schema({
  options: v8n().passesAnyOf(v8n().object(), v8n().array())
}).test(s2);
const sanitize$2 = (value, {
  values
}) => {
  if (values.indexOf(value) < 0)
    throw Error(`Selected value doesn't match Select options`);
  return value;
};
const format = (value, {
  values
}) => {
  return values.indexOf(value);
};
const normalize$1 = (input) => {
  let {
    value,
    options
  } = input;
  let keys2;
  let values;
  if (Array.isArray(options)) {
    values = options;
    keys2 = options.map((o2) => String(o2));
  } else {
    values = Object.values(options);
    keys2 = Object.keys(options);
  }
  if (!("value" in input))
    value = values[0];
  else if (!values.includes(value)) {
    keys2.unshift(String(value));
    values.unshift(value);
  }
  if (!Object.values(options).includes(value))
    options[String(value)] = value;
  return {
    value,
    settings: {
      keys: keys2,
      values
    }
  };
};
var props$2 = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  schema: schema$2,
  sanitize: sanitize$2,
  format,
  normalize: normalize$1
});
const SelectContainer = styled("div", {
  $flexCenter: "",
  position: "relative",
  "> svg": {
    pointerEvents: "none",
    position: "absolute",
    right: "$md"
  }
});
const NativeSelect = styled("select", {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  opacity: 0
});
const PresentationalSelect = styled("div", {
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "$rowHeight",
  backgroundColor: "$elevation3",
  borderRadius: "$sm",
  padding: "0 $sm",
  cursor: "pointer",
  [`${NativeSelect}:focus + &`]: {
    $focusStyle: ""
  },
  [`${NativeSelect}:hover + &`]: {
    $hoverStyle: ""
  }
});
function Select({
  displayValue,
  value,
  onUpdate,
  id,
  settings,
  disabled
}) {
  const {
    keys: keys2,
    values
  } = settings;
  const lastDisplayedValue = useRef();
  if (value === values[displayValue]) {
    lastDisplayedValue.current = keys2[displayValue];
  }
  return React__default.createElement(SelectContainer, null, React__default.createElement(NativeSelect, {
    id,
    value: displayValue,
    onChange: (e2) => onUpdate(values[Number(e2.currentTarget.value)]),
    disabled
  }, keys2.map((key, index) => React__default.createElement("option", {
    key,
    value: index
  }, key))), React__default.createElement(PresentationalSelect, null, lastDisplayedValue.current), React__default.createElement(Chevron, {
    toggled: true
  }));
}
function SelectComponent() {
  const {
    label,
    value,
    displayValue,
    onUpdate,
    id,
    disabled,
    settings
  } = useInputContext();
  return React__default.createElement(Row, {
    input: true
  }, React__default.createElement(Label, null, label), React__default.createElement(Select, {
    id,
    value,
    displayValue,
    onUpdate,
    settings,
    disabled
  }));
}
createInternalPlugin(_objectSpread2({
  component: SelectComponent
}, props$2));
const schema$1 = (o2) => v8n().string().test(o2);
const sanitize$1 = (v2) => {
  if (typeof v2 !== "string")
    throw Error(`Invalid string`);
  return v2;
};
const normalize = ({
  value,
  editable: _editable = true,
  rows: _rows = false
}) => {
  return {
    value,
    settings: {
      editable: _editable,
      rows: typeof _rows === "number" ? _rows : _rows ? 5 : 0
    }
  };
};
var props$1 = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  schema: schema$1,
  sanitize: sanitize$1,
  normalize
});
const _excluded$2 = ["displayValue", "onUpdate", "onChange", "editable"];
const NonEditableString = styled("div", {
  whiteSpace: "pre-wrap"
});
function String$1(_ref) {
  let {
    displayValue,
    onUpdate,
    onChange,
    editable = true
  } = _ref, props2 = _objectWithoutProperties(_ref, _excluded$2);
  if (editable)
    return React__default.createElement(ValueInput, _extends({
      value: displayValue,
      onUpdate,
      onChange
    }, props2));
  return React__default.createElement(NonEditableString, null, displayValue);
}
function StringComponent() {
  const {
    label,
    settings,
    displayValue,
    onUpdate,
    onChange
  } = useInputContext();
  return React__default.createElement(Row, {
    input: true
  }, React__default.createElement(Label, null, label), React__default.createElement(String$1, _extends({
    displayValue,
    onUpdate,
    onChange
  }, settings)));
}
createInternalPlugin(_objectSpread2({
  component: StringComponent
}, props$1));
const schema = (o2) => v8n().boolean().test(o2);
const sanitize = (v2) => {
  if (typeof v2 !== "boolean")
    throw Error("Invalid boolean");
  return v2;
};
var props = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  schema,
  sanitize
});
const StyledInputWrapper = styled("div", {
  position: "relative",
  $flex: "",
  height: "$rowHeight",
  input: {
    $reset: "",
    height: 0,
    width: 0,
    opacity: 0,
    margin: 0
  },
  label: {
    position: "relative",
    $flexCenter: "",
    userSelect: "none",
    cursor: "pointer",
    height: "$checkboxSize",
    width: "$checkboxSize",
    backgroundColor: "$elevation3",
    borderRadius: "$sm",
    $hover: ""
  },
  "input:focus + label": {
    $focusStyle: ""
  },
  "input:focus:checked + label, input:checked + label:hover": {
    $hoverStyle: "$accent3"
  },
  "input + label:active": {
    backgroundColor: "$accent1"
  },
  "input:checked + label:active": {
    backgroundColor: "$accent1"
  },
  "label > svg": {
    display: "none",
    width: "90%",
    height: "90%",
    stroke: "$highlight3"
  },
  "input:checked + label": {
    backgroundColor: "$accent2"
  },
  "input:checked + label > svg": {
    display: "block"
  }
});
function Boolean$1({
  value,
  onUpdate,
  id,
  disabled
}) {
  return React__default.createElement(StyledInputWrapper, null, React__default.createElement("input", {
    id,
    type: "checkbox",
    checked: value,
    onChange: (e2) => onUpdate(e2.currentTarget.checked),
    disabled
  }), React__default.createElement("label", {
    htmlFor: id
  }, React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24"
  }, React__default.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M5 13l4 4L19 7"
  }))));
}
function BooleanComponent() {
  const {
    label,
    value,
    onUpdate,
    disabled,
    id
  } = useInputContext();
  return React__default.createElement(Row, {
    input: true
  }, React__default.createElement(Label, null, label), React__default.createElement(Boolean$1, {
    value,
    onUpdate,
    id,
    disabled
  }));
}
createInternalPlugin(_objectSpread2({
  component: BooleanComponent
}, props));
styled("div", {
  display: "grid",
  columnGap: "$colGap",
  gridAutoFlow: "column dense",
  alignItems: "center",
  variants: {
    withLock: {
      true: {
        gridTemplateColumns: "10px auto",
        "> svg": {
          cursor: "pointer"
        }
      }
    }
  }
});
class TiledImage extends BaseObject {
  constructor(data) {
    super();
    this.type = "spacial-content";
    this.style = { opacity: 1 };
    this.format = "jpg";
    this.tileUrl = stripInfoJson(data.url);
    this.id = data.id || `${this.tileUrl}--${data.scaleFactor}`;
    this.points = data.displayPoints ? data.displayPoints : transform(data.points, scale(data.scaleFactor));
    this.tileWidth = data.tileWidth;
    this.display = {
      x: 0,
      y: 0,
      width: data.width / data.scaleFactor,
      height: data.height / data.scaleFactor,
      points: data.points,
      scale: data.scaleFactor
    };
    if (data.format) {
      this.format = data.format;
    }
  }
  applyProps(props2) {
    if (props2.style && typeof props2.style.opacity !== "undefined") {
      this.style.opacity = props2.style.opacity;
    }
    if (props2.service !== this.service) {
      this.service = props2.service;
    }
    if (props2.format) {
      this.format = props2.format;
    } else {
      this.format = "jpg";
    }
    if (props2.crop) {
      this.cropData = props2.crop;
      const crop = dna([...this.points]);
      const len = crop.length / 5;
      const minX = props2.crop.x || 0;
      const minY = props2.crop.y || 0;
      const maxX = props2.crop.x + props2.crop.width;
      const maxY = props2.crop.y + props2.crop.height;
      for (let i2 = 0; i2 < len; i2++) {
        const index = i2 * 5;
        if (crop[index + 1] < maxX && crop[index + 3] > minX && crop[index + 2] < maxY && crop[index + 4] > minY) {
          crop[index + 1] = clamp(crop[index + 1], minX, maxX);
          crop[index + 3] = clamp(crop[index + 3], minX, maxX);
          crop[index + 2] = clamp(crop[index + 2], minY, maxY);
          crop[index + 4] = clamp(crop[index + 4], minY, maxY);
        } else {
          crop[index] = 0;
        }
      }
      mutate(crop, translate(-props2.crop.x, -props2.crop.y));
      if (!this.crop) {
        this.crop = crop;
      } else {
        this.crop.set(crop);
      }
    }
  }
  static fromTile(url, canvas, tile, scaleFactor, service, format2) {
    tile.height = tile.height ? tile.height : tile.width;
    const fullWidth = Math.ceil(canvas.width / scaleFactor);
    const fullHeight = Math.ceil(canvas.height / scaleFactor);
    const mWidth = Math.ceil(fullWidth / tile.width);
    const mHeight = Math.ceil(fullHeight / tile.height);
    const pointsFactory = DnaFactory.grid(mWidth, mHeight);
    const displayPoints = DnaFactory.grid(mWidth, mHeight);
    for (let y2 = 0; y2 < mHeight; y2++) {
      for (let x2 = 0; x2 < mWidth; x2++) {
        const rx = x2 * tile.width;
        const ry = y2 * tile.height;
        displayPoints.addPoints(rx * scaleFactor, ry * scaleFactor, x2 === mWidth - 1 ? canvas.width : (rx + tile.width) * scaleFactor, y2 === mHeight - 1 ? canvas.height : (ry + tile.height) * scaleFactor);
        pointsFactory.addPoints(rx, ry, x2 === mWidth - 1 ? fullWidth : rx + tile.width, y2 === mHeight - 1 ? fullHeight : ry + tile.height);
      }
    }
    const tiledImage = new TiledImage({
      url,
      scaleFactor,
      points: pointsFactory.build(),
      displayPoints: displayPoints.build(),
      width: canvas.width,
      height: canvas.height,
      tileWidth: tile.width,
      format: format2
    });
    tiledImage.applyProps({
      service
    });
    return tiledImage;
  }
  getImageUrl(index) {
    const im = this.points.slice(index * 5, index * 5 + 5);
    const x2 = im[3] - im[1];
    const y2 = im[4] - im[2];
    const w2 = Math.ceil(x2 / this.display.scale);
    return `${this.tileUrl}/${im[1]},${im[2]},${x2},${y2}/${w2 > this.tileWidth ? this.tileWidth : w2},/0/default.${this.format || "jpg"}`;
  }
  getAllPointsAt(target, aggregate, scaleFactor) {
    const points = hidePointsOutsideRegion(this.crop || this.points, target);
    return [[this, points, aggregate]];
  }
  transform(op) {
    mutate(this.points, op);
  }
  getScheduledUpdates(target, scaleFactor) {
    return [];
  }
}
class AbstractContent extends BaseObject {
  constructor() {
    super(...arguments);
    this.type = "spacial-content";
  }
  getAllPointsAt(target, aggregate, scale2) {
    return [[this, this.points, aggregate]];
  }
}
class CompositeResource extends AbstractContent {
  constructor(data) {
    super();
    this.images = [];
    this.allImages = [];
    this.scaleFactors = [];
    this.aggregateBuffer = dna(9);
    this.isFullyLoaded = false;
    this.maxScaleFactor = 0;
    this._scheduleSortByScales = false;
    this._sortByScales = () => {
      this._scheduleSortByScales = false;
      this.allImages.sort((a2, b2) => b2.display.width - a2.display.width);
      this.images = [];
      let lastScale = 0.1;
      for (const image of this.allImages) {
        if (image.display.width < this.renderOptions.minSize && image.display.height < this.renderOptions.minSize && !image.priority) {
          continue;
        }
        if (image instanceof SingleImage && (image.display.width > this.renderOptions.maxImageSize || image.display.height > this.renderOptions.maxImageSize) && !image.priority) {
          continue;
        }
        const diff = Math.abs(image.display.scale - lastScale);
        if (diff < 0.25 || image.priority) {
          const otherImage = this.images.pop();
          if (otherImage && (otherImage instanceof SingleImage || otherImage.priority)) {
            if (image.priority) {
              this.images.push(image);
            }
            this.images.push(otherImage);
          } else {
            if (image) {
              this.images.push(image);
            }
          }
        } else {
          if (image) {
            this.images.push(image);
          }
        }
        lastScale = image.display.scale;
      }
      if (this.images.length === 0) {
        this.images = [...this.allImages];
      }
      this.scaleFactors = this.images.map((singleImage) => singleImage.display.scale);
      this.maxScaleFactor = Math.max(...this.scaleFactors);
    };
    this.loadFullResource = async () => {
      if (this.isFullyLoaded) {
        return;
      }
      if (this.lazyLoader) {
        this.isFullyLoaded = true;
        const newImages = await this.lazyLoader();
        this.addImages(newImages);
      }
    };
    this.fallback = [this.loadFullResource];
    this.id = data.id;
    this.points = DnaFactory.singleBox(data.width, data.height);
    this.lazyLoader = data.loadFullImages;
    if (!data.loadFullImages) {
      this.isFullyLoaded = true;
    }
    this.display = {
      x: 0,
      y: 0,
      points: DnaFactory.singleBox(data.width, data.height),
      height: data.height,
      width: data.width,
      scale: 1
    };
    this.renderOptions = {
      renderSmallestFallback: true,
      renderLayers: 3,
      minSize: 255,
      maxImageSize: 1024,
      quality: 1.2,
      ...data.renderOptions || {}
    };
    this.addImages(data.images);
  }
  applyProps(props2) {
    if (typeof props2.renderSmallestFallback !== "undefined" && props2.renderSmallestFallback !== this.renderOptions.renderSmallestFallback) {
      this.renderOptions.renderSmallestFallback = props2.renderSmallestFallback;
    }
    if (typeof props2.renderLayers !== "undefined" && props2.renderLayers !== this.renderOptions.renderLayers) {
      this.renderOptions.renderLayers = props2.renderLayers;
    }
    if (typeof props2.minSize !== "undefined" && props2.minSize !== this.renderOptions.minSize) {
      this.renderOptions.minSize = props2.minSize;
    }
    if (typeof props2.maxImageSize !== "undefined" && props2.maxImageSize !== this.renderOptions.maxImageSize) {
      this.renderOptions.maxImageSize = props2.maxImageSize;
    }
    if (typeof props2.quality !== "undefined" && props2.quality !== this.renderOptions.quality) {
      this.renderOptions.quality = props2.quality;
    }
  }
  appendChild(item) {
    this.addImages([item]);
  }
  removeChild(item) {
    if (this.images.indexOf(item) === -1) {
      return;
    }
    this.images = this.images.filter((image) => image !== item);
    this.sortByScales();
  }
  insertBefore(item, before) {
    this.addImages([item]);
  }
  hideInstance() {
  }
  addImages(images) {
    for (const image of images) {
      image.__parent = this;
      image.__owner = this.__owner;
    }
    this.allImages.push(...images.filter(Boolean));
    this.sortByScales();
  }
  sortByScales() {
    this._scheduleSortByScales = true;
  }
  getScheduledUpdates(target, scaleFactor) {
    if (this._scheduleSortByScales) {
      return [this._sortByScales];
    }
    if (this.isFullyLoaded) {
      return [];
    }
    if (scaleFactor > 1 / this.maxScaleFactor) {
      return this.fallback;
    }
    return [];
  }
  getAllPointsAt(target, aggregate, scale2) {
    if (this.images.length === 0) {
      return [];
    }
    const bestIndex = bestResourceIndexAtRatio(1 / (scale2 || 1) / (window.devicePixelRatio || 1), this.images, this.renderOptions.quality);
    const len = this.images.length;
    const newAggregate = aggregate ? compose(aggregate, translate(this.x, this.y)) : translate(this.x, this.y);
    if (bestIndex !== this.images.length - 1 && this.images[bestIndex + 1]) {
      let toPaintIdx = [];
      for (let i2 = len - 1; i2 >= bestIndex; i2--) {
        toPaintIdx.push(i2);
      }
      const smallestIdx = toPaintIdx[0];
      if (this.renderOptions.renderLayers) {
        toPaintIdx = toPaintIdx.slice(-this.renderOptions.renderLayers);
      }
      if (this.renderOptions.renderSmallestFallback && toPaintIdx.indexOf(smallestIdx) === -1) {
        toPaintIdx.unshift(smallestIdx);
      }
      const toPaint = [];
      for (let i2 = 0; i2 < toPaintIdx.length; i2++) {
        toPaint.push(...this.images[toPaintIdx[i2]].getAllPointsAt(target, newAggregate, scale2));
      }
      return toPaint;
    }
    return this.images[bestIndex].getAllPointsAt(target, newAggregate, scale2);
  }
}
function rotate(cx, cy, x2, y2, angle) {
  const radians = Math.PI / 180 * angle, cos2 = Math.cos(radians), sin2 = Math.sin(radians), nx = cos2 * (x2 - cx) + sin2 * (y2 - cy) + cx, ny = cos2 * (y2 - cy) - sin2 * (x2 - cx) + cy;
  return [nx, ny];
}
class WorldObject extends BaseObject {
  constructor(props2, position) {
    super();
    this.type = "world-object";
    this.intersectionBuffer = dna(5);
    this.aggregateBuffer = dna(9);
    this.invertedBuffer = dna(9);
    this.rotation = 0;
    this._updatedList = [];
    const { x: x2 = 0, y: y2 = 0 } = position || {};
    if (!props2) {
      this.id = "";
      this.scale = 1;
      this.layers = [];
      this.points = dna(5);
      this.worldPoints = dna(5);
      this.filteredPointsBuffer = dna(5);
    } else {
      this.id = props2.id || "";
      this.scale = 1;
      this.layers = props2.layers;
      this.points = dna([1, x2, y2, x2 + props2.width, y2 + props2.height]);
      this.worldPoints = dna([1, x2, y2, x2 + props2.width, y2 + props2.height]);
      this.filteredPointsBuffer = dna(props2.layers.length * 5);
    }
  }
  static createWithProps(props2) {
    const instance = new WorldObject();
    instance.applyProps(props2);
    return instance;
  }
  applyProps(props2) {
    const x2 = props2.x || 0;
    const y2 = props2.y || 0;
    this.id = props2.id;
    const s2 = typeof props2.scale !== "undefined" ? props2.scale : this.scale;
    this.points[0] = 1;
    this.points[1] = x2;
    this.points[2] = y2;
    this.points[3] = x2 + props2.width;
    this.points[4] = y2 + props2.height;
    this.rotation = props2.rotation || 0;
    this.worldPoints[3] = this.worldPoints[1] + props2.width;
    this.worldPoints[4] = this.worldPoints[2] + props2.height;
    if (props2.scale && props2.scale !== 1) {
      this.atScale(s2);
    }
    this.scale = s2;
  }
  appendChild(item) {
    if (item.points[0] === 0) {
      item.points.set(this.points);
    }
    item.__owner.value = this;
    this.addLayers([item]);
  }
  removeChild(item) {
    this.layers = this.layers.filter((layer) => layer !== item);
    this.filteredPointsBuffer = dna(this.layers.length * 5);
  }
  insertBefore(item, before) {
    const index = this.layers.indexOf(before);
    if (index === -1) {
      return;
    }
    if (this.layers.indexOf(item) !== -1) {
      return;
    }
    const beforeLayers = this.layers.slice(0, index);
    const afterLayers = this.layers.slice(index);
    this.layers = [...beforeLayers, item, ...afterLayers];
  }
  hideInstance() {
    console.warn("hideInstance: not yet implemented");
  }
  getObjectsAt(target, all) {
    if (this.rotation) {
      target = this.applyRotation(target);
    }
    const filteredPoints = hidePointsOutsideRegion(this.points, target, this.filteredPointsBuffer);
    if (filteredPoints[0] === 0) {
      return [];
    }
    const len = this.layers.length;
    const objects = [];
    for (let index = 0; index < len; index++) {
      const layer = this.layers[index];
      if (all && layer.isShape) {
        const t2 = transform(layer.points, translate(this.x, this.y));
        const int = layer.intersects([target[1] - t2[1], target[2] - t2[2]]);
        if (!int)
          continue;
      }
      const filter = hidePointsOutsideRegion(transform(layer.points, translate(this.x, this.y)), target, this.filteredPointsBuffer);
      if (filter[0] !== 0) {
        objects.push(layer);
      }
      if (all) {
        const object = layer;
        objects.push(...object.getObjectsAt(target, all));
      }
    }
    return objects;
  }
  applyRotation(target) {
    if (this.rotation) {
      const a2 = { x: target[1], y: target[2] };
      const b2 = { x: target[1], y: target[4] };
      const c6 = { x: target[3], y: target[2] };
      const d2 = { x: target[3], y: target[4] };
      const x2 = this.points[1] + (this.points[3] - this.points[1]) / 2;
      const y2 = this.points[2] + (this.points[4] - this.points[2]) / 2;
      const [x1, y1] = rotate(x2, y2, a2.x, a2.y, this.rotation);
      const [x22, y22] = rotate(x2, y2, b2.x, b2.y, this.rotation);
      const [x3, y3] = rotate(x2, y2, c6.x, c6.y, this.rotation);
      const [x4, y4] = rotate(x2, y2, d2.x, d2.y, this.rotation);
      const rx1 = Math.min(x1, x22, x3, x4);
      const rx2 = Math.max(x1, x22, x3, x4);
      const ry1 = Math.min(y1, y22, y3, y4);
      const ry2 = Math.max(y1, y22, y3, y4);
      return dna([target[0], rx1, ry1, rx2, ry2]);
    }
    return target;
  }
  getAllPointsAt(target, aggregate, scaleFactor) {
    const transformer = compose(translate(this.x, this.y), scale(this.scale), this.aggregateBuffer);
    if (this.rotation) {
      target = this.applyRotation(target);
    }
    const inter = getIntersection(target, this.points, this.intersectionBuffer);
    const len = this.layers.length;
    const arr = [];
    const t2 = transform(inter, compose(scale(1 / this.scale), translate(-this.x, -this.y), this.invertedBuffer));
    const agg = aggregate ? compose(aggregate, transformer, this.aggregateBuffer) : transformer;
    const s2 = scaleFactor * this.scale;
    for (let i2 = 0; i2 < len; i2++) {
      arr.push(...this.layers[i2].getAllPointsAt(t2, agg, s2));
    }
    return arr;
  }
  addLayers(paintables) {
    const paintablesToAdd = [];
    for (const paintable of paintables) {
      if (this.layers.indexOf(paintable) !== -1) {
        continue;
      }
      paintablesToAdd.push(paintable);
      if (paintable.points.length === 5 && (paintable.points[1] < this.worldPoints[1] / this.scale || paintable.points[2] < this.worldPoints[2] / this.scale || paintable.points[3] > this.worldPoints[3] / this.scale || paintable.points[4] > this.worldPoints[4] / this.scale)) {
        paintable.crop = paintable.crop || dna([
          1,
          Math.max(this.worldPoints[1] / this.scale, paintable.points[1]),
          Math.max(this.worldPoints[2] / this.scale, paintable.points[2]),
          Math.min(this.worldPoints[3] / this.scale, paintable.points[3]),
          Math.min(this.worldPoints[4] / this.scale, paintable.points[4])
        ]);
      }
    }
    this.layers = this.layers.concat(paintablesToAdd);
    this.filteredPointsBuffer = dna(this.layers.length * 5);
  }
  getScheduledUpdates(target, scaleFactor) {
    const len = this.layers.length;
    this._updatedList = [];
    const s2 = scaleFactor * this.scale;
    for (let i2 = 0; i2 < len; i2++) {
      const updates = this.layers[i2].getScheduledUpdates(target, s2);
      if (updates) {
        this._updatedList.push(...updates);
      }
    }
    return this._updatedList;
  }
}
class World extends BaseObject {
  constructor(width = 0, height = 0, worldObjectCount = 100, viewingDirection = "left-to-right") {
    super();
    this.id = "world";
    this.aggregateBuffer = dna(9);
    this.isDirty = false;
    this.zones = [];
    this.triggerQueue = [];
    this.activatedEvents = [];
    this._updatedList = [];
    this.translationBuffer = dna(9);
    this.needsRecalculate = true;
    this.emptyPaintables = [];
    this.renderOrder = [];
    this.objects = [];
    this.subscriptions = [];
    this._propagateEventTargets = [];
    this._alreadyFlushed = [];
    this._width = width;
    this._height = height;
    this.aspectRatio = Number.isNaN(width / height) ? 1 : width / height;
    this.viewingDirection = viewingDirection;
    this.points = dna(worldObjectCount * 5);
    this.filteredPointsBuffer = dna(worldObjectCount * 5);
  }
  get x() {
    return 0;
  }
  get y() {
    return 0;
  }
  get width() {
    return this._width;
  }
  get height() {
    return this._height;
  }
  static withProps(props2) {
    const instance = new World();
    instance.applyProps(props2);
    return instance;
  }
  applyProps(props2) {
    if (typeof props2.width !== "undefined" && typeof props2.height !== "undefined" && (props2.width !== this._width || props2.height !== this._height)) {
      this.resize(props2.width, props2.height);
    }
    if (props2.viewingDirection !== this.viewingDirection) {
      this.viewingDirection = props2.viewingDirection;
      this.triggerRepaint();
    }
  }
  propagateTouchEvent(eventName, e2, touchTargets) {
    const targets = [];
    for (const touch of touchTargets) {
      if (touch.x && touch.y) {
        const point = DnaFactory.singleBox(1, 1, touch.x, touch.y);
        targets.push(this.getObjectsAt(point, true).reverse());
      }
    }
    return targets.map((target) => this.propagateEvent(eventName, e2, target, { bubbles: true, cancelable: true }));
  }
  propagatePointerEvent(eventName, e2, x2, y2, opts = {}) {
    const point = DnaFactory.singleBox(1, 1, x2, y2);
    const worldObjects = this.getObjectsAt(point, true).reverse();
    return this.propagateEvent(eventName, e2, worldObjects, opts);
  }
  propagateEvent(eventName, e2, worldObjects, { bubbles = false, cancelable = false } = {}) {
    e2.atlasTarget = this;
    this._propagateEventTargets.length = 1;
    this._propagateEventTargets[0] = this;
    let stopped = false;
    e2.stopPropagation = () => {
      stopped = true;
    };
    const woLen = worldObjects.length;
    for (let w2 = woLen - 1; w2 >= 0; w2--) {
      this._propagateEventTargets.unshift(worldObjects[w2][0]);
      const len2 = worldObjects[w2][1].length;
      if (len2) {
        for (let i2 = 0; i2 < len2; i2++) {
          this._propagateEventTargets.unshift(worldObjects[w2][1][i2]);
        }
      }
    }
    const len = this._propagateEventTargets.length;
    let didFire = false;
    for (let i2 = 0; i2 < len; i2++) {
      e2.atlasTarget = this._propagateEventTargets[i2];
      e2.atlasWorld = this;
      didFire = this._propagateEventTargets[i2].dispatchEvent(eventName, e2) || didFire;
      if (stopped)
        break;
    }
    if (didFire) {
      this.triggerRepaint();
    }
    return this._propagateEventTargets;
  }
  appendChild(item) {
    const idx = this.appendWorldObject(item);
    this.renderOrder.push(idx / 5);
  }
  removeChild(item) {
    const index = this.objects.indexOf(item);
    if (index === -1) {
      for (const obj of this.objects) {
        if (obj && obj.id === item.id) {
          this.removeChild(obj);
          return;
        }
      }
      return;
    }
    this.objects[index] = null;
    this.renderOrder = this.renderOrder.filter((t2) => t2 !== index);
    this.points[index * 5] = 0;
    this.triggerRepaint();
    this.needsRecalculate = true;
  }
  insertBefore(item, before) {
    const beforeIndex = this.objects.indexOf(before);
    if (beforeIndex === -1) {
      return;
    }
    const idx = this.appendWorldObject(item);
    this.renderOrder.splice(beforeIndex - 1, 0, idx / 5);
  }
  hideInstance() {
  }
  asWorldObject() {
    return null;
  }
  addZone(zone) {
    this.zones.push(zone);
  }
  selectZone(id) {
    if (typeof id === "string") {
      const len = this.zones.length;
      for (let i2 = 0; i2 < len; i2++) {
        if (this.zones[i2].id === id) {
          this.selectedZone = i2;
          this.trigger("zone-changed");
          return;
        }
      }
    } else {
      if (this.zones[id]) {
        this.selectedZone = id;
        this.trigger("zone-changed");
      }
    }
  }
  deselectZone() {
    this.selectedZone = void 0;
  }
  getActiveZone() {
    if (this.selectedZone) {
      return this.zones[this.selectedZone];
    }
    return void 0;
  }
  hasActiveZone() {
    return typeof this.selectedZone !== "undefined";
  }
  checkResizeInternalBuffer() {
    if (dnaLength(this.points) === this.objects.length) {
      const points = this.points;
      const newPoints = dna(this.points.length * 2);
      newPoints.set(points, 0);
      this.points = newPoints;
    }
  }
  appendWorldObject(object) {
    this.checkResizeInternalBuffer();
    const index = this.objects.length * 5;
    const pointValues = object.points;
    object.points = this.points.subarray(this.objects.length * 5, this.objects.length * 5 + 5);
    object.points[1] = pointValues[1];
    object.points[2] = pointValues[2];
    object.points[3] = pointValues[3];
    object.points[4] = pointValues[4];
    this.objects.push(object);
    this.filteredPointsBuffer = dna(this.objects.length * 5);
    this.needsRecalculate = true;
    this.triggerRepaint();
    return index;
  }
  recalculateWorldSize() {
    let didChange = false;
    if (this.needsRecalculate) {
      const wBuffer = new Int32Array(this.objects.length);
      const hBuffer = new Int32Array(this.objects.length);
      const len = this.renderOrder.length;
      for (let _index = 0; _index < len; _index++) {
        const index = this.renderOrder[_index];
        const object = this.objects[index];
        if (object) {
          wBuffer[_index] = this.points[index * 5 + 3];
          hBuffer[_index] = this.points[index * 5 + 4];
        }
      }
      const newWidth = Math.max(...wBuffer);
      if (newWidth !== this._width) {
        this._width = newWidth;
        didChange = true;
      }
      const newHeight = Math.max(...hBuffer);
      if (newHeight !== this._height) {
        this._height = newHeight;
        didChange = true;
      }
      if (didChange) {
        this.trigger("recalculate-world-size", { width: newWidth, height: newHeight });
      }
      this.needsRecalculate = false;
    }
    return didChange;
  }
  addObjectAt(object, target) {
    if (target.width && !target.height) {
      target.height = target.width / object.width * object.height;
    } else if (target.height && !target.width) {
      target.width = target.height / object.height * object.width;
    }
    if (!target || !target.width || !target.height) {
      target.width = object.width;
      target.height = object.height;
    }
    const { width, x: x2, y: y2 } = target;
    const scaleFactor = width / object.width;
    this.checkResizeInternalBuffer();
    this.points.set(DnaFactory.singleBox(object.width, object.height, 0, 0), this.objects.length * 5);
    const worldObject = new WorldObject(object);
    worldObject.points = this.points.subarray(this.objects.length * 5, this.objects.length * 5 + 5);
    this.objects.push(worldObject);
    this.scaleWorldObject(this.objects.length - 1, scaleFactor);
    this.translateWorldObject(this.objects.length - 1, x2, y2);
    this.filteredPointsBuffer = dna(this.points.length * 2);
    this.triggerRepaint();
    this.needsRecalculate = true;
    return worldObject;
  }
  scaleWorldObject(index, factor) {
    mutate(this.points.subarray(index * 5, index * 5 + 5), scaleAtOrigin(factor, this.points[index * 5 + 1], this.points[index * 5 + 2]));
    const obj = this.objects[index];
    if (obj) {
      obj.atScale(factor);
      this.triggerRepaint();
    }
  }
  translateWorldObject(index, x2, y2) {
    mutate(this.points.subarray(index * 5, index * 5 + 5), translate(x2, y2));
    const obj = this.objects[index];
    if (obj) {
      obj.translate(x2, y2);
      this.triggerRepaint();
    }
  }
  resize(width, height) {
    this._width = width;
    this._height = height;
    this.aspectRatio = width / height;
    this.triggerRepaint();
    return this;
  }
  getObjects() {
    return this.objects;
  }
  getPoints() {
    return this.points;
  }
  getPointsFromViewer(target, aggregate) {
    const targetPoints = DnaFactory.singleBox(target.width, target.height, target.x, target.y);
    return this.getPointsAt(targetPoints, aggregate, target.scale);
  }
  addLayoutSubscriber(subscription) {
    this.subscriptions.push(subscription);
    return () => {
      this.subscriptions.splice(this.subscriptions.indexOf(subscription), 1);
    };
  }
  getScheduledUpdates(target, scaleFactor) {
    const filteredPoints = hidePointsOutsideRegion(this.points, target, this.filteredPointsBuffer);
    const len = this.objects.length;
    this._updatedList = [];
    for (let index = 0; index < len; index++) {
      if (filteredPoints[index * 5] !== 0) {
        if (!this.objects[index])
          continue;
        this._updatedList.push(...this.objects[index].getScheduledUpdates(target, scaleFactor));
      }
    }
    return this._updatedList;
  }
  getObjectsAt(target, all = false) {
    const zone = this.getActiveZone();
    const filteredPoints = hidePointsOutsideRegion(this.points, target, this.filteredPointsBuffer);
    const len = this.renderOrder.length;
    const objects = [];
    for (let _index = 0; _index < len; _index++) {
      const index = this.renderOrder[_index];
      if (filteredPoints[index * 5] !== 0) {
        const object = this.objects[index];
        if (!object || zone && zone.objects.indexOf(object) === -1) {
          continue;
        }
        if (object.type !== "world-object") {
          objects.push([object, [object]]);
          continue;
        }
        if (all) {
          objects.push([object, object.getObjectsAt(target, all)]);
        } else {
          objects.push([object, this.emptyPaintables]);
        }
      }
    }
    return objects;
  }
  getPointsAt(target, aggregate, scaleFactor = 1) {
    const objects = this.getObjectsAt(target);
    const translation = compose(scale(scaleFactor), translate(-target[1], -target[2]), this.translationBuffer);
    const transformer = aggregate ? compose(aggregate, translation, this.aggregateBuffer) : translation;
    const len = objects.length;
    const layers = [];
    for (let index = 0; index < len; index++) {
      if (objects[index]) {
        layers.push(...objects[index][0].getAllPointsAt(target, transformer, scaleFactor));
      }
    }
    return layers;
  }
  flushSubscriptions() {
    if (this.triggerQueue.length) {
      this._alreadyFlushed = [];
      const queueLen = this.triggerQueue.length;
      for (let x2 = 0; x2 < queueLen; x2++) {
        if (this._alreadyFlushed.indexOf(this.triggerQueue[x2][0]) !== -1) {
          continue;
        }
        if (typeof this.triggerQueue[x2][1] === "undefined") {
          this._alreadyFlushed.push(this.triggerQueue[x2][0]);
        }
        const len = this.subscriptions.length;
        for (let i2 = 0; i2 < len; i2++) {
          this.subscriptions[i2].apply(null, this.triggerQueue[x2]);
        }
      }
      this.triggerQueue = [];
    }
  }
  trigger(type, data) {
    this.triggerQueue.push([type, data]);
  }
  triggerEventActivation() {
    this.trigger("event-activation");
  }
  triggerRepaint() {
    this.trigger("repaint");
  }
  gotoRegion(data) {
    this.trigger("goto-region", data);
  }
  goHome(immediate = false) {
    this.trigger("go-home", {
      immediate
    });
  }
  zoomTo(factor, point, stream) {
    this.trigger("zoom-to", {
      point,
      factor,
      stream
    });
  }
  zoomIn(point) {
    this.trigger("zoom-to", {
      point,
      factor: 0.5
    });
  }
  zoomOut(point) {
    this.trigger("zoom-to", {
      point,
      factor: 2
    });
  }
  constraintBounds(immediate) {
    this.trigger("constrain-bounds", { immediate });
  }
}
function fromImage(image) {
  const { src, target } = image;
  const width = target ? target.width : image.width;
  const height = target ? target.height : image.height;
  return new WorldObject({
    id: src,
    height,
    width,
    layers: [SingleImage.fromImage(src, { height, width }, { width: image.width, height: image.height })]
  });
}
const defaultConfig$1 = {
  margin: 0
};
class Zone {
  constructor(objects, config = {}) {
    this.id = objects.map((obj) => obj.id).join("$$");
    this.config = {
      ...defaultConfig$1,
      ...config
    };
    this.points = dna(5);
    this.objects = objects;
    this.recalculateBounds();
  }
  recalculateBounds() {
    this.points.set([
      1,
      Math.min(...this.objects.map((obj) => obj.points[1])) - this.config.margin,
      Math.min(...this.objects.map((obj) => obj.points[2])) - this.config.margin,
      Math.max(...this.objects.map((obj) => obj.points[3])) + this.config.margin,
      Math.max(...this.objects.map((obj) => obj.points[4])) + this.config.margin
    ]);
  }
  getPointsAt(target, aggregate, scaleFactor) {
    return [];
  }
}
const borderRegex$1 = /([0-9]+(px|em)\s+)+(solid)\s+(.*)/g;
const borderRegexCache$1 = {};
const styleProps$1 = [
  "backgroundColor",
  "opacity",
  "boxShadow",
  "borderColor",
  "borderWidth",
  "borderStyle",
  "outlineColor",
  "outlineWidth",
  "outlineOffset",
  "outlineStyle"
];
class Box extends BaseObject {
  constructor() {
    super();
    this.type = "spacial-content";
    this.hoverEvents = false;
    this.activeEvents = false;
    this.display = {
      x: 0,
      y: 0,
      scale: 1,
      width: -1,
      height: -1,
      points: dna(5)
    };
    this._parsed = {
      border: { id: null, match: [] },
      outline: { id: null, match: [] }
    };
    this.props = {};
    this.addHover = () => {
      this.hovering = true;
      this.__revision++;
    };
    this.removeHover = () => {
      this.hovering = false;
      this.pressing = false;
      this.__revision++;
    };
    this.addPress = () => {
      this.pressing = true;
      this.__revision++;
    };
    this.removePress = () => {
      this.pressing = false;
      this.__revision++;
    };
    this.id = nanoid(12);
    this.points = dna(5);
  }
  getAllPointsAt(target, aggregate) {
    return [[this, this.points, aggregate]];
  }
  applyProps(props2 = {}) {
    let didUpdate = false;
    if (props2.interactive !== this.props.interactive) {
      didUpdate = true;
      this.props.interactive = props2.interactive;
    }
    if (props2.style) {
      const borderStyle = props2.border || props2.style.border;
      if (borderStyle !== this._parsed.border.id) {
        if (!borderStyle) {
          this._parsed.border.id = null;
          this._parsed.border.match = [];
        } else {
          const match = borderRegexCache$1[borderStyle] || borderRegex$1.exec(borderStyle) || borderRegex$1.exec(borderStyle);
          if (match) {
            this._parsed.border.id = borderStyle;
            this._parsed.border.match = borderRegexCache$1[borderStyle] = match;
          }
        }
      }
      if (this._parsed.border.id) {
        props2.style.borderWidth = this._parsed.border.match[1];
        props2.style.borderStyle = "solid";
        props2.style.borderColor = this._parsed.border.match[4];
      }
      if (props2.style.outline !== this._parsed.outline.id) {
        if (!props2.style.outline) {
          this._parsed.outline.id = null;
          this._parsed.outline.match = [];
        } else {
          const match = borderRegexCache$1[props2.style.outline] || borderRegex$1.exec(props2.style.outline) || borderRegex$1.exec(props2.style.outline);
          if (match) {
            this._parsed.outline.id = props2.style.outline;
            this._parsed.outline.match = borderRegexCache$1[props2.style.outline] = match;
          }
        }
      }
      if (this._parsed.outline.id) {
        props2.style.outlineWidth = this._parsed.outline.match[1];
        props2.style.outlineStyle = "solid";
        props2.style.outlineColor = this._parsed.outline.match[4];
      }
      this.props.style = props2.style;
      if (props2.backgroundColor && !this.props.style.backgroundColor) {
        this.props.style.backgroundColor = props2.backgroundColor;
        didUpdate = true;
      }
      if (props2.style.background && !this.props.style.backgroundColor) {
        this.props.style.backgroundColor = props2.style.background;
        didUpdate = true;
      }
      for (const prop of styleProps$1) {
        if (this.props.style[prop] !== props2.style[prop]) {
          didUpdate = true;
          break;
        }
      }
      if (props2.style[":hover"] !== this.props.hoverStyles) {
        this.props.hoverStyles = props2.style[":hover"];
        if (!this.hoverEvents) {
          this.hoverEvents = true;
          this.addEventListener("pointerenter", this.addHover);
          this.addEventListener("pointerleave", this.removeHover);
        }
        didUpdate = true;
      }
      if (props2.style[":active"] !== this.props.pressStyles) {
        this.props.pressStyles = props2.style[":active"];
        if (!this.activeEvents) {
          this.activeEvents = true;
          this.addEventListener("mousedown", this.addPress);
          this.addEventListener("mouseup", this.removePress);
        }
        didUpdate = true;
      }
    }
    if (props2.href !== this.props.href) {
      this.props.href = props2.href;
      didUpdate = true;
    }
    if (props2.hrefTarget !== this.props.hrefTarget) {
      this.props.hrefTarget = props2.hrefTarget;
      didUpdate = true;
    }
    if (props2.title !== this.props.title) {
      this.props.title = props2.title;
      didUpdate = true;
    }
    if (props2.className !== this.props.className) {
      this.props.className = props2.className;
      if (props2.className && !this.hoverEvents) {
        this.hoverEvents = true;
        this.addEventListener("pointerenter", this.addHover);
        this.addEventListener("pointerleave", this.removeHover);
      }
      if (props2.className && !this.activeEvents) {
        this.activeEvents = true;
        this.addEventListener("mousedown", this.addPress);
        this.addEventListener("mouseup", this.removePress);
      }
      didUpdate = true;
    }
    if (props2.relativeSize !== this.props.relativeSize) {
      this.props.relativeSize = props2.relativeSize;
      didUpdate = true;
    }
    if (props2.relativeStyle !== this.props.relativeStyle) {
      this.props.relativeStyle = props2.relativeStyle;
      didUpdate = true;
    }
    if (props2.html !== this.props.html) {
      this.props.html = props2.html;
      didUpdate = true;
    }
    if (props2.target) {
      if (props2.target.width !== this.display.width || props2.target.height !== this.display.height || props2.target.x !== this.points[1] || props2.target.y !== this.points[2]) {
        didUpdate = true;
        this.points = DnaFactory.singleBox(props2.target.width, props2.target.height, props2.target.x, props2.target.y);
        this.display.points = DnaFactory.singleBox(props2.target.width, props2.target.height, props2.target.x, props2.target.y);
        this.display.width = props2.target.width;
        this.display.height = props2.target.height;
      }
    }
    if (didUpdate) {
      this.__revision++;
    }
  }
}
class Text extends BaseObject {
  constructor() {
    super();
    this.type = "spacial-content";
    this.color = "#000";
    this.text = "";
    this.display = {
      x: 0,
      y: 0,
      scale: 1,
      width: 100,
      height: 100,
      points: dna(5)
    };
    this.interactive = false;
    this.props = {
      font: "18px Arial, sans-serif",
      lineHeight: 1,
      textAlign: "left",
      verticalAlign: "top",
      paddingX: 0,
      paddingY: 0,
      fitParent: false,
      lineBreak: "auto",
      strokeText: false,
      sizeToFill: false,
      maxFontSizeToFill: void 0,
      allowNewLine: true,
      justifyLines: false,
      renderHDPI: false,
      textDecoration: "none",
      interactive: false,
      relativeSize: false
    };
    this.id = "";
    this.points = dna(5);
  }
  getAllPointsAt(target, aggregate) {
    return [[this, this.points, aggregate]];
  }
  applyProps({
    id,
    target,
    text,
    color,
    backgroundColor,
    fontSize = 18,
    interactive,
    fontFamily = "Arial, sans-serif",
    ...props2
  }) {
    props2.font = `${fontSize}px ${fontFamily}`;
    this.interactive = interactive || false;
    if (typeof text !== "undefined") {
      this.text = text || "";
    }
    if (color) {
      this.color = color;
    }
    if (backgroundColor) {
      this.backgroundColor = backgroundColor;
    }
    if (id) {
      this.id = id;
    }
    if (target) {
      this.points = DnaFactory.singleBox(target.width, target.height, target.x, target.y);
      this.display.points = this.points;
      this.display.width = target.width;
      this.display.height = target.height;
    }
    this.props = { ...this.props, ...props2 };
    this.__revision++;
  }
}
const pow = Math.pow;
const sqrt = Math.sqrt;
const sin = Math.sin;
const cos = Math.cos;
const PI = Math.PI;
const c1 = 1.70158;
const c2 = c1 * 1.525;
const c3 = c1 + 1;
const c4 = 2 * PI / 3;
const c5 = 2 * PI / 4.5;
const bounceOut = function(x2) {
  const n1 = 7.5625;
  const d1 = 2.75;
  if (x2 < 1 / d1) {
    return n1 * x2 * x2;
  } else if (x2 < 2 / d1) {
    return n1 * (x2 -= 1.5 / d1) * x2 + 0.75;
  } else if (x2 < 2.5 / d1) {
    return n1 * (x2 -= 2.25 / d1) * x2 + 0.9375;
  } else {
    return n1 * (x2 -= 2.625 / d1) * x2 + 0.984375;
  }
};
const easingFunctions = {
  linear: (x2) => x2,
  easeInQuad: function(x2) {
    return x2 * x2;
  },
  easeOutQuad: function(x2) {
    return 1 - (1 - x2) * (1 - x2);
  },
  easeInOutQuad: function(x2) {
    return x2 < 0.5 ? 2 * x2 * x2 : 1 - pow(-2 * x2 + 2, 2) / 2;
  },
  easeInCubic: function(x2) {
    return x2 * x2 * x2;
  },
  easeOutCubic: function(x2) {
    return 1 - pow(1 - x2, 3);
  },
  easeInOutCubic: function(x2) {
    return x2 < 0.5 ? 4 * x2 * x2 * x2 : 1 - pow(-2 * x2 + 2, 3) / 2;
  },
  easeInQuart: function(x2) {
    return x2 * x2 * x2 * x2;
  },
  easeOutQuart: function(x2) {
    return 1 - pow(1 - x2, 4);
  },
  easeInOutQuart: function(x2) {
    return x2 < 0.5 ? 8 * x2 * x2 * x2 * x2 : 1 - pow(-2 * x2 + 2, 4) / 2;
  },
  easeInQuint: function(x2) {
    return x2 * x2 * x2 * x2 * x2;
  },
  easeOutQuint: function(x2) {
    return 1 - pow(1 - x2, 5);
  },
  easeInOutQuint: function(x2) {
    return x2 < 0.5 ? 16 * x2 * x2 * x2 * x2 * x2 : 1 - pow(-2 * x2 + 2, 5) / 2;
  },
  easeInSine: function(x2) {
    return 1 - cos(x2 * PI / 2);
  },
  easeOutSine: function(x2) {
    return sin(x2 * PI / 2);
  },
  easeInOutSine: function(x2) {
    return -(cos(PI * x2) - 1) / 2;
  },
  easeInExpo: function(x2) {
    return x2 === 0 ? 0 : pow(2, 10 * x2 - 10);
  },
  easeOutExpo: function(x2) {
    return x2 === 1 ? 1 : 1 - pow(2, -10 * x2);
  },
  easeInOutExpo: function(x2) {
    return x2 === 0 ? 0 : x2 === 1 ? 1 : x2 < 0.5 ? pow(2, 20 * x2 - 10) / 2 : (2 - pow(2, -20 * x2 + 10)) / 2;
  },
  easeInCirc: function(x2) {
    return 1 - sqrt(1 - pow(x2, 2));
  },
  easeOutCirc: function(x2) {
    return sqrt(1 - pow(x2 - 1, 2));
  },
  easeInOutCirc: function(x2) {
    return x2 < 0.5 ? (1 - sqrt(1 - pow(2 * x2, 2))) / 2 : (sqrt(1 - pow(-2 * x2 + 2, 2)) + 1) / 2;
  },
  easeInBack: function(x2) {
    return c3 * x2 * x2 * x2 - c1 * x2 * x2;
  },
  easeOutBack: function(x2) {
    return 1 + c3 * pow(x2 - 1, 3) + c1 * pow(x2 - 1, 2);
  },
  easeInOutBack: function(x2) {
    return x2 < 0.5 ? pow(2 * x2, 2) * ((c2 + 1) * 2 * x2 - c2) / 2 : (pow(2 * x2 - 2, 2) * ((c2 + 1) * (x2 * 2 - 2) + c2) + 2) / 2;
  },
  easeInElastic: function(x2) {
    return x2 === 0 ? 0 : x2 === 1 ? 1 : -pow(2, 10 * x2 - 10) * sin((x2 * 10 - 10.75) * c4);
  },
  easeOutElastic: function(x2) {
    return x2 === 0 ? 0 : x2 === 1 ? 1 : pow(2, -10 * x2) * sin((x2 * 10 - 0.75) * c4) + 1;
  },
  easeInOutElastic: function(x2) {
    return x2 === 0 ? 0 : x2 === 1 ? 1 : x2 < 0.5 ? -(pow(2, 20 * x2 - 10) * sin((20 * x2 - 11.125) * c5)) / 2 : pow(2, -20 * x2 + 10) * sin((20 * x2 - 11.125) * c5) / 2 + 1;
  },
  easeInBounce: function(x2) {
    return 1 - bounceOut(1 - x2);
  },
  easeOutBounce: bounceOut,
  easeInOutBounce: function(x2) {
    return x2 < 0.5 ? (1 - bounceOut(1 - 2 * x2)) / 2 : (1 + bounceOut(2 * x2 - 1)) / 2;
  }
};
class TransitionManager {
  constructor(runtime) {
    this.runtime = runtime;
    this.pendingTransition = {
      from: dna(5),
      to: dna(5),
      elapsed_time: 0,
      done: true,
      total_time: 0,
      timingFunction: easingFunctions.easeInOutQuad,
      constrain: false
    };
  }
  hasPending() {
    return !this.pendingTransition.done;
  }
  getPendingTransition() {
    return this.pendingTransition;
  }
  getPendingFrom() {
    return this.pendingTransition.from;
  }
  customTransition(func) {
    func(this.pendingTransition);
  }
  stopTransition() {
    this.pendingTransition.from = dna(this.runtime.target);
    this.pendingTransition.to = dna(this.runtime.target);
    this.pendingTransition.done = true;
    this.pendingTransition.elapsed_time = 0;
    this.pendingTransition.total_time = 0;
  }
  runTransition(target, delta) {
    if (!this.pendingTransition.done) {
      const transition = this.pendingTransition;
      const td = transition.total_time === 0 ? 0 : (transition.elapsed_time + delta) / transition.total_time;
      const step = transition.total_time === 0 ? 1 : td === 0 ? 0 : transition.timingFunction(td);
      target[1] = transition.from[1] + (transition.to[1] - transition.from[1]) * step;
      target[2] = transition.from[2] + (transition.to[2] - transition.from[2]) * step;
      target[3] = transition.from[3] + (transition.to[3] - transition.from[3]) * step;
      target[4] = transition.from[4] + (transition.to[4] - transition.from[4]) * step;
      this.pendingTransition.elapsed_time += delta;
      if (this.pendingTransition.elapsed_time >= this.pendingTransition.total_time) {
        this.pendingTransition.done = true;
        if (this.pendingTransition.constrain) {
          this.constrainBounds({
            transition: {
              duration: this.pendingTransition.total_time === 0 ? 0 : 500,
              easing: easingFunctions.easeOutExpo
            }
          });
        }
      }
    }
  }
  zoomTo(factor, {
    origin,
    stream = false,
    transition
  } = {}) {
    const newPoints = this.runtime.getZoomedPosition(factor, { origin });
    const dist = Math.abs(1 - factor);
    this.applyTransition(newPoints, transition, {
      duration: 2e3 * dist,
      easing: easingFunctions.easeOutExpo,
      constrain: true
    }, { stream: false });
  }
  constrainBounds({
    transition,
    panPadding = 0
  } = {}) {
    const [isConstrained, constrained] = this.runtime.constrainBounds(this.runtime.target, { panPadding });
    if (isConstrained) {
      this.applyTransition(constrained, transition, {
        duration: 500,
        easing: easingFunctions.easeOutQuart,
        constrain: false
      });
      this.runtime.updateNextFrame();
    }
  }
  applyTransition(target, transition, defaults, {
    stream
  } = {}) {
    this.pendingTransition.from = dna(this.runtime.target);
    this.pendingTransition.to = target;
    if (!stream) {
      this.pendingTransition.elapsed_time = 0;
    }
    this.pendingTransition.done = false;
    this.pendingTransition.total_time = typeof (transition == null ? void 0 : transition.duration) !== "undefined" ? transition.duration : typeof (defaults == null ? void 0 : defaults.duration) !== "undefined" ? defaults.duration : 1e3;
    this.pendingTransition.constrain = typeof (transition == null ? void 0 : transition.constrain) !== "undefined" ? transition.constrain : typeof (defaults == null ? void 0 : defaults.constrain) !== "undefined" ? defaults.constrain : false;
    this.pendingTransition.timingFunction = (transition == null ? void 0 : transition.easing) || (defaults == null ? void 0 : defaults.easing) || easingFunctions.easeInOutSine;
  }
  goToRegion(target, {
    transition
  } = {}) {
    const clampedRegion = this.runtime.clampRegion(target);
    this.applyTransition(DnaFactory.singleBox(clampedRegion.width, clampedRegion.height, clampedRegion.x, clampedRegion.y), transition, {
      duration: 1e3,
      easing: easingFunctions.easeOutExpo,
      constrain: true
    });
    this.runtime.updateNextFrame();
  }
}
class Runtime {
  constructor(renderer, world, target, controllers = [], options) {
    this.id = nanoid();
    this.ready = false;
    this.transformBuffer = dna(500);
    this.lastTarget = dna(5);
    this.zoomBuffer = dna(5);
    this.logNextRender = false;
    this.pendingUpdate = true;
    this.isCommitting = false;
    this.firstRender = true;
    this.mode = "explore";
    this.controllers = [];
    this.controllersRunning = false;
    this.controllerStopFunctions = [];
    this.maxScaleFactor = 1;
    this._viewerToWorld = { x: 0, y: 0 };
    this._lastGoodScale = 1;
    this.hooks = {
      useFrame: [],
      useBeforeFrame: [],
      useAfterPaint: [],
      useAfterFrame: []
    };
    this._viewport = { x: 0, y: 0, width: 0, height: 0 };
    this.setViewport = (data) => {
      const x2 = typeof data.x === "undefined" ? this.target[1] : data.x;
      const y2 = typeof data.y === "undefined" ? this.target[2] : data.y;
      if (data.width) {
        this.target[3] = x2 + data.width;
      } else {
        this.target[3] = this.target[3] - this.target[1] + x2;
      }
      if (data.height) {
        this.target[4] = y2 + data.height;
      } else {
        this.target[4] = this.target[4] - this.target[2] + y2;
      }
      if (Math.abs(this.target[1] - x2) > 0.01) {
        this.target[1] = x2;
      }
      if (Math.abs(this.target[2] - y2) > 0.01) {
        this.target[2] = y2;
      }
      this.pendingUpdate = true;
    };
    this.render = (t2) => {
      const delta = t2 - this.lastTime;
      if (this.isCommitting || this.fpsLimit && delta < 1e3 / this.fpsLimit) {
        this.stopId = window.requestAnimationFrame(this.render);
        return;
      }
      this.lastTime = t2;
      this.world.flushSubscriptions();
      this.stopId = window.requestAnimationFrame(this.render);
      this.hook("useFrame", delta);
      const pendingUpdate = this.pendingUpdate;
      const rendererPendingUpdate = this.renderer.pendingUpdate();
      if (this.transitionManager.hasPending()) {
        this.transitionManager.runTransition(this.target, delta);
        this.pendingUpdate = true;
        this.updateControllerPosition();
      }
      if (!this.firstRender && !pendingUpdate && !rendererPendingUpdate && this.target[0] === this.lastTarget[0] && this.target[1] === this.lastTarget[1] && this.target[2] === this.lastTarget[2] && this.target[3] === this.lastTarget[3] && this.target[4] === this.lastTarget[4]) {
        return;
      }
      this.hook("useBeforeFrame", delta);
      this.renderer.beforeFrame(this.world, delta, this.target);
      const scaleFactor = this.getScaleFactor();
      const points = this.renderer.getPointsAt(this.world, this.target, this.aggregate, scaleFactor);
      const pointsLen = points.length;
      for (let p2 = 0; p2 < pointsLen; p2++) {
        const paint = points[p2][0];
        const point = points[p2][1];
        const transformation = points[p2][2];
        const position = transformation ? transform(point, transformation, this.transformBuffer) : point;
        this.renderer.prepareLayer(paint, paint.__parent && transformation ? transform(paint.__parent.crop || paint.__parent.points, transformation) : position);
        const totalTiles = position.length / 5;
        for (let i2 = 0; i2 < totalTiles; i2++) {
          const key = i2 * 5;
          if (position[key] === 0) {
            continue;
          }
          this.renderer.paint(paint, i2, position[key + 1], position[key + 2], position[key + 3] - position[key + 1], position[key + 4] - position[key + 2]);
          this.hook("useAfterPaint", paint);
        }
        this.renderer.finishLayer(paint, point);
      }
      this.renderer.afterFrame(this.world, delta, this.target);
      this.hook("useAfterFrame", delta);
      this.lastTarget[0] = this.target[0];
      this.lastTarget[1] = this.target[1];
      this.lastTarget[2] = this.target[2];
      this.lastTarget[3] = this.target[3];
      this.lastTarget[4] = this.target[4];
      this.firstRender = false;
      this.pendingUpdate = false;
      this.logNextRender = false;
      if (this.renderer.isReady()) {
        this.ready = true;
        this.world.trigger("ready");
      }
      this.world.flushSubscriptions();
      const updates = this.world.getScheduledUpdates(this.target, scaleFactor);
      const len = updates.length;
      if (len > 0) {
        for (let i2 = 0; i2 < len; i2++) {
          const update = updates[len - i2 - 1]();
          if (update) {
            update.then(() => {
              this.pendingUpdate = true;
            });
          } else {
            this.pendingUpdate = true;
          }
        }
      }
    };
    this.renderer = renderer;
    this.world = world;
    this.options = {
      maxOverZoom: 1,
      maxUnderZoom: 1,
      visibilityRatio: 1.5,
      ...options || {}
    };
    this.target = DnaFactory.projection(target);
    this.manualHomePosition = false;
    this.pendingUpdate = true;
    this.homePosition = DnaFactory.projection(this.world);
    this.manualFocalPosition = false;
    this.focalPosition = this.target;
    this.updateFocalPosition();
    this.transitionManager = new TransitionManager(this);
    this.aggregate = scale(1);
    this.world.addLayoutSubscriber((type) => {
      if (type === "repaint") {
        this.pendingUpdate = true;
      }
      if (type === "recalculate-world-size") {
        if (!this.manualHomePosition) {
          this.setHomePosition();
          this.goHome();
        }
        this.updateFocalPosition();
      }
    });
    this.lastTime = performance.now();
    this.controllers = controllers;
    this.render(this.lastTime);
    this.startControllers();
  }
  get x() {
    return this.target[1];
  }
  set x(x2) {
    this.target[1] = x2;
  }
  get y() {
    return this.target[2];
  }
  set y(y2) {
    this.target[2] = y2;
  }
  get x2() {
    return this.target[3];
  }
  set x2(x2) {
    this.target[3] = x2;
  }
  get y2() {
    return this.target[4];
  }
  set y2(y2) {
    this.target[4] = y2;
  }
  get width() {
    return this.target[3] - this.target[1];
  }
  set width(width) {
    this.target[3] = this.target[1] + width;
  }
  get height() {
    return this.target[4] - this.target[2];
  }
  set height(height) {
    this.target[4] = this.target[2] = height;
  }
  setHomePosition(position) {
    this.homePosition.set(DnaFactory.projection(position ? position : this.world));
    this.pendingUpdate = true;
  }
  startControllers() {
    if (this.controllersRunning) {
      return;
    }
    for (const controller of this.controllers) {
      this.controllerStopFunctions.push(controller.start(this));
    }
    this.controllersRunning = true;
  }
  stopControllers() {
    if (!this.controllersRunning) {
      return;
    }
    for (const controller of this.controllerStopFunctions) {
      controller();
    }
    this.controllersRunning = false;
    this.controllerStopFunctions = [];
  }
  updateControllerPosition() {
    for (const controller of this.controllers) {
      controller.updatePosition(this.x, this.y, this.width, this.height);
    }
  }
  triggerResize() {
    if (this.renderer.triggerResize) {
      this.renderer.triggerResize();
    }
    this.pendingUpdate = true;
  }
  addController(controller) {
    this.controllers.push(controller);
    if (this.controllersRunning) {
      controller.start(this);
    }
    this.pendingUpdate = true;
  }
  cover() {
    return this.goHome({ cover: true });
  }
  getRendererScreenPosition() {
    return this.renderer.getRendererScreenPosition();
  }
  updateRendererScreenPosition() {
    this.pendingUpdate = true;
    this.renderer.resize();
  }
  setOptions(options) {
    this.options = { ...this.options, ...options };
  }
  goHome(options = {}) {
    if (this.world.width <= 0 || this.world.height <= 0)
      return;
    const scaleFactor = this.getScaleFactor();
    const target = options.position ? {
      x: options.position[1],
      y: options.position[2],
      width: options.position[3] - options.position[1],
      height: options.position[4] - options.position[2]
    } : {
      x: this.homePosition[1],
      y: this.homePosition[2],
      width: this.homePosition[3] - this.homePosition[1],
      height: this.homePosition[4] - this.homePosition[2]
    };
    const width = this.width * scaleFactor;
    const height = this.height * scaleFactor;
    const widthScale = target.width / width;
    const heightScale = target.height / height;
    const ar = width / height;
    if (options.cover ? widthScale > heightScale : widthScale < heightScale) {
      const fullWidth = ar * target.height;
      const space = (fullWidth - target.width) / 2;
      this.target[1] = -space + target.x;
      this.target[2] = target.y;
      this.target[3] = fullWidth - space + target.x;
      this.target[4] = target.height + target.y;
    } else {
      const fullHeight = target.width / ar;
      const space = (fullHeight - target.height) / 2;
      this.target[1] = target.x;
      this.target[2] = target.y - space;
      this.target[3] = target.x + target.width;
      this.target[4] = target.y + fullHeight - space;
    }
    this.constrainBounds(this.target);
    this.updateControllerPosition();
  }
  resize(fromWidth, toWidth, fromHeight, toHeight) {
    this.updateFocalPosition(fromWidth - toWidth, fromHeight - toHeight);
    const widthRatio = toWidth / fromWidth;
    const heightRatio = toHeight / fromHeight;
    this.target[3] = this.target[1] + (this.target[3] - this.target[1]) * widthRatio;
    this.target[4] = this.target[2] + (this.target[4] - this.target[2]) * heightRatio;
    this.goHome({ position: this.focalPosition });
    this.renderer.resize(toWidth, toHeight);
    this.pendingUpdate = true;
  }
  updateFocalPosition(widthDiff, heightDiff) {
    if (!this.manualFocalPosition) {
      const w2 = this.width;
      const h2 = this.height;
      const min = Math.min(w2, h2);
      const marginTrimWidth = 0;
      const marginTrimHeight = 0;
      const baseX = this.x + marginTrimWidth;
      const baseY = this.y + marginTrimHeight;
      if (w2 < h2) {
        const diff = this.height - this.width;
        this.focalPosition = DnaFactory.projection({
          x: baseX,
          y: baseY + diff / 2,
          width: min - marginTrimWidth * 2,
          height: min - marginTrimHeight * 2
        });
        this.pendingUpdate = true;
      } else {
        const diff = this.width - this.height;
        this.focalPosition = DnaFactory.projection({
          x: baseX + diff / 2,
          y: baseY,
          width: min - marginTrimWidth * 2,
          height: min - marginTrimHeight * 2
        });
        this.pendingUpdate = true;
      }
    }
  }
  getViewport() {
    this._viewport.x = this.target[1];
    this._viewport.y = this.target[2];
    this._viewport.width = this.target[3] - this.target[1];
    this._viewport.height = this.target[4] - this.target[2];
    return this._viewport;
  }
  constrainBounds(target, { panPadding = 0, ref = false } = {}) {
    const { minX, maxX, minY, maxY } = this.getBounds({ target, padding: panPadding });
    let isConstrained = false;
    const constrained = ref ? target : dna(target);
    const width = target[3] - target[1];
    const height = target[4] - target[2];
    if (minX > target[1]) {
      isConstrained = true;
      constrained[1] = minX;
      constrained[3] = minX + width;
    }
    if (minY > target[2]) {
      isConstrained = true;
      constrained[2] = minY;
      constrained[4] = minY + height;
    }
    if (maxX < target[1]) {
      isConstrained = true;
      constrained[1] = maxX;
      constrained[3] = maxX + width;
    }
    if (maxY < target[2]) {
      isConstrained = true;
      constrained[2] = maxY;
      constrained[4] = maxY + height;
    }
    return [isConstrained, constrained];
  }
  getBounds(options) {
    const target = options.target || this.target;
    const padding = options.padding;
    const visRatio = this.options.visibilityRatio;
    const hiddenRatio = Math.abs(1 - visRatio);
    if (this.world.hasActiveZone()) {
      const zone = this.world.getActiveZone();
      if (zone) {
        const xCon = target[3] - target[1] < zone.points[3] - zone.points[1];
        const yCon = target[4] - target[2] < zone.points[4] - zone.points[2];
        return {
          minX: xCon ? zone.points[1] - padding : zone.points[1] + (zone.points[3] - zone.points[1]) / 2 - (target[3] - target[1]) / 2,
          maxX: yCon ? zone.points[2] - padding : zone.points[2] + (zone.points[4] - zone.points[2]) / 2 - (target[4] - target[2]) / 2,
          minY: xCon ? zone.points[3] + padding : zone.points[1] + (zone.points[3] - zone.points[1]) / 2 - (target[3] - target[1]) / 2,
          maxY: yCon ? zone.points[4] + padding : zone.points[2] + (zone.points[4] - zone.points[2]) / 2 - (target[4] - target[2]) / 2
        };
      }
    }
    const wt = target[3] - target[1];
    const ww = this.world.width;
    const xB = -wt * hiddenRatio;
    const xD = ww - wt - xB;
    const ht = target[4] - target[2];
    const hw = this.world.height;
    const yB = -ht * hiddenRatio;
    const yD = hw - ht - yB;
    const maxX = Math.max(xB, xD);
    const minX = Math.min(xB, xD);
    const maxY = Math.max(yB, yD);
    const minY = Math.min(yB, yD);
    return { minX, maxX, minY, maxY };
  }
  getScaleFactor(dpi = false) {
    const scale2 = this.renderer.getScale(this.target[3] - this.target[1], this.target[4] - this.target[2], dpi);
    if (scale2 === 0) {
      return this._lastGoodScale;
    }
    this._lastGoodScale = scale2;
    return scale2;
  }
  getZoomedPosition(factor, {
    origin,
    fromPos: _fromPos
  }) {
    var _a;
    const fromPos = _fromPos ? { width: _fromPos[3] - _fromPos[1], height: _fromPos[4] - _fromPos[2] } : void 0;
    const scaleFactor = fromPos ? this.renderer.getScale(fromPos.width, fromPos.height) : this.getScaleFactor();
    const w2 = fromPos ? fromPos.width : this.width;
    const h2 = fromPos ? fromPos.height : this.height;
    const sWidth = (_a = this.getRendererScreenPosition()) == null ? void 0 : _a.width;
    const wWidth = this.world.width;
    const ratio = sWidth ? sWidth / wWidth : 1;
    const maxUnderZoom = this.options.maxUnderZoom;
    const maxOverZoom = Math.max(ratio || 1, this.options.maxOverZoom);
    const realFactor = 1 / factor;
    const proposedFactor = scaleFactor * realFactor;
    const isZoomingOut = realFactor < 1;
    if (isZoomingOut) {
      const width = w2 * scaleFactor;
      const height = h2 * scaleFactor;
      const widthScale = this.world.width / width;
      const heightScale = this.world.height / height;
      if (widthScale > heightScale) {
        const proposedWorldDisplayWidth = this.world.width * proposedFactor;
        const displayWidth = ~~(w2 * scaleFactor);
        const displayWidthAdjusted = displayWidth * maxUnderZoom;
        if (proposedWorldDisplayWidth < displayWidthAdjusted) {
          factor = this.world.width * scaleFactor / (w2 * scaleFactor * maxUnderZoom);
        }
      } else {
        const proposedWorldDisplayHeight = this.world.height * proposedFactor;
        const displayHeight = ~~(h2 * scaleFactor);
        const displayHeightAdjusted = displayHeight * maxUnderZoom;
        if (proposedWorldDisplayHeight < displayHeightAdjusted) {
          factor = this.world.height * scaleFactor / (h2 * scaleFactor * maxUnderZoom);
        }
      }
    } else {
      if (proposedFactor > maxOverZoom) {
        factor = scaleFactor / maxOverZoom;
      }
    }
    const proposedStrand = transform(this.target, scaleAtOrigin(factor, origin ? origin.x : this.target[1] + (this.target[3] - this.target[1]) / 2, origin ? origin.y : this.target[2] + (this.target[4] - this.target[2]) / 2), this.zoomBuffer);
    this.constrainBounds(proposedStrand, { ref: true, panPadding: 100 });
    return proposedStrand;
  }
  clampRegion({
    x: x2,
    y: y2,
    width,
    height,
    padding = 0
  }) {
    const w2 = this.width;
    const h2 = this.height;
    const matchesHeight = width / w2 < height / h2;
    const rx = x2 - padding;
    const ry = y2 - padding;
    const rWidth = width + padding * 2;
    const rHeight = height + padding * 2;
    if (matchesHeight) {
      const actualWidth = rHeight / h2 * w2;
      return {
        x: rx - (actualWidth - rWidth) / 2,
        y: ry,
        width: actualWidth,
        height: rHeight
      };
    }
    const actualHeight = rWidth / w2 * h2;
    return {
      x: rx,
      y: ry - (actualHeight - rHeight) / 2,
      width: rWidth,
      height: actualHeight
    };
  }
  viewerToWorld(x2, y2) {
    const scaleFactor = this.getScaleFactor();
    this._viewerToWorld.x = this.target[1] + x2 / scaleFactor;
    this._viewerToWorld.y = this.target[2] + y2 / scaleFactor;
    return this._viewerToWorld;
  }
  worldToViewer(x2, y2, width, height) {
    const strand = DnaFactory.singleBox(width, height, x2, y2);
    mutate(strand, compose(scale(this.getScaleFactor()), translate(-this.target[1], -this.target[2])));
    return {
      x: strand[1],
      y: strand[2],
      width: strand[3] - strand[1],
      height: strand[4] - strand[2],
      strand
    };
  }
  setScale(scaleFactor, origin) {
    mutate(this.target, scaleAtOrigin(scaleFactor, origin ? origin.x : this.target[1] + (this.target[3] - this.target[1]) / 2, origin ? origin.y : this.target[2] + (this.target[4] - this.target[2]) / 2));
    this.pendingUpdate = true;
  }
  syncTo(runtime) {
    const oldTarget = this.target;
    this.target = runtime.target;
    this.pendingUpdate = true;
    return () => {
      this.target = oldTarget;
    };
  }
  stop() {
    if (typeof this.stopId !== "undefined") {
      window.cancelAnimationFrame(this.stopId);
      this.stopId = void 0;
    }
    return () => {
      this.render(performance.now());
    };
  }
  reset() {
    this.renderer.reset();
  }
  selectZone(zone) {
    this.world.selectZone(zone);
    this.pendingUpdate = true;
  }
  deselectZone() {
    this.world.deselectZone();
    this.pendingUpdate = true;
  }
  hook(name, arg) {
    const len = this.hooks[name].length;
    if (len !== 0) {
      for (let x2 = 0; x2 < len; x2++) {
        this.hooks[name][x2](arg);
      }
    }
  }
  registerHook(name, hook) {
    this.hooks[name].push(hook);
    return () => {
      this.hooks[name] = this.hooks[name].filter((e2) => e2 !== hook);
    };
  }
  updateNextFrame() {
    this.pendingUpdate = true;
  }
}
const perf = typeof performance === "object" && performance && typeof performance.now === "function" ? performance : Date;
const hasAbortController = typeof AbortController === "function";
const AC = hasAbortController ? AbortController : class AbortController2 {
  constructor() {
    this.signal = new AS();
  }
  abort(reason = new Error("This operation was aborted")) {
    this.signal.reason = reason;
    this.signal.dispatchEvent({
      type: "abort",
      target: this.signal
    });
  }
};
const hasAbortSignal = typeof AbortSignal === "function";
const hasACAbortSignal = typeof AC.AbortSignal === "function";
const AS = hasAbortSignal ? AbortSignal : hasACAbortSignal ? AC.AbortController : class AbortSignal2 {
  constructor() {
    this.reason = void 0;
    this.aborted = false;
    this._listeners = [];
  }
  dispatchEvent(e2) {
    if (e2.type === "abort") {
      this.aborted = true;
      this.onabort(e2);
      this._listeners.forEach((f2) => f2(e2), this);
    }
  }
  onabort() {
  }
  addEventListener(ev, fn) {
    if (ev === "abort") {
      this._listeners.push(fn);
    }
  }
  removeEventListener(ev, fn) {
    if (ev === "abort") {
      this._listeners = this._listeners.filter((f2) => f2 !== fn);
    }
  }
};
const warned = /* @__PURE__ */ new Set();
const deprecatedOption = (opt, instead) => {
  const code = `LRU_CACHE_OPTION_${opt}`;
  if (shouldWarn(code)) {
    warn(code, `${opt} option`, `options.${instead}`, LRUCache);
  }
};
const deprecatedMethod = (method, instead) => {
  const code = `LRU_CACHE_METHOD_${method}`;
  if (shouldWarn(code)) {
    const { prototype } = LRUCache;
    const { get } = Object.getOwnPropertyDescriptor(prototype, method);
    warn(code, `${method} method`, `cache.${instead}()`, get);
  }
};
const deprecatedProperty = (field, instead) => {
  const code = `LRU_CACHE_PROPERTY_${field}`;
  if (shouldWarn(code)) {
    const { prototype } = LRUCache;
    const { get } = Object.getOwnPropertyDescriptor(prototype, field);
    warn(code, `${field} property`, `cache.${instead}`, get);
  }
};
const emitWarning = (...a2) => {
  typeof process === "object" && process && typeof process.emitWarning === "function" ? process.emitWarning(...a2) : console.error(...a2);
};
const shouldWarn = (code) => !warned.has(code);
const warn = (code, what, instead, fn) => {
  warned.add(code);
  const msg = `The ${what} is deprecated. Please use ${instead} instead.`;
  emitWarning(msg, "DeprecationWarning", code, fn);
};
const isPosInt = (n2) => n2 && n2 === Math.floor(n2) && n2 > 0 && isFinite(n2);
const getUintArray = (max) => !isPosInt(max) ? null : max <= Math.pow(2, 8) ? Uint8Array : max <= Math.pow(2, 16) ? Uint16Array : max <= Math.pow(2, 32) ? Uint32Array : max <= Number.MAX_SAFE_INTEGER ? ZeroArray : null;
class ZeroArray extends Array {
  constructor(size) {
    super(size);
    this.fill(0);
  }
}
class Stack {
  constructor(max) {
    if (max === 0) {
      return [];
    }
    const UintArray = getUintArray(max);
    this.heap = new UintArray(max);
    this.length = 0;
  }
  push(n2) {
    this.heap[this.length++] = n2;
  }
  pop() {
    return this.heap[--this.length];
  }
}
class LRUCache {
  constructor(options = {}) {
    const {
      max = 0,
      ttl,
      ttlResolution = 1,
      ttlAutopurge,
      updateAgeOnGet,
      updateAgeOnHas,
      allowStale,
      dispose,
      disposeAfter,
      noDisposeOnSet,
      noUpdateTTL,
      maxSize = 0,
      maxEntrySize = 0,
      sizeCalculation,
      fetchMethod,
      fetchContext,
      noDeleteOnFetchRejection,
      noDeleteOnStaleGet,
      allowStaleOnFetchRejection,
      allowStaleOnFetchAbort,
      ignoreFetchAbort
    } = options;
    const { length, maxAge, stale } = options instanceof LRUCache ? {} : options;
    if (max !== 0 && !isPosInt(max)) {
      throw new TypeError("max option must be a nonnegative integer");
    }
    const UintArray = max ? getUintArray(max) : Array;
    if (!UintArray) {
      throw new Error("invalid max value: " + max);
    }
    this.max = max;
    this.maxSize = maxSize;
    this.maxEntrySize = maxEntrySize || this.maxSize;
    this.sizeCalculation = sizeCalculation || length;
    if (this.sizeCalculation) {
      if (!this.maxSize && !this.maxEntrySize) {
        throw new TypeError("cannot set sizeCalculation without setting maxSize or maxEntrySize");
      }
      if (typeof this.sizeCalculation !== "function") {
        throw new TypeError("sizeCalculation set to non-function");
      }
    }
    this.fetchMethod = fetchMethod || null;
    if (this.fetchMethod && typeof this.fetchMethod !== "function") {
      throw new TypeError("fetchMethod must be a function if specified");
    }
    this.fetchContext = fetchContext;
    if (!this.fetchMethod && fetchContext !== void 0) {
      throw new TypeError("cannot set fetchContext without fetchMethod");
    }
    this.keyMap = /* @__PURE__ */ new Map();
    this.keyList = new Array(max).fill(null);
    this.valList = new Array(max).fill(null);
    this.next = new UintArray(max);
    this.prev = new UintArray(max);
    this.head = 0;
    this.tail = 0;
    this.free = new Stack(max);
    this.initialFill = 1;
    this.size = 0;
    if (typeof dispose === "function") {
      this.dispose = dispose;
    }
    if (typeof disposeAfter === "function") {
      this.disposeAfter = disposeAfter;
      this.disposed = [];
    } else {
      this.disposeAfter = null;
      this.disposed = null;
    }
    this.noDisposeOnSet = !!noDisposeOnSet;
    this.noUpdateTTL = !!noUpdateTTL;
    this.noDeleteOnFetchRejection = !!noDeleteOnFetchRejection;
    this.allowStaleOnFetchRejection = !!allowStaleOnFetchRejection;
    this.allowStaleOnFetchAbort = !!allowStaleOnFetchAbort;
    this.ignoreFetchAbort = !!ignoreFetchAbort;
    if (this.maxEntrySize !== 0) {
      if (this.maxSize !== 0) {
        if (!isPosInt(this.maxSize)) {
          throw new TypeError("maxSize must be a positive integer if specified");
        }
      }
      if (!isPosInt(this.maxEntrySize)) {
        throw new TypeError("maxEntrySize must be a positive integer if specified");
      }
      this.initializeSizeTracking();
    }
    this.allowStale = !!allowStale || !!stale;
    this.noDeleteOnStaleGet = !!noDeleteOnStaleGet;
    this.updateAgeOnGet = !!updateAgeOnGet;
    this.updateAgeOnHas = !!updateAgeOnHas;
    this.ttlResolution = isPosInt(ttlResolution) || ttlResolution === 0 ? ttlResolution : 1;
    this.ttlAutopurge = !!ttlAutopurge;
    this.ttl = ttl || maxAge || 0;
    if (this.ttl) {
      if (!isPosInt(this.ttl)) {
        throw new TypeError("ttl must be a positive integer if specified");
      }
      this.initializeTTLTracking();
    }
    if (this.max === 0 && this.ttl === 0 && this.maxSize === 0) {
      throw new TypeError("At least one of max, maxSize, or ttl is required");
    }
    if (!this.ttlAutopurge && !this.max && !this.maxSize) {
      const code = "LRU_CACHE_UNBOUNDED";
      if (shouldWarn(code)) {
        warned.add(code);
        const msg = "TTL caching without ttlAutopurge, max, or maxSize can result in unbounded memory consumption.";
        emitWarning(msg, "UnboundedCacheWarning", code, LRUCache);
      }
    }
    if (stale) {
      deprecatedOption("stale", "allowStale");
    }
    if (maxAge) {
      deprecatedOption("maxAge", "ttl");
    }
    if (length) {
      deprecatedOption("length", "sizeCalculation");
    }
  }
  getRemainingTTL(key) {
    return this.has(key, { updateAgeOnHas: false }) ? Infinity : 0;
  }
  initializeTTLTracking() {
    this.ttls = new ZeroArray(this.max);
    this.starts = new ZeroArray(this.max);
    this.setItemTTL = (index, ttl, start = perf.now()) => {
      this.starts[index] = ttl !== 0 ? start : 0;
      this.ttls[index] = ttl;
      if (ttl !== 0 && this.ttlAutopurge) {
        const t2 = setTimeout(() => {
          if (this.isStale(index)) {
            this.delete(this.keyList[index]);
          }
        }, ttl + 1);
        if (t2.unref) {
          t2.unref();
        }
      }
    };
    this.updateItemAge = (index) => {
      this.starts[index] = this.ttls[index] !== 0 ? perf.now() : 0;
    };
    let cachedNow = 0;
    const getNow = () => {
      const n2 = perf.now();
      if (this.ttlResolution > 0) {
        cachedNow = n2;
        const t2 = setTimeout(() => cachedNow = 0, this.ttlResolution);
        if (t2.unref) {
          t2.unref();
        }
      }
      return n2;
    };
    this.getRemainingTTL = (key) => {
      const index = this.keyMap.get(key);
      if (index === void 0) {
        return 0;
      }
      return this.ttls[index] === 0 || this.starts[index] === 0 ? Infinity : this.starts[index] + this.ttls[index] - (cachedNow || getNow());
    };
    this.isStale = (index) => {
      return this.ttls[index] !== 0 && this.starts[index] !== 0 && (cachedNow || getNow()) - this.starts[index] > this.ttls[index];
    };
  }
  updateItemAge(_index) {
  }
  setItemTTL(_index, _ttl, _start) {
  }
  isStale(_index) {
    return false;
  }
  initializeSizeTracking() {
    this.calculatedSize = 0;
    this.sizes = new ZeroArray(this.max);
    this.removeItemSize = (index) => {
      this.calculatedSize -= this.sizes[index];
      this.sizes[index] = 0;
    };
    this.requireSize = (k2, v2, size, sizeCalculation) => {
      if (this.isBackgroundFetch(v2)) {
        return 0;
      }
      if (!isPosInt(size)) {
        if (sizeCalculation) {
          if (typeof sizeCalculation !== "function") {
            throw new TypeError("sizeCalculation must be a function");
          }
          size = sizeCalculation(v2, k2);
          if (!isPosInt(size)) {
            throw new TypeError("sizeCalculation return invalid (expect positive integer)");
          }
        } else {
          throw new TypeError("invalid size value (must be positive integer). When maxSize or maxEntrySize is used, sizeCalculation or size must be set.");
        }
      }
      return size;
    };
    this.addItemSize = (index, size) => {
      this.sizes[index] = size;
      if (this.maxSize) {
        const maxSize = this.maxSize - this.sizes[index];
        while (this.calculatedSize > maxSize) {
          this.evict(true);
        }
      }
      this.calculatedSize += this.sizes[index];
    };
  }
  removeItemSize(_index) {
  }
  addItemSize(_index, _size) {
  }
  requireSize(_k, _v, size, sizeCalculation) {
    if (size || sizeCalculation) {
      throw new TypeError("cannot set size without setting maxSize or maxEntrySize on cache");
    }
  }
  *indexes({ allowStale = this.allowStale } = {}) {
    if (this.size) {
      for (let i2 = this.tail; true; ) {
        if (!this.isValidIndex(i2)) {
          break;
        }
        if (allowStale || !this.isStale(i2)) {
          yield i2;
        }
        if (i2 === this.head) {
          break;
        } else {
          i2 = this.prev[i2];
        }
      }
    }
  }
  *rindexes({ allowStale = this.allowStale } = {}) {
    if (this.size) {
      for (let i2 = this.head; true; ) {
        if (!this.isValidIndex(i2)) {
          break;
        }
        if (allowStale || !this.isStale(i2)) {
          yield i2;
        }
        if (i2 === this.tail) {
          break;
        } else {
          i2 = this.next[i2];
        }
      }
    }
  }
  isValidIndex(index) {
    return this.keyMap.get(this.keyList[index]) === index;
  }
  *entries() {
    for (const i2 of this.indexes()) {
      if (!this.isBackgroundFetch(this.valList[i2])) {
        yield [this.keyList[i2], this.valList[i2]];
      }
    }
  }
  *rentries() {
    for (const i2 of this.rindexes()) {
      if (!this.isBackgroundFetch(this.valList[i2])) {
        yield [this.keyList[i2], this.valList[i2]];
      }
    }
  }
  *keys() {
    for (const i2 of this.indexes()) {
      if (!this.isBackgroundFetch(this.valList[i2])) {
        yield this.keyList[i2];
      }
    }
  }
  *rkeys() {
    for (const i2 of this.rindexes()) {
      if (!this.isBackgroundFetch(this.valList[i2])) {
        yield this.keyList[i2];
      }
    }
  }
  *values() {
    for (const i2 of this.indexes()) {
      if (!this.isBackgroundFetch(this.valList[i2])) {
        yield this.valList[i2];
      }
    }
  }
  *rvalues() {
    for (const i2 of this.rindexes()) {
      if (!this.isBackgroundFetch(this.valList[i2])) {
        yield this.valList[i2];
      }
    }
  }
  [Symbol.iterator]() {
    return this.entries();
  }
  find(fn, getOptions = {}) {
    for (const i2 of this.indexes()) {
      if (fn(this.valList[i2], this.keyList[i2], this)) {
        return this.get(this.keyList[i2], getOptions);
      }
    }
  }
  forEach(fn, thisp = this) {
    for (const i2 of this.indexes()) {
      fn.call(thisp, this.valList[i2], this.keyList[i2], this);
    }
  }
  rforEach(fn, thisp = this) {
    for (const i2 of this.rindexes()) {
      fn.call(thisp, this.valList[i2], this.keyList[i2], this);
    }
  }
  get prune() {
    deprecatedMethod("prune", "purgeStale");
    return this.purgeStale;
  }
  purgeStale() {
    let deleted = false;
    for (const i2 of this.rindexes({ allowStale: true })) {
      if (this.isStale(i2)) {
        this.delete(this.keyList[i2]);
        deleted = true;
      }
    }
    return deleted;
  }
  dump() {
    const arr = [];
    for (const i2 of this.indexes({ allowStale: true })) {
      const key = this.keyList[i2];
      const v2 = this.valList[i2];
      const value = this.isBackgroundFetch(v2) ? v2.__staleWhileFetching : v2;
      if (value === void 0)
        continue;
      const entry = { value };
      if (this.ttls) {
        entry.ttl = this.ttls[i2];
        const age = perf.now() - this.starts[i2];
        entry.start = Math.floor(Date.now() - age);
      }
      if (this.sizes) {
        entry.size = this.sizes[i2];
      }
      arr.unshift([key, entry]);
    }
    return arr;
  }
  load(arr) {
    this.clear();
    for (const [key, entry] of arr) {
      if (entry.start) {
        const age = Date.now() - entry.start;
        entry.start = perf.now() - age;
      }
      this.set(key, entry.value, entry);
    }
  }
  dispose(_v, _k, _reason) {
  }
  set(k2, v2, {
    ttl = this.ttl,
    start,
    noDisposeOnSet = this.noDisposeOnSet,
    size = 0,
    sizeCalculation = this.sizeCalculation,
    noUpdateTTL = this.noUpdateTTL
  } = {}) {
    size = this.requireSize(k2, v2, size, sizeCalculation);
    if (this.maxEntrySize && size > this.maxEntrySize) {
      this.delete(k2);
      return this;
    }
    let index = this.size === 0 ? void 0 : this.keyMap.get(k2);
    if (index === void 0) {
      index = this.newIndex();
      this.keyList[index] = k2;
      this.valList[index] = v2;
      this.keyMap.set(k2, index);
      this.next[this.tail] = index;
      this.prev[index] = this.tail;
      this.tail = index;
      this.size++;
      this.addItemSize(index, size);
      noUpdateTTL = false;
    } else {
      this.moveToTail(index);
      const oldVal = this.valList[index];
      if (v2 !== oldVal) {
        if (this.isBackgroundFetch(oldVal)) {
          oldVal.__abortController.abort(new Error("replaced"));
        } else {
          if (!noDisposeOnSet) {
            this.dispose(oldVal, k2, "set");
            if (this.disposeAfter) {
              this.disposed.push([oldVal, k2, "set"]);
            }
          }
        }
        this.removeItemSize(index);
        this.valList[index] = v2;
        this.addItemSize(index, size);
      }
    }
    if (ttl !== 0 && this.ttl === 0 && !this.ttls) {
      this.initializeTTLTracking();
    }
    if (!noUpdateTTL) {
      this.setItemTTL(index, ttl, start);
    }
    if (this.disposeAfter) {
      while (this.disposed.length) {
        this.disposeAfter(...this.disposed.shift());
      }
    }
    return this;
  }
  newIndex() {
    if (this.size === 0) {
      return this.tail;
    }
    if (this.size === this.max && this.max !== 0) {
      return this.evict(false);
    }
    if (this.free.length !== 0) {
      return this.free.pop();
    }
    return this.initialFill++;
  }
  pop() {
    if (this.size) {
      const val = this.valList[this.head];
      this.evict(true);
      return val;
    }
  }
  evict(free) {
    const head = this.head;
    const k2 = this.keyList[head];
    const v2 = this.valList[head];
    if (this.isBackgroundFetch(v2)) {
      v2.__abortController.abort(new Error("evicted"));
    } else {
      this.dispose(v2, k2, "evict");
      if (this.disposeAfter) {
        this.disposed.push([v2, k2, "evict"]);
      }
    }
    this.removeItemSize(head);
    if (free) {
      this.keyList[head] = null;
      this.valList[head] = null;
      this.free.push(head);
    }
    this.head = this.next[head];
    this.keyMap.delete(k2);
    this.size--;
    return head;
  }
  has(k2, { updateAgeOnHas = this.updateAgeOnHas } = {}) {
    const index = this.keyMap.get(k2);
    if (index !== void 0) {
      if (!this.isStale(index)) {
        if (updateAgeOnHas) {
          this.updateItemAge(index);
        }
        return true;
      }
    }
    return false;
  }
  peek(k2, { allowStale = this.allowStale } = {}) {
    const index = this.keyMap.get(k2);
    if (index !== void 0 && (allowStale || !this.isStale(index))) {
      const v2 = this.valList[index];
      return this.isBackgroundFetch(v2) ? v2.__staleWhileFetching : v2;
    }
  }
  backgroundFetch(k2, index, options, context) {
    const v2 = index === void 0 ? void 0 : this.valList[index];
    if (this.isBackgroundFetch(v2)) {
      return v2;
    }
    const ac = new AC();
    if (options.signal) {
      options.signal.addEventListener("abort", () => ac.abort(options.signal.reason));
    }
    const fetchOpts = {
      signal: ac.signal,
      options,
      context
    };
    const cb = (v3, updateCache = false) => {
      const { aborted } = ac.signal;
      const ignoreAbort = options.ignoreFetchAbort && v3 !== void 0;
      if (aborted && !ignoreAbort && !updateCache) {
        return eb(ac.signal.reason);
      }
      if (this.valList[index] === p2) {
        if (v3 === void 0) {
          if (p2.__staleWhileFetching) {
            this.valList[index] = p2.__staleWhileFetching;
          } else {
            this.delete(k2);
          }
        } else {
          this.set(k2, v3, fetchOpts.options);
        }
      }
      return v3;
    };
    const eb = (er) => {
      const { aborted } = ac.signal;
      const allowStaleAborted = aborted && options.allowStaleOnFetchAbort;
      const allowStale = allowStaleAborted || options.allowStaleOnFetchRejection;
      const noDelete = allowStale || options.noDeleteOnFetchRejection;
      if (this.valList[index] === p2) {
        const del = !noDelete || p2.__staleWhileFetching === void 0;
        if (del) {
          this.delete(k2);
        } else if (!allowStaleAborted) {
          this.valList[index] = p2.__staleWhileFetching;
        }
      }
      if (allowStale) {
        return p2.__staleWhileFetching;
      } else if (p2.__returned === p2) {
        throw er;
      }
    };
    const pcall = (res, rej) => {
      this.fetchMethod(k2, v2, fetchOpts).then((v3) => res(v3), rej);
      ac.signal.addEventListener("abort", () => {
        if (!options.ignoreFetchAbort || options.allowStaleOnFetchAbort) {
          res();
          if (options.allowStaleOnFetchAbort) {
            res = (v3) => cb(v3, true);
          }
        }
      });
    };
    const p2 = new Promise(pcall).then(cb, eb);
    p2.__abortController = ac;
    p2.__staleWhileFetching = v2;
    p2.__returned = null;
    if (index === void 0) {
      this.set(k2, p2, fetchOpts.options);
      index = this.keyMap.get(k2);
    } else {
      this.valList[index] = p2;
    }
    return p2;
  }
  isBackgroundFetch(p2) {
    return p2 && typeof p2 === "object" && typeof p2.then === "function" && Object.prototype.hasOwnProperty.call(p2, "__staleWhileFetching") && Object.prototype.hasOwnProperty.call(p2, "__returned") && (p2.__returned === p2 || p2.__returned === null);
  }
  async fetch(k2, {
    allowStale = this.allowStale,
    updateAgeOnGet = this.updateAgeOnGet,
    noDeleteOnStaleGet = this.noDeleteOnStaleGet,
    ttl = this.ttl,
    noDisposeOnSet = this.noDisposeOnSet,
    size = 0,
    sizeCalculation = this.sizeCalculation,
    noUpdateTTL = this.noUpdateTTL,
    noDeleteOnFetchRejection = this.noDeleteOnFetchRejection,
    allowStaleOnFetchRejection = this.allowStaleOnFetchRejection,
    ignoreFetchAbort = this.ignoreFetchAbort,
    allowStaleOnFetchAbort = this.allowStaleOnFetchAbort,
    fetchContext = this.fetchContext,
    forceRefresh = false,
    signal
  } = {}) {
    if (!this.fetchMethod) {
      return this.get(k2, {
        allowStale,
        updateAgeOnGet,
        noDeleteOnStaleGet
      });
    }
    const options = {
      allowStale,
      updateAgeOnGet,
      noDeleteOnStaleGet,
      ttl,
      noDisposeOnSet,
      size,
      sizeCalculation,
      noUpdateTTL,
      noDeleteOnFetchRejection,
      allowStaleOnFetchRejection,
      allowStaleOnFetchAbort,
      ignoreFetchAbort,
      signal
    };
    let index = this.keyMap.get(k2);
    if (index === void 0) {
      const p2 = this.backgroundFetch(k2, index, options, fetchContext);
      return p2.__returned = p2;
    } else {
      const v2 = this.valList[index];
      if (this.isBackgroundFetch(v2)) {
        return allowStale && v2.__staleWhileFetching !== void 0 ? v2.__staleWhileFetching : v2.__returned = v2;
      }
      if (!forceRefresh && !this.isStale(index)) {
        this.moveToTail(index);
        if (updateAgeOnGet) {
          this.updateItemAge(index);
        }
        return v2;
      }
      const p2 = this.backgroundFetch(k2, index, options, fetchContext);
      return allowStale && p2.__staleWhileFetching !== void 0 ? p2.__staleWhileFetching : p2.__returned = p2;
    }
  }
  get(k2, {
    allowStale = this.allowStale,
    updateAgeOnGet = this.updateAgeOnGet,
    noDeleteOnStaleGet = this.noDeleteOnStaleGet
  } = {}) {
    const index = this.keyMap.get(k2);
    if (index !== void 0) {
      const value = this.valList[index];
      const fetching = this.isBackgroundFetch(value);
      if (this.isStale(index)) {
        if (!fetching) {
          if (!noDeleteOnStaleGet) {
            this.delete(k2);
          }
          return allowStale ? value : void 0;
        } else {
          return allowStale ? value.__staleWhileFetching : void 0;
        }
      } else {
        if (fetching) {
          return void 0;
        }
        this.moveToTail(index);
        if (updateAgeOnGet) {
          this.updateItemAge(index);
        }
        return value;
      }
    }
  }
  connect(p2, n2) {
    this.prev[n2] = p2;
    this.next[p2] = n2;
  }
  moveToTail(index) {
    if (index !== this.tail) {
      if (index === this.head) {
        this.head = this.next[index];
      } else {
        this.connect(this.prev[index], this.next[index]);
      }
      this.connect(this.tail, index);
      this.tail = index;
    }
  }
  get del() {
    deprecatedMethod("del", "delete");
    return this.delete;
  }
  delete(k2) {
    let deleted = false;
    if (this.size !== 0) {
      const index = this.keyMap.get(k2);
      if (index !== void 0) {
        deleted = true;
        if (this.size === 1) {
          this.clear();
        } else {
          this.removeItemSize(index);
          const v2 = this.valList[index];
          if (this.isBackgroundFetch(v2)) {
            v2.__abortController.abort(new Error("deleted"));
          } else {
            this.dispose(v2, k2, "delete");
            if (this.disposeAfter) {
              this.disposed.push([v2, k2, "delete"]);
            }
          }
          this.keyMap.delete(k2);
          this.keyList[index] = null;
          this.valList[index] = null;
          if (index === this.tail) {
            this.tail = this.prev[index];
          } else if (index === this.head) {
            this.head = this.next[index];
          } else {
            this.next[this.prev[index]] = this.next[index];
            this.prev[this.next[index]] = this.prev[index];
          }
          this.size--;
          this.free.push(index);
        }
      }
    }
    if (this.disposed) {
      while (this.disposed.length) {
        this.disposeAfter(...this.disposed.shift());
      }
    }
    return deleted;
  }
  clear() {
    for (const index of this.rindexes({ allowStale: true })) {
      const v2 = this.valList[index];
      if (this.isBackgroundFetch(v2)) {
        v2.__abortController.abort(new Error("deleted"));
      } else {
        const k2 = this.keyList[index];
        this.dispose(v2, k2, "delete");
        if (this.disposeAfter) {
          this.disposed.push([v2, k2, "delete"]);
        }
      }
    }
    this.keyMap.clear();
    this.valList.fill(null);
    this.keyList.fill(null);
    if (this.ttls) {
      this.ttls.fill(0);
      this.starts.fill(0);
    }
    if (this.sizes) {
      this.sizes.fill(0);
    }
    this.head = 0;
    this.tail = 0;
    this.initialFill = 1;
    this.free.length = 0;
    this.calculatedSize = 0;
    this.size = 0;
    if (this.disposed) {
      while (this.disposed.length) {
        this.disposeAfter(...this.disposed.shift());
      }
    }
  }
  get reset() {
    deprecatedMethod("reset", "clear");
    return this.clear;
  }
  get length() {
    deprecatedProperty("length", "size");
    return this.size;
  }
  static get AbortController() {
    return AC;
  }
  static get AbortSignal() {
    return AS;
  }
}
var LRUCache$1 = LRUCache;
const borderRegex = /([0-9]+(px|em)\s+)+(solid)\s+(.*)/g;
const borderRegexCache = {};
const styleProps = [
  "backgroundColor",
  "opacity",
  "boxShadow",
  "borderColor",
  "borderWidth",
  "borderStyle",
  "outlineColor",
  "outlineWidth",
  "outlineOffset",
  "outlineStyle"
];
class Geometry extends BaseObject {
  constructor() {
    super();
    this.type = "spacial-content";
    this.isShape = true;
    this.hoverEvents = false;
    this.activeEvents = false;
    this.display = {
      x: 0,
      y: 0,
      scale: 1,
      width: -1,
      height: -1,
      points: dna(5)
    };
    this.boundingBox = null;
    this._parsed = {
      border: { id: null, match: [] },
      outline: { id: null, match: [] }
    };
    this.props = {};
    this.shape = { type: "none" };
    this.addHover = () => {
      this.hovering = true;
      this.__revision++;
    };
    this.removeHover = () => {
      this.hovering = false;
      this.pressing = false;
      this.__revision++;
    };
    this.addPress = () => {
      this.pressing = true;
      this.__revision++;
    };
    this.removePress = () => {
      this.pressing = false;
      this.__revision++;
    };
    this.id = nanoid(12);
    this.points = dna(5);
    this.shape = { type: "none" };
  }
  updateBoundingBox() {
    if (this.shape.type === "none")
      return;
    const points = this.shape.points;
    if (this.shape.points.length > 2) {
      const x1 = Math.min(...points.map((p2) => p2[0]));
      const y1 = Math.min(...points.map((p2) => p2[1]));
      const x2 = Math.max(0, ...points.map((p2) => p2[0]));
      const y2 = Math.max(0, ...points.map((p2) => p2[1]));
      this.boundingBox = {
        x: x1,
        y: y1,
        width: x2 - x1,
        height: y2 - y1
      };
      return;
    }
    this.boundingBox = null;
  }
  intersects(pointer) {
    if (!pointer || this.shape.type === "none") {
      return false;
    }
    const [x2, y2] = pointer;
    const points = this.shape.points;
    let box = this.boundingBox;
    if (!box) {
      this.updateBoundingBox();
      box = this.boundingBox;
    }
    if (!box) {
      return false;
    }
    if (x2 < box.x || x2 > box.x + box.width || y2 < box.y || y2 > box.height + box.y) {
      return false;
    }
    let inside = false;
    for (let i2 = 0, j2 = points.length - 1; i2 < points.length; j2 = i2++) {
      if (points[i2][1] > y2 != points[j2][1] > y2 && x2 < (points[j2][0] - points[i2][0]) * (y2 - points[i2][1]) / (points[j2][1] - points[i2][1]) + points[i2][0]) {
        inside = !inside;
      }
    }
    return inside;
  }
  getAllPointsAt(target, aggregate) {
    if (target[3] - target[1] === 1 && target[4] - target[2] === 1) {
      if (this.intersects([target[1], target[2]])) {
        return [[this, this.points, aggregate]];
      }
      return [];
    }
    return [[this, this.points, aggregate]];
  }
  applyProps(props2 = {}) {
    let didUpdate = false;
    if (props2.points) {
      if (this.shape.type !== "polygon" || this.shape.points.length !== props2.points.length) {
        this.shape = {
          type: "polygon",
          points: props2.points,
          open: props2.open
        };
        this.updateBoundingBox();
      } else {
        let newPoints = false;
        const len = props2.points.length;
        for (let i2 = 0; i2 < len; i2++) {
          if (props2.points[i2][0] !== this.shape.points[i2][0] || props2.points[i2][1] !== this.shape.points[i2][1]) {
            newPoints = true;
            break;
          }
        }
        if (newPoints) {
          this.shape = {
            type: "polygon",
            points: props2.points,
            open: props2.open
          };
          this.updateBoundingBox();
        }
      }
    }
    if (props2.interactive !== this.props.interactive) {
      didUpdate = true;
      this.props.interactive = props2.interactive;
    }
    if (props2.open || props2.open === false) {
      didUpdate = true;
      this.shape.open = props2.open;
    }
    if (props2.style) {
      const borderStyle = props2.border || props2.style.border;
      if (borderStyle !== this._parsed.border.id) {
        if (!borderStyle) {
          this._parsed.border.id = null;
          this._parsed.border.match = [];
        } else {
          const match = borderRegexCache[borderStyle] || borderRegex.exec(borderStyle) || borderRegex.exec(borderStyle);
          if (match) {
            this._parsed.border.id = borderStyle;
            this._parsed.border.match = borderRegexCache[borderStyle] = match;
          }
        }
      }
      if (this._parsed.border.id) {
        props2.style.borderWidth = this._parsed.border.match[1];
        props2.style.borderStyle = "solid";
        props2.style.borderColor = this._parsed.border.match[4];
      }
      if (props2.style.outline !== this._parsed.outline.id) {
        if (!props2.style.outline) {
          this._parsed.outline.id = null;
          this._parsed.outline.match = [];
        } else {
          const match = borderRegexCache[props2.style.outline] || borderRegex.exec(props2.style.outline) || borderRegex.exec(props2.style.outline);
          if (match) {
            this._parsed.outline.id = props2.style.outline;
            this._parsed.outline.match = borderRegexCache[props2.style.outline] = match;
          }
        }
      }
      if (this._parsed.outline.id) {
        props2.style.outlineWidth = this._parsed.outline.match[1];
        props2.style.outlineStyle = "solid";
        props2.style.outlineColor = this._parsed.outline.match[4];
      }
      this.props.style = props2.style;
      if (props2.backgroundColor && !this.props.style.backgroundColor) {
        this.props.style.backgroundColor = props2.backgroundColor;
        didUpdate = true;
      }
      if (props2.style.background && !this.props.style.backgroundColor) {
        this.props.style.backgroundColor = props2.style.background;
        didUpdate = true;
      }
      for (const prop of styleProps) {
        if (this.props.style[prop] !== props2.style[prop]) {
          didUpdate = true;
          break;
        }
      }
      if (props2.style[":hover"] !== this.props.hoverStyles) {
        this.props.hoverStyles = props2.style[":hover"];
        if (!this.hoverEvents) {
          this.hoverEvents = true;
          this.addEventListener("pointerenter", this.addHover);
          this.addEventListener("pointerleave", this.removeHover);
        }
        didUpdate = true;
      }
      if (props2.style[":active"] !== this.props.pressStyles) {
        this.props.pressStyles = props2.style[":active"];
        if (!this.activeEvents) {
          this.activeEvents = true;
          this.addEventListener("mousedown", this.addPress);
          this.addEventListener("mouseup", this.removePress);
        }
        didUpdate = true;
      }
    }
    if (props2.href !== this.props.href) {
      this.props.href = props2.href;
      didUpdate = true;
    }
    if (props2.hrefTarget !== this.props.hrefTarget) {
      this.props.hrefTarget = props2.hrefTarget;
      didUpdate = true;
    }
    if (props2.title !== this.props.title) {
      this.props.title = props2.title;
      didUpdate = true;
    }
    if (props2.className !== this.props.className) {
      this.props.className = props2.className;
      if (props2.className && !this.hoverEvents) {
        this.hoverEvents = true;
        this.addEventListener("pointerenter", this.addHover);
        this.addEventListener("pointerleave", this.removeHover);
      }
      if (props2.className && !this.activeEvents) {
        this.activeEvents = true;
        this.addEventListener("mousedown", this.addPress);
        this.addEventListener("mouseup", this.removePress);
      }
      didUpdate = true;
    }
    if (props2.relativeSize !== this.props.relativeSize) {
      this.props.relativeSize = props2.relativeSize;
      didUpdate = true;
    }
    if (props2.relativeStyle !== this.props.relativeStyle) {
      this.props.relativeStyle = props2.relativeStyle;
      didUpdate = true;
    }
    if (props2.html !== this.props.html) {
      this.props.html = props2.html;
      didUpdate = true;
    }
    if (props2.target) {
      if (props2.target.width !== this.display.width || props2.target.height !== this.display.height || props2.target.x !== this.points[1] || props2.target.y !== this.points[2]) {
        didUpdate = true;
        this.points = DnaFactory.singleBox(props2.target.width, props2.target.height, props2.target.x, props2.target.y);
        this.display.points = DnaFactory.singleBox(props2.target.width, props2.target.height, props2.target.x, props2.target.y);
        this.display.width = props2.target.width;
        this.display.height = props2.target.height;
      }
    }
    if (didUpdate) {
      this.__revision++;
    }
  }
}
const shadowRegex = /(-?[0-9]+(px|em)\s+|0\s+)(-?[0-9]+(px|em)\s+|0\s+)(-?[0-9]+(px|em)\s+|0\s+)?(-?[0-9]+(px|em)\s+|0\s+)?(.*)/g;
const shadowRegexCache = {};
const imageCache = {};
class CanvasRenderer {
  constructor(canvas, options) {
    this.imagesPending = 0;
    this.imagesLoaded = 0;
    this.frameIsRendering = false;
    this.pendingDrawCall = false;
    this.firstMeaningfulPaint = false;
    this.parallelTasks = 8;
    this.frameTasks = 0;
    this.loadingQueueOrdered = true;
    this.loadingQueue = [];
    this.currentTask = Promise.resolve();
    this.tasksRunning = 0;
    this.averageJobTime = 1e3;
    this.lastKnownScale = 1;
    this.visible = [];
    this.previousVisible = [];
    this.drawCalls = [];
    this.invalidated = [];
    this._worker = () => {
      if (this.loadingQueue.length) {
        const next = this.loadingQueue.pop();
        if (next) {
          this.tasksRunning++;
          this.frameTasks++;
          this.currentTask = next.task().then(() => {
            this.tasksRunning--;
          }).catch(() => {
            this.tasksRunning--;
          });
        }
      }
    };
    this._scheduled = 0;
    this._doWork = () => {
      if (this.loadingQueue.length === 0 && this.tasksRunning === 0 && this._scheduled) {
        clearInterval(this._scheduled);
        this._scheduled = 0;
      }
      this._worker();
    };
    this.canvas = canvas;
    this.rendererPosition = canvas.getBoundingClientRect();
    this.ctx = canvas.getContext("2d", { alpha: true });
    this.ctx.imageSmoothingEnabled = true;
    this.options = options || {};
    this.canvas.style.opacity = "0";
    this.canvas.style.transition = "opacity .3s";
    this.dpi = (options == null ? void 0 : options.dpi) || 1;
    this.hostCache = (options == null ? void 0 : options.lruCache) ? new LRUCache$1({
      maxSize: 1024 * 512 * 512,
      dispose: (value, key, reason) => {
        this.invalidated.push(key);
        value.width = 0;
        value.height = 0;
      },
      sizeCalculation: (value, key) => {
        return value.width * value.height;
      }
    }) : {
      store: {},
      get(id) {
        return this.store[id];
      },
      set(id, value) {
        this.store[id] = value;
      }
    };
  }
  getCanvasDims() {
    return { width: this.canvas.width / this.dpi, height: this.canvas.height / this.dpi };
  }
  resize() {
    this.rendererPosition = this.canvas.getBoundingClientRect();
  }
  isReady() {
    return this.firstMeaningfulPaint;
  }
  afterFrame(world) {
    this.clearTransform();
    this.lastPaintedObject = void 0;
    this.frameIsRendering = false;
    this.imagesPending = this.imagesPending - this.imagesLoaded;
    this.imagesLoaded = 0;
    if (!this.loadingQueueOrdered) {
      this.loadingQueue = this.loadingQueue.sort((a2, b2) => {
        if (a2.network) {
          if (a2.scale === b2.scale) {
            return b2.distance - a2.distance;
          }
        }
        return a2.scale < b2.scale ? -1 : 1;
      });
      this.loadingQueueOrdered = true;
    }
    this.previousVisible = this.visible;
    this.pendingDrawCall = !!this.drawCalls.length;
    if (this.pendingDrawCall) {
      for (let i2 = 0; i2 < this.drawCalls.length; i2++) {
        const nextCall = this.drawCalls.shift();
        if (nextCall)
          nextCall();
      }
    }
    this.doOffscreenWork();
    if (this.options.debug && this.stats) {
      this.stats.end();
    }
  }
  doOffscreenWork() {
    this.frameTasks = 0;
    if (this.loadingQueue.length) {
      this._worker();
      if (this.loadingQueue.length) {
        if (!this._scheduled) {
          this._scheduled = setInterval(this._doWork, 0);
        }
      }
    }
  }
  getScale(width, height, dpi) {
    if (Number.isNaN(width) || Number.isNaN(height)) {
      return this.lastKnownScale;
    }
    const canvas = this.getCanvasDims();
    const w2 = canvas.width / width;
    const h2 = canvas.height / height;
    const scale2 = (w2 < h2 ? h2 : w2) * (dpi ? this.dpi || 1 : 1);
    if (!Number.isNaN(scale2)) {
      this.lastKnownScale = scale2;
    }
    return this.lastKnownScale;
  }
  beforeFrame(world, delta, target) {
    if (this.options.debug && this.stats) {
      this.stats.begin();
    }
    this.frameIsRendering = true;
    this.visible = [];
    if (this.options.beforeFrame) {
      this.options.beforeFrame(delta);
    }
    const canvas = this.getCanvasDims();
    this.ctx.globalAlpha = 1;
    this.ctx.fillStyle = this.options.background || "rgb(0, 0, 0)";
    this.ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  applyTransform(paint, x2, y2, width, height) {
    const owner = paint.__owner.value;
    if (owner && owner.rotation) {
      this.ctx.save();
      const moveX = x2 + width / 2;
      const moveY = y2 + height / 2;
      this.ctx.translate(moveX, moveY);
      this.ctx.rotate(owner.rotation * Math.PI / 180);
      this.ctx.translate(-moveX, -moveY);
      this.lastPaintedObject = owner;
    }
  }
  clearTransform() {
    if (this.lastPaintedObject) {
      if (this.lastPaintedObject.rotation) {
        this.ctx.restore();
      }
      this.lastPaintedObject = void 0;
    }
  }
  paint(paint, index, x2, y2, width, height) {
    const ga = this.ctx.globalAlpha;
    if (paint instanceof SingleImage || paint instanceof TiledImage) {
      if (paint.display.rotation) {
        this.ctx.save();
        let moveX = x2 + width / 2;
        let moveY = y2 + height / 2;
        if (paint.crop) {
          moveX -= paint.crop[index * 5 + 1];
          moveY -= paint.crop[index * 5 + 2];
        }
        this.ctx.translate(moveX, moveY);
        this.ctx.rotate(paint.display.rotation * Math.PI / 180);
        this.ctx.translate(-moveX, -moveY);
      }
      this.visible.push(paint);
      if (typeof paint.style && paint.style.opacity !== "undefined") {
        if (!paint.style.opacity) {
          return;
        }
        this.ctx.globalAlpha = paint.style.opacity;
      }
      try {
        const imageBuffer = paint.__host.canvas;
        const canvas = this.getCanvasDims();
        if (imageBuffer.indices.indexOf(index) === -1 || this.invalidated.indexOf(imageBuffer.canvases[index]) !== -1) {
          this.schedulePaintToCanvas(imageBuffer, paint, index, distance({ x: x2 + width / 2, y: y2 + width / 2 }, { x: canvas.width / 2, y: canvas.height / 2 }));
        }
        if (!this.firstMeaningfulPaint) {
          return;
        }
        const canvasToPaint = this.hostCache.get(imageBuffer.canvases[index]);
        if (canvasToPaint) {
          if (paint.crop && paint.cropData) {
            if (paint.crop[index * 5]) {
              const source = [
                paint.crop[index * 5 + 1] / paint.display.scale - paint.display.points[index * 5 + 1],
                paint.crop[index * 5 + 2] / paint.display.scale - paint.display.points[index * 5 + 2],
                1 + (paint.crop[index * 5 + 3] - paint.crop[index * 5 + 1]) / paint.display.scale,
                1 + (paint.crop[index * 5 + 4] - paint.crop[index * 5 + 2]) / paint.display.scale
              ];
              source[0] += paint.cropData.x / paint.display.scale;
              source[1] += paint.cropData.y / paint.display.scale;
              const translationDeltaX = paint.x * this.lastKnownScale;
              const translationDeltaY = paint.y * this.lastKnownScale;
              const target = [x2 + translationDeltaX, y2 + translationDeltaY, width, height];
              target[0] += translationDeltaX;
              target[1] += translationDeltaY;
              this.ctx.drawImage(canvasToPaint, source[0], source[1], source[2], source[3], target[0], target[1], target[2] + 1, target[3] + 1);
            }
          } else {
            this.ctx.drawImage(canvasToPaint, 0, 0, paint.display.points[index * 5 + 3] - paint.display.points[index * 5 + 1], paint.display.points[index * 5 + 4] - paint.display.points[index * 5 + 2], x2, y2, width + Number.MIN_VALUE, height + Number.MIN_VALUE);
          }
        }
      } catch (err) {
      }
      if (paint.display.rotation) {
        this.ctx.restore();
      }
    }
    const isBox = paint instanceof Box && this.options.box;
    const isGeometry = paint instanceof Geometry && this.options.polygon;
    if ((isBox || isGeometry) && !paint.props.className && !paint.props.html && !paint.props.href) {
      if (paint.props.style) {
        const style = Object.assign({}, paint.props.style || {}, paint.hovering ? paint.props.hoverStyles : {}, paint.pressing ? paint.props.pressStyles : {});
        const scale2 = paint.props.relativeStyle ? 1 : width / paint.width;
        if (typeof style.opacity !== "undefined") {
          this.ctx.globalAlpha = style.opacity;
        }
        let bw = 0;
        if (typeof style.borderWidth !== "undefined") {
          bw = parseInt(style.borderWidth, 10) * scale2;
        }
        let ow = 0;
        if (typeof style.outlineWidth !== "undefined") {
          ow = parseInt(style.outlineWidth, 10) * scale2;
        }
        let oo = 0;
        if (typeof style.outlineOffset !== "undefined") {
          oo = parseInt(style.outlineOffset, 10) * scale2;
        }
        if (style.borderColor) {
          this.ctx.strokeStyle = style.borderColor;
        }
        if (style.boxShadow) {
          const shadows = style.boxShadow.split(/,(?![^(]*\))/);
          for (const shadow of shadows) {
            const parsed = shadowRegexCache[shadow] || shadowRegex.exec(shadow) || shadowRegex.exec(shadow);
            shadowRegexCache[shadow] = parsed;
            if (parsed) {
              this.ctx.save();
              this.ctx.shadowOffsetX = parseInt(parsed[1]) * this.dpi * scale2;
              this.ctx.shadowOffsetY = parseInt(parsed[3]) * this.dpi * scale2;
              this.ctx.shadowBlur = parseInt(parsed[5]) * this.dpi * scale2;
              this.ctx.shadowColor = parsed[9];
              this.ctx.fillStyle = "rgba(0,0,0,1)";
              this.ctx.fillRect(x2 + bw, y2 + bw, width, height);
              this.ctx.restore();
            }
          }
        }
        this.ctx.fillStyle = style.backgroundColor || "transparent";
        this.ctx.lineWidth = bw;
        if (isGeometry) {
          const shape = paint.shape;
          const points = shape.points || [];
          const len = points.length;
          this.ctx.beginPath();
          for (let i2 = 0; i2 < len; i2++) {
            this.ctx.lineTo(x2 + points[i2][0] * this.lastKnownScale, y2 + points[i2][1] * this.lastKnownScale);
          }
          if (!shape.open) {
            this.ctx.closePath();
          }
          if (bw) {
            this.ctx.stroke();
          }
          if (!shape.open) {
            this.ctx.fill();
          }
        } else {
          if (bw) {
            this.ctx.strokeRect(x2 + bw / 2, y2 + bw / 2, width + bw, height + bw);
          }
          this.ctx.fillRect(x2 + bw, y2 + bw, width, height);
        }
        if (ow) {
          if (style.outlineColor) {
            this.ctx.strokeStyle = style.outlineColor;
          }
          this.ctx.lineWidth = ow;
          this.ctx.strokeRect(x2 - ow / 2 - oo, y2 - ow / 2 - oo, width + bw * 2 + ow + oo * 2, height + bw * 2 + ow + oo * 2);
        }
      }
      this.ctx.globalAlpha = ga;
    }
  }
  loadImage(url, callback, err, retry = false) {
    if (imageCache[url] && imageCache[url].naturalWidth > 0) {
      callback(imageCache[url]);
      return;
    }
    try {
      let loaded = false;
      if (!retry) {
        setTimeout(() => {
          if (!loaded) {
            this.loadImage(url, callback, err, true);
          }
        }, 3e3);
      }
      const image = document.createElement("img");
      image.decoding = "auto";
      image.onload = function() {
        loaded = true;
        callback(image);
        imageCache[url] = image;
        image.onload = null;
      };
      if (this.options.crossOrigin) {
        image.crossOrigin = "anonymous";
      }
      image.src = url;
      if (image.complete) {
        image.onload({});
      }
      if (image.width === 0) {
      }
    } catch (e2) {
      console.log("image error", e2);
      err(e2);
    }
  }
  schedulePaintToCanvas(imageBuffer, paint, index, priority) {
    this.imagesPending++;
    imageBuffer.indices.push(index);
    const id = `${paint.id}--${paint.display.scale}-${index}`;
    const idx = this.invalidated.indexOf(id);
    if (idx !== -1) {
      this.invalidated.splice(idx, 1);
    }
    imageBuffer.canvases[index] = id;
    paint.__host.canvas.loading = true;
    this.loadingQueueOrdered = false;
    this.loadingQueue.push({
      id,
      scale: paint.display.scale,
      network: true,
      distance: priority,
      task: () => new Promise((resolve) => {
        if (this.visible.indexOf(paint) === -1) {
          this.imagesPending--;
          imageBuffer.indices.splice(imageBuffer.indices.indexOf(index), 1);
          resolve();
          return;
        }
        const url = paint.getImageUrl(index);
        this.loadImage(url, (image) => {
          this.loadingQueue.push({
            id,
            scale: paint.display.scale,
            distance: priority,
            task: () => {
              return new Promise((innerResolve) => {
                this.imagesLoaded++;
                imageBuffer.loaded.push(index);
                if (imageBuffer.loaded.length === imageBuffer.indices.length) {
                  imageBuffer.loading = false;
                }
                const points = paint.display.points.slice(index * 5, index * 5 + 5);
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                canvas.width = points[3] - points[1];
                canvas.height = points[4] - points[2];
                this.hostCache.set(imageBuffer.canvases[index], canvas);
                this.drawCalls.push(() => {
                  ctx.drawImage(image, 0, 0, points[3] - points[1], points[4] - points[2]);
                  innerResolve();
                });
              });
            }
          });
          resolve();
        }, (err) => {
          this.imagesPending--;
          imageBuffer.indices.splice(imageBuffer.indices.indexOf(index), 1);
          resolve();
        });
      })
    });
  }
  afterPaintLayer(paint, transform2) {
  }
  prepareLayer(paint, points) {
    if (paint.__owner.value) {
      if (paint.cropData) {
        this.lastKnownScale * (1 / paint.display.scale);
        this.applyTransform(paint, points[1], points[2], points[3] - points[1], points[4] - points[2]);
      } else {
        this.applyTransform(paint, points[1], points[2], points[3] - points[1], points[4] - points[2]);
      }
    }
    if (!paint.__host || !paint.__host.canvas) {
      if (paint instanceof SingleImage || paint instanceof TiledImage) {
        this.createImageHost(paint);
      }
    }
  }
  finishLayer() {
    if (this.lastPaintedObject) {
      this.clearTransform();
    }
  }
  createImageHost(paint) {
    paint.__host = paint.__host ? paint.__host : {};
    paint.__host.canvas = { canvas: void 0, canvases: [], indices: [], loaded: [], loading: false };
  }
  getPointsAt(world, target, aggregate, scaleFactor) {
    return world.getPointsAt(target, aggregate, scaleFactor);
  }
  getViewportBounds(world, target, padding) {
    const zone = world.getActiveZone();
    if (zone) {
      const xCon = target[3] - target[1] < zone.points[3] - zone.points[1];
      const yCon = target[4] - target[2] < zone.points[4] - zone.points[2];
      return {
        x1: xCon ? zone.points[1] - padding : zone.points[1] + (zone.points[3] - zone.points[1]) / 2 - (target[3] - target[1]) / 2,
        y1: yCon ? zone.points[2] - padding : zone.points[2] + (zone.points[4] - zone.points[2]) / 2 - (target[4] - target[2]) / 2,
        x2: xCon ? zone.points[3] + padding : zone.points[1] + (zone.points[3] - zone.points[1]) / 2 - (target[3] - target[1]) / 2,
        y2: yCon ? zone.points[4] + padding : zone.points[2] + (zone.points[4] - zone.points[2]) / 2 - (target[4] - target[2]) / 2
      };
    }
    return null;
  }
  pendingUpdate() {
    const ready = !this.pendingDrawCall && this.drawCalls.length === 0 && this.imagesPending === 0 && this.loadingQueue.length === 0 && this.tasksRunning === 0;
    if (!this.firstMeaningfulPaint && ready) {
      this.canvas.style.opacity = "1";
      this.firstMeaningfulPaint = ready;
      return true;
    }
    if (this.options.debug) {
      return true;
    }
    return !ready;
  }
  getRendererScreenPosition() {
    return this.rendererPosition;
  }
  reset() {
    this.loadingQueue = [];
    this.drawCalls = [];
  }
}
class CompositeRenderer {
  constructor(renderers) {
    this.renderers = [];
    for (const renderer of renderers) {
      if (renderer) {
        this.renderers.push(renderer);
      }
    }
    this.length = this.renderers.length;
  }
  afterFrame(world, delta, target) {
    for (let i2 = 0; i2 < this.length; i2++) {
      this.renderers[i2].afterFrame(world, delta, target);
    }
  }
  afterPaintLayer(paint, transform2) {
    for (let i2 = 0; i2 < this.length; i2++) {
      this.renderers[i2].afterPaintLayer(paint, transform2);
    }
  }
  beforeFrame(world, delta, target) {
    for (let i2 = 0; i2 < this.length; i2++) {
      this.renderers[i2].beforeFrame(world, delta, target);
    }
  }
  triggerResize() {
    for (let i2 = 0; i2 < this.length; i2++) {
      const renderer = this.renderers[i2];
      if (renderer.triggerResize) {
        renderer.triggerResize();
      }
    }
  }
  getPointsAt(world, target, aggregate, scaleFactor) {
    return this.renderers[0].getPointsAt(world, target, aggregate, scaleFactor);
  }
  getScale(width, height) {
    return this.renderers[0].getScale(width, height);
  }
  getViewportBounds(world, target, padding) {
    return this.renderers[0].getViewportBounds(world, target, padding);
  }
  getRendererScreenPosition() {
    return this.renderers[0].getRendererScreenPosition();
  }
  isReady() {
    for (let i2 = 0; i2 < this.length; i2++) {
      if (!this.renderers[i2].isReady()) {
        return false;
      }
    }
    return true;
  }
  paint(paint, index, x2, y2, width, height) {
    for (let i2 = 0; i2 < this.length; i2++) {
      this.renderers[i2].paint(paint, index, x2, y2, width, height);
    }
  }
  pendingUpdate() {
    for (let i2 = 0; i2 < this.length; i2++) {
      if (this.renderers[i2].pendingUpdate()) {
        return true;
      }
    }
    return false;
  }
  prepareLayer(paint, point) {
    for (let i2 = 0; i2 < this.length; i2++) {
      this.renderers[i2].prepareLayer(paint, point);
    }
  }
  finishLayer(paint, point) {
    for (let i2 = 0; i2 < this.length; i2++) {
      this.renderers[i2].finishLayer(paint, point);
    }
  }
  resize(width, height) {
    for (let i2 = 0; i2 < this.length; i2++) {
      this.renderers[i2].resize(width, height);
    }
  }
  reset() {
    for (let i2 = 0; i2 < this.length; i2++) {
      this.renderers[i2].reset();
    }
  }
}
class DebugRenderer {
  constructor(canvas) {
    this.heightRatio = 1;
    this.widthRatio = 1;
    this.target = new Float32Array(5);
    this.delta = 0;
    this.renderNextFrame = true;
    this.canvas = canvas;
    this.initialWidth = canvas.width;
    this.initialHeight = canvas.height;
    this.context = canvas.getContext("2d");
    this.context.globalAlpha = 0.5;
    this.aggregate = scale(1);
  }
  isReady() {
    return true;
  }
  resize() {
    this.initialWidth = this.canvas.width;
    this.initialHeight = this.canvas.height;
    this.renderNextFrame = true;
  }
  afterFrame(world, delta, target) {
    if (this.renderNextFrame) {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      const widthRatio = this.initialWidth / world.width;
      const heightRatio = this.initialHeight / world.height;
      const ratio = this.widthRatio > this.heightRatio ? widthRatio : heightRatio;
      this.target = target;
      if (this.bounds) {
        const points = world.getPointsAt(this.bounds, this.aggregate, ratio);
        for (const [paint, point] of points) {
          if (paint instanceof SingleImage) {
            if (paint.__host.canvas) {
              const total = point.length / 5;
              for (let i2 = 0; i2 < total; i2++) {
                const toPaint = paint.__host.canvas.canvases[i2];
                if (toPaint) {
                  ({
                    x1: point[i2 + 1] * ratio,
                    y1: point[i2 + 2] * ratio,
                    width: (point[i2 + 3] - point[i2 + 1]) * ratio,
                    height: (point[i2 + 4] - point[i2 + 2]) * ratio
                  });
                }
              }
            }
          }
        }
      }
      world.getPoints().forEach((v2, k2, arr) => {
        if (k2 % 5 === 0 && v2) {
          const toDraw = {
            x1: arr[k2 + 1] * ratio,
            y1: arr[k2 + 2] * ratio,
            x2: arr[k2 + 3] * ratio,
            y2: arr[k2 + 4] * ratio,
            width: (arr[k2 + 3] - arr[k2 + 1]) * ratio,
            height: (arr[k2 + 4] - arr[k2 + 2]) * ratio
          };
          this.context.strokeStyle = "red";
          this.context.strokeRect(toDraw.x1, toDraw.y1, toDraw.width, toDraw.height);
        }
      });
      if (target) {
        this.context.strokeStyle = "red";
        this.context.lineWidth = window.devicePixelRatio || 1;
        this.context.strokeRect(target[1] * ratio, target[2] * ratio, (target[3] - target[1]) * ratio, (target[4] - target[2]) * ratio);
      }
    }
  }
  getActiveZone(world) {
    return null;
  }
  getPointsAt(world, target, aggregate, scaleFactor) {
    return world.getPointsAt(target, aggregate, scaleFactor);
  }
  getScale(width, height) {
    return 1;
  }
  beforeFrame(world, delta) {
    this.bounds = DnaFactory.singleBox(world.width, world.height);
  }
  drawImage() {
  }
  afterPaintLayer(paint, transform2) {
  }
  paint(paint, index, x2, y2, width, height) {
    this.renderNextFrame = true;
  }
  prepareLayer(paint) {
  }
  pendingUpdate() {
    return false;
  }
  hasActiveZone() {
    return false;
  }
  getViewportBounds(world, target, padding) {
    return null;
  }
  getRendererScreenPosition() {
    return this.canvas.getBoundingClientRect();
  }
  finishLayer() {
  }
  reset() {
  }
}
class GridBuilder {
  constructor() {
    this.autoWidth = false;
    this.autoHeight = true;
    this.content = [];
    this.viewingDirection = "left-to-right";
    this.columns = 4;
    this.spacing = 20;
    this.reversed = false;
    this.padding = 20;
    this.world = World.withProps({ width: 0, height: 0, viewingDirection: "left-to-right" });
    this.width = 0;
    this.height = 0;
  }
  setViewingDirection(viewingDirection) {
    this.viewingDirection = viewingDirection;
  }
  addContent(content) {
    this.content.push(...content.map((item) => this.world.addObjectAt(item, {
      width: 0,
      height: 0,
      x: 0,
      y: 0
    })));
  }
  setWidth(width) {
    this.width = width;
  }
  setHeight(height) {
    this.height = height;
  }
  setSpacing(spacing) {
    this.spacing = spacing;
  }
  setPadding(padding) {
    this.padding = padding;
  }
  setRows(rows) {
    this.autoWidth = true;
    this.rows = rows;
  }
  setColumns(columns) {
    this.autoHeight = true;
    this.columns = columns;
  }
  recalculate() {
    if (this.height === 0 && this.width === 0) {
      return;
    }
    if (this.rows === 0 || this.columns === 0) {
      return;
    }
    if (this.autoHeight && !this.width) {
      throw new Error("Cannot set auto height without setting a width");
    }
    if (this.autoWidth && !this.height) {
      throw new Error("Cannot set auto width without setting a height");
    }
    if ((this.viewingDirection === "left-to-right" || this.viewingDirection === "top-to-bottom") && this.reversed) {
      this.reversed = false;
      this.content.reverse();
    }
    if ((this.viewingDirection === "right-to-left" || this.viewingDirection === "bottom-to-top") && !this.reversed) {
      this.reversed = true;
      this.content.reverse();
    }
    const len = this.content.length;
    const getColumns = () => {
      if (this.autoWidth && this.rows) {
        const rowsValue = len > this.rows ? this.rows : len;
        return {
          columns: Math.ceil(len / rowsValue),
          rows: rowsValue
        };
      }
      if (this.autoHeight && this.columns) {
        const columnsValue = len > this.columns ? this.columns : len;
        return {
          columns: columnsValue,
          rows: Math.ceil(len / columnsValue)
        };
      }
      throw new Error("Something went wrong.");
    };
    const { columns, rows } = getColumns();
    const contentWidth = this.autoWidth ? -1 : this.width - this.padding * 2;
    const itemWidth = this.autoWidth ? -1 : (contentWidth - this.spacing * (columns - 1)) / columns;
    if (this.autoHeight && !this.autoWidth) {
      let index = 0;
      let rowHeights = this.padding;
      for (let r2 = 0; r2 < rows; r2++) {
        if (index === len) {
          break;
        }
        let rowHeight = 0;
        const row = [];
        for (let c6 = 0; c6 < columns; c6++) {
          const realIndex = this.reversed ? len - index : index;
          if (index === len) {
            break;
          }
          const item = this.content[realIndex];
          const width = item.width;
          const aspectRatio = item.width / item.height;
          const currentRowHeight = itemWidth / aspectRatio;
          row.push([
            index,
            itemWidth,
            currentRowHeight,
            itemWidth / width
          ]);
          if (currentRowHeight > rowHeight) {
            rowHeight = currentRowHeight;
          }
          index++;
        }
        for (let c6 = 0; c6 < columns; c6++) {
          if (!row[c6]) {
            break;
          }
          const worldPoints = this.world.getPoints();
          const currentIndex = row[c6][0];
          const width = row[c6][1];
          const height = row[c6][2];
          const scale2 = row[c6][3];
          const x2 = this.padding + c6 * (this.spacing + width);
          const y2 = rowHeights + (rowHeight - height) / 2;
          const realIndex = this.reversed ? len - currentIndex : currentIndex;
          const prevX = worldPoints[realIndex * 5 + 1];
          const prevY = worldPoints[realIndex * 5 + 2];
          this.world.scaleWorldObject(currentIndex, scale2);
          if (prevX !== x2 || prevY !== y2) {
            this.world.translateWorldObject(realIndex, x2 - prevX, y2 - prevY);
          }
        }
        rowHeights += rowHeight + this.spacing;
      }
      this.height = rowHeights + this.padding;
      this.world.resize(this.width, this.height);
      return;
    }
    if (this.autoWidth && !this.autoHeight)
      ;
    if (!this.autoWidth && !this.autoHeight)
      ;
  }
  getWorld() {
    return this.world;
  }
}
var _populated = false;
var _ie, _firefox, _opera, _webkit, _chrome;
var _ie_real_version;
var _osx, _windows, _linux, _android;
var _win64;
var _iphone, _ipad, _native;
var _mobile;
function _populate() {
  if (_populated) {
    return;
  }
  _populated = true;
  var uas = navigator.userAgent;
  var agent = /(?:MSIE.(\d+\.\d+))|(?:(?:Firefox|GranParadiso|Iceweasel).(\d+\.\d+))|(?:Opera(?:.+Version.|.)(\d+\.\d+))|(?:AppleWebKit.(\d+(?:\.\d+)?))|(?:Trident\/\d+\.\d+.*rv:(\d+\.\d+))/.exec(uas);
  var os = /(Mac OS X)|(Windows)|(Linux)/.exec(uas);
  _iphone = /\b(iPhone|iP[ao]d)/.exec(uas);
  _ipad = /\b(iP[ao]d)/.exec(uas);
  _android = /Android/i.exec(uas);
  _native = /FBAN\/\w+;/i.exec(uas);
  _mobile = /Mobile/i.exec(uas);
  _win64 = !!/Win64/.exec(uas);
  if (agent) {
    _ie = agent[1] ? parseFloat(agent[1]) : agent[5] ? parseFloat(agent[5]) : NaN;
    if (_ie && document && document.documentMode) {
      _ie = document.documentMode;
    }
    var trident = /(?:Trident\/(\d+.\d+))/.exec(uas);
    _ie_real_version = trident ? parseFloat(trident[1]) + 4 : _ie;
    _firefox = agent[2] ? parseFloat(agent[2]) : NaN;
    _opera = agent[3] ? parseFloat(agent[3]) : NaN;
    _webkit = agent[4] ? parseFloat(agent[4]) : NaN;
    if (_webkit) {
      agent = /(?:Chrome\/(\d+\.\d+))/.exec(uas);
      _chrome = agent && agent[1] ? parseFloat(agent[1]) : NaN;
    } else {
      _chrome = NaN;
    }
  } else {
    _ie = _firefox = _opera = _chrome = _webkit = NaN;
  }
  if (os) {
    if (os[1]) {
      var ver = /(?:Mac OS X (\d+(?:[._]\d+)?))/.exec(uas);
      _osx = ver ? parseFloat(ver[1].replace("_", ".")) : true;
    } else {
      _osx = false;
    }
    _windows = !!os[2];
    _linux = !!os[3];
  } else {
    _osx = _windows = _linux = false;
  }
}
var UserAgent_DEPRECATED$1 = {
  ie: function() {
    return _populate() || _ie;
  },
  ieCompatibilityMode: function() {
    return _populate() || _ie_real_version > _ie;
  },
  ie64: function() {
    return UserAgent_DEPRECATED$1.ie() && _win64;
  },
  firefox: function() {
    return _populate() || _firefox;
  },
  opera: function() {
    return _populate() || _opera;
  },
  webkit: function() {
    return _populate() || _webkit;
  },
  safari: function() {
    return UserAgent_DEPRECATED$1.webkit();
  },
  chrome: function() {
    return _populate() || _chrome;
  },
  windows: function() {
    return _populate() || _windows;
  },
  osx: function() {
    return _populate() || _osx;
  },
  linux: function() {
    return _populate() || _linux;
  },
  iphone: function() {
    return _populate() || _iphone;
  },
  mobile: function() {
    return _populate() || (_iphone || _ipad || _android || _mobile);
  },
  nativeApp: function() {
    return _populate() || _native;
  },
  android: function() {
    return _populate() || _android;
  },
  ipad: function() {
    return _populate() || _ipad;
  }
};
var UserAgent_DEPRECATED_1 = UserAgent_DEPRECATED$1;
var canUseDOM = !!(typeof window !== "undefined" && window.document && window.document.createElement);
var ExecutionEnvironment$1 = {
  canUseDOM,
  canUseWorkers: typeof Worker !== "undefined",
  canUseEventListeners: canUseDOM && !!(window.addEventListener || window.attachEvent),
  canUseViewport: canUseDOM && !!window.screen,
  isInWorker: !canUseDOM
};
var ExecutionEnvironment_1 = ExecutionEnvironment$1;
var ExecutionEnvironment = ExecutionEnvironment_1;
var useHasFeature;
if (ExecutionEnvironment.canUseDOM) {
  useHasFeature = document.implementation && document.implementation.hasFeature && document.implementation.hasFeature("", "") !== true;
}
/**
 * Checks if an event is supported in the current execution environment.
 *
 * NOTE: This will not work correctly for non-generic events such as `change`,
 * `reset`, `load`, `error`, and `select`.
 *
 * Borrows from Modernizr.
 *
 * @param {string} eventNameSuffix Event name, e.g. "click".
 * @param {?boolean} capture Check if the capture phase is supported.
 * @return {boolean} True if the event is supported.
 * @internal
 * @license Modernizr 3.0.0pre (Custom Build) | MIT
 */
function isEventSupported$1(eventNameSuffix, capture) {
  if (!ExecutionEnvironment.canUseDOM || capture && !("addEventListener" in document)) {
    return false;
  }
  var eventName = "on" + eventNameSuffix;
  var isSupported = eventName in document;
  if (!isSupported) {
    var element = document.createElement("div");
    element.setAttribute(eventName, "return;");
    isSupported = typeof element[eventName] === "function";
  }
  if (!isSupported && useHasFeature && eventNameSuffix === "wheel") {
    isSupported = document.implementation.hasFeature("Events.wheel", "3.0");
  }
  return isSupported;
}
var isEventSupported_1 = isEventSupported$1;
var UserAgent_DEPRECATED = UserAgent_DEPRECATED_1;
var isEventSupported = isEventSupported_1;
var PIXEL_STEP = 10;
var LINE_HEIGHT = 40;
var PAGE_HEIGHT = 800;
function normalizeWheel$1(event) {
  var sX = 0, sY = 0, pX = 0, pY = 0;
  if ("detail" in event) {
    sY = event.detail;
  }
  if ("wheelDelta" in event) {
    sY = -event.wheelDelta / 120;
  }
  if ("wheelDeltaY" in event) {
    sY = -event.wheelDeltaY / 120;
  }
  if ("wheelDeltaX" in event) {
    sX = -event.wheelDeltaX / 120;
  }
  if ("axis" in event && event.axis === event.HORIZONTAL_AXIS) {
    sX = sY;
    sY = 0;
  }
  pX = sX * PIXEL_STEP;
  pY = sY * PIXEL_STEP;
  if ("deltaY" in event) {
    pY = event.deltaY;
  }
  if ("deltaX" in event) {
    pX = event.deltaX;
  }
  if ((pX || pY) && event.deltaMode) {
    if (event.deltaMode == 1) {
      pX *= LINE_HEIGHT;
      pY *= LINE_HEIGHT;
    } else {
      pX *= PAGE_HEIGHT;
      pY *= PAGE_HEIGHT;
    }
  }
  if (pX && !sX) {
    sX = pX < 1 ? -1 : 1;
  }
  if (pY && !sY) {
    sY = pY < 1 ? -1 : 1;
  }
  return {
    spinX: sX,
    spinY: sY,
    pixelX: pX,
    pixelY: pY
  };
}
normalizeWheel$1.getEventType = function() {
  return UserAgent_DEPRECATED.firefox() ? "DOMMouseScroll" : isEventSupported("wheel") ? "wheel" : "mousewheel";
};
var normalizeWheel_1 = normalizeWheel$1;
var normalizeWheel = normalizeWheel_1;
function toBox(strand) {
  return {
    x: strand[1],
    y: strand[2],
    width: strand[3] - strand[1],
    height: strand[4] - strand[2]
  };
}
const defaultConfig = {
  zoomOutFactor: 0.8,
  zoomInFactor: 1.25,
  maxZoomFactor: 1,
  minZoomFactor: 0.05,
  zoomDuration: 300,
  zoomWheelConstant: 20,
  zoomClamp: 0.6,
  panBounceStiffness: 120,
  panBounceDamping: 15,
  panTimeConstant: 240,
  panPower: 0.1,
  nudgeDistance: 100,
  panPadding: 0,
  devicePixelRatio: 1,
  enableWheel: true,
  enableClickToZoom: false,
  onPanInSketchMode: () => {
  }
};
const popmotionController = (config = {}) => {
  return {
    start: function(runtime) {
      const { zoomWheelConstant, enableWheel, enableClickToZoom } = {
        ...defaultConfig,
        ...config
      };
      const state = {
        pointerStart: { x: 0, y: 0 },
        isPressing: false,
        mousemoveBuffer: dna(5),
        multiTouch: {
          distance: 0
        }
      };
      runtime.world.activatedEvents.push("onMouseUp", "onMouseDown", "onMouseMove", "onTouchStart", "onTouchEnd", "onTouchMove", "onPointerUp", "onPointerDown", "onPointerMove");
      function onMouseUp() {
        runtime.world.constraintBounds();
        currentDistance = 0;
      }
      function onMouseDown(e2) {
        if (e2.which > 1) {
          state.isPressing = false;
          return;
        }
        if (runtime.mode === "explore") {
          e2.preventDefault();
          state.pointerStart.x = e2.atlas.x;
          state.pointerStart.y = e2.atlas.y;
          runtime.transitionManager.stopTransition();
          state.isPressing = true;
        }
      }
      function onWindowMouseUp() {
        if (state.isPressing) {
          if (runtime.mode === "explore") {
            runtime.world.constraintBounds();
          }
          state.isPressing = false;
        }
      }
      let currentDistance = 0;
      function onTouchStart(e2) {
        if (runtime.mode === "explore") {
          if (e2.atlasTouches.length === 1) {
            e2.preventDefault();
            state.pointerStart.x = e2.atlasTouches[0].x;
            state.pointerStart.y = e2.atlasTouches[0].y;
          }
          if (e2.atlasTouches.length === 2) {
            e2.preventDefault();
            const x1 = e2.atlasTouches[0].x;
            const x2 = e2.atlasTouches[1].x;
            state.pointerStart.x = (x1 + x2) / 2;
            const y1 = e2.atlasTouches[0].y;
            const y2 = e2.atlasTouches[1].y;
            state.pointerStart.y = (y1 + y2) / 2;
            currentDistance = distance({ x: e2.touches[0].clientX, y: e2.touches[0].clientY }, { x: e2.touches[1].clientX, y: e2.touches[1].clientY });
          }
          runtime.transitionManager.stopTransition();
          state.isPressing = true;
        }
      }
      function onTouchMove(e2) {
        let clientX = null;
        let clientY = null;
        let newDistance = 0;
        if (state.isPressing && e2.touches.length === 2) {
          const x1 = e2.touches[0].clientX;
          const x2 = e2.touches[1].clientX;
          clientX = (x1 + x2) / 2;
          const y1 = e2.touches[0].clientY;
          const y2 = e2.touches[1].clientY;
          clientY = (y1 + y2) / 2;
          newDistance = distance({ x: e2.touches[0].clientX, y: e2.touches[0].clientY }, { x: e2.touches[1].clientX, y: e2.touches[1].clientY });
        }
        if (state.isPressing && e2.touches.length === 1) {
          const touch = e2.touches[0];
          clientX = touch.clientX;
          clientY = touch.clientY;
        }
        if (clientX !== null && clientY !== null) {
          const bounds = runtime.getRendererScreenPosition();
          if (bounds) {
            const { x: x2, y: y2 } = runtime.viewerToWorld(clientX - bounds.x, clientY - bounds.y);
            const deltaDistance = newDistance && currentDistance ? newDistance / currentDistance : 1;
            runtime.transitionManager.customTransition((pendingTransition) => {
              pendingTransition.from = dna(runtime.target);
              pendingTransition.to = transform(pendingTransition.from, compose(translate(state.pointerStart.x - x2, state.pointerStart.y - y2), scaleAtOrigin(1 / deltaDistance, x2, y2)), state.mousemoveBuffer);
              pendingTransition.elapsed_time = 0;
              pendingTransition.total_time = 0;
              pendingTransition.timingFunction = easingFunctions.easeInOutExpo;
              pendingTransition.done = false;
            });
          }
          currentDistance = newDistance;
        }
      }
      function onMouseMove(e2) {
        if (state.isPressing) {
          const bounds = runtime.getRendererScreenPosition();
          if (bounds) {
            const { x: x2, y: y2 } = runtime.viewerToWorld(e2.clientX - bounds.x, e2.clientY - bounds.y);
            runtime.transitionManager.customTransition((pendingTransition) => {
              pendingTransition.from = dna(runtime.target);
              pendingTransition.to = transform(pendingTransition.from, translate(state.pointerStart.x - x2, state.pointerStart.y - y2), state.mousemoveBuffer);
              pendingTransition.elapsed_time = 0;
              pendingTransition.total_time = 0;
              pendingTransition.timingFunction = easingFunctions.easeInOutExpo;
              pendingTransition.done = false;
            });
          }
        }
      }
      function onClick(e2) {
        if (runtime.mode === "explore") {
          runtime.world.zoomIn(e2.atlas);
        }
      }
      function onWheel(e2) {
        const normalized = normalizeWheel(e2);
        const zoomFactor = 1 + normalized.spinY / zoomWheelConstant;
        runtime.world.zoomTo(zoomFactor, e2.atlas, true);
      }
      runtime.world.addEventListener("mouseup", onMouseUp);
      runtime.world.addEventListener("touchend", onMouseUp);
      runtime.world.addEventListener("touchstart", onTouchStart);
      runtime.world.addEventListener("mousedown", onMouseDown);
      window.addEventListener("touchend", onWindowMouseUp);
      window.addEventListener("mouseup", onWindowMouseUp);
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("touchmove", onTouchMove);
      if (enableClickToZoom) {
        runtime.world.activatedEvents.push("onClick");
        runtime.world.addEventListener("click", onClick);
      }
      if (enableWheel) {
        runtime.world.activatedEvents.push("onWheel");
        runtime.world.addEventListener("wheel", onWheel);
      }
      const removeLayout = runtime.world.addLayoutSubscriber((type, data) => {
        if (type === "zone-changed") {
          runtime.transitionManager.constrainBounds({
            transition: { duration: 0 }
          });
        }
        if (type === "zoom-to" && data) {
          runtime.transitionManager.zoomTo(data.factor, {
            origin: data.point,
            stream: data.stream
          });
        }
        if (type === "go-home") {
          const transition = data.immediate ? { duration: 0 } : void 0;
          runtime.transitionManager.goToRegion(toBox(runtime.homePosition), { transition });
        }
        if (type === "goto-region" && data) {
          const transition = data.immediate ? { duration: 0 } : {};
          runtime.transitionManager.goToRegion(data, { transition });
        }
        if (type === "constrain-bounds") {
          runtime.transitionManager.constrainBounds({
            transition: (data == null ? void 0 : data.immediate) ? { duration: 0 } : void 0
          });
        }
      });
      return () => {
        runtime.world.removeEventListener("mouseup", onMouseUp);
        runtime.world.removeEventListener("touchend", onMouseUp);
        runtime.world.removeEventListener("touchstart", onTouchStart);
        runtime.world.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("touchend", onWindowMouseUp);
        window.removeEventListener("mouseup", onWindowMouseUp);
        runtime.world.removeEventListener("mousemove", onMouseMove);
        runtime.world.removeEventListener("touchmove", onMouseMove);
        if (enableClickToZoom) {
          runtime.world.removeEventListener("click", onClick);
        }
        if (enableWheel) {
          runtime.world.removeEventListener("wheel", onWheel);
        }
        removeLayout();
      };
    },
    updatePosition() {
    }
  };
};
const ModeContext = React__default.createContext("explore");
ModeContext.displayName = "Mode";
const useMode = () => {
  return useContext(ModeContext);
};
function ModeProvider(props2) {
  return /* @__PURE__ */ React__default.createElement(ModeContext.Provider, {
    value: props2.mode
  }, props2.children);
}
function debounce(func, wait, immediate) {
  var timeout, args, context, timestamp, result;
  if (null == wait)
    wait = 100;
  function later() {
    var last = Date.now() - timestamp;
    if (last < wait && last >= 0) {
      timeout = setTimeout(later, wait - last);
    } else {
      timeout = null;
      if (!immediate) {
        result = func.apply(context, args);
        context = args = null;
      }
    }
  }
  var debounced = function() {
    context = this;
    args = arguments;
    timestamp = Date.now();
    var callNow = immediate && !timeout;
    if (!timeout)
      timeout = setTimeout(later, wait);
    if (callNow) {
      result = func.apply(context, args);
      context = args = null;
    }
    return result;
  };
  debounced.clear = function() {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  debounced.flush = function() {
    if (timeout) {
      result = func.apply(context, args);
      context = args = null;
      clearTimeout(timeout);
      timeout = null;
    }
  };
  return debounced;
}
debounce.debounce = debounce;
var debounce_1 = debounce;
function useMeasure(_temp) {
  let {
    debounce: debounce2,
    scroll,
    polyfill,
    offsetSize
  } = _temp === void 0 ? {
    debounce: 0,
    scroll: false,
    offsetSize: false
  } : _temp;
  const ResizeObserver2 = polyfill || (typeof window === "undefined" ? class ResizeObserver {
  } : window.ResizeObserver);
  if (!ResizeObserver2) {
    throw new Error("This browser does not support ResizeObserver out of the box. See: https://github.com/react-spring/react-use-measure/#resize-observer-polyfills");
  }
  const [bounds, set] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    bottom: 0,
    right: 0,
    x: 0,
    y: 0
  });
  const state = useRef({
    element: null,
    scrollContainers: null,
    resizeObserver: null,
    lastBounds: bounds
  });
  const scrollDebounce = debounce2 ? typeof debounce2 === "number" ? debounce2 : debounce2.scroll : null;
  const resizeDebounce = debounce2 ? typeof debounce2 === "number" ? debounce2 : debounce2.resize : null;
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => void (mounted.current = false);
  });
  const [forceRefresh, resizeChange, scrollChange] = useMemo(() => {
    const callback = () => {
      if (!state.current.element)
        return;
      const {
        left,
        top,
        width,
        height,
        bottom,
        right,
        x: x2,
        y: y2
      } = state.current.element.getBoundingClientRect();
      const size = {
        left,
        top,
        width,
        height,
        bottom,
        right,
        x: x2,
        y: y2
      };
      if (state.current.element instanceof HTMLElement && offsetSize) {
        size.height = state.current.element.offsetHeight;
        size.width = state.current.element.offsetWidth;
      }
      Object.freeze(size);
      if (mounted.current && !areBoundsEqual(state.current.lastBounds, size))
        set(state.current.lastBounds = size);
    };
    return [callback, resizeDebounce ? debounce_1(callback, resizeDebounce) : callback, scrollDebounce ? debounce_1(callback, scrollDebounce) : callback];
  }, [set, offsetSize, scrollDebounce, resizeDebounce]);
  function removeListeners() {
    if (state.current.scrollContainers) {
      state.current.scrollContainers.forEach((element) => element.removeEventListener("scroll", scrollChange, true));
      state.current.scrollContainers = null;
    }
    if (state.current.resizeObserver) {
      state.current.resizeObserver.disconnect();
      state.current.resizeObserver = null;
    }
  }
  function addListeners() {
    if (!state.current.element)
      return;
    state.current.resizeObserver = new ResizeObserver2(scrollChange);
    state.current.resizeObserver.observe(state.current.element);
    if (scroll && state.current.scrollContainers) {
      state.current.scrollContainers.forEach((scrollContainer) => scrollContainer.addEventListener("scroll", scrollChange, {
        capture: true,
        passive: true
      }));
    }
  }
  const ref = (node) => {
    if (!node || node === state.current.element)
      return;
    removeListeners();
    state.current.element = node;
    state.current.scrollContainers = findScrollContainers(node);
    addListeners();
  };
  useOnWindowScroll(scrollChange, Boolean(scroll));
  useOnWindowResize(resizeChange);
  useEffect(() => {
    removeListeners();
    addListeners();
  }, [scroll, scrollChange, resizeChange]);
  useEffect(() => removeListeners, []);
  return [ref, bounds, forceRefresh];
}
function useOnWindowResize(onWindowResize) {
  useEffect(() => {
    const cb = onWindowResize;
    window.addEventListener("resize", cb);
    return () => void window.removeEventListener("resize", cb);
  }, [onWindowResize]);
}
function useOnWindowScroll(onScroll, enabled) {
  useEffect(() => {
    if (enabled) {
      const cb = onScroll;
      window.addEventListener("scroll", cb, {
        capture: true,
        passive: true
      });
      return () => void window.removeEventListener("scroll", cb, true);
    }
  }, [onScroll, enabled]);
}
function findScrollContainers(element) {
  const result = [];
  if (!element || element === document.body)
    return result;
  const {
    overflow,
    overflowX,
    overflowY
  } = window.getComputedStyle(element);
  if ([overflow, overflowX, overflowY].some((prop) => prop === "auto" || prop === "scroll"))
    result.push(element);
  return [...result, ...findScrollContainers(element.parentElement)];
}
const keys = ["x", "y", "top", "bottom", "left", "right", "width", "height"];
const areBoundsEqual = (a2, b2) => keys.every((key) => a2[key] === b2[key]);
const AtlasContext = React__default.createContext(null);
AtlasContext.displayName = "Atlas";
const BoundsContext = React__default.createContext(null);
BoundsContext.displayName = "Bounds";
let getCurrentTime;
const hasPerformanceNow = typeof performance === "object" && typeof performance.now === "function";
if (hasPerformanceNow) {
  const localPerformance = performance;
  getCurrentTime = () => localPerformance.now();
} else {
  const localDate = Date;
  const initialTime = localDate.now();
  getCurrentTime = () => localDate.now() - initialTime;
}
function appendChild(parent, child) {
  if (parent && parent.appendChild && child) {
    parent.appendChild(child);
  }
}
function removeChild(parent, child) {
  if (parent && parent.removeChild && child) {
    parent.removeChild(child);
  }
}
function removeChildFromContainer(parent, child) {
  return removeChild(parent.world, child);
}
function insertInContainerBefore(container, child, beforeChild) {
  return insertBefore(container.world, child, beforeChild);
}
function insertBefore(parent, child, before) {
  if (parent && parent instanceof Runtime) {
    parent = parent.world;
  }
  if (parent && parent.insertBefore) {
    parent.insertBefore(child, before);
  }
}
function applyProps(instance, oldProps, newProps) {
  if (!newProps) {
    return;
  }
  if (instance.applyProps) {
    instance.applyProps(newProps);
  }
  if (instance instanceof BaseObject) {
    for (const ev of supportedEventAttributes) {
      const event = ev.slice(2).toLowerCase();
      if (newProps[ev] !== oldProps[ev]) {
        if (oldProps[ev]) {
          instance.removeEventListener(event, oldProps[ev]);
        }
        instance.addEventListener(event, newProps[ev]);
      }
    }
  }
}
function activateEvents(world, props2) {
  const keys2 = Object.keys(props2);
  let didActivate = false;
  for (const key of keys2) {
    if (supportedEventAttributes.indexOf(key) !== -1) {
      const ev = supportedEventMap[key];
      if (ev) {
        if (world.activatedEvents.indexOf(ev) !== -1)
          continue;
        didActivate = true;
        world.activatedEvents.push(ev);
      }
    }
  }
  if (didActivate) {
    world.triggerEventActivation();
  }
}
const roots = /* @__PURE__ */ new Map();
const emptyObject = {};
function createInstance(type, { args = [], ...props2 }, runtime, hostContext, internalInstanceHandle) {
  if (!(runtime instanceof Runtime) && internalInstanceHandle) {
    const fn = (node) => {
      if (!node.return)
        return node.stateNode && node.stateNode.containerInfo;
      else
        return fn(node.return);
    };
    runtime = fn(internalInstanceHandle);
  }
  let instance;
  let world = runtime.world;
  switch (type) {
    case "world":
      instance = World.withProps({ width: props2.width, height: props2.height, viewingDirection: "left-to-right" });
      instance.activatedEvents = world.activatedEvents;
      instance.eventHandlers = world.eventHandlers;
      instance.subscriptions = world.subscriptions;
      instance.triggerEventActivation();
      world = instance;
      break;
    case "box":
      instance = new Box();
      break;
    case "shape":
      instance = new Geometry();
      break;
    case "worldObject":
    case "world-object":
      instance = new WorldObject();
      break;
    case "worldImage":
    case "world-image":
      instance = new SingleImage();
      break;
    case "texture":
      instance = new ImageTexture();
      break;
    case "compositeImage":
    case "composite-image":
      instance = new CompositeResource({
        id: props2.id,
        width: props2.width,
        height: props2.height,
        images: []
      });
      break;
    case "tiledImage":
    case "tiled-image":
      instance = TiledImage.fromTile(props2.uri, props2.display, props2.tile, props2.scaleFactor, void 0, props2.format);
      break;
    case "paragraph":
      instance = new Text();
      instance.text = props2.children;
      break;
    default:
      return;
  }
  activateEvents(world, props2);
  applyProps(instance, {}, props2);
  return instance;
}
function appendChildToContainer(runtime, world) {
  if (world instanceof World) {
    runtime.world = world;
  } else if (world instanceof WorldObject) {
    runtime.world.appendChild(world);
  } else if (world) {
    throw new Error("Invalid root");
  }
}
const reconciler = Reconciler({
  unstable_now: getCurrentTime,
  now: getCurrentTime,
  createInstance,
  removeChild,
  appendChild,
  appendInitialChild: appendChild,
  insertBefore,
  warnsIfNotActing: true,
  supportsMutation: true,
  isPrimaryRenderer: false,
  scheduleTimeout: typeof setTimeout !== "undefined" ? setTimeout : void 0,
  cancelTimeout: typeof clearTimeout !== "undefined" ? clearTimeout : void 0,
  setTimeout: typeof setTimeout !== "undefined" ? setTimeout : void 0,
  clearTimeout: typeof clearTimeout !== "undefined" ? clearTimeout : void 0,
  noTimeout: -1,
  appendChildToContainer,
  removeChildFromContainer,
  createTextInstance() {
  },
  insertInContainerBefore,
  prepareUpdate(instance, type, oldProps, newProps, runtime) {
    activateEvents(runtime.world, newProps);
    return newProps;
  },
  commitUpdate(instance, updatePayload, type, prevProps, nextProps, internalHandle) {
    if (instance.applyProps && updatePayload) {
      applyProps(instance, prevProps, updatePayload);
    }
  },
  finalizeInitialChildren(instance) {
    return instance == null ? void 0 : instance.__handlers;
  },
  getChildHostContext() {
    return emptyObject;
  },
  getRootHostContext() {
    return emptyObject;
  },
  prepareForCommit(runtime) {
    runtime.isCommitting = true;
    return null;
  },
  preparePortalMount() {
  },
  hideInstance(instance) {
    if (instance && instance.points) {
      instance.points[0] = 0;
    }
  },
  unhideInstance(instance, props2) {
    if (instance && instance.points) {
      instance.points[0] = 1;
    }
  },
  getPublicInstance(instance) {
    return instance;
  },
  hideTextInstance() {
    throw new Error("Text is not allowed in the react-three-fibre tree. You may have extraneous whitespace between components.");
  },
  resetAfterCommit(runtime) {
    runtime.isCommitting = false;
    runtime.pendingUpdate = true;
    if (runtime.world) {
      if (runtime.world.needsRecalculate) {
        runtime.world.recalculateWorldSize();
        runtime.world.triggerRepaint();
      }
    }
  },
  shouldSetTextContent() {
    return false;
  },
  clearContainer() {
    return false;
  }
});
reconciler.injectIntoDevTools({
  bundleType: 0,
  version,
  rendererPackageName: "@atlas-viewer/atlas"
});
function unmountComponentAtNode(runtime, callback) {
  const root = roots.get(runtime);
  if (root) {
    reconciler.updateContainer(null, root, null, () => {
      roots.delete(runtime);
      if (callback)
        callback(runtime);
    });
  }
}
const ReactAtlas = {
  render(whatToRender, runtime) {
    const root = roots.get(runtime);
    if (root) {
      reconciler.updateContainer(whatToRender, root, null, () => void 0);
    } else {
      const newRoot = reconciler.createContainer(runtime, 0, null, false, null, "", () => {
      }, null);
      reconciler.updateContainer(whatToRender, newRoot, null, null);
      roots.set(runtime, newRoot);
    }
  },
  unmountComponentAtNode(runtime, callback) {
    unmountComponentAtNode(runtime, callback);
  }
};
const AtlasWithReconciler = React__default.memo(({ children, setIsReady, onCreated, bounds, preset, mode = "explore" }) => {
  const AtlasRoot = useCallback(function AtlasRoot2(props2) {
    const strictModeDoubleRender = useRef(false);
    const activate = () => {
      setIsReady(true);
    };
    useEffect(() => {
      if (preset && !strictModeDoubleRender.current) {
        preset.runtime.goHome();
        const result = onCreated && onCreated(preset);
        return void (result && result.then ? result.then(activate) : activate());
      }
      return () => {
      };
    }, []);
    useEffect(() => {
      strictModeDoubleRender.current = true;
    }, []);
    return props2.children;
  }, [preset]);
  useLayoutEffect$1(() => {
    if (preset) {
      const runtime = preset.runtime;
      if (mode !== runtime.mode) {
        runtime.mode = mode;
      }
      ReactAtlas.render(/* @__PURE__ */ React__default.createElement(React__default.StrictMode, null, /* @__PURE__ */ React__default.createElement(AtlasRoot, null, /* @__PURE__ */ React__default.createElement(BoundsContext.Provider, {
        value: bounds
      }, /* @__PURE__ */ React__default.createElement(ModeContext.Provider, {
        value: mode
      }, /* @__PURE__ */ React__default.createElement(AtlasContext.Provider, {
        value: preset
      }, children))))), runtime);
    }
  }, [preset, mode, children]);
  useLayoutEffect$1(() => {
    if (preset) {
      const runtime = preset.runtime;
      return () => {
        ReactAtlas.unmountComponentAtNode(runtime);
      };
    }
    return () => {
    };
  }, [preset]);
  return null;
});
class WebGLRenderer {
  constructor(canvas, options) {
    this.fragmentShaderSource = `
    precision mediump float;

    uniform sampler2D u_image;
    varying vec2 v_texCoord;

    void main() {
        gl_FragColor = texture2D(u_image, v_texCoord);
    }
  `;
    this.vertexShaderSource = `
    attribute vec2 a_position;
    uniform vec2 u_resolution;
    varying vec4 v_color;
    uniform sampler2D u_texture;

    attribute vec2 a_texCoord;
    varying vec2 v_texCoord;

    void main() {

        // convert the position from pixels to 0.0 to 1.0
        vec2 zeroToOne = a_position / u_resolution;

        // convert from 0->1 to 0->2
        vec2 zeroToTwo = zeroToOne * 2.0;

        // convert from 0->2 to -1->+1 (clip space)
        vec2 clipSpace = zeroToTwo - 1.0;

        gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);
        
        v_texCoord = a_texCoord;
    }
  `;
    this.lastResize = 0;
    this.lastKnownScale = 1;
    this.canvas = canvas;
    this.rendererPosition = canvas.getBoundingClientRect();
    this.gl = canvas.getContext("webgl2");
    this.fragmentShader = this.createShader(this.gl.FRAGMENT_SHADER, this.fragmentShaderSource);
    this.vertexShader = this.createShader(this.gl.VERTEX_SHADER, this.vertexShaderSource);
    this.program = this.createProgram(this.vertexShader, this.fragmentShader);
    this.dpi = (options == null ? void 0 : options.dpi) || 1;
    this.attributes = {
      position: this.gl.getAttribLocation(this.program, "a_position"),
      texCoord: this.gl.getAttribLocation(this.program, "a_texCoord")
    };
    this.uniforms = {
      resolution: this.gl.getUniformLocation(this.program, "u_resolution"),
      texture: this.gl.getUniformLocation(this.program, "u_texture")
    };
    this.buffers = {
      position: this.createArrayBuffer(),
      texCoord: this.createArrayBuffer(new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]))
    };
    this.rectBuffer = new Float32Array(12);
    this.resize();
    this.gl.viewport(0, 0, this.gl.canvas.width, this.gl.canvas.height);
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    this.gl.useProgram(this.program);
    this.gl.enableVertexAttribArray(this.attributes.position);
  }
  resize() {
    this.resizeCanvasToDisplaySize();
    this.gl.viewport(0, 0, this.gl.canvas.width, this.gl.canvas.height);
    this.rendererPosition = this.canvas.getBoundingClientRect();
  }
  isReady() {
    return true;
  }
  beforeFrame(world, delta, target) {
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    this.gl.vertexAttribPointer(this.attributes.position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.uniform2f(this.uniforms.resolution, this.gl.canvas.width, this.gl.canvas.height);
    if (this.lastResize > 1e3) {
      this.lastResize = 0;
      this.resizeCanvasToDisplaySize();
    }
    this.lastResize += delta;
  }
  prepareLayer(paint) {
    if (!paint.__host || !paint.__host.webgl) {
      if (paint instanceof SingleImage || paint instanceof TiledImage) {
        this.createImageHost(paint);
      }
      if (paint instanceof ImageTexture) {
        this.createTextureHost(paint);
      }
    }
  }
  createTextureHost(paint) {
    paint.__host = paint.__host ? paint.__host : {};
    const gl = this.gl;
    const texture = this.gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    let lastImage;
    if (paint instanceof ImageTexture) {
      const initial = paint.getTexture();
      if (initial.source) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, initial.source);
      }
      lastImage = initial;
    }
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    paint.__host.webgl = {
      height: paint.height,
      width: paint.width,
      texture,
      lastImage
    };
  }
  createImageHost(paint) {
    const textures = [...new Array(paint.points.length / 5)];
    paint.__host = paint.__host ? paint.__host : {};
    paint.__host.webgl = {
      height: paint.height,
      width: paint.width,
      textures,
      loading: [],
      loaded: [],
      lastLevelRendered: -1,
      onLoad: (index, image) => {
        const gl = this.gl;
        const texture = this.gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.bindTexture(gl.TEXTURE_2D, null);
        paint.__host.webgl.textures[index] = texture;
        paint.__host.webgl.loaded.push(index);
      }
    };
  }
  paint(paint, index, x2, y2, width, height) {
    if (paint.type === "spacial-content") {
      if (paint.__host && paint.__host.webgl) {
        if (paint.getTexture) {
          const newText = paint == null ? void 0 : paint.getTexture();
          if (newText && paint.__host.webgl.lastImage !== newText.hash && newText.source && !paint.__host.webgl.error) {
            try {
              const level = 0;
              const internalFormat = this.gl.RGBA;
              const srcFormat = this.gl.RGBA;
              const srcType = this.gl.UNSIGNED_BYTE;
              this.gl.bindTexture(this.gl.TEXTURE_2D, paint.__host.webgl.texture);
              this.gl.texImage2D(this.gl.TEXTURE_2D, level, internalFormat, srcFormat, srcType, newText.source);
              paint.__host.webgl.lastImage = newText.hash;
            } catch (e2) {
              paint.__host.webgl.error = e2;
            }
          }
        }
        if (paint.__host.webgl.loading && paint.__host.webgl.loading.indexOf(index) === -1 && paint.getImageUrl) {
          paint.__host.webgl.loading.push(index);
          const image = document.createElement("img");
          image.decoding = "async";
          image.crossOrigin = "anonymous";
          image.src = paint.getImageUrl(index);
          image.onload = () => {
            image.onload = null;
            return paint.__host.webgl.onLoad(index, image);
          };
        }
        const texture = paint.__host.webgl.texture ? paint.__host.webgl.texture : paint.__host.webgl.textures[index];
        if (texture) {
          this.gl.enableVertexAttribArray(this.attributes.texCoord);
          this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.texCoord);
          this.gl.enableVertexAttribArray(this.attributes.texCoord);
          this.gl.vertexAttribPointer(this.attributes.texCoord, 2, this.gl.FLOAT, false, 0, 0);
          this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.position);
          this.gl.enableVertexAttribArray(this.attributes.position);
          this.gl.vertexAttribPointer(this.attributes.position, 2, this.gl.FLOAT, false, 0, 0);
          this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
          this.gl.uniform1i(this.uniforms.texture, 0);
          this.setRectangle(x2, y2, width, height);
          this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
        }
      }
    }
  }
  afterPaintLayer(paint, transform2) {
  }
  pendingUpdate() {
    return true;
  }
  getPointsAt(world, target, aggregate, scaleFactor) {
    return world.getPointsAt(target, aggregate, scaleFactor);
  }
  afterFrame() {
  }
  getScale(width, height, dpi) {
    if (Number.isNaN(width) || Number.isNaN(height)) {
      return this.lastKnownScale;
    }
    const canvas = this.getCanvasDims();
    const w2 = canvas.width / width;
    const h2 = canvas.height / height;
    const scale2 = (w2 < h2 ? h2 : w2) * (dpi ? this.dpi || 1 : 1);
    if (!Number.isNaN(scale2)) {
      this.lastKnownScale = scale2;
    }
    return this.lastKnownScale;
  }
  getCanvasDims() {
    return { width: this.canvas.width / this.dpi, height: this.canvas.height / this.dpi };
  }
  getViewportBounds(world, target, padding) {
    return null;
  }
  createShader(type, source) {
    const shader = this.gl.createShader(type);
    if (shader) {
      this.gl.shaderSource(shader, source);
      this.gl.compileShader(shader);
      const success = this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS);
      if (success) {
        return shader;
      }
      const info = this.gl.getShaderInfoLog(shader);
      this.gl.deleteShader(shader);
      if (info) {
        throw new Error(info);
      }
    }
    throw new Error("Invalid shader");
  }
  createProgram(vertexShader, fragmentShader) {
    const program = this.gl.createProgram();
    if (program) {
      this.gl.attachShader(program, vertexShader);
      this.gl.attachShader(program, fragmentShader);
      this.gl.linkProgram(program);
      const success = this.gl.getProgramParameter(program, this.gl.LINK_STATUS);
      if (success) {
        return program;
      }
      const info = this.gl.getProgramInfoLog(program);
      this.gl.deleteProgram(program);
      if (info) {
        throw new Error(info);
      }
    }
    throw new Error("Invalid program");
  }
  resizeCanvasToDisplaySize() {
    const canvas = this.gl.canvas;
    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;
    const needResize = canvas.width !== displayWidth || canvas.height !== displayHeight;
    if (needResize) {
      canvas.width = displayWidth;
      canvas.height = displayHeight;
    }
    return needResize;
  }
  createArrayBuffer(data) {
    const buffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer);
    if (data) {
      this.gl.bufferData(this.gl.ARRAY_BUFFER, data, this.gl.STATIC_DRAW);
    }
    if (!buffer) {
      throw new Error("Cannot create buffer");
    }
    return buffer;
  }
  setRectangle(x2, y2, width, height) {
    this.gl.bufferData(this.gl.ARRAY_BUFFER, this.getRectangle(x2, y2, width, height), this.gl.STATIC_DRAW);
  }
  getRectangle(x2, y2, width, height) {
    const x1 = x2;
    const x22 = x2 + width;
    const y1 = y2;
    const y22 = y2 + height;
    this.rectBuffer.set([x1, y1, x22, y1, x1, y22, x1, y22, x22, y1, x22, y22]);
    return this.rectBuffer;
  }
  getRendererScreenPosition() {
    return this.rendererPosition;
  }
  finishLayer() {
  }
  reset() {
  }
}
function hash(object, str) {
  const text = str ? object : JSON.stringify(object);
  let numHash = 5381, index = text.length;
  while (index) {
    numHash = numHash * 33 ^ text.charCodeAt(--index);
  }
  const num = numHash >>> 0;
  const hexString = num.toString(16);
  if (hexString.length % 2) {
    return "0" + hexString;
  }
  return hexString;
}
class Stylesheet {
  constructor(options) {
    this.sheetPrefix = (options == null ? void 0 : options.sheetPrefix) || "a-";
    this.$element = document.createElement("style");
    this.stylesheetClasses = [];
    this.activeStylesheetClasses = [];
    this.sheetsDidUpdate = false;
    this.stylesheetEntries = {};
  }
  getElement() {
    return this.$element;
  }
  addStylesheet(_sheet) {
    const sheet = _sheet.replace(/\s\s+/g, " ").replace(/: /g, ":").replace(/; /g, ";").trim();
    const className = this.sheetPrefix + hash(sheet, true);
    if (this.stylesheetClasses.indexOf(className) !== -1) {
      return className;
    }
    this.stylesheetClasses.push(className);
    this.activeStylesheetClasses.push(className);
    this.stylesheetEntries[className] = sheet;
    this.sheetsDidUpdate = true;
    return className;
  }
  removeStylesheet(obj) {
    const className = this.sheetPrefix + hash(obj, true);
    if (this.stylesheetClasses.indexOf(className)) {
      this.stylesheetClasses = this.stylesheetClasses.filter((t2) => t2 !== className);
    }
    this.sheetsDidUpdate = true;
  }
  clearClasses() {
    this.activeStylesheetClasses = [];
  }
  didUpdateActive() {
    if (this.activeStylesheetClasses.length) {
      for (const sheet of this.activeStylesheetClasses) {
        if (this.stylesheetClasses.indexOf(sheet) === -1) {
          return true;
        }
      }
      for (const sheet of this.stylesheetClasses) {
        if (this.activeStylesheetClasses.indexOf(sheet) === -1) {
          return true;
        }
      }
    }
    return false;
  }
  updateSheet() {
    if (this.sheetsDidUpdate || this.didUpdateActive()) {
      this.$element.innerText = this.activeStylesheetClasses.map((className) => `.${className}{${this.stylesheetEntries[className]}}`).join("");
      this.sheetsDidUpdate = false;
      this.stylesheetClasses = [...this.activeStylesheetClasses];
    }
  }
}
class OverlayRenderer {
  constructor(htmlContainer, options) {
    this.visible = [];
    this.previousVisible = [];
    this.htmlIds = [];
    this.firstMeaningfulPaint = false;
    this.paintTx = 1;
    this.zIndex = 0;
    this.htmlContainer = htmlContainer;
    this.htmlContainer.innerHTML = "";
    this.rendererPosition = this.htmlContainer.getBoundingClientRect();
    this.options = {
      triggerResize: () => {
      },
      box: false,
      text: false,
      sheetPrefix: "",
      inlineStyles: false,
      background: "",
      ...options || {}
    };
    this.stylesheet = new Stylesheet({ sheetPrefix: this.options.sheetPrefix });
    if (!this.options.inlineStyles) {
      this.htmlContainer.appendChild(this.stylesheet.getElement());
    }
    if (this.options.background) {
      this.htmlContainer.classList.add(this.stylesheet.addStylesheet(`
        background: ${this.options.background};
      `));
    }
    this.classes = {
      hostClassName: this.stylesheet.addStylesheet(`
        position: absolute;
        transform-origin: 0px 0px;
      `),
      interactive: this.stylesheet.addStylesheet(`pointer-events: all`),
      nonInteractive: this.stylesheet.addStylesheet(`pointer-events: none`)
    };
    this.stylesheet.updateSheet();
  }
  createHtmlHost(paint) {
    if (this.htmlContainer && (this.options.box || paint.props.className || paint.props.html || paint.props.href)) {
      const div2 = document.createElement(paint.props.href ? "a" : "div");
      if (paint.props.href) {
        div2.style.display = "block";
        div2.href = paint.props.href;
        const target = paint.props.hrefTarget || "_blank";
        div2.target = target;
        if (target !== "_self") {
          div2.rel = "noopener noreferrer";
        }
      }
      div2.title = paint.props.title || "";
      if (this.options.inlineStyles) {
        div2.style.display = "block";
        div2.style.position = "absolute";
        div2.style.overflow = "hidden";
        div2.style.transformOrigin = "0px 0px";
      } else {
        div2.classList.add(this.classes.hostClassName);
      }
      paint.__host = { element: div2, revision: null, relative: false };
      this.updateHtmlHost(paint, paint.width, paint.height);
      if (paint.__onCreate) {
        paint.__onCreate();
      }
    }
  }
  triggerResize() {
    this.options.triggerResize();
  }
  updateHtmlHost(paint, width, height) {
    if (paint.__revision !== paint.__host.revision) {
      const div2 = paint.__host.element;
      const classes = [this.classes.hostClassName];
      if (paint.props.interactive) {
        if (this.options.inlineStyles) {
          div2.style.pointerEvents = "all";
        } else {
          classes.push(this.classes.interactive);
        }
      } else {
        if (this.options.inlineStyles) {
          div2.style.pointerEvents = "none";
        } else {
          classes.push(this.classes.nonInteractive);
        }
      }
      if (paint.props.href) {
        div2.style.display = "block";
        div2.href = paint.props.href;
        const target = paint.props.hrefTarget || "_blank";
        div2.target = target;
        if (target !== "_self") {
          div2.rel = "noopener noreferrer";
        } else {
          div2.rel = "";
        }
      } else if (div2.href) {
        div2.removeAttribute("href");
      }
      if (paint.props.title) {
        div2.title = paint.props.title || "";
      }
      if (paint.props.className) {
        classes.push(paint.props.className);
        if (paint.hovering) {
          classes.push(`${paint.props.className}--hover`);
        }
        if (paint.pressing) {
          classes.push(`${paint.props.className}--active`);
        }
      }
      if (paint.props.relativeStyle) {
        div2.style.width = `${width || paint.width}px`;
        div2.style.height = `${height || paint.height}px`;
      } else {
        div2.style.width = `${paint.width}px`;
        div2.style.height = `${paint.height}px`;
      }
      const style = paint.props.style;
      if (style) {
        Object.assign(div2.style, paint.props.style || {}, paint.hovering ? paint.props.hoverStyles || {} : {}, paint.pressing ? paint.props.pressStyles || {} : {});
        return;
      }
      if (this.options.text && paint instanceof Text) {
        if (paint.text) {
          div2.innerText = paint.text;
        }
        if (paint.backgroundColor) {
          div2.style.backgroundColor = paint.backgroundColor;
        }
        if (paint.color) {
          div2.style.color = paint.color;
        }
        if (paint.props.font) {
          div2.style.font = paint.props.font;
        }
        if (paint.props.textAlign) {
          div2.style.textAlign = paint.props.textAlign;
        }
        paint.__host.revision = paint.__revision;
        const classNames2 = classes.join(" ");
        div2.className = classNames2;
        div2.part = classNames2;
      }
      if (paint instanceof Box && (this.options.box || paint.props.className || paint.props.html)) {
        if (paint.props.backgroundColor) {
          div2.style.backgroundColor = paint.props.backgroundColor;
        }
        if (paint.props.border !== div2.style.border) {
          div2.style.border = paint.props.border;
        }
      }
      const classNames = classes.join(" ");
      div2.className = classNames;
      div2.part = classNames;
    }
  }
  afterFrame(world, delta, target) {
    this.stylesheet.updateSheet();
    for (const prev of this.previousVisible) {
      if (this.visible.indexOf(prev) === -1) {
        if (this.htmlContainer && prev.__id && this.htmlIds.indexOf(prev.__id) !== -1) {
          this.htmlContainer.removeChild(prev.__host.element);
        }
      }
    }
    this.previousVisible = this.visible;
  }
  afterPaintLayer(paint, transform2) {
  }
  beforeFrame(world, delta, target) {
    this.stylesheet.clearClasses();
    this.paintTx++;
    this.zIndex = 0;
    this.visible = [];
  }
  getPointsAt(world, target, aggregate, scaleFactor) {
    return world.getPointsAt(target, aggregate, scaleFactor);
  }
  getScale(width, height) {
    const w2 = this.rendererPosition.width / width;
    const h2 = this.rendererPosition.height / height;
    return w2 < h2 ? h2 : w2;
  }
  getViewportBounds(world, target, padding) {
    return null;
  }
  isReady() {
    return false;
  }
  paint(paint, index, x2, y2, width, height) {
    this.zIndex++;
    if ((this.options.text && paint instanceof Text || paint instanceof Box && (this.options.box || paint.props.className || paint.props.html)) && paint.__host.tx !== this.paintTx) {
      this.visible.push(paint);
      paint.__host.tx = this.paintTx;
      if (this.htmlContainer) {
        this.updateHtmlHost(paint, width, height);
        const scale2 = width / paint.width;
        const element = paint.__host.element;
        element.style.zIndex = `${this.zIndex}`;
        if (paint.props.relativeStyle) {
          element.style.transform = `translate(${Math.round(x2)}px, ${Math.round(y2)}px)`;
        } else {
          element.style.transform = `translate(${Math.round(x2)}px, ${Math.round(y2)}px) scale(${scale2})`;
        }
        if (this.previousVisible.indexOf(paint) === -1) {
          this.htmlContainer.appendChild(element);
        }
        if (this.htmlIds.indexOf(paint.__id) === -1) {
          this.htmlIds.push(paint.__id);
        }
      }
    }
  }
  pendingUpdate() {
    return false;
  }
  prepareLayer(paint) {
    if (!paint.__host) {
      if (paint instanceof Text || paint instanceof Box) {
        this.createHtmlHost(paint);
      }
    }
  }
  resize(width, height) {
    if (typeof width !== "undefined" && typeof height !== "undefined") {
      this.htmlContainer.style.width = `${width}px`;
      this.htmlContainer.style.height = `${height}px`;
    }
    this.rendererPosition = this.htmlContainer.getBoundingClientRect();
  }
  getRendererScreenPosition() {
    return this.rendererPosition;
  }
  finishLayer() {
  }
  reset() {
  }
}
const _BrowserEventManager = class {
  constructor(element, runtime, options) {
    this.activatedEvents = [];
    this.eventHandlers = [];
    this.pointerMoveEvent = void 0;
    this.pointerEventState = {
      isClicking: false,
      isDragging: false,
      isPressed: false,
      mousedOver: [],
      itemsBeingDragged: [],
      mouseDownStart: { x: 0, y: 0 },
      lastTouches: []
    };
    this._realPointerMove = (e2) => {
      this.pointerMoveEvent = e2;
    };
    this.onWheelEvent = (e2) => {
      e2.preventDefault();
      this.onPointerEvent(e2);
    };
    this.onTouchEvent = (e2) => {
      const type = supportedEventMap[e2.type];
      const atlasTouches = [];
      const len = e2.touches.length;
      for (let i2 = 0; i2 < len; i2++) {
        const touch = e2.touches.item(i2);
        if (!touch)
          continue;
        const { x: x2, y: y2 } = this.runtime.viewerToWorld(touch.clientX - this.bounds.left, touch.clientY - this.bounds.top);
        const atlasTouch = { id: touch.identifier, x: x2, y: y2 };
        atlasTouches.push(atlasTouch);
      }
      if (atlasTouches.length) {
        this.assignToEvent(e2, atlasTouches[0].x, atlasTouches[0].y);
      }
      if (type !== "onTouchEnd") {
        this.pointerEventState.lastTouches = atlasTouches;
        e2.atlasTouches = atlasTouches;
        this.runtime.world.propagateTouchEvent(type, e2, atlasTouches);
      } else {
        e2.atlasTouches = [];
        this.runtime.world.propagateTouchEvent(type, e2, this.pointerEventState.lastTouches);
        this.pointerEventState.lastTouches = [];
      }
    };
    this.onPointerEvent = (e2) => {
      const ev = supportedEventMap[e2.type];
      if (ev && this.runtime.world.activatedEvents.indexOf(ev) !== -1) {
        const { x: x2, y: y2 } = this.runtime.viewerToWorld(e2.clientX - this.bounds.left, e2.clientY - this.bounds.top);
        this.assignToEvent(e2, x2, y2);
        this.runtime.world.propagatePointerEvent(ev, e2, x2, y2);
      }
    };
    this.onPointerDown = (e2) => {
      this.pointerEventState.isPressed = true;
      this.pointerEventState.isClicking = true;
      this.pointerEventState.mouseDownStart.x = e2.clientX;
      this.pointerEventState.mouseDownStart.y = e2.clientY;
      setTimeout(() => {
        if (this.runtime) {
          this.pointerEventState.isClicking = false;
        }
      }, 250);
      setTimeout(() => {
        if (this.runtime && this.pointerEventState.isPressed && !this.pointerEventState.isDragging) {
          const dragStart = this.runtime.viewerToWorld(this.pointerEventState.mouseDownStart.x - this.bounds.left, this.pointerEventState.mouseDownStart.y - this.bounds.top);
          this.pointerEventState.isDragging = true;
          this.pointerEventState.itemsBeingDragged = this.runtime.world.propagatePointerEvent("onDragStart", e2, dragStart.x, dragStart.y);
        }
      }, 800);
      this.onPointerEvent(e2);
    };
    this.onPointerUp = (e2) => {
      if (this.pointerEventState.isClicking) {
        const { x: x2, y: y2 } = this.runtime.viewerToWorld(e2.clientX - this.bounds.left, e2.clientY - this.bounds.top);
        this.assignToEvent(e2, x2, y2);
        this.runtime.world.propagatePointerEvent("onClick", e2, x2, y2);
      }
      if (this.pointerEventState.isDragging) {
        for (const item of this.pointerEventState.itemsBeingDragged) {
          item.dispatchEvent("onDragEnd", e2);
        }
        this.pointerEventState.isDragging = false;
      }
      this.pointerEventState.isClicking = false;
      this.pointerEventState.isPressed = false;
      this.pointerEventState.itemsBeingDragged = [];
      this.onPointerEvent(e2);
    };
    this.onPointerMove = (e2) => {
      this.pointerMoveEvent = void 0;
      const { x: x2, y: y2 } = this.runtime.viewerToWorld(e2.clientX - this.bounds.left, e2.clientY - this.bounds.top);
      if (Number.isNaN(x2) || Number.isNaN(y2)) {
        return;
      }
      this.assignToEvent(e2, x2, y2);
      this.runtime.world.propagatePointerEvent("onPointerMove", e2, x2, y2);
      const newList = this.runtime.world.propagatePointerEvent("onMouseMove", e2, x2, y2);
      const newIds = [];
      const newItems = [];
      for (const item of newList) {
        newIds.push(item.id);
        newItems.push(item);
        if (this.pointerEventState.mousedOver.indexOf(item) === -1) {
          item.dispatchEvent("onMouseEnter", e2);
          item.dispatchEvent("onPointerEnter", e2);
          item.dispatchEvent("onMouseOver", e2);
          item.dispatchEvent("onPointerOver", e2);
        }
      }
      for (const oldItem of this.pointerEventState.mousedOver) {
        if (newIds.indexOf(oldItem.id) === -1) {
          oldItem.dispatchEvent("onMouseLeave", e2);
          oldItem.dispatchEvent("onPointerLeave", e2);
          oldItem.dispatchEvent("onMouseOut", e2);
          oldItem.dispatchEvent("onPointerOut", e2);
        }
      }
      if (this.pointerEventState.isDragging) {
        for (const item of this.pointerEventState.itemsBeingDragged) {
          item.dispatchEvent("onDrag", e2);
        }
      }
      if (this.pointerEventState.isPressed && !this.pointerEventState.isDragging && distance(this.pointerEventState.mouseDownStart, { x: e2.clientX, y: e2.clientY }) > 50) {
        const dragStart = this.runtime.viewerToWorld(this.pointerEventState.mouseDownStart.x - this.bounds.left, this.pointerEventState.mouseDownStart.y - this.bounds.top);
        this.pointerEventState.isDragging = true;
        this.pointerEventState.itemsBeingDragged = this.runtime.world.propagatePointerEvent("onDragStart", { ...e2, atlas: { x: dragStart.x, y: dragStart.y } }, dragStart.x, dragStart.y);
      }
      this.pointerEventState.mousedOver = newItems;
    };
    this.element = element;
    this.runtime = runtime;
    this.unsubscribe = runtime.world.addLayoutSubscriber(this.layoutSubscriber.bind(this));
    this.bounds = element.getBoundingClientRect();
    this.options = {
      simulationRate: 0,
      ...options || {}
    };
    let tAcc = 0;
    runtime.registerHook("useFrame", (t2) => {
      tAcc += t2;
      if (tAcc > this.options.simulationRate && this.pointerMoveEvent) {
        tAcc = 0;
        runtime.updateNextFrame();
      }
    });
    runtime.registerHook("useBeforeFrame", () => {
      if (this.pointerMoveEvent) {
        this.onPointerMove(this.pointerMoveEvent);
      }
    });
    this.activateEvents();
  }
  updateBounds() {
    this.bounds = this.element.getBoundingClientRect();
    this.runtime.updateRendererScreenPosition();
  }
  layoutSubscriber(type) {
    if (type === "event-activation") {
      this.activateEvents();
    }
  }
  assignToEvent(e2, x2, y2) {
    _BrowserEventManager.eventPool.atlas.x = x2;
    _BrowserEventManager.eventPool.atlas.y = y2;
    e2.atlas = _BrowserEventManager.eventPool.atlas;
  }
  activateEvents() {
    this.element.addEventListener("pointermove", this._realPointerMove);
    this.element.addEventListener("pointerup", this.onPointerUp);
    this.element.addEventListener("pointerdown", this.onPointerDown);
    this.element.addEventListener("mousedown", this.onPointerEvent);
    this.element.addEventListener("mouseup", this.onPointerEvent);
    this.element.addEventListener("pointercancel", this.onPointerEvent);
    this.element.addEventListener("wheel", this.onWheelEvent);
    this.element.addEventListener("touchstart", this.onTouchEvent);
    this.element.addEventListener("touchcancel", this.onTouchEvent);
    this.element.addEventListener("touchend", this.onTouchEvent);
    this.element.addEventListener("touchmove", this.onTouchEvent);
  }
  normalizeEventName(event) {
    if (event.startsWith("on")) {
      return event.slice(2).toLowerCase();
    }
    return event.toLowerCase();
  }
  stop() {
    this.unsubscribe();
    for (const [event, handler] of this.eventHandlers) {
      this.element.removeEventListener(this.normalizeEventName(event), handler);
    }
  }
};
let BrowserEventManager = _BrowserEventManager;
BrowserEventManager.eventPool = {
  atlas: { x: 0, y: 0 }
};
function defaultPreset({
  interactive = true,
  viewport,
  forceRefresh,
  canvasElement,
  overlayElement,
  controllerConfig,
  unstable_webglRenderer,
  dpi,
  debug,
  canvasBox = true,
  polygon = true,
  navigatorElement,
  background,
  runtimeOptions
}) {
  if (!canvasElement) {
    throw new Error("Invalid container");
  }
  canvasElement.style.userSelect = "none";
  const controller = interactive ? popmotionController({
    minZoomFactor: 0.5,
    maxZoomFactor: 3,
    enableClickToZoom: false,
    ...controllerConfig || {}
  }) : void 0;
  const renderer = new CompositeRenderer([
    unstable_webglRenderer ? new WebGLRenderer(canvasElement, { dpi }) : new CanvasRenderer(canvasElement, { dpi, debug, box: canvasBox, polygon, background }),
    overlayElement ? new OverlayRenderer(overlayElement, {
      box: unstable_webglRenderer || !canvasBox,
      text: true,
      triggerResize: forceRefresh
    }) : void 0,
    navigatorElement ? new DebugRenderer(navigatorElement) : void 0
  ]);
  const runtime = new Runtime(renderer, new World(1024, 1024), viewport, controller ? [controller] : [], runtimeOptions);
  const em = new BrowserEventManager(canvasElement, runtime);
  return {
    name: "default-preset",
    em,
    runtime,
    renderer,
    controller,
    canvas: canvasElement,
    navigator: navigatorElement,
    unmount() {
      unmountComponentAtNode(runtime);
      runtime.stopControllers();
      runtime.stop();
      runtime.reset();
      if (em) {
        em.stop();
      }
    }
  };
}
class StaticRenderer {
  constructor(container, options) {
    this.pending = true;
    this.zIndex = 0;
    this.lastKnownScale = 1;
    this.currentlyVisible = [];
    this.previouslyVisible = [];
    this.container = container;
    this.rendererPosition = container.getBoundingClientRect();
    const { width, height } = this.rendererPosition;
    this.width = width;
    this.height = height;
    this.options = {
      addPart: false,
      setDraggableFalse: false,
      imageClass: "",
      widthStylesheet: false,
      sheetPrefix: "position-",
      background: "#000",
      ...options || {}
    };
    this.stylesheet = new Stylesheet({ sheetPrefix: this.options.sheetPrefix });
    this.container.classList.add(this.stylesheet.addStylesheet(`
        background: ${this.options.background};
      `));
    if (this.options.widthStylesheet) {
      this.container.appendChild(this.stylesheet.getElement());
    }
  }
  isReady() {
    return true;
  }
  resize() {
    this.rendererPosition = this.container.getBoundingClientRect();
    this.width = this.rendererPosition.width;
    this.height = this.rendererPosition.height;
  }
  getRendererScreenPosition() {
    return this.rendererPosition;
  }
  afterFrame(world, delta, target) {
    this.stylesheet.updateSheet();
    for (const item of this.previouslyVisible) {
      if (this.currentlyVisible.indexOf(item) === -1) {
        this.container.removeChild(item);
      }
    }
    for (const item of this.currentlyVisible) {
      if (this.previouslyVisible.indexOf(item) === -1) {
        this.container.appendChild(item);
      }
    }
    this.previouslyVisible = this.currentlyVisible;
    this.currentlyVisible = [];
  }
  afterPaintLayer(paint, transform2) {
  }
  beforeFrame(world, delta, target) {
    this.stylesheet.clearClasses();
    this.zIndex = 0;
  }
  getPointsAt(world, target, aggregate, scaleFactor) {
    return world.getPointsAt(target, aggregate, scaleFactor);
  }
  getScale(width, height) {
    if (Number.isNaN(width) || Number.isNaN(height)) {
      return this.lastKnownScale;
    }
    const w2 = this.width / width;
    const h2 = this.height / height;
    const scale2 = w2 < h2 ? h2 : w2;
    if (!Number.isNaN(scale2)) {
      this.lastKnownScale = scale2;
    }
    return this.lastKnownScale;
  }
  getViewportBounds(world, target, padding) {
    return null;
  }
  createImage() {
    const image = document.createElement("img");
    if (this.options.imageClass) {
      image.className = this.options.imageClass;
      if (this.options.addPart) {
        image.setAttribute("part", this.options.imageClass);
      }
    } else {
      image.style.position = "absolute";
      image.style.pointerEvents = "none";
      image.style.userSelect = "none";
    }
    if (this.options.setDraggableFalse) {
      image.setAttribute("draggable", "false");
    }
    return image;
  }
  paint(paint, index, x2, y2, width, height) {
    this.pending = false;
    this.zIndex++;
    if (paint instanceof SingleImage) {
      if (!paint.__host) {
        const image = this.createImage();
        image.src = paint.uri;
        paint.__host = image;
        this.container.appendChild(paint.__host);
      }
      const element = paint.__host;
      this.currentlyVisible.push(element);
      element.style.zIndex = `${this.zIndex}`;
      element.style.opacity = `${paint.style.opacity}`;
      if (this.options.widthStylesheet) {
        element.className = this.options.imageClass + " " + this.stylesheet.addStylesheet(`width:${(width + Number.MIN_VALUE).toFixed(2)}px;height:${(height + Number.MIN_VALUE).toFixed(2)}px;`);
      } else {
        element.style.width = `${width + Number.MIN_VALUE}px`;
        element.style.height = `${height + Number.MIN_VALUE}px`;
      }
      element.style.transform = `translate(${x2}px, ${y2}px)`;
    }
    if (paint instanceof TiledImage) {
      if (!paint.__host) {
        paint.__host = {
          images: []
        };
      }
      if (!paint.__host.images[index]) {
        const url = paint.getImageUrl(index);
        const image = this.createImage();
        image.src = url;
        paint.__host.images[index] = image;
        this.container.appendChild(image);
      }
      const element = paint.__host.images[index];
      element.style.zIndex = `${this.zIndex}`;
      element.style.opacity = `${paint.style.opacity}`;
      this.currentlyVisible.push(element);
      if (this.options.widthStylesheet) {
        element.className = this.options.imageClass + " " + this.stylesheet.addStylesheet(`width:${(width + Number.MIN_VALUE).toFixed(2)}px;height:${(height + Number.MIN_VALUE).toFixed(2)}px;`);
      } else {
        element.style.width = `${width + Number.MIN_VALUE}px`;
        element.style.height = `${height + Number.MIN_VALUE}px`;
      }
      element.style.transform = `translate(${x2}px, ${y2}px)`;
    }
  }
  pendingUpdate() {
    return this.pending;
  }
  prepareLayer(paint) {
  }
  finishLayer(paint) {
  }
  reset() {
  }
}
function staticPreset({
  interactive,
  viewport,
  forceRefresh,
  containerElement,
  overlayElement,
  controllerConfig,
  background
}) {
  if (!containerElement) {
    throw new Error("Invalid container");
  }
  containerElement.style.userSelect = "none";
  const controller = interactive ? popmotionController({
    minZoomFactor: 0.5,
    maxZoomFactor: 3,
    enableClickToZoom: false,
    ...controllerConfig || {}
  }) : void 0;
  const staticRenderer = new StaticRenderer(containerElement, {
    addPart: false,
    setDraggableFalse: false,
    imageClass: "atlas-static-image",
    background
  });
  const renderer = overlayElement ? new CompositeRenderer([
    staticRenderer,
    new OverlayRenderer(overlayElement, {
      box: true,
      text: true,
      triggerResize: forceRefresh
    })
  ]) : staticRenderer;
  const runtime = new Runtime(renderer, new World(1024, 1024), viewport, controller ? [controller] : []);
  const em = new BrowserEventManager(containerElement, runtime);
  return {
    name: "static-preset",
    em,
    runtime,
    renderer,
    controller,
    container: containerElement,
    overlay: overlayElement,
    unmount() {
      unmountComponentAtNode(runtime);
      runtime.stopControllers();
      runtime.stop();
      if (em) {
        em.stop();
      }
    }
  };
}
const presets = {
  "default-preset": defaultPreset,
  "static-preset": staticPreset
};
const defaultArgs = {};
function usePreset(renderPreset, options) {
  const overlayRef = useRef();
  const canvasRef = useRef();
  const navigatorRef = useRef();
  const containerRef = useRef();
  const viewport = useRef({
    width: options.width,
    height: options.height,
    didUpdate: true
  });
  const [presetName = "default-preset", presetArgs = defaultArgs] = Array.isArray(renderPreset) ? renderPreset || [] : [renderPreset];
  const [preset, setPreset] = useState(null);
  useLayoutEffect$1(() => {
    const canvasElement = canvasRef.current;
    const containerElement = containerRef.current;
    const overlayElement = overlayRef.current;
    const navigatorElement = navigatorRef.current;
    const presetFn = presets[presetName] || defaultPreset;
    const createdPreset = presetFn({
      containerElement,
      canvasElement,
      overlayElement,
      navigatorElement,
      viewport: viewport.current,
      dpi: window.devicePixelRatio || 1,
      forceRefresh: options.forceRefresh,
      unstable_webglRenderer: options.unstable_webglRenderer,
      ...presetArgs || {}
    });
    setPreset(createdPreset);
    return () => {
      if (createdPreset) {
        createdPreset.unmount();
        if (canvasElement) {
          canvasElement.height = 0;
          canvasElement.width = 0;
        }
        if (overlayElement) {
          overlayElement.innerHTML = "";
        }
        if (navigatorElement) {
          navigatorElement.height = 0;
          navigatorElement.width = 0;
        }
      }
    };
  }, [presetName, presetArgs]);
  const refs = useMemo(() => ({
    canvas: canvasRef,
    overlay: overlayRef,
    container: containerRef,
    navigator: navigatorRef
  }), []);
  return [presetName, preset, viewport, refs];
}
function useClassname(deps) {
  return useMemo(() => hash(deps), deps);
}
const Container = React__default.forwardRef((props2, ref) => {
  return /* @__PURE__ */ React__default.createElement("div", {
    ...props2,
    ref,
    part: props2.className
  });
});
const Atlas = ({
  htmlChildren,
  renderPreset: _renderPreset,
  onCreated,
  mode: _mode = "explore",
  resetWorldOnChange = true,
  unstable_webglRenderer = false,
  unstable_noReconciler = false,
  hideInlineStyle = false,
  controllerConfig,
  children,
  overlayStyle,
  containerStyle,
  enableNavigator,
  className,
  containerProps = {},
  homePosition,
  background: _background,
  runtimeOptions,
  debug,
  ...restProps
}) => {
  const [background, setBackground] = useState(_background);
  const [mode, setMode] = useState(_mode);
  const [isReady, setIsReady] = useState(false);
  const strictModeDoubleRender = useRef(false);
  const renderPreset = useMemo(() => {
    if (typeof _renderPreset === "string") {
      _renderPreset = [_renderPreset, {}];
    }
    if (background || debug) {
      if (_renderPreset) {
        return [_renderPreset[0], { debug, background, ..._renderPreset[1] || {} }];
      }
      return ["default-preset", { background, debug }];
    }
    return _renderPreset || "default-preset";
  }, [_renderPreset, background, debug]);
  const [_ref, bounds, forceRefresh] = useMeasure({ scroll: true });
  const outerContainerRef = useRef();
  const ref = (component) => {
    outerContainerRef.current = component;
    _ref(component);
  };
  const [presetName, preset, viewport, refs] = usePreset(renderPreset, {
    width: restProps.width,
    height: restProps.height,
    forceRefresh,
    unstable_webglRenderer
  });
  const [containerClassName, setContainerClassName] = useState("");
  useEffect(() => {
    setMode(_mode);
  }, [_mode]);
  useEffect(() => {
    if (preset && preset.em) {
      preset.em.updateBounds();
    }
  }, [preset, bounds]);
  useEffect(() => {
    preset == null ? void 0 : preset.runtime.setOptions(runtimeOptions || {});
  }, [runtimeOptions]);
  useEffect(() => {
    if (preset && preset.runtime) {
      preset.runtime.mode = mode;
    }
    if (isReady && preset) {
      preset.ready = true;
    }
  }, [preset, isReady, mode]);
  useEffect(() => {
    if (preset) {
      preset.runtime.manualHomePosition = !!homePosition;
      preset.runtime.setHomePosition(homePosition);
    }
  }, [preset, homePosition]);
  useEffect(() => {
    if (preset) {
      const rt = preset.runtime;
      rt.resize(viewport.current.width, restProps.width, viewport.current.height, restProps.height);
      viewport.current.width = restProps.width;
      viewport.current.height = restProps.height;
      rt.updateNextFrame();
      viewport.current.didUpdate = true;
    }
  }, [preset, restProps.width, restProps.height]);
  useLayoutEffect$1(() => {
    if (preset) {
      if (preset.overlay) {
        preset.overlay.style.width = `${bounds.width}px`;
        preset.overlay.style.height = `${bounds.height}px`;
      }
      if (preset.container) {
        preset.container.style.width = `${bounds.width}px`;
        preset.container.style.height = `${bounds.height}px`;
      }
    }
  }, [preset, bounds.height, bounds.width]);
  useLayoutEffect$1(() => {
    const windowResizeCallback = () => {
      if (preset) {
        const rt = preset.runtime;
        if (viewport.current.width !== restProps.width && viewport.current.height !== restProps.height) {
          rt.resize(viewport.current.width, restProps.width, viewport.current.height, restProps.height);
          viewport.current.width = restProps.width;
          viewport.current.height = restProps.height;
          rt.updateNextFrame();
          viewport.current.didUpdate = true;
        }
      }
    };
    window.addEventListener("resize", windowResizeCallback);
    return () => window.removeEventListener("resize", windowResizeCallback);
  }, [preset, restProps.height, restProps.width]);
  const navigatorOptions = {
    width: 120
  };
  const recalculateNavigatorDimensions = () => {
    if (preset && preset.navigator) {
      const wHeight = preset.runtime.world.height;
      const wWidth = preset.runtime.world.width;
      const ratio = window.devicePixelRatio || 1;
      const canvasWidth = navigatorOptions.width;
      const canvasHeight = navigatorOptions.width / wWidth * wHeight;
      preset.navigator.width = canvasWidth * ratio;
      preset.navigator.height = canvasHeight * ratio;
      preset.navigator.style.width = canvasWidth + "px";
      preset.navigator.style.height = canvasHeight + "px";
    }
  };
  useLayoutEffect$1(() => {
    if (preset) {
      recalculateNavigatorDimensions();
      const rt = preset.runtime;
      return rt.world.addLayoutSubscriber((type) => {
        if (type === "recalculate-world-size") {
          recalculateNavigatorDimensions();
          rt.resize(viewport.current.width, restProps.width, viewport.current.height, restProps.height);
        }
      });
    }
    return () => {
    };
  }, [preset]);
  const Canvas = useCallback(function Canvas2(props2) {
    const activate = () => {
      setIsReady(true);
    };
    useEffect(() => {
      if (preset) {
        preset.runtime.goHome();
        const result = onCreated && onCreated(preset);
        return void (result && result.then ? result.then(activate) : activate());
      } else {
        throw new Error("Invalid configuration - no runtime found");
      }
    }, []);
    return props2.children;
  }, [preset]);
  useEffect(() => {
    if (preset) {
      const rt = preset.runtime;
      if (resetWorldOnChange) {
        return rt.world.addLayoutSubscriber((type) => {
          if (type === "recalculate-world-size") {
            rt.goHome();
          }
        });
      }
    }
    return () => {
    };
  }, [preset, resetWorldOnChange]);
  useEffect(() => {
    if (preset) {
      const rt = preset.runtime;
      return rt.registerHook("useBeforeFrame", () => {
        var _a;
        if (viewport.current.didUpdate && preset.canvas) {
          const ratio = window.devicePixelRatio || 1;
          const canvasWidth = viewport.current.width;
          const canvasHeight = viewport.current.height;
          preset.canvas.width = canvasWidth * ratio;
          preset.canvas.height = canvasHeight * ratio;
          preset.canvas.style.width = canvasWidth + "px";
          preset.canvas.style.height = canvasHeight + "px";
          (_a = preset.canvas.getContext("2d")) == null ? void 0 : _a.scale(ratio, ratio);
          if (preset && preset.em) {
            preset.em.updateBounds();
          }
          viewport.current.didUpdate = false;
        }
      });
    }
    return () => {
    };
  }, [preset, resetWorldOnChange]);
  useEffect(() => {
    const keyupSpace = () => {
      if (preset) {
        setMode("sketch");
        setContainerClassName("mode-sketch");
      }
      window.removeEventListener("keyup", keyupSpace);
    };
    const keydownSpace = (e2) => {
      if (e2.code === "Space" && preset && preset.runtime.mode === "sketch") {
        if (e2.target && e2.target.tagName && e2.target.tagName.toLowerCase() === "input")
          return;
        e2.preventDefault();
        setMode("explore");
        setContainerClassName("mode-explore");
        window.addEventListener("keyup", keyupSpace);
      }
    };
    window.addEventListener("keydown", keydownSpace);
    return () => {
      window.removeEventListener("keydown", keydownSpace);
      window.removeEventListener("keyup", keyupSpace);
    };
  }, [preset]);
  useLayoutEffect$1(() => {
    if (outerContainerRef.current && !background) {
      const computed = getComputedStyle(outerContainerRef.current);
      const _background2 = computed.getPropertyValue("--atlas-background");
      if (_background2) {
        setBackground(_background2);
      }
    }
  }, [background]);
  strictModeDoubleRender.current = true;
  const { height: _2, width: __, ...canvasProps } = restProps;
  const widthClassName = useClassname([restProps.width, restProps.height]);
  return /* @__PURE__ */ React__default.createElement(Container, {
    ref,
    className: ["atlas", hideInlineStyle ? "" : `atlas-width-${widthClassName}`, containerClassName, className].filter(Boolean).join(" ").trim(),
    style: {
      ...containerStyle,
      ...hideInlineStyle ? {} : { width: restProps.width, height: restProps.height }
    }
  }, presetName === "static-preset" ? /* @__PURE__ */ React__default.createElement(Container, {
    className: "atlas-static-container",
    style: preset && preset.controller ? void 0 : { pointerEvents: "none" },
    ref: refs.container,
    tabIndex: 0,
    ...containerProps
  }) : /* @__PURE__ */ React__default.createElement("canvas", {
    className: "atlas-canvas",
    part: "atlas-canvas",
    tabIndex: 0,
    ...canvasProps,
    ...containerProps,
    ref: refs.canvas
  }), /* @__PURE__ */ React__default.createElement(Container, {
    className: "atlas-overlay",
    style: { ...overlayStyle || {} },
    ref: refs.overlay
  }, unstable_noReconciler ? /* @__PURE__ */ React__default.createElement(Canvas, null, /* @__PURE__ */ React__default.createElement(BoundsContext.Provider, {
    value: bounds
  }, /* @__PURE__ */ React__default.createElement(ModeContext.Provider, {
    value: mode
  }, /* @__PURE__ */ React__default.createElement(AtlasContext.Provider, {
    value: preset
  }, children)))) : /* @__PURE__ */ React__default.createElement(AtlasWithReconciler, {
    bounds,
    preset,
    mode,
    setIsReady,
    onCreated
  }, children)), enableNavigator ? /* @__PURE__ */ React__default.createElement(Container, {
    className: "atlas-navigator"
  }, /* @__PURE__ */ React__default.createElement("canvas", {
    className: "atlas-navigator-canvas",
    part: "atlas-navigator-canvas",
    ref: refs.navigator
  })) : null, hideInlineStyle ? /* @__PURE__ */ React__default.createElement("style", null, `.atlas-width-${widthClassName} { width: ${restProps.width}px; height: ${restProps.height}px; }`) : /* @__PURE__ */ React__default.createElement("style", null, `
        .atlas { position: relative; user-select: none; display: flex; background: var(--atlas-background, #000); z-index: var(--atlas-z-index, 10); touch-action: none; }
        .atlas-width-${widthClassName} { width: ${restProps.width}px; height: ${restProps.height}px; }
        .atlas-canvas { flex: 1 1 0px; }
        .atlas-canvas:focus, .atlas-static-container:focus { outline: none }
        .atlas-canvas:focus-visible, .atlas-canvas-container:focus-visible { outline: var(--atlas-focus, 2px solid darkorange) }
        .atlas-static-container { position: relative; overflow: hidden; flex: 1 1 0px; }
        .atlas-overlay { position: absolute; top: 0; left: 0; pointer-events: none; overflow: hidden; }
        .atlas-static-image { position: absolute; pointer-events: none; user-select: none; transform-origin: 0px 0px; }
        .atlas-navigator { position: absolute; top: var(--atlas-navigator-top, 10px); right: var(--atlas-navigator-bottom, 10px); left: var(--atlas-navigator-left); bottom: var(--atlas-navigator-bottom); opacity: .8 }
        .atlas-navigator-canvas { width: 100%; }
      `), htmlChildren);
};
function toPx(str) {
  if (Number(str) == str) {
    return `${str}px`;
  }
  return str;
}
const AtlasAuto = ({ resizeHash, aspectRatio, containerProps = {}, ...props2 }) => {
  const [ref, _bounds, forceRefresh] = useMeasure();
  const { height, width, ...restProps } = props2;
  useEffect(() => {
    forceRefresh();
  }, [width, height, resizeHash, forceRefresh]);
  const bounds = useMemo(() => {
    if (!aspectRatio) {
      return _bounds;
    }
    return {
      width: _bounds.width,
      height: _bounds.width * (1 / aspectRatio)
    };
  }, [_bounds, aspectRatio]);
  return /* @__PURE__ */ React__default.createElement(Container, {
    ref,
    className: "atlas-container",
    ...containerProps
  }, bounds.width ? /* @__PURE__ */ React__default.createElement(Atlas, {
    width: bounds.width || 100,
    height: bounds.height || 100,
    ...restProps
  }, props2.children) : null, props2.hideInlineStyle ? null : /* @__PURE__ */ React__default.createElement("style", null, `
          .atlas-container { 
            display: var(--atlas-container-display, block);
            flex: var(--atlas-container-flex, none);
            width: var(--atlas-container-width, ${width ? `${width}px` : "100%"});
            height: var(--atlas-container-height, ${toPx(height ? height : aspectRatio ? bounds.height : 512)})  
          }
      `), props2.htmlChildren);
};
const useAtlas = () => {
  return useContext(AtlasContext);
};
const useRuntime = () => {
  const atlas = useAtlas();
  return atlas ? atlas.runtime : void 0;
};
const useAfterFrame = (callback, deps = []) => {
  const runtime = useRuntime();
  useEffect(() => {
    if (runtime) {
      return runtime.registerHook("useAfterFrame", callback);
    }
    return () => {
    };
  }, deps);
};
const useFrame = (callback, deps = []) => {
  const runtime = useRuntime();
  useEffect(() => {
    if (runtime) {
      return runtime.registerHook("useFrame", callback);
    }
    return () => {
    };
  }, deps);
};
const useCanvas = () => {
  const atlas = useAtlas();
  return atlas && atlas.canvas ? atlas.canvas : void 0;
};
function useCanvasPosition() {
  return useContext(BoundsContext);
}
const DrawBox = ({ onCreate }) => {
  const mousePosition = useRef({ x: 0, y: 0 });
  const canvas = useCanvas();
  const canvasPosition = useCanvasPosition();
  const runtime = useRuntime();
  const [firstCorner, setFirstCorner] = useState();
  const [secondCorner, setSecondCorner] = useState();
  const mode = useMode();
  useFrame(() => {
    if (runtime && firstCorner && !secondCorner) {
      runtime.pendingUpdate = true;
    }
  }, [firstCorner, secondCorner]);
  useAfterFrame(() => {
    if (firstCorner && canvas && runtime) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const { x: x2, y: y2, width, height } = runtime.worldToViewer(firstCorner.x, firstCorner.y, (secondCorner ? secondCorner.x : mousePosition.current.x) - firstCorner.x, (secondCorner ? secondCorner.y : mousePosition.current.y) - firstCorner.y);
        ctx.lineWidth = secondCorner ? 3 : 1;
        ctx.strokeStyle = "#fff";
        ctx.strokeRect(x2, y2, width, height);
        ctx.lineWidth = secondCorner ? 3 : 1;
        ctx.strokeStyle = "#000";
        ctx.strokeRect(x2 + 1, y2 + 1, width - 2, height - 2);
      }
    }
  }, [firstCorner, secondCorner]);
  useEffect(() => {
    const cb = (e2) => {
      if (canvasPosition && runtime) {
        const { x: x2, y: y2 } = runtime.viewerToWorld(e2.clientX - canvasPosition.left, e2.clientY - canvasPosition.top);
        mousePosition.current.x = ~~x2;
        mousePosition.current.y = ~~y2;
      }
    };
    if (canvas) {
      canvas.addEventListener("mousemove", cb);
      return () => canvas.removeEventListener("mousemove", cb);
    }
    return () => {
    };
  }, [canvasPosition, canvas, runtime]);
  useEffect(() => {
    const cb = (e2) => {
      if (mode === "sketch") {
        setFirstCorner({ x: Math.round(mousePosition.current.x), y: Math.round(mousePosition.current.y) });
        setSecondCorner(void 0);
      }
    };
    if (canvas) {
      canvas.addEventListener("mousedown", cb);
      return () => canvas.removeEventListener("mousedown", cb);
    }
    return () => {
    };
  }, [canvas, mode]);
  useEffect(() => {
    const cb = (e2) => {
      if (firstCorner && !secondCorner) {
        setSecondCorner({ x: Math.round(mousePosition.current.x), y: Math.round(mousePosition.current.y) });
      }
    };
    if (canvas) {
      canvas.addEventListener("mouseup", cb);
      return () => canvas.removeEventListener("mouseup", cb);
    }
    return () => {
    };
  }, [canvas, firstCorner, secondCorner]);
  useEffect(() => {
    if (firstCorner && secondCorner) {
      onCreate({
        x: Math.min(firstCorner.x, secondCorner.x),
        y: Math.min(firstCorner.y, secondCorner.y),
        width: Math.abs(secondCorner.x - firstCorner.x),
        height: Math.abs(secondCorner.y - firstCorner.y)
      });
    }
  }, [firstCorner, onCreate, secondCorner]);
  return null;
};
async function renderReactDom(html, toRender, root) {
  if (version.startsWith("18.")) {
    const module = await import("react-dom/client");
    const createRoot = module.default ? module.default.createRoot : module.createRoot;
    if (!root.current) {
      root.current = createRoot(html);
    }
    root.current.render(toRender);
  } else {
    if (typeof ReactDOM !== "undefined") {
      const { render, unmountComponentAtNode: unmountComponentAtNode2 } = ReactDOM;
      render(toRender, html);
      root.current = {
        unmount() {
          unmountComponentAtNode2(html);
        }
      };
    } else {
      const module = await import("react-dom");
      const render = module.default ? module.default.render : module.render;
      const unmountComponentAtNode2 = module.default ? module.default.unmountComponentAtNode : module.unmountComponentAtNode;
      render(toRender, html);
      root.current = {
        unmount() {
          unmountComponentAtNode2(html);
        }
      };
    }
  }
}
const HTMLPortal = React__default.forwardRef(({ children, ...props2 }, fwdRef) => {
  const ref = useRef();
  const runtime = useRuntime();
  const lastScale = useRef(0);
  const boxRef = useRef();
  const root = useRef();
  useFrame(() => {
    var _a, _b, _c, _d;
    if (props2.relative) {
      const relativeBox = ref.current;
      if (relativeBox && runtime) {
        const scaleFactor = runtime.getScaleFactor();
        if (lastScale.current !== scaleFactor) {
          lastScale.current = scaleFactor;
          relativeBox.style.transformOrigin = "0 0";
          relativeBox.style.transform = `scale(${1 / lastScale.current})`;
          relativeBox.style.width = `${lastScale.current * 100}%`;
          relativeBox.style.height = `${lastScale.current * 100}%`;
          if (ref.current && ((_b = (_a = boxRef.current) == null ? void 0 : _a.__owner.value) == null ? void 0 : _b.rotation)) {
            relativeBox.style.transform = `scale(${1 / lastScale.current}) translate(50%, 50%) rotate(${((_d = (_c = boxRef.current) == null ? void 0 : _c.__owner.value) == null ? void 0 : _d.rotation) || 0}deg)  translate(-50%, -50%)`;
          }
        }
      }
    }
  }, [props2.relative]);
  useLayoutEffect$1(() => {
    const box = boxRef.current;
    if (fwdRef && box) {
      if (typeof fwdRef === "function") {
        fwdRef(box);
      } else {
        fwdRef.current = box;
      }
    }
    async function renderHost() {
      if (box && box.__host) {
        const toRender = props2.relative ? /* @__PURE__ */ React__default.createElement("div", {
          ref
        }, children) : children;
        await renderReactDom(box.__host.element, toRender, root);
      }
    }
    if (box && box.__host) {
      renderHost();
    } else if (box) {
      box.__onCreate = renderHost;
    }
  }, [fwdRef, children, boxRef, props2.relative]);
  useLayoutEffect$1(() => {
    return () => {
      if (root.current) {
        setTimeout(() => {
          root.current.unmount();
        }, 0);
      }
    };
  }, []);
  return /* @__PURE__ */ React__default.createElement("box", {
    html: true,
    ...props2,
    ref: boxRef
  });
});
HTMLPortal.displayName = "HTMLPortal";
const useWorldEvent = (name, cb, deps = []) => {
  const runtime = useRuntime();
  const world = runtime ? runtime.world : void 0;
  useEffect(() => {
    if (runtime) {
      const callback = cb;
      const realName = supportedEventMap[name];
      runtime.world.activatedEvents.push(realName);
      const ev = realName.slice(2).toLowerCase();
      runtime.world.addEventListener(ev, callback);
      return () => {
        runtime.world.removeEventListener(ev, callback);
      };
    }
    return () => {
    };
  }, [world, name, ...deps]);
};
function getRatio(a2, b2) {
  if (a2 === 0) {
    return [0, 1];
  }
  if (b2 === 0) {
    return [1, 0];
  }
  const ratio = Math.abs(a2) / Math.abs(b2);
  return [ratio, 1 - ratio];
}
function useModifierKeys() {
  const modifierKeys = useRef({
    ctrl: false,
    shift: false,
    alt: false
  });
  useLayoutEffect$1(() => {
    function keyDown(e2) {
      if (e2.key === "Shift")
        modifierKeys.current.shift = true;
      if (e2.key === "Control")
        modifierKeys.current.ctrl = true;
      if (e2.key === "Alt")
        modifierKeys.current.alt = true;
    }
    function keyUp(e2) {
      if (e2.key === "Shift")
        modifierKeys.current.shift = false;
      if (e2.key === "Control")
        modifierKeys.current.ctrl = false;
      if (e2.key === "Alt")
        modifierKeys.current.alt = false;
    }
    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);
    return () => {
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
    };
  }, []);
  return modifierKeys;
}
const useResizeWorldItem = (props2, onSave) => {
  const mode = useMode();
  const runtime = useRuntime();
  const canvasPosition = useCanvasPosition();
  const resizeMode = useRef();
  const portalRef = useRef(null);
  const mouseStart = useRef();
  const [isEditing, setIsEditing] = useState(false);
  const isEditingRef = useRef(false);
  const cardinalDeltas = useRef({ north: 0, south: 0, east: 0, west: 0 });
  const keys2 = useModifierKeys();
  const mouseEvent = useCallback((direction) => (e2) => {
    isEditingRef.current = true;
    setIsEditing(true);
    if (canvasPosition && runtime) {
      const { top, left } = canvasPosition;
      const current = runtime.viewerToWorld(e2.pageX - left, e2.pageY - top);
      mouseStart.current = { x: current.x, y: current.y };
      resizeMode.current = direction;
    }
  }, [canvasPosition, runtime]);
  const aspectRatio = useMemo(() => {
    return props2.width / props2.height;
  }, [props2.width, props2.height]);
  const constrainToAspectRatio = useCallback((deltas) => {
    const hasDeltas = Math.abs(deltas.north - deltas.south + (deltas.east - deltas.west));
    if (!hasDeltas)
      return;
    const xDelta = -deltas.west + deltas.east;
    const yDelta = -deltas.north + deltas.south;
    const originalWidth = props2.width + xDelta;
    const originalHeight = props2.height + yDelta;
    const newAspectRatio = originalWidth / originalHeight;
    if (newAspectRatio >= aspectRatio) {
      const width = originalHeight * aspectRatio;
      const margin = originalWidth - width;
      const [eastRatio, westRatio] = getRatio(deltas.east, deltas.west);
      deltas.west = deltas.west + margin * westRatio;
      deltas.east = deltas.east - margin * eastRatio;
    } else {
      const height = originalWidth / aspectRatio;
      const margin = originalHeight - height;
      const [northRatio, southRatio] = getRatio(deltas.north, deltas.south);
      deltas.north = deltas.north + margin * northRatio;
      deltas.south = deltas.south - margin * southRatio;
    }
  }, [props2.width, props2.height, aspectRatio]);
  useFrame(() => {
    if (mouseStart && runtime) {
      runtime.updateNextFrame();
    }
  });
  useEffect(() => {
    if (runtime) {
      runtime.updateNextFrame();
    }
  }, [runtime, isEditing]);
  const onPointerMoveCallback = useCallback((e2) => {
    var _a;
    if (!runtime || !canvasPosition || runtime.mode !== "sketch")
      return;
    const { top, left } = canvasPosition;
    const position = runtime.viewerToWorld(e2.pageX - left, e2.pageY - top);
    const box = portalRef.current;
    const alt = !props2.maintainAspectRatio && keys2.current.alt;
    const shift = !alt && keys2.current.shift && ((_a = resizeMode.current) == null ? void 0 : _a.indexOf("-")) !== -1;
    if (resizeMode.current === "translate" || resizeMode.current === "east" || resizeMode.current === "north-east" || resizeMode.current === "south-east") {
      cardinalDeltas.current.east = position.x - (mouseStart.current ? mouseStart.current.x : 0);
      if (alt) {
        cardinalDeltas.current.west = -cardinalDeltas.current.east;
      }
    }
    if (resizeMode.current === "translate" || resizeMode.current === "west" || resizeMode.current === "north-west" || resizeMode.current === "south-west") {
      cardinalDeltas.current.west = position.x - (mouseStart.current ? mouseStart.current.x : 0);
      if (alt) {
        cardinalDeltas.current.east = -cardinalDeltas.current.west;
      }
    }
    if (resizeMode.current === "translate" || resizeMode.current === "north" || resizeMode.current === "north-east" || resizeMode.current === "north-west") {
      cardinalDeltas.current.north = position.y - (mouseStart.current ? mouseStart.current.y : 0);
      if (alt) {
        cardinalDeltas.current.south = -cardinalDeltas.current.north;
      }
    }
    if (resizeMode.current === "translate" || resizeMode.current === "south" || resizeMode.current === "south-west" || resizeMode.current === "south-east") {
      cardinalDeltas.current.south = position.y - (mouseStart.current ? mouseStart.current.y : 0);
      if (alt) {
        cardinalDeltas.current.north = -cardinalDeltas.current.south;
      }
    }
    if (props2.maintainAspectRatio || shift) {
      constrainToAspectRatio(cardinalDeltas.current);
    }
    if (box) {
      const dX1 = cardinalDeltas.current.west;
      const dY1 = cardinalDeltas.current.north;
      const dX2 = props2.width + cardinalDeltas.current.east;
      const dY2 = props2.height + cardinalDeltas.current.south;
      box.points[1] = Math.min(dX1, dX2);
      box.points[2] = Math.min(dY1, dY2);
      box.points[3] = Math.max(dX1, dX2);
      box.points[4] = Math.max(dY1, dY2);
      runtime.updateNextFrame();
    }
  }, [runtime, props2.width, props2.height, props2.maintainAspectRatio, canvasPosition]);
  useWorldEvent("mousemove", onPointerMoveCallback, [props2.width, props2.height, canvasPosition]);
  useWorldEvent("pointermove", onPointerMoveCallback, [props2.width, props2.height, canvasPosition]);
  const windowPointerUp = useRef();
  useEffect(() => {
    windowPointerUp.current = () => {
      if (isEditingRef.current) {
        const dX1 = cardinalDeltas.current.west;
        const dY1 = cardinalDeltas.current.north;
        const dX2 = props2.width + cardinalDeltas.current.east;
        const dY2 = props2.height + cardinalDeltas.current.south;
        const x1 = Math.min(dX1, dX2);
        const y1 = Math.min(dY1, dY2);
        const x2 = Math.max(dX1, dX2);
        const y2 = Math.max(dY1, dY2);
        const realSize = {
          x: (props2.x || 0) + x1,
          y: (props2.y || 0) + y1,
          width: x2 - x1 || 1,
          height: y2 - y1 || 1
        };
        if (props2.maintainAspectRatio) {
          onSave(realSize);
        } else {
          onSave(realSize);
        }
        resizeMode.current = void 0;
        mouseStart.current = void 0;
        cardinalDeltas.current.east = 0;
        cardinalDeltas.current.west = 0;
        cardinalDeltas.current.north = 0;
        cardinalDeltas.current.south = 0;
        isEditingRef.current = false;
        setIsEditing(false);
      }
    };
  }, [onSave, props2.height, props2.width, props2.x, props2.y]);
  useEffect(() => {
    const cb = () => {
      if (windowPointerUp.current) {
        windowPointerUp.current();
      }
    };
    window.addEventListener("pointerup", cb);
    window.addEventListener("touchend", cb);
    return () => {
      window.removeEventListener("pointerup", cb);
      window.removeEventListener("touchend", cb);
    };
  }, []);
  return {
    portalRef,
    mode,
    mouseEvent,
    onPointerMoveCallback,
    isEditing
  };
};
function ResizeWorldItem({
  handleSize: _handleSize,
  resizable,
  onSave,
  children,
  maintainAspectRatio,
  disableCardinalControls,
  ...props2
}) {
  const handleSize = typeof _handleSize === "undefined" ? maintainAspectRatio ? 10 : 8 : _handleSize;
  const { portalRef, mode, mouseEvent, isEditing } = useResizeWorldItem({ x: props2.x || 0, y: props2.y || 0, width: props2.width, height: props2.height, maintainAspectRatio }, onSave);
  const translate2 = useMemo(() => mouseEvent("translate"), [mouseEvent]);
  const east = useMemo(() => mouseEvent("east"), [mouseEvent]);
  const west = useMemo(() => mouseEvent("west"), [mouseEvent]);
  const south = useMemo(() => mouseEvent("south"), [mouseEvent]);
  const north = useMemo(() => mouseEvent("north"), [mouseEvent]);
  const southEast = useMemo(() => mouseEvent("south-east"), [mouseEvent]);
  const southWest = useMemo(() => mouseEvent("south-west"), [mouseEvent]);
  const northEast = useMemo(() => mouseEvent("north-east"), [mouseEvent]);
  const northWest = useMemo(() => mouseEvent("north-west"), [mouseEvent]);
  const inSketchMode = mode === "sketch";
  const baseStyle = {
    zIndex: 999,
    boxShadow: "0px 2px 3px 0 rgba(0,0,0,0.2)",
    border: "1px solid rgba(155,155,155,.7)",
    borderRadius: maintainAspectRatio || disableCardinalControls ? "50%" : 2,
    position: "absolute",
    background: "#fff",
    pointerEvents: isEditing ? "none" : inSketchMode ? "initial" : "none"
  };
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement("world-object", {
    ...props2
  }, children, inSketchMode && resizable ? /* @__PURE__ */ React__default.createElement(HTMLPortal, {
    ref: portalRef,
    target: { x: 0, y: 0, height: props2.height, width: props2.width },
    relative: true,
    interactive: false
  }, inSketchMode && resizable ? /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement("div", {
    onMouseDown: translate2,
    onTouchStart: translate2,
    style: {
      display: "block",
      width: "100%",
      height: "100%",
      position: "relative",
      border: "1px solid rgba(155,155,155, .7)",
      boxSizing: "border-box",
      pointerEvents: isEditing ? "none" : inSketchMode ? "initial" : "none"
    }
  }), !maintainAspectRatio ? /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement("div", {
    title: "east",
    onTouchStart: east,
    onMouseDown: east,
    style: {
      ...baseStyle,
      cursor: "e-resize",
      height: handleSize * 2,
      width: handleSize,
      right: 0,
      top: "50%",
      opacity: disableCardinalControls ? 0 : 1,
      transform: `translate(${handleSize / 2}px, -${handleSize}px)`
    }
  }), /* @__PURE__ */ React__default.createElement("div", {
    title: "west",
    onMouseDown: west,
    style: {
      ...baseStyle,
      cursor: "w-resize",
      position: "absolute",
      height: handleSize * 2,
      width: handleSize,
      left: 0,
      top: "50%",
      opacity: disableCardinalControls ? 0 : 1,
      transform: `translate(-${handleSize / 2}px, -${handleSize}px)`
    }
  }), /* @__PURE__ */ React__default.createElement("div", {
    title: "north",
    onMouseDown: north,
    style: {
      ...baseStyle,
      cursor: "n-resize",
      position: "absolute",
      height: handleSize,
      width: handleSize * 2,
      left: "50%",
      top: 0,
      opacity: disableCardinalControls ? 0 : 1,
      transform: `translate(-${handleSize}px, -${handleSize / 2}px)`
    }
  }), /* @__PURE__ */ React__default.createElement("div", {
    title: "south",
    onMouseDown: south,
    style: {
      ...baseStyle,
      cursor: "s-resize",
      position: "absolute",
      height: handleSize,
      width: handleSize * 2,
      left: "50%",
      bottom: 0,
      opacity: disableCardinalControls ? 0 : 1,
      transform: `translate(-${handleSize}px, ${handleSize / 2}px)`
    }
  })) : null, /* @__PURE__ */ React__default.createElement("div", {
    title: "north-east",
    onMouseDown: northEast,
    style: {
      ...baseStyle,
      cursor: "ne-resize",
      position: "absolute",
      height: handleSize,
      width: handleSize,
      right: 0,
      top: 0,
      transform: `translate(${handleSize / 2}px, -${handleSize / 2}px)`
    }
  }), /* @__PURE__ */ React__default.createElement("div", {
    title: "south-east",
    onMouseDown: southEast,
    style: {
      ...baseStyle,
      cursor: "se-resize",
      position: "absolute",
      height: handleSize,
      width: handleSize,
      bottom: 0,
      right: 0,
      transform: `translate(${handleSize / 2}px, ${handleSize / 2}px)`
    }
  }), /* @__PURE__ */ React__default.createElement("div", {
    title: "south-west",
    onMouseDown: southWest,
    style: {
      ...baseStyle,
      cursor: "sw-resize",
      position: "absolute",
      height: handleSize,
      width: handleSize,
      bottom: 0,
      left: 0,
      transform: `translate(-${handleSize / 2}px, ${handleSize / 2}px)`
    }
  }), /* @__PURE__ */ React__default.createElement("div", {
    title: "north-west",
    onMouseDown: northWest,
    style: {
      ...baseStyle,
      cursor: "nw-resize",
      position: "absolute",
      height: handleSize,
      width: handleSize,
      top: 0,
      left: 0,
      transform: `translate(-${handleSize / 2}px, -${handleSize / 2}px)`
    }
  })) : null) : null));
}
function RegionHighlight({
  interactive,
  region,
  onClick,
  onSave,
  maintainAspectRatio,
  disableCardinalControls,
  isEditing,
  rotation,
  style = { backgroundColor: "rgba(0,0,0,.5)" }
}) {
  const saveCallback = useCallback((bounds) => {
    onSave({ id: region.id, x: region.x, y: region.y, height: region.height, width: region.width, ...bounds });
  }, [onSave, region.id, region.x, region.y, region.height, region.width]);
  return /* @__PURE__ */ React__default.createElement(ResizeWorldItem, {
    x: region.x,
    y: region.y,
    rotation,
    width: region.width,
    height: region.height,
    resizable: isEditing,
    onSave: saveCallback,
    maintainAspectRatio,
    disableCardinalControls
  }, /* @__PURE__ */ React__default.createElement("box", {
    interactive,
    onClick: (e2) => {
      e2.preventDefault();
      e2.stopPropagation();
      onClick(region);
    },
    target: { x: 0, y: 0, width: region.width, height: region.height },
    style
  }));
}
const TileSet = (props2) => {
  var _a, _b, _c, _d, _e2;
  const scale2 = props2.width / (((_a = props2.crop) == null ? void 0 : _a.width) || props2.tiles.width);
  const tiles = props2.tiles.imageService.tiles || [];
  const sizes = props2.tiles.imageService.sizes || [];
  const enableThumbnail = props2.enableThumbnail;
  const enableSizes = props2.enableSizes;
  const canonicalId = useMemo(() => {
    const id = props2.tiles.imageService.id || props2.tiles.imageService["@id"];
    if (id && id.endsWith("/info.json")) {
      return id.slice(0, -1 * "/info.json".length);
    }
    return id;
  }, [props2.tiles.imageService.id]);
  return /* @__PURE__ */ React__default.createElement("world-object", {
    rotation: props2.rotation,
    key: props2.tiles.imageService.id,
    scale: scale2,
    height: ((_b = props2.crop) == null ? void 0 : _b.height) || props2.tiles.height,
    width: ((_c = props2.crop) == null ? void 0 : _c.width) || props2.tiles.width,
    x: props2.x,
    y: props2.y,
    onClick: props2.onClick
  }, /* @__PURE__ */ React__default.createElement("composite-image", {
    key: props2.tiles.imageService.id,
    id: props2.tiles.imageService.id,
    width: ((_d = props2.crop) == null ? void 0 : _d.width) || props2.tiles.width,
    height: ((_e2 = props2.crop) == null ? void 0 : _e2.height) || props2.tiles.height,
    crop: props2.crop
  }, enableThumbnail && props2.tiles.thumbnail ? /* @__PURE__ */ React__default.createElement("world-image", {
    priority: true,
    uri: props2.tiles.thumbnail.id,
    target: { width: props2.tiles.width, height: props2.tiles.height },
    display: { width: props2.tiles.thumbnail.width, height: props2.tiles.thumbnail.height },
    crop: props2.crop
  }) : null, enableSizes && sizes.map((size, n2) => /* @__PURE__ */ React__default.createElement("world-image", {
    key: n2,
    uri: `${canonicalId}/full/${size.width},${size.height}/0/default.jpg`,
    target: { width: props2.tiles.width, height: props2.tiles.height },
    display: { width: size.width, height: size.height },
    crop: props2.crop
  })), tiles.map((tile) => (tile.scaleFactors || []).map((size) => {
    return /* @__PURE__ */ React__default.createElement("tiled-image", {
      key: `${props2.tiles.imageService.id}-tile-${size}`,
      uri: props2.tiles.imageService.id,
      display: { width: props2.tiles.width, height: props2.tiles.height },
      tile,
      scaleFactor: size,
      crop: props2.crop
    });
  }))));
};
function getId(entity) {
  return entity.id || entity["@id"];
}
function g(i2) {
  return i2.endsWith("info.json") ? i2 : i2.endsWith("/") ? `${i2}info.json` : `${i2}/info.json`;
}
const Y = "http://library.stanford.edu/iiif/image-api/compliance.html#level0", F = "http://library.stanford.edu/iiif/image-api/compliance.html#level1", v = "http://library.stanford.edu/iiif/image-api/compliance.html#level2", q = "http://library.stanford.edu/iiif/image-api/conformance.html#level0", O = "http://library.stanford.edu/iiif/image-api/conformance.html#level1", z = "http://library.stanford.edu/iiif/image-api/conformance.html#level2", ee = "http://library.stanford.edu/iiif/image-api/1.1/compliance.html#level0", E = "http://library.stanford.edu/iiif/image-api/1.1/compliance.html#level1", A = "http://library.stanford.edu/iiif/image-api/1.1/compliance.html#level2", ie = "http://library.stanford.edu/iiif/image-api/1.1/conformance.html#level0", b$1 = "http://library.stanford.edu/iiif/image-api/1.1/conformance.html#level1", L = "http://library.stanford.edu/iiif/image-api/1.1/conformance.html#level2", te = "http://iiif.io/api/image/1/level0.json", re = "http://iiif.io/api/image/1/profiles/level0.json", M = "http://iiif.io/api/image/1/level1.json", W = "http://iiif.io/api/image/1/profiles/level1.json", j = "http://iiif.io/api/image/1/level2.json", P = "http://iiif.io/api/image/1/profiles/level2.json", ne = "http://iiif.io/api/image/2/level0.json", se = "http://iiif.io/api/image/2/profiles/level0.json", C = "http://iiif.io/api/image/2/level1.json", B = "http://iiif.io/api/image/2/profiles/level1.json", N = "http://iiif.io/api/image/2/level2.json", R$1 = "http://iiif.io/api/image/2/profiles/level2.json", ae = "level0", $ = "level1", H = "level2", oe = "http://iiif.io/api/image/2/level0", G = "http://iiif.io/api/image/2/level1", T = "http://iiif.io/api/image/2/level2", k = [T, v, z, A, L, j, P, N, R$1, H], Q = [...k, G, F, O, E, b$1, M, W, C, B, $], V = [oe, G, T, Y, F, v, q, O, z, ee, E, A, ie, b$1, L, te, re, M, W, j, P, ne, se, C, B, N, R$1, ae, $, H], fe = { extraFormats: ["jpg"], extraQualities: ["default"], extraFeatures: ["sizeByWhListed"] }, le = { extraFormats: ["jpg"], extraQualities: ["default"], extraFeatures: ["baseUriRedirect", "cors", "jsonldMediaType", "regionByPx", "regionSquare", "sizeByWhListed", "sizeByH", "sizeByW", "sizeByWh"] }, he = { extraFormats: ["jpg", "png"], extraQualities: ["default"], extraFeatures: ["baseUriRedirect", "cors", "jsonldMediaType", "regionByPct", "regionByPx", "regionSquare", "rotationBy90s", "sizeByWhListed", "sizeByConfinedWh", "sizeByH", "sizeByPct", "sizeByW", "sizeByWh"] };
function ue(i2) {
  return k.indexOf(i2) !== -1 ? he : Q.indexOf(i2) !== -1 ? le : fe;
}
function U(i2) {
  const e2 = i2 ? Array.isArray(i2.profile) ? i2.profile : [i2.profile] : [], t2 = { extraQualities: [], extraFormats: [], extraFeatures: [] };
  for (let r2 of e2)
    if (typeof r2 == "string" && (r2 = ue(r2)), !!r2) {
      if (r2.formats)
        for (const n2 of r2.formats)
          t2.extraFormats.indexOf(n2) === -1 && t2.extraFormats.push(n2);
      if (r2.qualities)
        for (const n2 of r2.qualities)
          t2.extraQualities.indexOf(n2) === -1 && t2.extraQualities.push(n2);
      if (r2.supports)
        for (const n2 of r2.supports)
          t2.extraFeatures.indexOf(n2) === -1 && t2.extraFeatures.push(n2);
      if (r2.maxHeight && (t2.maxHeight = r2.maxHeight), r2.maxWidth && (t2.maxWidth = r2.maxWidth), r2.maxArea && (t2.maxArea = r2.maxArea), r2.extraFormats)
        for (const n2 of r2.extraFormats)
          t2.extraFormats.indexOf(n2) === -1 && t2.extraFormats.push(n2);
      if (r2.extraQualities)
        for (const n2 of r2.extraQualities)
          t2.extraQualities.indexOf(n2) === -1 && t2.extraQualities.push(n2);
      if (r2.extraFeatures)
        for (const n2 of r2.extraFeatures)
          t2.extraFeatures.indexOf(n2) === -1 && t2.extraFeatures.push(n2);
      r2.maxHeight && (t2.maxHeight = r2.maxHeight), r2.maxWidth && (t2.maxWidth = r2.maxWidth), r2.maxArea && (t2.maxArea = r2.maxArea);
    }
  if (i2.extraFormats)
    for (const r2 of i2.extraFormats)
      t2.extraFormats.indexOf(r2) === -1 && t2.extraFormats.push(r2);
  if (i2.extraFeatures)
    for (const r2 of i2.extraFeatures)
      t2.extraFeatures.indexOf(r2) === -1 && t2.extraFeatures.push(r2);
  if (i2.extraQualities)
    for (const r2 of i2.extraQualities)
      t2.extraQualities.indexOf(r2) === -1 && t2.extraQualities.push(r2);
  return t2;
}
function ce(i2) {
  try {
    if (i2 === "full")
      return { full: true };
    if (i2 === "square")
      return { square: true };
    const e2 = i2.startsWith("pct:"), r2 = i2.substr(e2 ? 4 : 0).split(",").map((n2) => parseFloat(n2));
    return { x: r2[0], y: r2[1], w: r2[2], h: r2[3], percent: e2 };
  } catch {
    throw new Error("Expected 'full', 'square' or 'x,y,w,h'. Found " + i2);
  }
}
function de(i2) {
  const e2 = { upscaled: false, max: false, confined: false };
  if (i2[0] === "^" && (e2.upscaled = true, i2 = i2.slice(1)), i2 === "max" || i2 === "full")
    return e2.max = true, e2.serialiseAsFull = i2 === "full", e2;
  if (i2[0] === "!" && (e2.confined = true, i2 = i2.slice(1)), i2[0] === "p")
    return e2.percentScale = parseFloat(i2.slice(4)), e2;
  const t2 = i2.split(",").map((r2) => r2.trim());
  return t2.length && (t2[0] !== "" && (e2.width = parseInt(t2[0], 10)), t2[1] !== "" && (e2.height = parseInt(t2[1], 10))), e2;
}
function ge(i2) {
  const e2 = { angle: 0 };
  if (i2[0] === "!" && (e2.mirror = true, i2 = i2.substr(1)), e2.angle = parseFloat(i2) % 360, Number.isNaN(e2.angle))
    throw new Error(`Invalid rotation ${i2}`);
  return e2;
}
function me(i2, e2 = "") {
  const t2 = i2.match(/^(([a-zA-Z]+):\/\/([^/]+))?((.*)+)/);
  if (!t2)
    throw new Error(`Invalid or unknown input ${i2}`);
  const r2 = t2[2], n2 = t2[3];
  let s2 = t2[4];
  if (s2[0] === "/" && (s2 = s2.substr(1)), e2.length > 0) {
    if (e2[0] === "/" && (e2 = e2.substr(1)), e2 !== s2.substr(0, e2.length))
      throw new Error(`Path does not start with prefix (path: ${s2}, prefix: ${e2})`);
    s2 = s2.substr(e2.length);
  }
  return { scheme: r2, server: n2, path: s2, prefix: e2 };
}
function pe(i2, e2 = "") {
  const { path: t2, scheme: r2, server: n2, prefix: s2 } = me(i2, e2), a2 = t2.split("/").reverse(), [o2, l2, f2, u2, ...I2] = a2, d2 = I2.reverse().filter(Boolean).join("/");
  if (a2.length === 1 || o2 === "")
    return { type: "base", scheme: r2, server: n2, prefix: s2, identifier: d2 };
  if (o2 === "info.json") {
    const [, ...m2] = a2;
    return { type: "info", scheme: r2, server: n2, prefix: s2, identifier: m2.reverse().filter(Boolean).join("/") };
  }
  const c6 = o2.split(".");
  return { type: "image", scheme: r2, server: n2, prefix: s2, identifier: d2, originalPath: t2, region: ce(u2), size: de(f2), rotation: ge(l2), quality: c6[0], format: c6[1] };
}
function xe(i2) {
  const e2 = pe(g(i2.id));
  if (e2.type !== "info")
    throw new Error("Invalid service URL");
  const t2 = U(i2);
  return { identifier: e2.identifier, originalPath: "", server: e2.server, prefix: e2.prefix, scheme: e2.scheme, type: "image", quality: t2.extraQualities.indexOf("default") === -1 ? t2.extraQualities[0] : "default", region: { full: true }, size: { max: true, upscaled: false, confined: false }, format: "jpg", rotation: { angle: 0 } };
}
function Ie(i2, e2, t2) {
  const r2 = t2.length, n2 = [];
  for (let s2 = 0; s2 < r2; s2++) {
    const o2 = t2[s2].width;
    n2.push(i2 / o2);
  }
  return n2;
}
function _e(i2, e2, t2) {
  const r2 = t2.length, n2 = [];
  for (let s2 = 0; s2 < r2; s2++) {
    const a2 = t2[s2];
    n2.push({ width: Math.floor(i2 / a2), height: Math.floor(e2 / a2) });
  }
  return n2;
}
function h(i2) {
  if (i2["@id"])
    return i2["@id"];
  if (i2.id)
    return i2.id;
}
function _(i2) {
  if (!i2 || !i2.profile || !h(i2))
    return false;
  const e2 = Array.isArray(i2.profile) ? i2.profile : [i2.profile];
  for (const t2 of e2)
    if (typeof t2 == "string" && V.indexOf(t2) !== -1)
      return true;
  return false;
}
function ye(i2) {
  if (!_(i2))
    return false;
  const e2 = Array.isArray(i2.profile) ? i2.profile : [i2.profile];
  for (const t2 of e2)
    if (typeof t2 == "string") {
      if (Q.indexOf(t2) !== -1)
        return true;
    } else {
      const r2 = [...t2.supports || [], ...t2.extraFeatures || []];
      if (r2.indexOf("regionByPx") !== -1 && (r2.indexOf("sizeByW") !== -1 || r2.indexOf("sizeByWh") !== -1))
        return true;
    }
  return false;
}
function we(i2) {
  if (!ye(i2))
    return [];
  const e2 = [], t2 = Array.isArray(i2.profile) ? i2.profile : [i2.profile], r2 = t2.length;
  for (let n2 = 0; n2 < r2; n2++) {
    const s2 = t2[n2];
    if (typeof s2 != "string" && (s2.maxHeight || s2.maxWidth))
      return [{ id: h(i2), type: "variable", minWidth: 0, minHeight: 0, maxHeight: s2.maxHeight || s2.maxWidth, maxWidth: s2.maxWidth || s2.maxHeight }];
  }
  if (i2.tiles) {
    const n2 = i2.tiles.length;
    for (let s2 = 0; s2 < n2; s2++) {
      const a2 = i2.tiles[s2];
      (a2.height || a2.width) && e2.push({ id: h(i2), type: "variable", minHeight: 0, minWidth: 0, maxHeight: a2.height || a2.width, maxWidth: a2.width });
    }
  }
  return e2;
}
function J(i2) {
  const e2 = /^.*\/(full)\/(((\d+),(\d+)?)|max)\/(\d+)\/default\.(jpg|png|jpeg)$/, t2 = i2.match(e2);
  if (t2) {
    const r2 = t2[1], n2 = parseInt(t2[4], 10), s2 = parseInt(t2[5], 10), a2 = t2[7];
    if ((r2 === "max" || r2 === "full") && n2 && s2 && a2)
      return { type: "fixed", id: i2, height: s2, width: n2 };
  }
  return { type: "unknown", id: i2 };
}
function Se(i2) {
  if (i2["@type"])
    return i2["@type"];
  if (i2.type)
    return i2.type;
}
function Fe(i2) {
  if (typeof i2 == "string")
    return J(i2);
  const e2 = Se(i2);
  if (e2 !== "Image" && e2 !== "sc:Image")
    return null;
  const t2 = i2, r2 = h(t2);
  return r2 ? r2 && t2.width && t2.height ? { id: r2, type: "fixed", width: t2.width, height: t2.height, unsafe: true } : J(r2) : null;
}
function ve(i2) {
  return _(i2) ? (i2 && i2.sizes ? i2.sizes : []).map((e2) => ({ id: h(i2), type: "fixed-service", height: e2.height, width: e2.width })) : [];
}
function Z(i2) {
  const e2 = [], t2 = i2.length;
  for (let r2 = 0; r2 < t2; r2++) {
    const n2 = ve(i2[r2]);
    n2.length && e2.push(...n2);
    const s2 = we(i2[r2]);
    s2.length && e2.push(...s2);
  }
  return e2;
}
function K(i2) {
  const e2 = i2.service ? Array.isArray(i2.service) ? i2.service : [i2.service] : [], t2 = e2.length, r2 = [];
  for (let n2 = 0; n2 < t2; n2++)
    _(e2[n2]) && r2.push(e2[n2]);
  return r2;
}
function Oe(i2, e2 = true, t2) {
  const r2 = [], n2 = Fe(i2);
  if (n2 === null)
    return r2;
  const s2 = i2;
  if (r2.push(n2), e2 && s2.width && s2.height) {
    const a2 = [], o2 = K(s2);
    for (const l2 of o2) {
      const f2 = { id: h(l2), width: s2.width, height: s2.height };
      if (t2.canLoadSync(f2)) {
        const u2 = t2.loadServiceSync(f2);
        u2 && (u2.height || (u2.height = s2.height), u2.width || (u2.width = s2.width), a2.push(...Z([u2])));
      }
    }
    if (a2.length)
      return r2.push(...a2), r2;
  }
  return s2.service && r2.push(...Z(s2.service)), r2;
}
function ze({ x: i2 = 0, y: e2 = 0, w: t2, h: r2, full: n2, square: s2, percent: a2 }) {
  if (n2)
    return "full";
  if (s2)
    return "square";
  if (typeof t2 > "u" || typeof r2 > "u")
    throw new Error("RegionParameter: invalid region");
  const o2 = `${i2},${e2},${t2},${r2}`;
  return a2 ? `pct:${o2}` : o2;
}
function Ee({ max: i2, percentScale: e2, upscaled: t2, confined: r2, width: n2, height: s2, serialiseAsFull: a2 }) {
  const o2 = [];
  return t2 && o2.push("^"), i2 ? (o2.push(a2 ? "full" : "max"), o2.join("")) : (r2 && o2.push("!"), e2 && o2.push(`pct:${e2}`), n2 && o2.push(`${n2}`), o2.push(","), s2 && o2.push(`${s2}`), o2.join(""));
}
function Ae(i2) {
  return `${i2.mirror ? "!" : ""}${(i2.angle || 0) % 360}`;
}
var He = Object.defineProperty, Ge = Object.defineProperties, Te = Object.getOwnPropertyDescriptors, be = Object.getOwnPropertySymbols, ke = Object.prototype.hasOwnProperty, Qe = Object.prototype.propertyIsEnumerable, Le = (i2, e2, t2) => e2 in i2 ? He(i2, e2, { enumerable: true, configurable: true, writable: true, value: t2 }) : i2[e2] = t2, y$1 = (i2, e2) => {
  for (var t2 in e2 || (e2 = {}))
    ke.call(e2, t2) && Le(i2, t2, e2[t2]);
  if (be)
    for (var t2 of be(e2))
      Qe.call(e2, t2) && Le(i2, t2, e2[t2]);
  return i2;
}, w = (i2, e2) => Ge(i2, Te(e2));
function Me(i2, e2) {
  const t2 = i2.prefix.startsWith("/") ? i2.prefix.substr(1) : i2.prefix, r2 = `${i2.scheme}://${i2.server}/${t2 ? `${t2}/` : ""}${i2.identifier}`;
  if (i2.type === "base")
    return r2;
  if (i2.type === "info")
    return `${r2}/info.json`;
  let { region: n2, size: s2, rotation: a2, format: o2, quality: l2 } = i2;
  if (e2) {
    const f2 = e2["@context"] ? Array.isArray(e2["@context"]) ? e2["@context"] : [e2["@context"]] : [], u2 = f2.indexOf("http://iiif.io/api/image/2/context.json") !== -1, I2 = f2.indexOf("http://iiif.io/api/image/3/context.json") !== -1;
    if ((s2.width === e2.width && !s2.height || s2.height === e2.height && !s2.width || s2.width === e2.width && s2.height === e2.height) && (s2 = w(y$1({}, s2), { max: true })), u2 && (s2.max && !s2.serialiseAsFull && (s2 = w(y$1({}, s2), { serialiseAsFull: true })), !s2.max && s2.width && s2.height && (s2 = w(y$1({}, s2), { height: void 0 }))), I2 && (s2.max && s2.serialiseAsFull && (s2 = w(y$1({}, s2), { serialiseAsFull: false })), s2.width && !s2.height && e2.width && e2.height)) {
      const d2 = e2.height / e2.width;
      s2 = w(y$1({}, s2), { height: Math.ceil(s2.width * d2) });
    }
  }
  return [r2, ze(n2), Ee(s2), Ae(a2), `${l2}.${o2}`].filter(Boolean).join("/");
}
function S(i2, e2, t2) {
  const r2 = xe({ id: g(h(i2)), profile: "level2", type: "ImageService2" });
  if (r2.type !== "image")
    throw new Error("Invalid service");
  return r2.size.max = false, r2.size.width = e2, r2.size.height = t2, { id: Me(r2), type: "fixed", width: e2, height: t2 || i2.height / (i2.width || 1) * e2, unsafe: i2.width > e2 };
}
function p(i2) {
  const e2 = i2.replace(/(https?:\/\/)?(www.)?/i, "");
  return e2.indexOf("/") !== -1 ? e2.split("/")[0] : e2;
}
function We(i2, e2, t2) {
  const r2 = i2.width ? i2.width : i2.maxWidth;
  return t2.height <= i2.maxHeight && t2.width <= i2.maxWidth && t2.height >= i2.minHeight && t2.width >= i2.minWidth && (!e2 || Math.abs(t2.width - r2) < Math.abs(e2.width - r2));
}
function je(i2, e2) {
  const t2 = [], r2 = Object.assign({ unsafeImageService: false, atAnyCost: true, fallback: true, minHeight: 64, minWidth: 64, maxHeight: 1 / 0, maxWidth: 1 / 0, returnAllOptions: false, preferFixedSize: false, allowUnsafe: false, explain: false, height: 0, width: 0 }, i2), n2 = [], s2 = [];
  let a2 = null;
  const o2 = (f2, u2) => {
    if (We(r2, u2, f2)) {
      if (r2.preferFixedSize && f2.unsafe) {
        s2.push(f2);
        return;
      }
      r2.returnAllOptions && u2 && s2.push(u2), a2 = f2;
    } else
      r2.returnAllOptions && s2.push(f2);
  }, l2 = e2.length;
  for (let f2 = 0; f2 < l2; f2++) {
    const u2 = e2[f2](), I2 = u2.length;
    for (let d2 = 0; d2 < I2; d2++) {
      const c6 = u2[d2];
      if (c6.type === "unknown" && r2.atAnyCost && n2.push(c6), c6.type === "fixed" && (c6.unsafe ? n2.push(c6) : o2(c6, a2)), c6.type === "fixed-service")
        if (r2.unsafeImageService) {
          const m2 = S(c6, r2.width, r2.height);
          o2(m2, a2);
        } else {
          const m2 = S(c6, c6.width, c6.height);
          o2(m2, a2);
        }
      if (c6.type === "variable" && c6.maxWidth) {
        const m2 = S({ id: c6.id, type: "fixed-service", width: c6.maxWidth, height: c6.maxWidth }, c6.maxWidth);
        o2(m2, a2);
      }
    }
    if (a2 && !r2.returnAllOptions) {
      if (a2.unsafe || r2.allowUnsafe)
        continue;
      break;
    }
  }
  return r2.atAnyCost && s2.length === 0 ? { best: a2 || n2[0], fallback: n2.slice(1), log: t2 } : r2.returnAllOptions ? { best: r2.atAnyCost ? a2 || s2[0] || n2[0] : a2 || s2[0], fallback: [...s2, ...n2], log: t2 } : { best: a2 || s2[0] || null, fallback: a2 ? s2 : s2.slice(1), log: t2 };
}
var Je = Object.defineProperty, Ze = Object.defineProperties, Ke = Object.getOwnPropertyDescriptors, Pe = Object.getOwnPropertySymbols, Xe = Object.prototype.hasOwnProperty, Ye = Object.prototype.propertyIsEnumerable, Ce = (i2, e2, t2) => e2 in i2 ? Je(i2, e2, { enumerable: true, configurable: true, writable: true, value: t2 }) : i2[e2] = t2, qe = (i2, e2) => {
  for (var t2 in e2 || (e2 = {}))
    Xe.call(e2, t2) && Ce(i2, t2, e2[t2]);
  if (Pe)
    for (var t2 of Pe(e2))
      Ye.call(e2, t2) && Ce(i2, t2, e2[t2]);
  return i2;
}, ei = (i2, e2) => Ze(i2, Ke(e2));
function Be(i2, e2, t2) {
  const r2 = i2 > e2 ? i2 : e2, n2 = t2.length, s2 = [];
  for (let a2 = 0; a2 < n2; a2++) {
    const o2 = t2[a2];
    let l2 = o2.scaleFactors[0], f2 = r2 / l2;
    const u2 = [l2];
    for (; f2 >= o2.width; )
      l2 = l2 * 2, u2.push(l2), f2 = f2 / 2;
    s2.push(ei(qe({}, o2), { scaleFactors: u2 }));
  }
  return s2;
}
function Ne(i2, e2) {
  if (i2.length !== e2.length)
    return false;
  if (i2.length === 0 && e2.length === 0)
    return true;
  const t2 = i2.length;
  let r2 = true;
  for (let s2 = 0; s2 < t2; s2++) {
    const a2 = i2[s2], o2 = e2[s2];
    if (a2.width !== o2.width || a2.height !== o2.height) {
      r2 = false;
      break;
    }
  }
  if (r2)
    return true;
  let n2 = 0;
  for (let s2 = 0; s2 < t2; s2++)
    for (let a2 = 0; a2 < t2; a2++)
      if (i2[s2].width === e2[a2].width && i2[s2].height === e2[a2].height) {
        n2++;
        break;
      }
  return n2 === t2;
}
var x = (i2, e2, t2) => new Promise((r2, n2) => {
  var s2 = (l2) => {
    try {
      o2(t2.next(l2));
    } catch (f2) {
      n2(f2);
    }
  }, a2 = (l2) => {
    try {
      o2(t2.throw(l2));
    } catch (f2) {
      n2(f2);
    }
  }, o2 = (l2) => l2.done ? r2(l2.value) : Promise.resolve(l2.value).then(s2, a2);
  o2((t2 = t2.apply(i2, e2)).next());
});
class Re {
  constructor() {
    this.config = { verificationsRequired: 1, approximateServices: true, enableFetching: true, disableThrottling: false }, this.fetchingCount = 0, this.imageServices = {}, this.knownImageServers = {};
  }
  setConfig(e2) {
    Object.assign(this.config, e2);
  }
  sample(e2, t2, r2 = true) {
    const n2 = p(h(e2)), s2 = g(h(e2)), a2 = this.knownImageServers[n2];
    return this.imageServices[s2] = Object.assign(e2, { real: true }), !a2 && e2.tiles ? (this.knownImageServers[n2] = { verifications: 0, malformed: false, root: n2, preLoaded: r2, sampledId: h(e2), verified: false, server: null, result: { context: e2["@context"] || [], sampledProfile: e2.profile, resourceServiceRatio: t2 && e2.height ? t2.height / e2.height : 1, sampledSizes: e2.sizes || [], sizeRatios: Ie(e2.width, e2.height, e2.sizes || []), sampledTiles: e2.tiles || [] } }, true) : this.verify(e2);
  }
  preLoad(e2, t2 = true) {
    this.knownImageServers[e2.root] = e2, t2 && (this.knownImageServers[e2.root].malformed = false, this.knownImageServers[e2.root].verifications = this.config.verificationsRequired);
  }
  predict(e2, t2 = false, r2 = false) {
    const n2 = e2 == null ? void 0 : e2.source, s2 = p(h(e2)), a2 = this.knownImageServers[s2];
    if (!a2 || !a2.result || !r2 && (a2.malformed || a2.verifications < this.config.verificationsRequired))
      return null;
    const o2 = g(h(e2));
    return this.imageServices[o2] || (this.imageServices[o2] = { "@context": a2.result.context, "@id": h(e2), id: h(e2), protocol: "http://iiif.io/api/image", tiles: (n2 == null ? void 0 : n2.tiles) || Be(e2.width, e2.height, a2.result.sampledTiles), sizes: (n2 == null ? void 0 : n2.sizes) || _e(Math.round(e2.width / a2.result.resourceServiceRatio), Math.round(e2.height / a2.result.resourceServiceRatio), a2.result.sizeRatios), profile: (n2 == null ? void 0 : n2.profile) || a2.result.sampledProfile, height: (n2 == null ? void 0 : n2.height) || e2.height, width: (n2 == null ? void 0 : n2.width) || e2.width, real: false }), this.imageServices[o2];
  }
  getThumbnailFromResource(e2, t2) {
    return x(this, arguments, function* (r2, n2, s2 = true, a2 = []) {
      const o2 = yield this.getImageCandidates(r2, s2);
      return je(n2, [() => a2, () => o2]);
    });
  }
  getImageCandidates(e2, t2 = true) {
    return x(this, null, function* () {
      const r2 = e2;
      if (t2 && r2.height && r2.width) {
        const n2 = K(r2);
        for (const s2 of n2) {
          const a2 = { id: h(s2), width: s2.width ? s2.width : r2.width, height: s2.height ? s2.height : r2.height, source: s2 };
          yield this.loadService(a2);
        }
      }
      return Oe(e2, t2, this);
    });
  }
  verify(e2) {
    return x(this, null, function* () {
      const t2 = this.predict(e2, false, true), r2 = yield this.fetchService(h(e2));
      if (!t2)
        return false;
      const n2 = t2.height === r2.height && t2.width === r2.width && t2["@context"] === r2["@context"] && Ne(t2.sizes || [], r2.sizes || []);
      if (n2) {
        const s2 = p(h(e2));
        this.knownImageServers[s2].verifications += 1, this.knownImageServers[s2].verifications >= this.config.verificationsRequired && (this.knownImageServers[s2].verified = true);
      }
      return n2;
    });
  }
  canLoadSync(e2) {
    const t2 = typeof e2 == "string" ? e2 : h(e2), r2 = g(t2);
    if (this.imageServices[r2])
      return true;
    const n2 = this.knownImageServers[p(t2)];
    return n2 && !n2.malformed && n2.verifications >= this.config.verificationsRequired;
  }
  markAsMalformed(e2) {
    return x(this, null, function* () {
      return this.knownImageServers[p(h(e2))].malformed = true, this.loadService(e2, true);
    });
  }
  fetchService(e2, t2 = false) {
    return x(this, null, function* () {
      const r2 = g(e2);
      if (this.imageServices[r2] && (!t2 || this.imageServices[r2].real))
        return this.imageServices[r2];
      if (!this.config.enableFetching)
        throw new Error("Fetching is not enabled");
      const n2 = yield this.fetch(r2).then((s2) => s2.json());
      return !n2.id && n2["@id"] && (n2.id = n2["@id"]), n2.id !== e2 && (n2.id = e2, n2["@id"] && (n2["@id"] = e2)), this.imageServices[r2] = Object.assign(n2, { real: true }), this.imageServices[r2];
    });
  }
  fetch(e2, t2) {
    return x(this, null, function* () {
      return fetch(e2, t2);
    });
  }
  loadService(e2, t2 = false) {
    return x(this, null, function* () {
      if (!this.config.disableThrottling) {
        let s2 = true;
        for (; s2; )
          if (this.fetchingCount >= this.config.verificationsRequired)
            yield new Promise((a2) => setTimeout(a2, 500));
          else {
            s2 = false;
            break;
          }
      }
      const r2 = this.knownImageServers[p(h(e2))];
      if (r2 && !r2.malformed && !t2) {
        yield r2.result;
        const s2 = this.loadServiceSync(e2);
        if (s2)
          return s2;
      }
      this.fetchingCount++;
      const n2 = yield this.fetchService(h(e2), t2);
      return this.fetchingCount--, n2.real && this.sample(n2, e2), n2;
    });
  }
  loadServiceSync(e2) {
    const t2 = g(h(e2));
    return this.imageServices[t2] ? this.imageServices[t2] : this.predict(e2);
  }
}
new Re();
const m = {}, b = {
  get(i2) {
    return i2;
  },
  setMetaValue([i2, n2, o2], r2) {
    const c6 = b.getResourceMeta(i2, n2), s2 = c6 ? c6[o2] : void 0, e2 = typeof r2 == "function" ? r2(s2) : r2;
    m[i2] = {
      ...m[i2] || {},
      [n2]: {
        ...(m[i2] || {})[n2] || {},
        [o2]: e2
      }
    };
  },
  getResourceMeta: (i2, n2) => {
    const o2 = m[i2];
    if (o2)
      return n2 ? o2[n2] : o2;
  }
}, R = new Re();
function y(i2 = b, n2 = {}) {
  const o2 = n2.imageServiceLoader || R;
  async function r2(c6, s2, e2 = false, h2 = [], l2) {
    const g2 = () => o2.getThumbnailFromResource(void 0, s2, e2, h2);
    if (!c6)
      return await o2.getThumbnailFromResource(void 0, s2, e2, h2);
    if (typeof c6 == "string") {
      const t2 = Fe(c6);
      return t2 && h2.push(t2), await o2.getThumbnailFromResource(void 0, s2, e2, h2);
    }
    const a2 = i2.get(c6, { skipSelfReturn: false });
    if (typeof a2 == "string")
      return { best: Fe(a2), fallback: [], log: [] };
    if (!a2)
      return await g2();
    switch (await (async (t2) => {
      if (t2 && t2.thumbnail && t2.thumbnail.length) {
        const u2 = i2.get(t2.thumbnail[0]), f2 = await o2.getImageCandidates(u2, e2);
        f2 && f2.length && h2.push(...f2);
      }
    })(a2), a2.type) {
      case "Annotation": {
        const t2 = Array.isArray(a2.body) ? a2.body : [a2.body], u2 = i2.get(t2[0]);
        return l2 && !u2.width && (u2.width = l2.width, u2.height = l2.height), await o2.getThumbnailFromResource(u2, s2, e2, h2);
      }
      case "Canvas": {
        const t2 = a2;
        return r2(t2.items[0], s2, e2, h2, {
          width: t2.width,
          height: t2.height
        });
      }
      case "AnnotationPage":
        return r2(a2.items[0], s2, e2, h2, l2);
      case "Choice": {
        const t2 = a2;
        return !t2.items || t2.items[0] ? await g2() : r2(t2.items[0], s2, e2, h2, l2);
      }
      case "Collection": {
        const u2 = a2.items[0];
        return u2 ? r2(u2, s2, e2, h2, l2) : await g2();
      }
      case "Manifest": {
        const u2 = a2.items[0];
        return u2 ? r2(u2, s2, e2, h2, l2) : await g2();
      }
      case "SpecificResource":
      case "Image":
      case "Dataset":
      case "Sound":
      case "Text":
      case "TextualBody":
      case "Video":
        return l2 && !a2.width && (a2.width = l2.width, a2.height = l2.height), o2.getThumbnailFromResource(a2, s2, e2, h2);
    }
    return await g2();
  }
  return {
    getBestThumbnailAtSize: r2
  };
}
function getGlobal() {
  if (typeof self !== "undefined") {
    return self;
  }
  if (typeof window !== "undefined") {
    return window;
  }
  if (typeof global !== "undefined") {
    return global;
  }
  return {};
}
function getGlobalVault() {
  const g2 = getGlobal();
  if (typeof g2["IIIF_VAULT"] !== "undefined") {
    return g2["IIIF_VAULT"];
  }
  if (typeof g2.IIIFVault === "undefined") {
    throw new Error("Vault not found");
  }
  g2["IIIF_VAULT"] = new g2.IIIFVault.Vault();
  return g2["IIIF_VAULT"];
}
const moduleState = {};
function getVaultHelper() {
  if (!moduleState.helper) {
    moduleState.vault = getGlobalVault();
    moduleState.loader = new Re();
    moduleState.helper = y(moduleState.vault, { imageServiceLoader: moduleState.loader });
  }
  return moduleState;
}
async function getTileFromImageService(infoJsonId, width, height) {
  const { loader } = getVaultHelper();
  const imageService = await loader.loadService({
    id: infoJsonId,
    width,
    height
  });
  return {
    id: getId(imageService),
    width,
    height,
    imageService,
    thumbnail: void 0
  };
}
async function getTileFromCanvas(canvas, thumbnailSize = 512) {
  const { vault, loader, helper } = getVaultHelper();
  const tiles = [];
  for (const page of canvas.items) {
    for (const anno of vault.get(page).items) {
      const annotation = vault.get(vault.get(anno).body[0]);
      const serviceSnippet = annotation.service[0];
      const tile = await getTileFromImageService(serviceSnippet.id, canvas.width, canvas.height);
      const { best: thumbnail } = await helper.getBestThumbnailAtSize(vault, loader, canvas, {
        maxHeight: thumbnailSize,
        maxWidth: thumbnailSize
      }, true);
      if (thumbnail) {
        tile.thumbnail = thumbnail;
      }
      tiles.push(tile);
    }
  }
  return tiles;
}
async function getTilesFromManifest(manifest) {
  const { vault } = getVaultHelper();
  const tiles = [];
  for (const canvasRef of manifest.items) {
    const canvas = vault.get(canvasRef);
    tiles.push(...await getTileFromCanvas(canvas));
  }
  return tiles;
}
async function getTiles(manifestId) {
  try {
    const { vault } = getVaultHelper();
    const manifest = await vault.loadManifest(manifestId);
    if (!manifest) {
      return [];
    }
    return getTilesFromManifest(manifest);
  } catch (err) {
    console.log("ERR", err);
    return [];
  }
}
const ImageService = (props2) => {
  var _a, _b;
  const [tiles, setTile] = useState();
  useEffect(() => {
    getTileFromImageService(props2.id, props2.width, props2.height).then((s2) => {
      setTile(s2);
    });
  }, [props2.height, props2.id, props2.width]);
  return /* @__PURE__ */ React__default.createElement("world-object", {
    x: props2.x || 0,
    y: props2.y || 0,
    width: props2.width,
    height: props2.height,
    scale: props2.scale
  }, tiles ? /* @__PURE__ */ React__default.createElement(TileSet, {
    tiles,
    x: props2.x,
    y: props2.y,
    width: ((_a = props2.crop) == null ? void 0 : _a.width) || props2.width,
    height: ((_b = props2.crop) == null ? void 0 : _b.height) || props2.height,
    rotation: props2.rotation,
    crop: props2.crop
  }, props2.children) : null);
};
const useAfterPaint = (callback, deps = []) => {
  const runtime = useRuntime();
  useEffect(() => {
    if (runtime) {
      return runtime.registerHook("useAfterPaint", callback);
    }
    return () => {
    };
  }, deps);
};
const useAtlasImage = (children, { onCreated, resetWorldOnChange = true, mode = "explore", cover, containerRef, ...restProps }) => {
  const [ready, setReady] = useState(false);
  const [imageUrl, setImageUrl] = useState(void 0);
  const [imageError, setError] = useState(void 0);
  const canvasRef = useRef();
  const overlayRef = useRef();
  const bounds = useMemo(() => {
    return {
      width: restProps.width,
      height: restProps.height
    };
  }, [restProps.width, restProps.height]);
  const [presetName, preset, viewport, refs] = usePreset(void 0, {
    width: restProps.width,
    height: restProps.height
  });
  useLayoutEffect$1(() => {
    const $cvs = document.createElement("canvas");
    $cvs.height = bounds.height;
    $cvs.width = bounds.width;
    canvasRef.current = $cvs;
  }, []);
  useLayoutEffect$1(() => {
    const $cvs = canvasRef.current;
    if ($cvs) {
      $cvs.height = bounds.height;
      $cvs.width = bounds.width;
    }
  }, [bounds.width, bounds.height]);
  useEffect(() => {
    if (preset) {
      const runtime = preset.runtime;
      return runtime.registerHook("useAfterFrame", () => {
        if (canvasRef.current) {
          try {
            setImageUrl(canvasRef.current.toDataURL());
          } catch (e2) {
            if (e2 instanceof Error) {
              setError(e2.message);
            }
          }
        }
      });
    }
    return () => {
    };
  }, []);
  useEffect(() => {
    if (preset) {
      const runtime = preset.runtime;
      return runtime.world.addLayoutSubscriber((type) => {
        if (type === "ready") {
          setReady(true);
        }
      });
    }
    return () => {
    };
  }, []);
  useEffect(() => {
    if (preset && preset.em) {
      preset.em.updateBounds();
    }
  }, [bounds]);
  useEffect(() => {
    if (preset) {
      preset.runtime.mode = mode;
    }
  }, [mode]);
  useEffect(() => {
    if (preset) {
      const rt = preset.runtime;
      rt.resize(viewport.current.width, restProps.width, viewport.current.height, restProps.height);
      if (cover) {
        rt.cover();
      } else {
        rt.goHome();
      }
      viewport.current.width = restProps.width;
      viewport.current.height = restProps.height;
      rt.updateNextFrame();
    }
  }, [restProps.width, restProps.height]);
  useLayoutEffect$1(() => {
    const overlay = overlayRef.current;
    if (!overlay)
      return;
    overlay.style.width = `${bounds.width}px`;
    overlay.style.height = `${bounds.height}px`;
    overlay.style.pointerEvents = "none";
    overlay.style.overflow = "hidden";
  }, [bounds.height, bounds.width]);
  useLayoutEffect$1(() => {
    const windowResizeCallback = () => {
      if (preset && preset.runtime) {
        const rt = preset.runtime;
        rt.resize(viewport.current.width, restProps.width, viewport.current.height, restProps.height);
        viewport.current.width = restProps.width;
        viewport.current.height = restProps.height;
        rt.updateNextFrame();
      }
    };
    window.addEventListener("resize", windowResizeCallback);
    return () => window.removeEventListener("resize", windowResizeCallback);
  }, [preset, restProps.height, restProps.width]);
  const Canvas = useCallback(function Canvas2(props2) {
    const activate = () => {
      if (preset) {
        preset.ready = true;
      }
    };
    useEffect(() => {
      if (preset) {
        const result = onCreated && onCreated(preset);
        return void (result && result.then ? result.then(activate) : activate());
      }
      return () => {
      };
    }, []);
    return props2.children;
  }, [preset]);
  useEffect(() => {
    if (preset && preset.runtime) {
      const rt = preset.runtime;
      if (resetWorldOnChange) {
        return rt.world.addLayoutSubscriber((type) => {
          if (type === "recalculate-world-size") {
            rt.goHome({ cover });
          }
        });
      }
    }
    return () => {
    };
  }, [preset, cover, resetWorldOnChange]);
  useLayoutEffect$1(() => {
    if (preset) {
      ReactAtlas.render(/* @__PURE__ */ React__default.createElement(Canvas, null, /* @__PURE__ */ React__default.createElement(ModeContext.Provider, {
        value: mode
      }, /* @__PURE__ */ React__default.createElement(AtlasContext.Provider, {
        value: preset
      }, children))), preset.runtime);
    }
  }, [preset, mode, children]);
  return {
    loading: !imageUrl && ready,
    uri: imageUrl,
    imageError
  };
};
const useBeforeFrame = (callback, deps = []) => {
  const runtime = useRuntime();
  useEffect(() => {
    if (runtime) {
      return runtime.registerHook("useBeforeFrame", callback);
    }
    return () => {
    };
  }, deps);
};
const useControlledAnnotationList = (initialList = []) => {
  const [annotations, setAnnotations] = useState(initialList);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState();
  const addNewAnnotation = useCallback(() => {
    setIsEditing(true);
    setSelectedAnnotation(void 0);
  }, []);
  const editAnnotation = useCallback((id) => {
    setIsEditing(true);
    setSelectedAnnotation(id);
  }, []);
  const onUpdateAnnotation = (newAnno) => {
    setAnnotations((val) => val.map((ann) => {
      if (ann.id === newAnno.id) {
        return newAnno;
      }
      return ann;
    }));
  };
  const onCreateNewAnnotation = useCallback((bounds) => {
    const id = nanoid();
    setAnnotations((a2) => [...a2, { id, ...bounds }]);
    setIsEditing(false);
    setSelectedAnnotation(void 0);
  }, []);
  const onDeselect = useCallback(() => {
    setIsEditing(false);
    setSelectedAnnotation(void 0);
  }, []);
  return {
    isEditing,
    onDeselect,
    selectedAnnotation,
    onCreateNewAnnotation,
    annotations,
    onUpdateAnnotation,
    setIsEditing,
    setSelectedAnnotation,
    editAnnotation,
    addNewAnnotation
  };
};
function canDrag(ref) {
  return ref.current === "sketch";
}
function mergeStyles(defaultStyle, style) {
  if (!defaultStyle) {
    return style;
  }
  if (!style) {
    return defaultStyle;
  }
  return {
    ...defaultStyle,
    ...style || {},
    ":hover": defaultStyle[":hover"] ? Object.assign(defaultStyle[":hover"] || {}, style[":hover"] || {}) : style[":hover"],
    ":active": defaultStyle[":active"] ? Object.assign(defaultStyle[":active"] || {}, style[":active"] || {}) : style[":hover"]
  };
}
export { Atlas, AtlasAuto, AtlasContext, BoundsContext, Box, BrowserEventManager, CanvasRenderer, CompositeRenderer, CompositeResource, DebugRenderer, DrawBox, GridBuilder, HTMLPortal, ImageService, ImageTexture, ModeContext, ModeProvider, OverlayRenderer, ReactAtlas, RegionHighlight, ResizeWorldItem, Runtime, SingleImage, StaticRenderer, Text, TileSet, TiledImage, WebGLRenderer, World, WorldObject, Zone, activateEvents, applyProps, bounceOut, canDrag, createDefaultEventMap, defaultConfig, defaultPreset, easingFunctions, fromImage, getId, getTileFromCanvas, getTileFromImageService, getTiles, getTilesFromManifest, mergeStyles, popmotionController, presets, staticPreset, supportedEventAttributes, supportedEventMap, unmountComponentAtNode, useAfterFrame, useAfterPaint, useAtlas, useAtlasImage, useBeforeFrame, useCanvas, useControlledAnnotationList, useFrame, useMode, useResizeWorldItem, useRuntime, useWorldEvent };
//# sourceMappingURL=index.mjs.map
